import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Search, Upload, CheckCircle, Eye, Edit2, X, ChevronLeft, ChevronRight, ChevronUp, ChevronDown, Sparkles, Database, Shield, ArrowLeft, RefreshCw, Settings, Plus, Trash2, ToggleLeft, ToggleRight, Save, TrendingUp, DollarSign, Activity, Info, Copy, ShoppingBag, Tag, Package, ShoppingCart, Store, Send, Download, ExternalLink, GripVertical, Filter, ImageIcon, Star, Folder, Users, Lock, UserPlus, BarChart2, Zap, AlertTriangle, Key, Calendar, Layers, Ruler, RotateCcw } from 'lucide-react';

// API Base URL
import config from './config';
import notify from './lib/notify';
import OccasionReview from './OccasionReview';
import CategoryReview from './CategoryReview';
import { useConfirm } from './components/ConfirmDialog';
import {
  buildFormModel, requiredKeys, initEnrichmentForm,
  buildEnrichmentPayload, buildComboFormData,
} from './enrichmentForm';
import CombinationTab from './CombinationTab';

// Kushals Design System Colors - Matching Website
const COLORS = {
  // Primary Brand
  maroon: '#8B1D3C',
  gold: '#D4AF37',
  
  // Backgrounds
  bg: '#FFFFFF',
  bgLight: '#F9FAFB',
  
  // Text
  textPrimary: '#111827',
  textSecondary: '#6B7280',
  textLight: '#9CA3AF',
  
  // Borders
  border: '#E5E7EB',
  borderHover: '#D1D5DB',
  
  // Status
  pending: '#F59E0B',
  verified: '#10B981',
  error: '#EF4444',
  errorDark: '#DC2626',
  errorBg: '#FEE2E2',
  errorText: '#991B1B',
};

// ── Inline SVG Sparkline ──────────────────────────────────────────────────────
const Sparkline = ({ data, color = '#D4AF37', height = 36, width = 120 }) => {
  if (!data || data.length < 2) return <span style={{ color: '#9CA3AF', fontSize: 12 }}>No data</span>;
  const values = data.map(d => d.revenue || 0);
  const max = Math.max(...values, 1);
  const min = Math.min(...values, 0);
  const range = max - min || 1;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * width;
    const y = height - ((v - min) / range) * height;
    return `${x},${y}`;
  });
  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: 'block' }}>
      <polyline points={pts.join(' ')} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

// ── Format Currency ───────────────────────────────────────────────────────────
const formatCurrency = (amount, currency = 'INR') => {
  if (!amount && amount !== 0) return '—';
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency, maximumFractionDigits: 0 }).format(amount);
};

// ── Format Big Numbers ────────────────────────────────────────────────────────
const formatNumber = (n) => {
  if (!n && n !== 0) return '—';
  if (n >= 10000000) return `${(n / 10000000).toFixed(1)}Cr`;
  if (n >= 100000) return `${(n / 100000).toFixed(1)}L`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return String(n);
};

// ============================================================================
// TRENDING COMPONENTS - START
// ============================================================================

// Component 1: Trending Badge
const TrendingBadge = ({ score, attributes, isHiddenGem }) => {
  if (!score || score < 80) return null;
  
  const getBadgeColor = () => {
    if (score >= 95) return 'bg-red-600';
    if (score >= 90) return 'bg-orange-600';
    return 'bg-yellow-600';
  };
  
  return (
    <div className="absolute top-2 left-2 z-10">
      <div className={`${getBadgeColor()} text-white px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1 shadow-lg`}>
        🔥 {Math.round(score)}%
        {isHiddenGem && <span title="Hidden Gem">💎</span>}
      </div>
      {attributes && attributes.length > 0 && (
        <div className="bg-black bg-opacity-75 text-white px-2 py-0.5 rounded mt-1 text-[9px]">
          {attributes.slice(0, 2).join(', ')}
        </div>
      )}
    </div>
  );
};

// Component 2: Collection Metrics Card
const CollectionMetricsCard = ({ collectionId }) => {
  const [metrics, setMetrics] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    if (!collectionId) return;
    
    config.apiFetch(config.endpoints.collectionMetrics(collectionId))
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setMetrics(data);
        }
      })
      .catch(err => console.error('Error loading metrics:', err))
      .finally(() => setLoading(false));
  }, [collectionId]);
  
  if (loading) {
    return <div className="text-xs text-gray-400 py-2">Loading metrics...</div>;
  }
  
  if (!metrics) return null;
  
  const formatCurrency = (amount) => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)}Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)}L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)}K`;
    return `₹${amount}`;
  };
  
  const { sales, stock, trending } = metrics;
  
  return (
    <div className="space-y-3 text-xs my-2">
      <div className="border-t pt-2" style={{ borderColor: COLORS.border }}>
        <div className="font-semibold text-[10px] uppercase tracking-wider mb-1" style={{ color: COLORS.textSecondary }}>
          📊 Sales
        </div>
        <div className="grid grid-cols-3 gap-2 text-[10px]">
          <div>
            <div style={{ color: COLORS.textSecondary }}>30d</div>
            <div className="font-bold" style={{ color: COLORS.maroon }}>
              {formatCurrency(sales['30days'].amount)}
            </div>
            <div style={{ color: COLORS.textSecondary }}>{sales['30days'].qty} units</div>
          </div>
          <div>
            <div style={{ color: COLORS.textSecondary }}>60d</div>
            <div className="font-bold" style={{ color: COLORS.maroon }}>
              {formatCurrency(sales['60days'].amount)}
            </div>
            <div style={{ color: COLORS.textSecondary }}>{sales['60days'].qty} units</div>
          </div>
          <div>
            <div style={{ color: COLORS.textSecondary }}>90d</div>
            <div className="font-bold" style={{ color: COLORS.maroon }}>
              {formatCurrency(sales['90days'].amount)}
            </div>
            <div style={{ color: COLORS.textSecondary }}>{sales['90days'].qty} units</div>
          </div>
        </div>
      </div>
      
      <div className="border-t py-2" style={{ borderColor: COLORS.border }}>
        <div className="font-semibold text-[10px] uppercase tracking-wider mb-1" style={{ color: COLORS.textSecondary }}>
          📦 Stock
        </div>
        <div className="flex justify-between text-[10px]">
          <span><span style={{ color: COLORS.textSecondary }}>Qty:</span> <b>{stock.totalQty}</b></span>
          <span><span style={{ color: COLORS.textSecondary }}>Value:</span> <b>{formatCurrency(stock.avgMRP)}</b></span>
        </div>
        <div className="flex justify-between text-[10px]">
          <div>
            <span style={{ color: COLORS.textSecondary }}>PO Pending: </span><b>{stock.poPending}</b>
          </div>
          <div>
            <span style={{ color: COLORS.textSecondary }}>Days Left:</span>
            <span className="font-semibold ml-1" style={{ 
              color: stock.daysRemaining < 30 ? '#EF4444' : 
                    stock.daysRemaining < 90 ? '#F59E0B' : '#10B981'
            }}>
              {stock.daysRemaining === 999 ? '∞' : stock.daysRemaining}
            </span>
          </div>
        </div>
      </div>
      
      {trending.count > 0 && (
        <div className="border-t pt-2" style={{ borderColor: COLORS.border }}>
          <div className="text-[10px] font-semibold flex items-center gap-1" style={{ color: COLORS.maroon }}>
            🔥 {trending.count} Trending
          </div>
          {trending.topAttributes.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {trending.topAttributes.map((attr, idx) => (
                <span key={idx} className="px-2 py-0.5 rounded text-[9px]" style={{ backgroundColor: COLORS.bgLight, color: COLORS.maroon }}>
                  {attr}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Component 3: Trending Section
const TrendingSection = ({ collectionId }) => {
  const [trending, setTrending] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  
  React.useEffect(() => {
    if (!collectionId) return;
    
    config.apiFetch(config.endpoints.collectionTrending(collectionId))
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setTrending(data.trending);
        }
      })
      .catch(err => console.error('Error loading trending:', err))
      .finally(() => setLoading(false));
  }, [collectionId]);
  
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="animate-spin" size={32} style={{ color: COLORS.maroon }} />
      </div>
    );
  }
  
  if (trending.length === 0) return null;
  
  return (
    <div className="mb-8">
      <div className="flex items-center gap-3 mb-6">
        <span className="text-4xl">🔥</span>
        <div>
          <h3 className="font-serif text-2xl font-bold">Trending Designs</h3>
          <p className="text-sm text-gray-500">AI recommended based on sales trends</p>
        </div>
      </div>
      
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {trending.map(design => (
          <div 
            key={design.designNumber}
            className="cursor-pointer bg-white rounded-lg overflow-hidden shadow hover:shadow-xl transition-all border"
            style={{ borderColor: COLORS.border }}
          >
            <div className="relative aspect-[4/5] bg-gray-50">
              <img 
                src={design.imageUrl}
                alt={design.designNumber}
                className="w-full h-full object-cover"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f5f5f5" width="400" height="500"/%3E%3Ctext fill="%23999" font-family="serif" font-size="14" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
              <TrendingBadge 
                score={design.trendScore}
                attributes={design.trendingAttributes}
                isHiddenGem={design.isHiddenGem}
              />
            </div>
            
            <div className="p-3">
              <div className="font-bold text-sm">{design.designNumber}</div>
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>{design.productType}</div>
              <div className="flex justify-between items-center mt-2 text-xs">
                <span className="font-semibold">₹{design.mrp?.toLocaleString()}</span>
                <span style={{ color: COLORS.maroon }} className="font-bold">{Math.round(design.trendScore)}%</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Component: Date Range Filter
const DateRangeFilter = ({ label, currentPreset, startDate, endDate, onFilterChange }) => {
  const [showCustom, setShowCustom] = React.useState(currentPreset === 'custom');
  const [customStart, setCustomStart] = React.useState(startDate || '');
  const [customEnd, setCustomEnd] = React.useState(endDate || '');

  const today = new Date();
  const fmt = (d) => d.toISOString().split('T')[0];
  const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return fmt(d); };
  const quarterStart = () => {
    const m = today.getMonth();
    const qMonth = m - (m % 3);
    return fmt(new Date(today.getFullYear(), qMonth, 1));
  };

  const presets = [
    { key: 'all', label: 'All Time', startDate: null, endDate: null },
    { key: 'today', label: 'Today', startDate: fmt(today), endDate: fmt(today) },
    { key: '7d', label: '7 Days', startDate: daysAgo(7), endDate: fmt(today) },
    { key: '30d', label: '30 Days', startDate: daysAgo(30), endDate: fmt(today) },
    { key: '90d', label: '90 Days', startDate: daysAgo(90), endDate: fmt(today) },
    { key: 'quarter', label: 'Quarter', startDate: quarterStart(), endDate: fmt(today) },
  ];

  const handlePreset = (p) => {
    setShowCustom(false);
    onFilterChange({ preset: p.key, startDate: p.startDate, endDate: p.endDate });
  };

  const handleCustomToggle = () => {
    setShowCustom(!showCustom);
    if (currentPreset !== 'custom') {
      onFilterChange({ preset: 'custom', startDate: customStart || null, endDate: customEnd || null });
    }
  };

  const handleApplyCustom = () => {
    onFilterChange({ preset: 'custom', startDate: customStart || null, endDate: customEnd || null });
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3 flex-wrap">
        <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
          <Calendar size={11} className="inline mr-1" style={{ verticalAlign: '-1px' }} />
          Filters by: {label}
        </span>
        <div className="flex items-center gap-1.5 flex-wrap">
          {presets.map((p) => (
            <button
              key={p.key}
              onClick={() => handlePreset(p)}
              className="px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all"
              style={{
                backgroundColor: currentPreset === p.key ? COLORS.maroon : 'white',
                color: currentPreset === p.key ? 'white' : COLORS.textSecondary,
                border: `1px solid ${currentPreset === p.key ? COLORS.maroon : '#E5E7EB'}`,
                boxShadow: currentPreset === p.key ? '0 1px 3px rgba(139,29,60,0.3)' : 'none',
              }}
            >
              {p.label}
            </button>
          ))}
          <button
            onClick={handleCustomToggle}
            className="px-2.5 py-1 text-[10px] font-semibold rounded-md transition-all"
            style={{
              backgroundColor: currentPreset === 'custom' ? COLORS.maroon : 'white',
              color: currentPreset === 'custom' ? 'white' : COLORS.textSecondary,
              border: `1px solid ${currentPreset === 'custom' ? COLORS.maroon : '#E5E7EB'}`,
              boxShadow: currentPreset === 'custom' ? '0 1px 3px rgba(139,29,60,0.3)' : 'none',
            }}
          >
            Custom
          </button>
        </div>
      </div>
      {showCustom && (
        <div className="flex items-center gap-2 ml-0 pl-0">
          <input
            type="date"
            value={customStart}
            onChange={(e) => setCustomStart(e.target.value)}
            className="px-2 py-1 text-xs border rounded-md focus:outline-none focus:ring-1"
            style={{ borderColor: '#E5E7EB', color: COLORS.textPrimary, focusRingColor: COLORS.maroon }}
          />
          <span className="text-xs" style={{ color: COLORS.textLight }}>to</span>
          <input
            type="date"
            value={customEnd}
            onChange={(e) => setCustomEnd(e.target.value)}
            className="px-2 py-1 text-xs border rounded-md focus:outline-none focus:ring-1"
            style={{ borderColor: '#E5E7EB', color: COLORS.textPrimary }}
          />
          <button
            onClick={handleApplyCustom}
            className="px-3 py-1 text-[10px] font-bold text-white rounded-md transition-all hover:opacity-90"
            style={{ backgroundColor: COLORS.maroon }}
          >
            Apply
          </button>
        </div>
      )}
    </div>
  );
};

// Component 1: Page Size Selector Dropdown
const PageSizeSelector = ({ pageSize, setPageSize, totalItems }) => {
  const options = [10, 25, 50, 100];
  
  return (
    <div className="flex items-center gap-3">
      <label className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
        Show
      </label>
      <select
        value={pageSize === totalItems ? 'all' : pageSize}
        onChange={(e) => {
          const value = e.target.value;
          setPageSize(value === 'all' ? totalItems : parseInt(value));
        }}
        className="px-3 py-2 border rounded-lg text-sm font-medium focus:outline-none focus:ring-2 focus:ring-opacity-50"
        style={{ 
          borderColor: COLORS.border,
          color: COLORS.textPrimary,
          focusRingColor: COLORS.maroon
        }}
      >
        {options.map(option => (
          <option key={option} value={option}>{option}</option>
        ))}
        <option value="all">All</option>
      </select>
      <label className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
        per page
      </label>
    </div>
  );
};

// Component 2: Pagination Controls
const PaginationControls = ({ currentPage, totalPages, totalItems, pageSize, onPageChange }) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);
  
  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      // Show all pages if total is less than max
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show smart pagination
      if (currentPage <= 3) {
        // Near start: 1 2 3 4 5
        for (let i = 1; i <= maxPagesToShow; i++) {
          pages.push(i);
        }
      } else if (currentPage >= totalPages - 2) {
        // Near end: ... N-4 N-3 N-2 N-1 N
        for (let i = totalPages - maxPagesToShow + 1; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // In middle: ... currentPage-2 currentPage-1 currentPage currentPage+1 currentPage+2
        for (let i = currentPage - 2; i <= currentPage + 2; i++) {
          pages.push(i);
        }
      }
    }
    
    return pages;
  };
  
  const pageNumbers = getPageNumbers();
  
  // Hide pagination if all items fit on one page
  if (totalPages <= 1) {
    return (
      <div className="flex justify-center py-6">
        <p className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
          Showing all {totalItems} items
        </p>
      </div>
    );
  }
  
  return (
    <div className="flex flex-col items-center gap-4 py-6">
      {/* Info Text */}
      <p className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>
        Showing {startItem}-{endItem} of {totalItems} items
      </p>
      
      {/* Pagination Buttons */}
      <div className="flex items-center gap-2">
        {/* Previous Button */}
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
          style={{
            backgroundColor: currentPage === 1 ? COLORS.border : COLORS.maroon,
            color: currentPage === 1 ? COLORS.textSecondary : 'white'
          }}
        >
          ← Previous
        </button>
        
        {/* Page Numbers */}
        <div className="flex items-center gap-1">
          {pageNumbers[0] > 1 && (
            <>
              <button
                onClick={() => onPageChange(1)}
                className="w-10 h-10 rounded-lg font-semibold text-sm transition-all hover:bg-gray-100"
                style={{ color: COLORS.textPrimary }}
              >
                1
              </button>
              {pageNumbers[0] > 2 && (
                <span className="px-2" style={{ color: COLORS.textSecondary }}>...</span>
              )}
            </>
          )}
          
          {pageNumbers.map(page => (
            <button
              key={page}
              onClick={() => onPageChange(page)}
              className="w-10 h-10 rounded-lg font-semibold text-sm transition-all"
              style={{
                backgroundColor: currentPage === page ? COLORS.maroon : 'transparent',
                color: currentPage === page ? 'white' : COLORS.textPrimary,
                border: currentPage === page ? 'none' : `1px solid ${COLORS.border}`
              }}
            >
              {page}
            </button>
          ))}
          
          {pageNumbers[pageNumbers.length - 1] < totalPages && (
            <>
              {pageNumbers[pageNumbers.length - 1] < totalPages - 1 && (
                <span className="px-2" style={{ color: COLORS.textSecondary }}>...</span>
              )}
              <button
                onClick={() => onPageChange(totalPages)}
                className="w-10 h-10 rounded-lg font-semibold text-sm transition-all hover:bg-gray-100"
                style={{ color: COLORS.textPrimary }}
              >
                {totalPages}
              </button>
            </>
          )}
        </div>
        
        {/* Next Button */}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-4 py-2 rounded-lg font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80"
          style={{
            backgroundColor: currentPage === totalPages ? COLORS.border : COLORS.maroon,
            color: currentPage === totalPages ? COLORS.textSecondary : 'white'
          }}
        >
          Next →
        </button>
      </div>
    </div>
  );
};

// Component: Design Catalog Filters
const DesignCatalogFilters = ({ 
  itemNameFilters,
  setItemNameFilters,
  productTypeFilters,
  setProductTypeFilters,
  availableFilters,
  setAvailableFilters,
  allFilters,
  setAllFilters,
  onApplyFilters,
  onClearFilters,
  isLoading,
  isLoadingFilters,
  fetchFilterOptions
}) => {
  const [showItemNameDropdown, setShowItemNameDropdown] = useState(false);
  const [showProductTypeDropdown, setShowProductTypeDropdown] = useState(false);
  
  // Refs for click outside detection
  const itemNameRef = useRef(null);
  const productTypeRef = useRef(null);
  
  const hasActiveFilters = itemNameFilters.length > 0 || productTypeFilters.length > 0;
  
  // ============================================================================
  // CLICK OUTSIDE TO CLOSE
  // ============================================================================
  
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (itemNameRef.current && !itemNameRef.current.contains(event.target)) {
        setShowItemNameDropdown(false);
      }
      if (productTypeRef.current && !productTypeRef.current.contains(event.target)) {
        setShowProductTypeDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // ============================================================================
  // FETCH COMPATIBLE OPTIONS (WITHOUT AUTO-REMOVING SELECTIONS)
  // ============================================================================
  
  const fetchCompatibleProductTypes = async (selectedItemNames) => {
    if (selectedItemNames.length === 0) {
      // No items selected, show all types
      setAvailableFilters(prev => ({
        ...prev,
        productTypes: allFilters.productTypes
      }));
      return;
    }
    
    try {
      const params = new URLSearchParams();
      selectedItemNames.forEach(name => {
        params.append('item_name', name);
      });
      
      const response = await config.apiFetch(config.buildUrl('/api/designs/filters') + `?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Update available product types to show compatible ones
        setAvailableFilters(prev => ({
          ...prev,
          productTypes: data.filters.productTypes
        }));
        
        // REMOVED: No longer auto-removing user selections
        // User keeps their selections regardless of compatibility
      }
    } catch (error) {
      console.error('Error fetching compatible types:', error);
    }
  };
  
  const fetchCompatibleItemNames = async (selectedProductTypes) => {
    if (selectedProductTypes.length === 0) {
      // No types selected, show all items
      setAvailableFilters(prev => ({
        ...prev,
        itemNames: allFilters.itemNames
      }));
      return;
    }
    
    try {
      const params = new URLSearchParams();
      selectedProductTypes.forEach(type => {
        params.append('product_type', type);
      });
      
      const response = await config.apiFetch(config.buildUrl('/api/designs/filters') + `?${params}`);
      const data = await response.json();
      
      if (data.success) {
        // Update available item names to show compatible ones
        setAvailableFilters(prev => ({
          ...prev,
          itemNames: data.filters.itemNames
        }));
        
        // REMOVED: No longer auto-removing user selections
        // User keeps their selections regardless of compatibility
      }
    } catch (error) {
      console.error('Error fetching compatible items:', error);
    }
  };
  
  // ============================================================================
  // HANDLE SELECTIONS
  // ============================================================================
  
  const toggleItemName = async (itemName) => {
    let newSelection;
    
    if (itemNameFilters.includes(itemName)) {
      newSelection = itemNameFilters.filter(name => name !== itemName);
    } else {
      newSelection = [...itemNameFilters, itemName];
    }
    
    setItemNameFilters(newSelection);
    
    // Update compatible product types (but don't remove selections)
    await fetchCompatibleProductTypes(newSelection);
  };
  
  const toggleProductType = async (productType) => {
    let newSelection;
    
    if (productTypeFilters.includes(productType)) {
      newSelection = productTypeFilters.filter(type => type !== productType);
    } else {
      newSelection = [...productTypeFilters, productType];
    }
    
    setProductTypeFilters(newSelection);
    
    // Update compatible item names (but don't remove selections)
    await fetchCompatibleItemNames(newSelection);
  };
  
  const selectAllItemNames = async () => {
    console.log('availableFilters:', availableFilters);  // Debug
    console.log('itemNames:', availableFilters?.itemNames);  // Debug
    
    const items = availableFilters?.itemNames || [];
    setItemNameFilters([...items]);
    await fetchCompatibleProductTypes(items);
  };
  
  const deselectAllItemNames = async () => {
    setItemNameFilters([]);
    await fetchCompatibleProductTypes([]);
  };
  
  const selectAllProductTypes = async () => {
    const types = availableFilters?.productTypes || [];  // ✅ Safe
    setProductTypeFilters([...types]);
    await fetchCompatibleItemNames(types);
  };
  
  const deselectAllProductTypes = async () => {
    setProductTypeFilters([]);
    await fetchCompatibleItemNames([]);
  };
  
  // ============================================================================
  // CHECK IF SELECTIONS ARE COMPATIBLE (for visual indication)
  // ============================================================================
  
  const isItemNameInDropdown = (itemName) => {
    return availableFilters?.itemNames?.includes(itemName) || false;  // ✅ Safe
  };
  
  const isProductTypeInDropdown = (productType) => {
    return availableFilters?.productTypes?.includes(productType) || false;  // ✅ Safe
  };
  
  return (
    <div className="bg-white border rounded-lg p-4 mb-6" style={{ borderColor: COLORS.border }}>
      <div className="flex items-center gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
            Filters:
          </span>
        </div>
        
        {/* Item Name Multi-Select */}
        <div className="flex-1 min-w-[250px] relative" ref={itemNameRef}>
          <button
            onClick={() => !isLoadingFilters && setShowItemNameDropdown(!showItemNameDropdown)}
            disabled={isLoadingFilters || isLoading}
            className="w-full px-4 py-2 border rounded-lg text-sm text-left flex items-center justify-between disabled:cursor-not-allowed transition-colors"
            style={{ 
              borderColor: showItemNameDropdown ? COLORS.maroon : COLORS.border,
              color: isLoadingFilters ? '#D1D5DB' : COLORS.textPrimary,
              backgroundColor: isLoadingFilters ? '#F9FAFB' : 'white',
              opacity: isLoadingFilters ? 0.6 : 1
            }}
          >
            <span>
              {isLoadingFilters ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={14} className="animate-spin" />
                  Loading...
                </span>
              ) : itemNameFilters.length === 0 ? (
                'All Item Names'
              ) : (
                `${itemNameFilters.length} item${itemNameFilters.length > 1 ? 's' : ''} selected`
              )}
            </span>
            <ChevronRight 
              size={16} 
              className={`transform transition-transform ${showItemNameDropdown ? 'rotate-90' : ''}`}
              style={{ color: showItemNameDropdown ? COLORS.maroon : COLORS.textSecondary }}
            />
          </button>
          
          {showItemNameDropdown && !isLoadingFilters && (
            <div 
              className="absolute z-50 w-full mt-2 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto"
              style={{ borderColor: COLORS.border }}
            >
              <div className="sticky top-0 bg-gray-50 border-b px-4 py-2 flex justify-between" style={{ borderColor: COLORS.border }}>
                <button
                  onClick={selectAllItemNames}
                  className="text-xs font-semibold hover:opacity-70"
                  style={{ color: COLORS.maroon }}
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllItemNames}
                  className="text-xs font-semibold hover:opacity-70"
                  style={{ color: COLORS.textSecondary }}
                >
                  Clear
                </button>
              </div>
              
              <div className="p-2">
                {(allFilters?.itemNames?.length || 0) === 0 ? (  // ✅ Safe
                  <div className="px-3 py-4 text-center text-sm">
                    No items found
                  </div>
                ) : (
                  (allFilters?.itemNames || []).map((name) => { 
                    const isSelected = itemNameFilters.includes(name);
                    const isInFilteredList = isItemNameInDropdown(name);
                    
                    return (
                      <label
                        key={name}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        style={{
                          opacity: !isInFilteredList && productTypeFilters.length > 0 ? 0.5 : 1
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleItemName(name)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: COLORS.maroon }}
                        />
                        <span className="text-sm flex-1">{name}</span>
                        {!isInFilteredList && productTypeFilters.length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ 
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B'
                          }}>
                            Not compatible
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              
              {productTypeFilters.length > 0 && (
                <div className="sticky bottom-0 bg-blue-50 border-t px-4 py-2 text-xs" style={{ 
                  borderColor: COLORS.border,
                  color: '#1E40AF'
                }}>
                  💡 Showing all items. Compatible ones highlighted.
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Product Type Multi-Select */}
        <div className="flex-1 min-w-[250px] relative" ref={productTypeRef}>
          <button
            onClick={() => !isLoadingFilters && setShowProductTypeDropdown(!showProductTypeDropdown)}
            disabled={isLoadingFilters || isLoading}
            className="w-full px-4 py-2 border rounded-lg text-sm text-left flex items-center justify-between disabled:cursor-not-allowed transition-colors"
            style={{ 
              borderColor: showProductTypeDropdown ? COLORS.maroon : COLORS.border,
              color: isLoadingFilters ? '#D1D5DB' : COLORS.textPrimary,
              backgroundColor: isLoadingFilters ? '#F9FAFB' : 'white',
              opacity: isLoadingFilters ? 0.6 : 1
            }}
          >
            <span>
              {isLoadingFilters ? (
                <span className="flex items-center gap-2">
                  <RefreshCw size={14} className="animate-spin" />
                  Loading...
                </span>
              ) : productTypeFilters.length === 0 ? (
                'All Product Types'
              ) : (
                `${productTypeFilters.length} type${productTypeFilters.length > 1 ? 's' : ''} selected`
              )}
            </span>
            <ChevronRight 
              size={16} 
              className={`transform transition-transform ${showProductTypeDropdown ? 'rotate-90' : ''}`}
              style={{ color: showProductTypeDropdown ? COLORS.maroon : COLORS.textSecondary }}
            />
          </button>
          
          {showProductTypeDropdown && !isLoadingFilters && (
            <div 
              className="absolute z-50 w-full mt-2 bg-white border rounded-lg shadow-lg max-h-80 overflow-y-auto"
              style={{ borderColor: COLORS.border }}
            >
              <div className="sticky top-0 bg-gray-50 border-b px-4 py-2 flex justify-between" style={{ borderColor: COLORS.border }}>
                <button
                  onClick={selectAllProductTypes}
                  className="text-xs font-semibold hover:opacity-70"
                  style={{ color: COLORS.maroon }}
                >
                  Select All
                </button>
                <button
                  onClick={deselectAllProductTypes}
                  className="text-xs font-semibold hover:opacity-70"
                  style={{ color: COLORS.textSecondary }}
                >
                  Clear
                </button>
              </div>
              
              <div className="p-2">
                {(allFilters?.productTypes?.length || 0) === 0 ? (  // ✅ Safe
                  <div className="px-3 py-4 text-center text-sm">
                    No types found
                  </div>
                ) : (
                  (allFilters?.productTypes || []).map((type) => { 
                    const isSelected = productTypeFilters.includes(type);
                    const isInFilteredList = isProductTypeInDropdown(type);
                    
                    return (
                      <label
                        key={type}
                        className="flex items-center gap-3 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer"
                        style={{
                          opacity: !isInFilteredList && itemNameFilters.length > 0 ? 0.5 : 1
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleProductType(type)}
                          className="w-4 h-4 rounded"
                          style={{ accentColor: COLORS.maroon }}
                        />
                        <span className="text-sm flex-1">{type}</span>
                        {!isInFilteredList && itemNameFilters.length > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded" style={{ 
                            backgroundColor: '#FEE2E2',
                            color: '#991B1B'
                          }}>
                            Not compatible
                          </span>
                        )}
                      </label>
                    );
                  })
                )}
              </div>
              
              {itemNameFilters.length > 0 && (
                <div className="sticky bottom-0 bg-blue-50 border-t px-4 py-2 text-xs" style={{ 
                  borderColor: COLORS.border,
                  color: '#1E40AF'
                }}>
                  💡 Showing all types. Compatible ones highlighted.
                </div>
              )}
            </div>
          )}
        </div>
        
        <button
          onClick={onApplyFilters}
          disabled={isLoading || isLoadingFilters || (!hasActiveFilters)}
          className="px-6 py-2 rounded-lg font-semibold text-sm transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ 
            backgroundColor: COLORS.maroon,
            color: 'white'
          }}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin" />
              Loading...
            </span>
          ) : (
            'Apply Filters'
          )}
        </button>
        
        {hasActiveFilters && (
          <button
            onClick={() => {
              onClearFilters();
              setShowItemNameDropdown(false);
              setShowProductTypeDropdown(false);
              setAvailableFilters({
                itemNames: allFilters.itemNames,
                productTypes: allFilters.productTypes
              });
            }}
            disabled={isLoading || isLoadingFilters}
            className="px-6 py-2 border rounded-lg font-semibold text-sm transition-all hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ 
              borderColor: COLORS.border,
              color: COLORS.textSecondary
            }}
          >
            Clear Filters
          </button>
        )}
      </div>
      
      {hasActiveFilters && (
        <div className="mt-3 pt-3 border-t flex items-center gap-2 flex-wrap" style={{ borderColor: COLORS.border }}>
          <span className="text-xs font-semibold uppercase" style={{ color: COLORS.textSecondary }}>
            Active:
          </span>
          
          {itemNameFilters.map((name) => {
            const isCompatible = isItemNameInDropdown(name) || productTypeFilters.length === 0;
            return (
              <span 
                key={name}
                className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-2"
                style={{ 
                  backgroundColor: isCompatible ? '#FEF3C7' : '#FEE2E2',
                  color: isCompatible ? '#92400E' : '#991B1B'
                }}
              >
                {name}
                {!isCompatible && <span title="May not match selected types">⚠️</span>}
                <button
                  onClick={() => toggleItemName(name)}
                  className="hover:opacity-70"
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
          
          {productTypeFilters.map((type) => {
            const isCompatible = isProductTypeInDropdown(type) || itemNameFilters.length === 0;
            return (
              <span 
                key={type}
                className="px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-2"
                style={{ 
                  backgroundColor: isCompatible ? '#DBEAFE' : '#FEE2E2',
                  color: isCompatible ? '#1E40AF' : '#991B1B'
                }}
              >
                {type}
                {!isCompatible && <span title="May not match selected items">⚠️</span>}
                <button
                  onClick={() => toggleProductType(type)}
                  className="hover:opacity-70"
                >
                  <X size={14} />
                </button>
              </span>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// TRENDING COMPONENTS - END
// ============================================================================

// ============================================================================
// LOGIN PAGE
// ============================================================================

const LoginPage = ({ onLogin, sessionExpired = false }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      const response = await fetch(config.endpoints.authLogin, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });
      const data = await response.json();
      if (data.success) {
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('authUser', JSON.stringify(data.user));
        onLogin(data.user);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Connection error. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#F9FAFB' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-10">
          <div className="w-16 h-16 rounded-2xl flex items-center justify-center text-white font-bold text-2xl mx-auto mb-4" style={{ backgroundColor: COLORS.maroon }}>
            K
          </div>
          <h1 className="font-serif text-3xl font-bold tracking-tight" style={{ color: COLORS.textPrimary }}>
            Kushals
          </h1>
          <p className="text-xs tracking-widest uppercase font-semibold mt-1" style={{ color: COLORS.maroon }}>
            Fashion Jewellery
          </p>
          <p className="text-[10px] tracking-[0.3em] uppercase mt-2" style={{ color: COLORS.textSecondary }}>
            AI Attribute Manager
          </p>
        </div>
        <div className="bg-white rounded-2xl shadow-lg border p-8" style={{ borderColor: COLORS.border }}>
          <h2 className="text-lg font-semibold mb-6 text-center" style={{ color: COLORS.textPrimary }}>
            Sign In
          </h2>
          {sessionExpired && !error && (
            <div className="mb-4 p-3 rounded-lg text-sm text-amber-800 bg-amber-50 border border-amber-200">
              Your session expired. Please sign in again — your data is safe.
            </div>
          )}
          {error && (
            <div className="mb-4 p-3 rounded-lg text-sm text-red-700 bg-red-50 border border-red-200">
              {error}
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: COLORS.textSecondary }}>
                Username
              </label>
              <input
                type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: COLORS.border }}
                placeholder="Enter username" autoFocus required
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: COLORS.textSecondary }}>
                Password
              </label>
              <input
                type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: COLORS.border }}
                placeholder="Enter password" required
              />
            </div>
            <button
              type="submit" disabled={isLoading}
              className="w-full py-3 text-white text-sm font-semibold tracking-wider uppercase rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {isLoading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>
        </div>
        <p className="text-center mt-6 text-[10px] tracking-widest uppercase" style={{ color: COLORS.textLight }}>
          Kushals Fashion Jewellery
        </p>
      </div>
    </div>
  );
};

const App = () => {
  // Authentication state
  const [authUser, setAuthUser] = useState(() => {
    const saved = localStorage.getItem('authUser');
    return saved ? JSON.parse(saved) : null;
  });
  const [authChecked, setAuthChecked] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [currentPage, setCurrentPage] = useState('generate');
  const [generateItems, setGenerateItems] = useState([]);
  const [pendingItems, setPendingItems] = useState([]);
  const [pendingTotalItems, setPendingTotalItems] = useState(0);
  // Pagination state declared early so useEffect deps below can reference it without TDZ
  const [pendingPageSize, setPendingPageSize] = useState(25);
  const [pendingCurrentPage, setPendingCurrentPage] = useState(1);
  const [verifiedItems, setVerifiedItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [manualInput, setManualInput] = useState('');
  const [selectedDesign, setSelectedDesign] = useState(null);
  const [attributes, setAttributes] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [retryingDesign, setRetryingDesign] = useState(null);
  const [movingDesign, setMovingDesign] = useState(null);
  const confirm = useConfirm();
  const [isLoading, setIsLoading] = useState(false);
  const [currentJobId, setCurrentJobId] = useState(null);
  const [generationProgress, setGenerationProgress] = useState(null);
  
  // Attribute configuration state
  const [attributesConfig, setAttributesConfig] = useState({});
  const [analyticsData, setAnalyticsData] = useState(null);
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  
  // Analytics filters
  const [analyticsDateFilter, setAnalyticsDateFilter] = useState('');
  const [analyticsLimitFilter, setAnalyticsLimitFilter] = useState(50);
  
  // Collections state
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [showCollectionModal, setShowCollectionModal] = useState(false);
  const [showCollectionDetail, setShowCollectionDetail] = useState(false);
  const [collectionDetailData, setCollectionDetailData] = useState(null);
  const [showCollectionDesigns, setShowCollectionDesigns] = useState(false);
  const [collectionDesigns, setCollectionDesigns] = useState([]);
  const [attributeValuesForFilters, setAttributeValuesForFilters] = useState({});
  const [showSubCollectionModal, setShowSubCollectionModal] = useState(false);

  // Channel platforms state
  const [platforms, setPlatforms] = useState([]);
  const [channelsExpanded, setChannelsExpanded] = useState(false);
  const [platformDesigns, setPlatformDesigns] = useState([]);
  const [platformConfig, setPlatformConfig] = useState(null);
  const [showPushModal, setShowPushModal] = useState(false);
  const [showAddDesignsModal, setShowAddDesignsModal] = useState(false);
  const [showPlatformConfigModal, setShowPlatformConfigModal] = useState(false);
  const [showPlatformFieldEditor, setShowPlatformFieldEditor] = useState(false);
  const [showAddCustomMf, setShowAddCustomMf] = useState(false);
  const [selectedMfForDelete, setSelectedMfForDelete] = useState(new Set());
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [customMfForm, setCustomMfForm] = useState({ name: '', namespace: 'kushals', key: '', defaultValue: '' });
  const [selectedDesignsForPush, setSelectedDesignsForPush] = useState([]);
  const [selectedPlatformsForPush, setSelectedPlatformsForPush] = useState([]);
  const [editingPlatformDesign, setEditingPlatformDesign] = useState(null);
  const [editingVariants, setEditingVariants] = useState(null); // { variantOptions, variants, totalVariants }
  const [platformStatusFilter, setPlatformStatusFilter] = useState('draft');
  const [pendingDateFilter, setPendingDateFilter] = useState({ preset: 'all', startDate: null, endDate: null });
  const [verifiedDateFilter, setVerifiedDateFilter] = useState({ preset: 'all', startDate: null, endDate: null });
  const [channelDateFilter, setChannelDateFilter] = useState({ preset: 'all', startDate: null, endDate: null });
  const [showPlatformSettings, setShowPlatformSettings] = useState(false);
  const [platformCredentials, setPlatformCredentials] = useState({});
  const [connectionStatus, setConnectionStatus] = useState(null); // { success, message, details }
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [isSavingCredentials, setIsSavingCredentials] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [publishingDesign, setPublishingDesign] = useState(null);
  const [isPushing, setIsPushing] = useState(false);
  const [isBulkRemoving, setIsBulkRemoving] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageModalDesign, setImageModalDesign] = useState(null);
  const [designShootImages, setDesignShootImages] = useState([]);
  const [selectedPrimaryImage, setSelectedPrimaryImage] = useState(null); // image id or 'ai'
  const [selectedGalleryImages, setSelectedGalleryImages] = useState([]); // array of image ids or 'ai'
  const [variantImageMap, setVariantImageMap] = useState({}); // { combinationId: imageId }
  const [isSavingImages, setIsSavingImages] = useState(false);
  const [selectedCardDesigns, setSelectedCardDesigns] = useState(new Set());

  // Batch history state (replaces external designs / design catalog)
  const [batchHistory, setBatchHistory] = useState([]);
  const [batchHistoryTotal, setBatchHistoryTotal] = useState(0);
  const [batchHistoryLoading, setBatchHistoryLoading] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null); // 'sourceApp|date'
  const [batchDesigns, setBatchDesigns] = useState([]);
  const [batchDesignsLoading, setBatchDesignsLoading] = useState(false);

  // Legacy external designs state (kept for API compatibility)
  const [catalogTab, setCatalogTab] = useState('erp'); // 'erp' or 'external'
  const [externalDesigns, setExternalDesigns] = useState([]);
  const [externalDesignsTotal, setExternalDesignsTotal] = useState(0);
  const [externalDesignsPage, setExternalDesignsPage] = useState(1);
  const [externalDesignsLoading, setExternalDesignsLoading] = useState(false);
  const [selectedExternalDesigns, setSelectedExternalDesigns] = useState([]);

  // Enrichment page state
  // Inbox model: this page only ever shows items needing enrichment
  const enrichmentFilter = 'needs_enrichment';
  const [enrichmentSearch, setEnrichmentSearch] = useState('');
  const [enrichmentSearchInput, setEnrichmentSearchInput] = useState('');
  const [enrichmentDesigns, setEnrichmentDesigns] = useState([]);
  // Global counts powering the sidebar badge
  const [enrichmentCounts, setEnrichmentCounts] = useState({ needsEnrichment: 0, complete: 0, total: 0 });
  // New modal-based enrichment flow (Phase G — replaces enrichmentView='edit')
  const [selectedEnrichmentDesignNumber, setSelectedEnrichmentDesignNumber] = useState(null);
  // Opens the enrichment editor (product/core/combination) for a design from the
  // Verification Queue. Separate from selectedEnrichmentDesignNumber so its save
  // refreshes the pending list (not the enrichment list) and navigation is disabled.
  const [verifyEditDesignNumber, setVerifyEditDesignNumber] = useState(null);
  const [coreAttributes, setCoreAttributes] = useState({});
  const [coreCategories, setCoreCategories] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const [uploading, setUploading] = useState(false);
  // Bulk update template (sibling to import)
  const [updateFile, setUpdateFile] = useState(null);
  const [updateResult, setUpdateResult] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [enrichmentSourceTab, setEnrichmentSourceTab] = useState('all');
  // Pagination state for enrichment list
  const [enrichmentPage, setEnrichmentPage] = useState(1);
  const [enrichmentPerPage, setEnrichmentPerPage] = useState(100);
  const [enrichmentTotal, setEnrichmentTotal] = useState(0);
  const [enrichmentLoading, setEnrichmentLoading] = useState(false);
  // Brand / Category / Product Type filters (values sourced from attributes config)
  const [enrichmentFilterBrand, setEnrichmentFilterBrand] = useState('');
  const [enrichmentFilterCategory, setEnrichmentFilterCategory] = useState('');
  const [enrichmentFilterProductType, setEnrichmentFilterProductType] = useState('');
  const [enrichmentFilterPoNumber, setEnrichmentFilterPoNumber] = useState('');
  // Cascading filter options — recomputed by the backend from the current selection
  const [enrichmentFacets, setEnrichmentFacets] = useState({ brands: [], categories: [], productTypes: [], poNumbers: [] });

  // Export templates dropdown state
  const [exportTemplates, setExportTemplates] = useState([]);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const exportDropdownRef = useRef(null);

  // Channel collections state
  const [channelCollections, setChannelCollections] = useState([]);
  const [showCollectionsTab, setShowCollectionsTab] = useState(false);
  const [showCreateCollectionModal, setShowCreateCollectionModal] = useState(false);
  const [showEditCollectionModal, setShowEditCollectionModal] = useState(false);
  const [editingCollection, setEditingCollection] = useState(null);
  const [collectionName, setCollectionName] = useState('');
  const [collectionDescription, setCollectionDescription] = useState('');
  const [collectionTags, setCollectionTags] = useState([]);
  const [collectionTagInput, setCollectionTagInput] = useState('');
  const [showCollectionDesignPicker, setShowCollectionDesignPicker] = useState(false);
  const [pickerTargetCollection, setPickerTargetCollection] = useState(null);
  const [collectionDesignNumbers, setCollectionDesignNumbers] = useState([]);
  const [isPublishingCollection, setIsPublishingCollection] = useState(false);
  const [publishingCollectionId, setPublishingCollectionId] = useState(null);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSelected, setPickerSelected] = useState(new Set());
  const [pickerFilters, setPickerFilters] = useState([]);
  const [pickerExpandedFilter, setPickerExpandedFilter] = useState(null);
  const [pickerFilterSearchTerms, setPickerFilterSearchTerms] = useState({});

  // ── Shopify Products Dashboard State ─────────────────────────────────────────
  const [shopifyProductsTab, setShopifyProductsTab] = useState(false);
  const [shopifyProducts, setShopifyProducts] = useState([]);
  const [shopifyProductsLoading, setShopifyProductsLoading] = useState(false);
  const [shopifyProductsPagination, setShopifyProductsPagination] = useState({ hasNextPage: false, endCursor: null });
  const [shopifyProductsTotal, setShopifyProductsTotal] = useState(0);
  const [shopifyProductsFilter, setShopifyProductsFilter] = useState('ACTIVE');
  const [shopifyProductsSearch, setShopifyProductsSearch] = useState('');
  const [shopifyProductsAnalytics, setShopifyProductsAnalytics] = useState({});
  const [shopifyAnalyticsSummary, setShopifyAnalyticsSummary] = useState(null);
  const [shopifyAnalyticsDays, setShopifyAnalyticsDays] = useState(30);
  const [shopifyAnalyticsLoading, setShopifyAnalyticsLoading] = useState(false);
  const [shopifySelectedProduct, setShopifySelectedProduct] = useState(null);
  const [shopifyTrends, setShopifyTrends] = useState([]);
  const [shopifyTrendsLoading, setShopifyTrendsLoading] = useState(false);
  const [shopifyAnalyticsScopeError, setShopifyAnalyticsScopeError] = useState(false);
  const [shopifyPageSize, setShopifyPageSize] = useState(25);
  const [shopifyCurrentPage, setShopifyCurrentPage] = useState(1);

  const [itemNameFilters, setItemNameFilters] = useState([]);
  const [productTypeFilters, setProductTypeFilters] = useState([]);
  const [isLoadingFilters, setIsLoadingFilters] = useState(true);
  const [availableFilters, setAvailableFilters] = useState({
    itemNames: [],
    productTypes: []
  });
  const [allFilters, setAllFilters] = useState({
    itemNames: [],
    productTypes: []
  });

  // Verify token on mount
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      setAuthChecked(true);
      return;
    }
    config.apiFetch(config.endpoints.authMe)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setAuthUser(data.user);
          localStorage.setItem('authUser', JSON.stringify(data.user));
        } else {
          setAuthUser(null);
          localStorage.removeItem('authToken');
          localStorage.removeItem('authUser');
        }
      })
      .catch(() => {
        // A failed/non-JSON verify (e.g. expired token returning a 401 page)
        // must not leave the optimistic localStorage user in place, or the app
        // renders with a dead token and flashes a blank page.
        setAuthUser(null);
        localStorage.removeItem('authToken');
        localStorage.removeItem('authUser');
      })
      .finally(() => setAuthChecked(true));
  }, []);

  // Redirect to the login screen when any API call reports an expired/invalid
  // session (401). apiFetch already cleared the stored credentials and fired
  // this event; here we flip React state so LoginPage renders without a hard
  // page reload (which previously looked like "data gone").
  useEffect(() => {
    const handleSessionExpired = () => {
      setAuthUser(null);
      setAuthChecked(true);
      setSessionExpired(true);
      setCurrentPage('generate');
    };
    window.addEventListener('auth:session-expired', handleSessionExpired);
    return () => window.removeEventListener('auth:session-expired', handleSessionExpired);
  }, []);

  // Fetch initial data when authenticated (must wait for authChecked to avoid race condition)
  useEffect(() => {
    if (!authChecked || !authUser) return;
    fetchDesigns();
    fetchPending();
    fetchVerified();
    fetchAttributesConfig();
    fetchPlatforms();
    fetchFilterOptions();
  }, [authUser, authChecked]);

  // Fetch analytics when analytics page is opened
  useEffect(() => {
    if (currentPage === 'analytics') {
      fetchAnalytics();
    }
  }, [currentPage]);

  // Fetch collections when collections page is opened
  useEffect(() => {
    if (currentPage === 'collections') {
      fetchCollections();
      fetchAttributeValuesForFilters();
    }
  }, [currentPage]);

  // Fetch platform data when channel page is opened
  useEffect(() => {
    if (currentPage.startsWith('channel_')) {
      const platformId = currentPage.replace('channel_', '');
      // Clear stale data immediately to prevent showing previous channel's designs
      setPlatformDesigns([]);
      setSelectedCardDesigns(new Set());
      setPlatformStatusFilter('draft');
      setChannelDateFilter({ preset: 'all', startDate: null, endDate: null });
      setShowCollectionsTab(false);
      setShowExportDropdown(false);
      // Then fetch fresh data for this channel
      fetchPlatformDesigns(platformId);
      fetchPlatformConfig(platformId);
      fetchChannelCollections(platformId);
      // Fetch available export templates for this platform
      config.apiFetch(config.endpoints.channelExportTemplates(platformId))
        .then(r => r.json())
        .then(data => {
          if (data.success) setExportTemplates(data.templates);
          else setExportTemplates([{ key: 'generic', label: 'Generic Export', description: 'All fields, one row per variant', extension: 'csv' }]);
        })
        .catch(() => setExportTemplates([{ key: 'generic', label: 'Generic Export', description: 'All fields, one row per variant', extension: 'csv' }]));
    }
  }, [currentPage]);

  // Auto-fetch Shopify products when tab opens
  useEffect(() => {
    if (shopifyProductsTab && shopifyProducts.length === 0) {
      const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
      fetchShopifyProducts(shopifyPlatformId);
    }
  }, [shopifyProductsTab]);

  // Re-fetch when date filters change
  useEffect(() => {
    if (currentPage === 'pending') fetchPending();
    setPendingCurrentPage(1);
  }, [pendingDateFilter]);

  // Re-fetch when pagination changes (server-side pagination)
  useEffect(() => {
    if (currentPage === 'pending') fetchPending();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCurrentPage, pendingPageSize]);

  useEffect(() => {
    if (currentPage === 'verified') fetchVerified();
    setVerifiedCurrentPage(1);
  }, [verifiedDateFilter]);

  useEffect(() => {
    if (currentPage.startsWith('channel_')) {
      const platformId = currentPage.replace('channel_', '');
      fetchPlatformDesigns(platformId);
    }
  }, [channelDateFilter]);

  // Auto-fetch batch history when navigating to generate page
  useEffect(() => {
    if (authChecked && authUser && currentPage === 'generate') fetchBatchHistory();
  }, [currentPage, authChecked, authUser]);

  // Fetch enrichment data when enrichment page is opened or any query-param changes
  useEffect(() => {
    if (currentPage === 'enrichment') {
      fetchCoreAttributes();
      // Attribute config powers core attribute editing (full configured vocabulary)
      fetchAttributesConfig();
      // Global sidebar count — only on page open (NOT on tab change)
      fetchEnrichmentCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  // Cascading filter options — refetched whenever the page, source tab, or any
  // selected filter changes, so picking one facet narrows the other three.
  useEffect(() => {
    if (currentPage === 'enrichment') {
      fetchFacets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, enrichmentSourceTab,
      enrichmentFilterBrand, enrichmentFilterCategory, enrichmentFilterProductType, enrichmentFilterPoNumber]);

  // Paginated list — refetched on any query-param change
  useEffect(() => {
    if (currentPage === 'enrichment') {
      fetchEnrichmentDesigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, enrichmentSourceTab, enrichmentPage, enrichmentPerPage, enrichmentSearch,
      enrichmentFilterBrand, enrichmentFilterCategory, enrichmentFilterProductType, enrichmentFilterPoNumber]);

  // Reset to page 1 whenever the source tab, search, or any filter changes
  useEffect(() => {
    setEnrichmentPage(1);
  }, [enrichmentSourceTab, enrichmentSearch,
      enrichmentFilterBrand, enrichmentFilterCategory, enrichmentFilterProductType, enrichmentFilterPoNumber]);

  // Debounce the search input (300ms) so we don't hit the API on every keystroke
  useEffect(() => {
    const handle = setTimeout(() => {
      setEnrichmentSearch(enrichmentSearchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [enrichmentSearchInput]);

  // Reset Shopify catalog tab when switching away from Shopify channel
  useEffect(() => {
    if (!currentPage.startsWith('channel_shopify')) {
      setShopifyProductsTab(false);
    }
  }, [currentPage]);

  // Close export dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(e.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Poll generation status
  useEffect(() => {
    let interval;
    if (currentJobId && isGenerating) {
      interval = setInterval(() => {
        checkGenerationStatus(currentJobId);
      }, 2000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentJobId, isGenerating]);

  // Fetch available filter options
  const fetchFilterOptions = async () => {
    setIsLoadingFilters(true);
    try {
      const response = await config.apiFetch(config.buildUrl('/api/designs/filters'));
      const data = await response.json();
      if (data.success && data.filters) {  // ✅ Check data.filters exists
        // Store in BOTH availableFilters and allFilters
        setAvailableFilters({
          itemNames: data.filters.itemNames || [],      // ✅ Safe default
          productTypes: data.filters.productTypes || [] // ✅ Safe default
        });
        setAllFilters({
          itemNames: data.filters.itemNames || [],      // ✅ Safe default
          productTypes: data.filters.productTypes || [] // ✅ Safe default
        });
      }
    } catch (error) {
      console.error('Error fetching filter options:', error);
      // ✅ Set safe defaults on error
      setAvailableFilters({ itemNames: [], productTypes: [] });
      setAllFilters({ itemNames: [], productTypes: [] });
    } finally {
      setIsLoadingFilters(false);
    }
  };

  // Fetch designs with filters
  const fetchDesignsWithFilters = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Add multiple item names
      if (itemNameFilters.length > 0) {
        itemNameFilters.forEach(name => {
          params.append('item_name', name);  // Multiple params with same key
        });
      }
      
      // Add multiple product types
      if (productTypeFilters.length > 0) {
        productTypeFilters.forEach(type => {
          params.append('product_type', type);  // Multiple params with same key
        });
      }
      
      const queryString = params.toString();
      const url = config.buildUrl('/api/designs') + `${queryString ? '?' + queryString : ''}`;
      
      const response = await config.apiFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setGenerateItems(data.designs);
      } else {
        alert('Failed to fetch designs: ' + data.error);
      }
    } catch (error) {
      console.error('Error fetching filtered designs:', error);
      alert('Failed to fetch designs');
    } finally {
      setIsLoading(false);
    }
  };

  // Apply filters
  const handleApplyFilters = () => {
    fetchDesignsWithFilters();
    setGenerateCurrentPage(1); // Reset to page 1 when filtering
    fetchDesigns(); 
  };

  // Clear all filters
  const handleClearFilters = () => {
    setItemNameFilters([]);      // Changed from ''
    setProductTypeFilters([]);   // Changed from ''
    fetchDesigns();
    setGenerateCurrentPage(1);
  };

  const fetchAttributesConfig = async () => {
    setIsConfigLoading(true);
    try {
      const response = await config.apiFetch(config.endpoints.attributesConfig);
      const data = await response.json();
      if (data.success) {
        setAttributesConfig(data.data);
      }
    } catch (error) {
      console.error('Error fetching attributes config:', error);
    } finally {
      setIsConfigLoading(false);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await config.apiFetch(config.buildUrl("/api/analytics"));
      const data = await response.json();
      if (data.success) {
        setAnalyticsData(data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await config.apiFetch(config.endpoints.collections);
      const data = await response.json();
      if (data.success) {
        setCollections(data.data);
      }
    } catch (error) {
      console.error('Error fetching collections:', error);
    }
  };

  const fetchAttributeValuesForFilters = async () => {
    try {
      const response = await config.apiFetch(config.endpoints.collectionsAttributesValues);
      const data = await response.json();
      if (data.success) {
        setAttributeValuesForFilters(data.data);
      }
    } catch (error) {
      console.error('Error fetching attribute values:', error);
    }
  };

  const fetchCollectionDetail = async (collectionId) => {
    try {
      const response = await config.apiFetch(config.endpoints.collections + '/' + collectionId);
      const data = await response.json();
      if (data.success) {
        setCollectionDetailData(data.data);
        setShowCollectionDetail(true);
      }
    } catch (error) {
      console.error('Error fetching collection details:', error);
    }
  };

  const fetchCollectionDesigns = async (collectionId) => {
    try {
      const response = await config.apiFetch(config.endpoints.collections + '/' + collectionId);
      const data = await response.json();
      if (data.success) {
        setCollectionDesigns(data.data.designs);
        setShowCollectionDesigns(true);
      }
    } catch (error) {
      console.error('Error fetching collection designs:', error);
    }
  };

  // Authentication handlers
  const handleLogin = (user) => {
    setAuthUser(user);
    setAuthChecked(true);
    setSessionExpired(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setAuthUser(null);
    setSessionExpired(false);
    setCurrentPage('generate');
  };

  const userHasRole = (...roles) => {
    return authUser && roles.includes(authUser.role);
  };

  const fetchDesigns = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      
      // Use YOUR filter state variables
      if (itemNameFilters.length > 0) {
        itemNameFilters.forEach(name => {
          params.append('item_name', name);
        });
      }
      
      if (productTypeFilters.length > 0) {
        productTypeFilters.forEach(type => {
          params.append('product_type', type);
        });
      }
      
      const queryString = params.toString();
      const url = queryString 
        ? `${config.endpoints.designs}?${queryString}`
        : config.endpoints.designs;
      
      const response = await config.apiFetch(url);
      const data = await response.json();
      
      if (data.success) {
        setGenerateItems(data.designs || []);
      }
    } catch (error) {
      console.error('Error fetching designs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchExternalDesigns = async (page = 1) => {
    setExternalDesignsLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50, status: 'active' });
      const response = await config.apiFetch(config.buildUrl(`/api/external-designs?${params}`));
      const data = await response.json();
      if (data.success) {
        setExternalDesigns(data.designs || []);
        setExternalDesignsTotal(data.pagination?.totalItems || 0);
        setExternalDesignsPage(page);
      }
    } catch (error) {
      console.error('Error fetching external designs:', error);
    } finally {
      setExternalDesignsLoading(false);
    }
  };

  const fetchBatchHistory = async () => {
    setBatchHistoryLoading(true);
    try {
      const response = await config.apiFetch(config.buildUrl('/api/batch-history'));
      const data = await response.json();
      if (data.success) {
        setBatchHistory(data.batches || []);
        setBatchHistoryTotal(data.pagination?.totalItems || 0);
      }
    } catch (err) {
      console.error('Error fetching batch history:', err);
    } finally {
      setBatchHistoryLoading(false);
    }
  };

  const fetchCoreAttributes = async () => {
    try {
      const resp = await config.apiFetch(config.endpoints.coreAttributes);
      if (resp.ok) {
        const data = await resp.json();
        if (data.success) {
          setCoreAttributes(data.attributes || {});
          setCoreCategories(data.categories || {});
        }
      }
    } catch (err) {
      console.error('Failed to fetch core attributes:', err);
    }
  };

  // Global counts — powers the sidebar badge. Never scoped by tab.
  const fetchEnrichmentCounts = async () => {
    try {
      const resp = await config.apiFetch(config.endpoints.enrichmentCounts);
      if (resp.ok) {
        const data = await resp.json();
        setEnrichmentCounts(data);
      }
    } catch (err) {
      console.error('Failed to fetch enrichment counts:', err);
    }
  };

  const fetchFacets = async () => {
    try {
      const params = new URLSearchParams();
      params.set('enrichment_status', 'needs_enrichment');
      if (enrichmentSourceTab && enrichmentSourceTab !== 'all') {
        params.set('source_type', enrichmentSourceTab);
      }
      // Each facet excludes its own selection server-side, so passing all
      // current selections makes every filter narrow the others.
      if (enrichmentFilterBrand) params.set('brand', enrichmentFilterBrand);
      if (enrichmentFilterCategory) params.set('category', enrichmentFilterCategory);
      if (enrichmentFilterProductType) params.set('product_type', enrichmentFilterProductType);
      if (enrichmentFilterPoNumber) params.set('po_number', enrichmentFilterPoNumber);
      const resp = await config.apiFetch(`${config.endpoints.enrichmentFacets}?${params}`);
      if (resp.ok) {
        const data = await resp.json();
        setEnrichmentFacets({
          brands: data.brands || [],
          categories: data.categories || [],
          productTypes: data.productTypes || [],
          poNumbers: data.poNumbers || [],
        });
      }
    } catch (err) {
      console.error('Failed to fetch enrichment facets:', err);
    }
  };

  const fetchEnrichmentDesigns = async () => {
    try {
      setEnrichmentLoading(true);
      const params = new URLSearchParams();
      params.set('page', String(enrichmentPage));
      params.set('per_page', String(enrichmentPerPage));
      // Inbox lockdown: this page only ever fetches needs_enrichment items
      params.set('enrichment_status', 'needs_enrichment');
      if (enrichmentSourceTab && enrichmentSourceTab !== 'all') {
        params.set('source_type', enrichmentSourceTab);
      }
      if (enrichmentSearch) {
        params.set('search', enrichmentSearch);
      }
      if (enrichmentFilterBrand) params.set('brand', enrichmentFilterBrand);
      if (enrichmentFilterCategory) params.set('category', enrichmentFilterCategory);
      if (enrichmentFilterProductType) params.set('product_type', enrichmentFilterProductType);
      if (enrichmentFilterPoNumber) params.set('po_number', enrichmentFilterPoNumber);
      const url = `${config.endpoints.externalDesignsUI}?${params}`;
      const resp = await config.apiFetch(url);
      if (resp.ok) {
        const data = await resp.json();
        setEnrichmentDesigns(data.designs || []);
        setEnrichmentTotal(data.total || 0);
      }
    } catch (err) {
      console.error('Failed to fetch enrichment designs:', err);
    } finally {
      setEnrichmentLoading(false);
    }
  };

  const fetchBatchDesigns = async (sourceApp, batchDate) => {
    const key = `${sourceApp}|${batchDate}`;
    if (expandedBatch === key) {
      setExpandedBatch(null);
      setBatchDesigns([]);
      return;
    }
    setExpandedBatch(key);
    setBatchDesignsLoading(true);
    try {
      const response = await config.apiFetch(config.buildUrl(`/api/batch-history/${encodeURIComponent(sourceApp)}/${batchDate}/designs`));
      const data = await response.json();
      if (data.success) setBatchDesigns(data.designs || []);
    } catch (err) {
      console.error('Error fetching batch designs:', err);
    } finally {
      setBatchDesignsLoading(false);
    }
  };

  const handleImportExternalDesign = async (designNumber) => {
    try {
      // Add to generate items so it can go through AI processing
      const design = externalDesigns.find(d => d.designNumber === designNumber);
      if (!design) return;

      const importItem = {
        designNumber: design.designNumber,
        itemName: design.itemName || '',
        productType: design.productType,
        baseMaterial: design.baseMaterial,
        imageUrl: design.imageUrl || '',
        source: 'external'
      };

      // Add to generate items if not already there
      setGenerateItems(prev => {
        if (prev.some(item => item.designNumber === importItem.designNumber)) {
          return prev;
        }
        return [...prev, importItem];
      });

      // Switch to ERP tab to show the imported design
      setCatalogTab('erp');
    } catch (error) {
      console.error('Error importing external design:', error);
      alert('Failed to import design');
    }
  };

  const handleImportSelectedExternal = () => {
    if (selectedExternalDesigns.length === 0) return;

    const importItems = externalDesigns
      .filter(d => selectedExternalDesigns.includes(d.designNumber))
      .map(d => ({
        designNumber: d.designNumber,
        itemName: d.itemName || '',
        productType: d.productType,
        baseMaterial: d.baseMaterial,
        imageUrl: d.imageUrl || '',
        source: 'external'
      }));

    setGenerateItems(prev => {
      const existingNos = new Set(prev.map(item => item.designNumber));
      const newItems = importItems.filter(item => !existingNos.has(item.designNumber));
      return [...prev, ...newItems];
    });

    setSelectedExternalDesigns([]);
    setCatalogTab('erp');
  };

  const fetchPending = async () => {
    try {
      const pendingParams = [
        `page=${pendingCurrentPage}`,
        `per_page=${pendingPageSize}`,
      ];
      if (pendingDateFilter.startDate) pendingParams.push(`start_date=${pendingDateFilter.startDate}`);
      if (pendingDateFilter.endDate) pendingParams.push(`end_date=${pendingDateFilter.endDate}`);
      const pendingUrl = `/api/pending?${pendingParams.join('&')}`;
      const response = await config.apiFetch(config.buildUrl(pendingUrl));
      const data = await response.json();
      if (data.success) {
        // Server filters out Error/empty rows and returns the page slice + total
        setPendingItems(data.data || []);
        setPendingTotalItems(typeof data.total === 'number' ? data.total : (data.data?.length || 0));
      }
    } catch (error) {
      console.error('Error fetching pending:', error);
    }
  };

  const fetchVerified = async () => {
    try {
      let verifiedUrl = "/api/verified";
      const verifiedParams = [];
      if (verifiedDateFilter.startDate) verifiedParams.push(`start_date=${verifiedDateFilter.startDate}`);
      if (verifiedDateFilter.endDate) verifiedParams.push(`end_date=${verifiedDateFilter.endDate}`);
      if (verifiedParams.length > 0) verifiedUrl += '?' + verifiedParams.join('&');
      const response = await config.apiFetch(config.buildUrl(verifiedUrl));
      const data = await response.json();
      if (data.success) {
        setVerifiedItems(data.data);
      }
    } catch (error) {
      console.error('Error fetching verified:', error);
    }
  };

  // Channel platform functions
  const fetchPlatforms = async () => {
    try {
      const response = await config.apiFetch(config.endpoints.channelPlatforms);
      const data = await response.json();
      if (data.success) {
        setPlatforms(data.platforms);
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }
  };

  const fetchPlatformDesigns = async (platformId) => {
    try {
      let channelUrl = config.endpoints.channelDesigns(platformId);
      const channelParams = [];
      if (channelDateFilter.startDate) channelParams.push(`start_date=${channelDateFilter.startDate}`);
      if (channelDateFilter.endDate) channelParams.push(`end_date=${channelDateFilter.endDate}`);
      if (channelDateFilter.startDate || channelDateFilter.endDate) {
        const dateField = platformStatusFilter === 'published' ? 'published_at' : platformStatusFilter === 'reviewed' ? 'updated_at' : 'assigned_at';
        channelParams.push(`date_field=${dateField}`);
      }
      if (channelParams.length > 0) channelUrl += (channelUrl.includes('?') ? '&' : '?') + channelParams.join('&');
      const response = await config.apiFetch(channelUrl);
      const data = await response.json();
      if (data.success) {
        setPlatformDesigns(data.designs);
        setSelectedCardDesigns(new Set());
      }
    } catch (error) {
      console.error('Error fetching platform designs:', error);
    }
  };

  const fetchPlatformConfig = async (platformId) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelConfig(platformId));
      const data = await response.json();
      if (data.success) {
        setPlatformConfig(data.config);
      }
    } catch (error) {
      console.error('Error fetching platform config:', error);
    }
  };

  const fetchDesignVariants = async (designNumber, existingPlatformData) => {
    // First check if variants already exist in platformData (from auto-assign)
    if (existingPlatformData && existingPlatformData.variants && existingPlatformData.variants.length > 0) {
      setEditingVariants({
        variantOptions: existingPlatformData.variantOptions || [],
        variants: existingPlatformData.variants,
        totalVariants: existingPlatformData.variants.length,
      });
      return;
    }
    // Otherwise fetch fresh from tbl_item_data via API
    try {
      const response = await config.apiFetch(config.endpoints.designVariants(designNumber));
      const data = await response.json();
      if (data.success && data.variants && data.variants.length > 0) {
        setEditingVariants({
          variantOptions: data.variantOptions || [],
          variants: data.variants,
          totalVariants: data.totalVariants || data.variants.length,
        });
      } else {
        setEditingVariants(null);
      }
    } catch (error) {
      console.error('Error fetching design variants:', error);
      setEditingVariants(null);
    }
  };

  const handlePushToChannels = async () => {
    if (selectedDesignsForPush.length === 0 || selectedPlatformsForPush.length === 0) return;
    setIsPushing(true);
    try {
      const response = await config.apiFetch(config.endpoints.channelPush, {
        method: 'POST',
        body: JSON.stringify({
          designNumbers: selectedDesignsForPush,
          platformIds: selectedPlatformsForPush
        })
      });
      const data = await response.json();
      if (data.success) {
        setShowPushModal(false);
        setSelectedDesignsForPush([]);
        setSelectedPlatformsForPush([]);
        fetchPlatforms();
        alert(`Successfully pushed ${selectedDesignsForPush.length} designs to ${selectedPlatformsForPush.length} platform(s)`);
      } else {
        alert('Failed to push: ' + data.error);
      }
    } catch (error) {
      console.error('Error pushing to channels:', error);
      alert('Failed to push designs to channels');
    }
    setIsPushing(false);
  };

  const handleAddDesignsToPlatform = async (platformId, designNumbers) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelDesigns(platformId), {
        method: 'POST',
        body: JSON.stringify({ designNumbers })
      });
      const data = await response.json();
      if (data.success) {
        setShowAddDesignsModal(false);
        fetchPlatformDesigns(platformId);
        fetchPlatforms();
      } else {
        alert('Failed to assign: ' + data.error);
      }
    } catch (error) {
      console.error('Error assigning designs:', error);
    }
  };

  const handleRemoveDesignFromPlatform = async (platformId, designNumber, skipConfirm = false) => {
    if (!skipConfirm && !window.confirm(`Remove ${designNumber} from this platform?`)) return;
    try {
      const response = await config.apiFetch(config.endpoints.channelDesignDetail(platformId, designNumber), {
        method: 'DELETE'
      });
      const data = await response.json();
      if (data.success) {
        fetchPlatformDesigns(platformId);
        fetchPlatforms();
      }
    } catch (error) {
      console.error('Error removing design:', error);
    }
  };

  const handleUpdateDesignPlatformData = async (platformId, designNumber, platformData, status) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelDesignDetail(platformId, designNumber), {
        method: 'PUT',
        body: JSON.stringify({ platformData, status })
      });
      const data = await response.json();
      if (data.success) {
        fetchPlatformDesigns(platformId);
        setShowPlatformFieldEditor(false);
        setEditingPlatformDesign(null);
        setEditingVariants(null);
      } else {
        alert('Failed to update: ' + data.error);
      }
    } catch (error) {
      console.error('Error updating platform data:', error);
    }
  };

  const handleSavePlatformConfig = async (platformId, configData) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelConfig(platformId), {
        method: 'POST',
        body: JSON.stringify(configData)
      });
      const data = await response.json();
      if (data.success) {
        setShowPlatformConfigModal(false);
        fetchPlatformConfig(platformId);
      } else {
        alert('Failed to save config: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving platform config:', error);
    }
  };

  // ─── Credential & Publishing Functions ───
  const fetchPlatformCredentials = async (platformId) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelCredentials(platformId));
      const data = await response.json();
      if (data.success) {
        setPlatformCredentials(data.credentials || {});
      }
    } catch (error) {
      console.error('Error fetching credentials:', error);
    }
  };

  const handleSaveCredentials = async (platformId, credentials) => {
    setIsSavingCredentials(true);
    try {
      const response = await config.apiFetch(config.endpoints.channelCredentials(platformId), {
        method: 'POST',
        body: JSON.stringify({ credentials })
      });
      const data = await response.json();
      if (data.success) {
        setConnectionStatus({ success: true, message: 'Credentials saved!' });
        fetchPlatforms(); // Refresh to update hasCredentials flag
        fetchPlatformCredentials(platformId);
      } else {
        setConnectionStatus({ success: false, message: 'Failed to save: ' + data.error });
      }
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Error saving credentials' });
    }
    setIsSavingCredentials(false);
  };

  const handleTestConnection = async (platformId) => {
    setIsTestingConnection(true);
    setConnectionStatus(null);
    try {
      const response = await config.apiFetch(config.endpoints.channelTestConnection(platformId), {
        method: 'POST'
      });
      const data = await response.json();
      setConnectionStatus(data);
    } catch (error) {
      setConnectionStatus({ success: false, message: 'Connection test failed' });
    }
    setIsTestingConnection(false);
  };

  // ── Image Selection Modal handlers ──
  const openImageModal = async (design) => {
    setImageModalDesign(design);
    setDesignShootImages([]);
    setSelectedPrimaryImage(null);
    setSelectedGalleryImages([]);
    setVariantImageMap({});

    // Fetch shoot images from DB
    try {
      const res = await config.apiFetch(config.endpoints.designImages(design.designNumber));
      const data = await res.json();
      if (data.success) setDesignShootImages(data.images || []);
    } catch (e) { console.error('Failed to fetch design images', e); }

    // Restore any saved selections from platform_data
    const saved = design.platformData?.images;
    if (saved) {
      setSelectedPrimaryImage(saved.primaryImageId || null);
      setSelectedGalleryImages(saved.galleryImageIds || []);
      setVariantImageMap(saved.variantImages || {});
    }

    setShowImageModal(true);
  };

  const handleSaveImageSelections = async () => {
    const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
    if (!imageModalDesign || !platformId) return;
    setIsSavingImages(true);
    try {
      const res = await config.apiFetch(
        config.endpoints.channelDesignImages(platformId, imageModalDesign.designNumber),
        {
          method: 'PUT',
          body: JSON.stringify({
            primaryImageId: selectedPrimaryImage,
            galleryImageIds: selectedGalleryImages,
            variantImages: variantImageMap,
          }),
        }
      );
      const data = await res.json();
      if (data.success) {
        fetchPlatformDesigns(platformId);
        setShowImageModal(false);
      } else {
        alert('Failed to save: ' + (data.error || 'Unknown error'));
      }
    } catch (e) {
      alert('Error saving image selections');
    }
    setIsSavingImages(false);
  };

  const toggleGalleryImage = (imgId) => {
    setSelectedGalleryImages(prev => {
      if (prev.includes(imgId)) return prev.filter(id => id !== imgId);
      return [...prev, imgId];
    });
    // If setting as gallery, remove from primary if it was primary
    if (selectedPrimaryImage === imgId) setSelectedPrimaryImage(null);
  };

  const setPrimaryImage = (imgId) => {
    setSelectedPrimaryImage(imgId);
    // Remove from gallery if it was there
    setSelectedGalleryImages(prev => prev.filter(id => id !== imgId));
  };

  const handlePublishDesign = async (platformId, designNumber) => {
    setIsPublishing(true);
    setPublishingDesign(designNumber);
    try {
      const response = await config.apiFetch(config.endpoints.channelPublishDesign(platformId, designNumber), {
        method: 'POST'
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ Published! ${data.message}\n\nListing: ${data.listingUrl || 'N/A'}`);
        fetchPlatformDesigns(platformId);
        setPlatformStatusFilter('published');  // Auto-switch to Published tab
      } else {
        alert(`❌ Publish failed: ${data.error || data.message}`);
      }
    } catch (error) {
      alert('Error publishing: ' + error.message);
    }
    setIsPublishing(false);
    setPublishingDesign(null);
  };

  const handleBulkPublish = async (platformId, designNumbers) => {
    setIsPublishing(true);
    try {
      const response = await config.apiFetch(config.endpoints.channelPublishBulk(platformId), {
        method: 'POST',
        body: JSON.stringify({ designNumbers })
      });
      const data = await response.json();
      if (data.success) {
        alert(`✅ All published! ${data.published} design(s) pushed to ${platforms.find(p => p.id === platformId)?.name || platformId}.`);
      } else {
        alert(`Published: ${data.published}, Failed: ${data.failed}\n\n${data.results?.filter(r => !r.success).map(r => `${r.designNumber}: ${r.message}`).join('\n') || ''}`);
      }
      fetchPlatformDesigns(platformId);
      if (data.published > 0) setPlatformStatusFilter('published');  // Auto-switch to Published tab
    } catch (error) {
      alert('Error bulk publishing: ' + error.message);
    }
    setIsPublishing(false);
  };

  const getActivePlatformId = () => {
    if (currentPage.startsWith('channel_')) {
      return currentPage.replace('channel_', '');
    }
    return null;
  };

  // ── Channel Collection Handlers ──

  const fetchChannelCollections = async (platformId) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelCollections(platformId));
      const data = await response.json();
      if (data.success) setChannelCollections(data.collections || []);
    } catch (error) {
      console.error('Error fetching channel collections:', error);
    }
  };

  // ── Shopify: Fetch Published Products ────────────────────────────────────────
  const fetchShopifyProducts = useCallback(async (platformId, cursor = null, append = false, statusOverride = null) => {
    if (!platformId) return;
    setShopifyProductsLoading(true);
    try {
      const response = await config.channels.getChannelProducts(platformId, {
        cursor,
        limit: 50,
        status: statusOverride ?? shopifyProductsFilter,
      });
      const result = await response.json();
      if (result.success) {
        setShopifyProducts(prev => append ? [...prev, ...result.products] : result.products);
        setShopifyProductsPagination(result.pageInfo);
        setShopifyProductsTotal(result.totalCount);
      }
    } catch (err) {
      console.error('[shopify products]', err);
    } finally {
      setShopifyProductsLoading(false);
    }
  }, [shopifyProductsFilter]);

  const [statusUpdatingProductId, setStatusUpdatingProductId] = useState(null);
  const [shopifyProductMetafields, setShopifyProductMetafields] = useState([]);
  const [shopifyMfLoading, setShopifyMfLoading] = useState(false);
  const [shopifyMfSaving, setShopifyMfSaving] = useState(false);

  const updateShopifyProductStatus = async (productGid, newStatus) => {
    const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
    setStatusUpdatingProductId(productGid);
    try {
      const response = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/products/status`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_gid: productGid, status: newStatus }),
      });
      const result = await response.json();
      if (result.success) {
        // Update in-place in shopifyProducts state
        setShopifyProducts(prev => prev.map(p =>
          p.id === productGid ? { ...p, status: newStatus.toUpperCase() } : p
        ));
        // Also update selected product if open
        if (shopifySelectedProduct?.id === productGid) {
          setShopifySelectedProduct(prev => prev ? { ...prev, status: newStatus.toUpperCase() } : null);
        }
      } else {
        alert(`Failed to update status: ${result.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('[shopify status update]', err);
      alert('Failed to update product status');
    } finally {
      setStatusUpdatingProductId(null);
    }
  };

  const fetchProductMetafields = async (productGid) => {
    const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
    setShopifyMfLoading(true);
    try {
      const resp = await config.apiFetch(config.buildUrl(
        `/api/channels/${shopifyPlatformId}/products/metafields?product_gid=${encodeURIComponent(productGid)}`
      ));
      const result = await resp.json();
      if (result.success) {
        setShopifyProductMetafields(result.metafields || []);
      }
    } catch (err) { console.error('[metafields fetch]', err); }
    finally { setShopifyMfLoading(false); }
  };

  const saveProductMetafields = async (productGid, metafields) => {
    const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
    setShopifyMfSaving(true);
    try {
      const resp = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/products/metafields`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ product_gid: productGid, metafields }),
      });
      const result = await resp.json();
      if (result.success) {
        await fetchProductMetafields(productGid);
      } else {
        alert(`Failed to save: ${result.error}`);
      }
    } catch (err) { console.error('[metafields save]', err); alert('Failed to save metafields'); }
    finally { setShopifyMfSaving(false); }
  };

  const fetchShopifyAnalytics = useCallback(async (platformId, productIds, days = 30) => {
    if (!platformId || !productIds?.length) return;
    setShopifyAnalyticsLoading(true);
    setShopifyAnalyticsScopeError(false);
    try {
      const response = await config.channels.getProductAnalytics(platformId, productIds, days);
      const result = await response.json();
      if (result.scopeError) {
        setShopifyAnalyticsScopeError(true);
      } else if (result.success) {
        setShopifyProductsAnalytics(prev => ({ ...prev, ...result.analytics }));
        setShopifyAnalyticsSummary(result.summary);
      }
    } catch (err) {
      console.error('[shopify analytics]', err);
    } finally {
      setShopifyAnalyticsLoading(false);
    }
  }, []);

  const fetchShopifyTrends = useCallback(async (platformId, productId, days = 30) => {
    if (!platformId || !productId) return;
    setShopifyTrendsLoading(true);
    try {
      const response = await config.channels.getProductTrends(platformId, productId, { period: 'daily', days });
      const result = await response.json();
      if (result.success) setShopifyTrends(result.trends);
    } catch (err) {
      console.error('[shopify trends]', err);
    } finally {
      setShopifyTrendsLoading(false);
    }
  }, []);

  const handleCreateCollection = async (platformId) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelCollections(platformId), {
        method: 'POST',
        body: JSON.stringify({ name: collectionName, description: collectionDescription, tags: collectionTags })
      });
      const data = await response.json();
      if (data.success) {
        setShowCreateCollectionModal(false);
        setCollectionName(''); setCollectionDescription(''); setCollectionTags([]);
        fetchChannelCollections(platformId);
      } else {
        alert(data.error || 'Failed to create collection');
      }
    } catch (error) {
      alert('Error creating collection: ' + error.message);
    }
  };

  const handleUpdateCollection = async (collectionId) => {
    try {
      const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
      const response = await config.apiFetch(config.endpoints.channelCollection(collectionId), {
        method: 'PUT',
        body: JSON.stringify({ name: collectionName, description: collectionDescription, tags: collectionTags })
      });
      const data = await response.json();
      if (data.success) {
        setShowEditCollectionModal(false);
        setEditingCollection(null);
        setCollectionName(''); setCollectionDescription(''); setCollectionTags([]);
        if (platformId) fetchChannelCollections(platformId);
      } else {
        alert(data.error || 'Failed to update collection');
      }
    } catch (error) {
      alert('Error updating collection: ' + error.message);
    }
  };

  const handleDeleteCollection = async (collectionId) => {
    try {
      const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
      const response = await config.apiFetch(config.endpoints.channelCollection(collectionId), { method: 'DELETE' });
      const data = await response.json();
      if (data.success) {
        if (platformId) fetchChannelCollections(platformId);
      } else {
        alert(data.error || 'Failed to delete collection');
      }
    } catch (error) {
      alert('Error deleting collection: ' + error.message);
    }
  };

  const fetchCollectionDesignNumbers = async (collectionId) => {
    try {
      const response = await config.apiFetch(config.endpoints.channelCollectionDesigns(collectionId));
      const data = await response.json();
      if (data.success) {
        const dns = data.designNumbers || [];
        setCollectionDesignNumbers(dns);
        setPickerSelected(new Set(dns));
      }
    } catch (error) {
      console.error('Error fetching collection designs:', error);
    }
  };

  const handleAddDesignsToCollection = async (collectionId, designNumbers) => {
    try {
      const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
      const response = await config.apiFetch(config.endpoints.channelCollectionDesigns(collectionId), {
        method: 'POST',
        body: JSON.stringify({ designNumbers })
      });
      const data = await response.json();
      if (data.success) {
        setShowCollectionDesignPicker(false);
        setPickerTargetCollection(null);
        if (platformId) fetchChannelCollections(platformId);
      } else {
        alert(data.error || 'Failed to add designs');
      }
    } catch (error) {
      alert('Error adding designs: ' + error.message);
    }
  };

  const handleRemoveDesignFromCollection = async (collectionId, designNumber) => {
    try {
      const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
      const response = await config.apiFetch(config.endpoints.channelCollectionDesignRemove(collectionId, designNumber), { method: 'DELETE' });
      const data = await response.json();
      if (data.success && platformId) fetchChannelCollections(platformId);
    } catch (error) {
      console.error('Error removing design from collection:', error);
    }
  };

  const handlePublishCollection = async (collectionId) => {
    setIsPublishingCollection(true);
    setPublishingCollectionId(collectionId);
    try {
      const response = await config.apiFetch(config.endpoints.channelCollectionPublish(collectionId), { method: 'POST' });
      const data = await response.json();
      if (data.success) {
        const parts = [`Designs published: ${data.published}`];
        if (data.failed > 0) parts.push(`Failed: ${data.failed}`);
        parts.push(`Designs tagged: ${data.tagged}`);
        parts.push(`Marketplace collection: ${data.collectionCreated ? 'Created' : 'Not supported on this platform'}`);
        if (data.marketplaceUrl) parts.push(`URL: ${data.marketplaceUrl}`);
        alert(parts.join('\n'));
      } else {
        alert(data.error || 'Failed to publish collection');
      }
      const platformId = currentPage.startsWith('channel_') ? currentPage.replace('channel_', '') : null;
      if (platformId) {
        fetchChannelCollections(platformId);
        fetchPlatformDesigns(platformId);
      }
    } catch (error) {
      alert('Error publishing collection: ' + error.message);
    }
    setIsPublishingCollection(false);
    setPublishingCollectionId(null);
  };

  const checkGenerationStatus = async (jobId) => {
    try {
      const response = await config.apiFetch(config.buildUrl(`/api/status/${jobId}`));
      const data = await response.json();
      if (data.success) {
        setGenerationProgress(data.data);
        
        if (data.data.status === 'completed') {
          setIsGenerating(false);
          setCurrentJobId(null);
          setGenerationProgress(null);
          fetchPending();
          fetchDesigns();
          
          // Check how many succeeded vs failed
          const results = data.data.results || [];
          const succeeded = results.filter(r => r.status === 'success').length;
          const failed = results.filter(r => r.status === 'error').length;
          const total = data.data.total;
          
          if (succeeded === 0 && failed > 0) {
            // All failed - show error
            const firstError = results.find(r => r.status === 'error')?.error || 'Unknown error';

            if (firstError.includes('quota') || firstError.includes('insufficient_quota')) {
              notify.error('Generation failed — API quota exceeded. Add credits and retry.', { duration: 7000 });
            } else if (firstError.includes('api_key') || firstError.includes('authentication')) {
              notify.error('Generation failed — API key issue. Check your OpenAI configuration.', { duration: 7000 });
            } else {
              notify.error(`Generation failed — all ${failed} design(s): ${firstError}`, { duration: 7000 });
            }
          } else if (succeeded > 0 && failed > 0) {
            // Partial success
            notify.info(`Partial success — ${succeeded} generated, ${failed} failed. Opening Verification…`, { duration: 5000 });
            setCurrentPage('pending');
          } else if (succeeded > 0) {
            // All succeeded
            notify.success(`Generated ${succeeded} design${succeeded === 1 ? '' : 's'}`);
            setCurrentPage('pending');
          } else {
            // No results (shouldn't happen)
            notify.info('No designs were processed.');
          }
        } else if (data.data.status === 'error') {
          setIsGenerating(false);
          setCurrentJobId(null);
          setGenerationProgress(null);
          notify.error('Generation error: ' + data.data.error, { duration: 7000 });
        }
      }
    } catch (error) {
      console.error('Error checking status:', error);
    }
  };

  const toggleItemSelection = (designNumber) => {
    setSelectedItems(prev => 
      prev.includes(designNumber) 
        ? prev.filter(d => d !== designNumber)
        : [...prev, designNumber]
    );
  };

  const handleGenerate = async () => {
    const itemsToProcess = selectedItems.length > 0 
      ? selectedItems
      : generateItems.map(item => item.designNumber);
    
    if (itemsToProcess.length === 0) {
      notify.info('No designs to process');
      return;
    }

    setIsGenerating(true);
    try {
      const response = await config.apiFetch(config.endpoints.designsGenerate, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ designNumbers: itemsToProcess })
      });

      if (!response.ok) {
        const errText = await response.text();
        let errMsg;
        try { errMsg = JSON.parse(errText).error; } catch { errMsg = `HTTP ${response.status}: ${errText.substring(0, 200)}`; }
        notify.error('Failed to start generation: ' + errMsg);
        setIsGenerating(false);
        return;
      }

      const data = await response.json();
      if (data.success) {
        setCurrentJobId(data.jobId);
        setSelectedItems([]);

        setGenerateItems(prev =>
          prev.filter(item => !itemsToProcess.includes(item.designNumber))
        );
      } else {
        notify.error('Failed to start generation: ' + data.error);
        setIsGenerating(false);
      }
    } catch (error) {
      console.error('Error starting generation:', error);
      notify.error('Failed to start generation: ' + (error.message || error));
      setIsGenerating(false);
    }
  };

  const handleManualAdd = async () => {
    if (!manualInput.trim()) return;
    
    const designNumbers = manualInput.split(',').map(d => d.trim()).filter(Boolean);
    
    const existingInGenerate = designNumbers.filter(dn => 
      generateItems.some(item => item.designNumber === dn)
    );
    const existingInPending = designNumbers.filter(dn => 
      pendingItems.some(item => item.designNumber === dn)
    );
    const existingInVerified = designNumbers.filter(dn => 
      verifiedItems.some(item => item.designNumber === dn)
    );
    
    const allDuplicates = [...existingInGenerate, ...existingInPending, ...existingInVerified];
    const uniqueDuplicates = [...new Set(allDuplicates)];
    const newDesigns = designNumbers.filter(dn => !allDuplicates.includes(dn));
    
    if (uniqueDuplicates.length > 0) {
      let message = '⚠️ Following designs already exist:\n\n';
      
      if (existingInGenerate.length > 0) {
        message += `In Generate: ${existingInGenerate.join(', ')}\n`;
      }
      if (existingInPending.length > 0) {
        message += `In Pending: ${existingInPending.join(', ')}\n`;
      }
      if (existingInVerified.length > 0) {
        message += `In Verified: ${existingInVerified.join(', ')}\n`;
      }
      
      if (newDesigns.length > 0) {
        message += `\n✅ Adding ${newDesigns.length} new design(s): ${newDesigns.join(', ')}`;
      } else {
        message += '\n❌ No new designs to add.';
      }
      
      alert(message);
    }
    
    if (newDesigns.length === 0) {
      setManualInput('');
      return;
    }
    
    try {
      const response = await config.apiFetch(config.buildUrl('/api/designs/manual'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ designNumbers: newDesigns })
      });

      const data = await response.json();
      if (data.success) {
        const formattedDesigns = data.data.map(d => ({
          designNumber: d['Design Number'],
          productType: d['Product Type'],
          baseMaterial: d['Base Material'],
          imageUrl: d['Image URL']
        }));
        setGenerateItems(prev => [...prev, ...formattedDesigns]);
        setManualInput('');
        
        if (uniqueDuplicates.length === 0) {
          alert(`✅ Successfully added ${formattedDesigns.length} design(s)!`);
        }
      }
    } catch (error) {
      console.error('Error adding manual designs:', error);
      alert('❌ Failed to add designs. Please try again.');
    }
  };

  const openDesignDetails = (item) => {
    setSelectedDesign(item);
    setAttributes(item.attributes || {});
  };

  const updateAttribute = (key, value) => {
    setAttributes(prev => ({ ...prev, [key]: value }));
  };

  const handleVerify = async () => {
    try {
      const response = await config.apiFetch(config.buildUrl(`/api/verify/${selectedDesign.designNumber}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes })
      });

      const data = await response.json();
      if (data.success) {
        fetchPending();
        fetchVerified();
        setSelectedDesign(null);
        notify.success('Design verified');
      } else {
        notify.error('Failed to verify: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error verifying design:', error);
      notify.error('Failed to verify design');
    }
  };

  const handleRerun = async (designNumber) => {
    // Store current pending list in case we need to restore
    const currentPending = [...pendingItems];
    
    try {
      setIsRerunning(true);
      
      // Call rerun API endpoint (don't remove from pending yet)
      const response = await config.apiFetch(config.buildUrl(`/api/rerun/${designNumber}`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      
      if (data.success) {
        // Success: Remove from pending, design will reappear after regeneration
        setPendingItems(prev => prev.filter(d => d.designNumber !== designNumber));
        setSelectedDesign(null);
        setIsRerunning(false);
        
        // Refresh pending list after 2 seconds to show regenerated design
        setTimeout(fetchPending, 2000);
      } else {
        // API returned error (e.g., quota exceeded, API key issue)
        setIsRerunning(false);
        const errorMsg = data.error || 'Unknown error';
        
        if (errorMsg.includes('quota') || errorMsg.includes('insufficient_quota') || errorMsg.includes('rate_limit')) {
          notify.error('API quota or rate limit hit — add credits or wait for reset. Previous values kept.', { duration: 7000 });
        } else if (errorMsg.includes('api_key') || errorMsg.includes('authentication')) {
          notify.error('API key error — check your OpenAI configuration. Previous values kept.', { duration: 7000 });
        } else {
          notify.error(`Rerun failed: ${errorMsg}. Previous values kept.`, { duration: 7000 });
        }

        // Keep design in pending with old values (don't remove)
      }
    } catch (error) {
      // Network error or exception
      setIsRerunning(false);
      console.error('Error rerunning design:', error);

      notify.error(`Rerun failed — network error: ${error.message}. Previous values kept.`, { duration: 7000 });

      // Ensure pending list is intact (restore if needed)
      setPendingItems(currentPending);
    }
  };

  const handleRetry = async (designNumber) => {
    try {
      setRetryingDesign(designNumber);
      const response = await config.apiFetch(
        config.buildUrl(`/api/verification/${designNumber}/retry`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      if (!data.success) {
        setRetryingDesign(null);
        notify.error(`Retry failed: ${data.error || 'Unknown error'}`);
        return;
      }
      const jobId = data.jobId;
      // Poll job status, then refresh the list. A timeout fence prevents an
      // indefinite poll if the job never reaches a terminal status.
      let timeoutFence;
      const poll = setInterval(async () => {
        try {
          const sres = await config.apiFetch(config.buildUrl(`/api/status/${jobId}`));
          const sdata = await sres.json();
          const st = sdata?.data?.status;
          if (st === 'completed' || st === 'error') {
            clearInterval(poll);
            clearTimeout(timeoutFence);
            setRetryingDesign(null);
            fetchPending();
            notify.success('Retry finished');
          }
        } catch (e) {
          clearInterval(poll);
          clearTimeout(timeoutFence);
          setRetryingDesign(null);
          notify.error(`Status check failed: ${e.message}`);
          fetchPending();
        }
      }, 2500);
      timeoutFence = setTimeout(() => {
        clearInterval(poll);
        setRetryingDesign(null);
        notify.error('Retry timed out — please check manually.');
      }, 120000);
    } catch (error) {
      setRetryingDesign(null);
      notify.error(`Retry failed — network error: ${error.message}`);
    }
  };

  // Discard a design's AI-generated attributes and return it to Catalog
  // Enrichment. Works for both pending-review and failed designs.
  const handleMoveToEnrichment = async (designNumber) => {
    const ok = await confirm({
      title: 'Move back to Catalog Enrichment?',
      message: `This discards the AI-generated attributes for design ${designNumber} and returns it to the Catalog Enrichment pool. This cannot be undone.`,
      confirmLabel: 'Move to Enrichment',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      setMovingDesign(designNumber);
      const response = await config.apiFetch(
        config.buildUrl(`/api/verification/${designNumber}/move-to-enrichment`),
        { method: 'POST', headers: { 'Content-Type': 'application/json' } }
      );
      const data = await response.json();
      if (!data.success) {
        notify.error(`Move failed: ${data.error || 'Unknown error'}`);
        return;
      }
      notify.success(`Design ${designNumber} moved to Catalog Enrichment`);
      fetchPending();
    } catch (error) {
      notify.error(`Move failed — network error: ${error.message}`);
    } finally {
      setMovingDesign(null);
    }
  };

  const handleExport = async () => {
    try {
      const response = await config.apiFetch(config.buildUrl("/api/export/verified"));
      if (!response.ok) {
        const data = await response.json();
        alert('Export failed: ' + (data.error || 'Unknown error'));
        return;
      }
      const blob = await response.blob();
      const filename = response.headers.get('content-disposition')
        ?.match(/filename="?([^"]+)"?/)?.[1] || `verified_export_${Date.now()}.csv`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error exporting:', error);
      alert('Failed to export designs');
    }
  };

  const handleSaveCollection = async (collectionData) => {
    try {
      const url = selectedCollection
        ? config.endpoints.collections + '/' + selectedCollection.id
        : config.endpoints.collections;
      
      const method = selectedCollection ? 'PUT' : 'POST';
      
      const response = await config.apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(collectionData)
      });

      const data = await response.json();
      
      if (data.success) {
        setShowCollectionModal(false);
        fetchCollections();
        alert(selectedCollection ? 'Collection updated successfully!' : 'Collection created successfully!');
      } else {
        alert('Failed to save collection: ' + data.error);
      }
    } catch (error) {
      console.error('Error saving collection:', error);
      alert('Failed to save collection');
    }
  };

  // Pagination state for each page (pendingPageSize/pendingCurrentPage moved to top)
  const [generatePageSize, setGeneratePageSize] = useState(25);
  const [generateCurrentPage, setGenerateCurrentPage] = useState(1);

  const [verifiedPageSize, setVerifiedPageSize] = useState(25);
  const [verifiedCurrentPage, setVerifiedCurrentPage] = useState(1);

  const [analyticsPageSize, setAnalyticsPageSize] = useState(25);
  const [analyticsCurrentPage, setAnalyticsCurrentPage] = useState(1);

  // Helper function to paginate items
  const paginateItems = (items, currentPage, pageSize) => {
    // Add null/undefined check to prevent runtime errors
    if (!items || !Array.isArray(items)) {
      return [];
    }
    
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return items.slice(startIndex, endIndex);
  };

  // Calculate paginated items - with defensive checks for undefined
  const paginatedGenerateItems = paginateItems(generateItems || [], generateCurrentPage, generatePageSize);
  const generateTotalPages = Math.ceil(((generateItems?.length || 0) / generatePageSize) || 1);

  // Server returns the page slice directly; no client-side slicing needed.
  const paginatedPendingItems = pendingItems || [];
  const pendingTotalPages = Math.max(1, Math.ceil((pendingTotalItems || 0) / pendingPageSize));

  const paginatedVerifiedItems = paginateItems(verifiedItems || [], verifiedCurrentPage, verifiedPageSize);
  const verifiedTotalPages = Math.ceil(((verifiedItems?.length || 0) / verifiedPageSize) || 1);

  const paginatedAnalyticsHistory = analyticsData?.recentHistory
    ? paginateItems(analyticsData.recentHistory, analyticsCurrentPage, analyticsPageSize)
    : [];
  const analyticsTotalPages = analyticsData?.recentHistory?.length
    ? Math.ceil(analyticsData.recentHistory.length / analyticsPageSize)
    : 1;

  // Reset to page 1 when page size changes
  useEffect(() => {
    setGenerateCurrentPage(1);
  }, [generatePageSize]);

  useEffect(() => {
    setPendingCurrentPage(1);
  }, [pendingPageSize]);

  useEffect(() => {
    setVerifiedCurrentPage(1);
  }, [verifiedPageSize]);

  useEffect(() => {
    setAnalyticsCurrentPage(1);
  }, [analyticsPageSize]);

  // Auth gate: loading spinner
  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.bg }}>
        <RefreshCw className="animate-spin" size={48} style={{ color: COLORS.maroon }} />
      </div>
    );
  }

  // Auth gate: login page
  if (!authUser) {
    return <LoginPage onLogin={handleLogin} sessionExpired={sessionExpired} />;
  }

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: COLORS.bg }}>
      {/* Sidebar */}
      <Sidebar
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        counts={{
          generate: batchHistoryTotal || 0,
          pending: (pendingTotalItems || 0),
          verified: (verifiedItems?.length || 0)
        }}
        enrichmentCounts={enrichmentCounts}
        platforms={platforms}
        channelsExpanded={channelsExpanded}
        setChannelsExpanded={setChannelsExpanded}
        authUser={authUser}
        onLogout={handleLogout}
      />
      
      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Generation Progress Banner */}
        {isGenerating && generationProgress && (
          <div className="bg-[#FFF9F9] border-b border-[#F5E6E9] px-12 py-4">
            <div className="max-w-6xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <RefreshCw className="animate-spin" style={{ color: COLORS.maroon }} size={20} />
                <div>
                  <p className="text-sm font-medium" style={{ color: COLORS.maroon }}>
                    Generating attributes... {generationProgress.completed} / {generationProgress.total}
                  </p>
                  <div className="w-64 h-1.5 bg-[#F5E6E9] rounded-full mt-2 overflow-hidden">
                    <div 
                      className="h-full transition-all duration-300"
                      style={{ 
                        width: `${(generationProgress.completed / generationProgress.total) * 100}%`,
                        backgroundColor: COLORS.maroon
                      }}
                    />
                  </div>
                </div>
              </div>
              <p className="text-xs" style={{ color: COLORS.textSecondary }}>This may take a few minutes...</p>
            </div>
          </div>
        )}

        <div className="p-12">
          {/* Page Header - hide for collections, settings, activity, enrichment & channel pages (they have their own headers) */}
          {currentPage !== 'collections' && currentPage !== 'settings' && currentPage !== 'activity' && currentPage !== 'enrichment' && currentPage !== 'occasion_review' && currentPage !== 'category_review' && !currentPage.startsWith('channel_') && (
            <header className="max-w-6xl mx-auto mb-12">
              {/* Logo - Top Right */}
              <div className="flex justify-end mb-8">
                <div className="flex items-center gap-3">
                  <span className="text-xs font-light tracking-wide" style={{ color: COLORS.textSecondary }}>
                    A PRODUCT BY
                  </span>
                  <img 
                    src="/company-logo.png" 
                    alt="Company Logo" 
                    className="h-8 object-contain"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div style={{ display: 'none', color: COLORS.textSecondary, fontSize: '12px' }}>
                    [Logo not found]
                  </div>
                </div>
              </div>

              {/* Page Title */}
              <div className="text-center">
                <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">
                  {currentPage === 'generate' && 'Batch History'}
                  {currentPage === 'pending' && 'Verification Queue'}
                  {currentPage === 'verified' && 'Published Designs'}
                  {currentPage === 'analytics' && 'Analytics & Usage'}
                  {currentPage === 'config' && 'Attribute Configuration'}
                  {currentPage === 'trend_predictor' && 'Trend Predictor'}
                </h2>
                <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: COLORS.maroon }} />
                <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
                  {currentPage === 'generate' && 'Designs pushed via API — auto-processed on arrival'}
                  {currentPage === 'pending' && 'Review and edit AI-generated attributes'}
                  {currentPage === 'verified' && 'Approved designs ready for synchronization'}
                  {currentPage === 'analytics' && 'Track token usage, costs, and API statistics'}
                  {currentPage === 'config' && 'Manage attribute categories and AI generation settings'}
                  {currentPage === 'trend_predictor' && 'Upload a design image to predict its market performance'}
                </p>
              </div>
            </header>
          )}

          {/* Batch History Page (replaces Design Catalog) */}
          {currentPage === 'generate' && (
            <div className="max-w-7xl mx-auto space-y-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                    Designs pushed via API are auto-processed on arrival
                  </p>
                  {platformConfig?.autoDeployEnabled && (
                    <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      Auto-Deploy ON
                    </span>
                  )}
                </div>
                <button
                  onClick={fetchBatchHistory}
                  disabled={batchHistoryLoading}
                  className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2 rounded-lg"
                  style={{ color: COLORS.textSecondary }}
                >
                  <RefreshCw size={14} className={batchHistoryLoading ? 'animate-spin' : ''} />
                  Refresh
                </button>
              </div>

              {batchHistoryLoading && batchHistory.length === 0 ? (
                <div className="text-center py-16">
                  <RefreshCw size={40} className="animate-spin mx-auto mb-4" style={{ color: COLORS.textSecondary }} />
                  <p style={{ color: COLORS.textSecondary }}>Loading batch history...</p>
                </div>
              ) : batchHistory.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl border" style={{ borderColor: COLORS.border }}>
                  <Activity size={48} className="mx-auto mb-4" style={{ color: COLORS.textLight }} />
                  <p className="font-medium mb-2 text-lg" style={{ color: COLORS.textSecondary }}>No batches yet</p>
                  <p className="text-sm max-w-md mx-auto" style={{ color: COLORS.textLight }}>
                    External apps push designs via <code className="px-1.5 py-0.5 bg-gray-100 rounded text-xs font-mono">POST /api/v1/catalog/designs</code> — attributes are auto-generated on arrival.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {batchHistory.map((batch, idx) => {
                    const total = batch.totalCombinations;
                    const verified = batch.statusBreakdown?.verified || 0;
                    const pending = batch.statusBreakdown?.pending || 0;
                    const processing = batch.statusBreakdown?.needs_processing || 0;
                    const errors = batch.statusBreakdown?.error || 0;
                    const verifiedPct = total > 0 ? (verified / total) * 100 : 0;
                    const pendingPct = total > 0 ? (pending / total) * 100 : 0;
                    const errorPct = total > 0 ? (errors / total) * 100 : 0;
                    const allDone = verified === total && total > 0;
                    const batchDate = new Date(batch.firstPushedAt);
                    const batchKey = `${batch.sourceApp}|${batch.date}`;
                    const isExpanded = expandedBatch === batchKey;
                    return (
                      <div key={idx}>
                        <div
                          onClick={() => fetchBatchDesigns(batch.sourceApp, batch.date)}
                          className="bg-white rounded-xl border p-5 transition-all hover:shadow-md cursor-pointer"
                          style={{ borderColor: isExpanded ? COLORS.maroon : allDone ? '#86EFAC' : COLORS.border }}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                                style={{ backgroundColor: '#6366f1' }}>
                                {(batch.sourceApp || '?')[0].toUpperCase()}
                              </div>
                              <div>
                                <div className="flex items-center gap-2">
                                  <span className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>{batch.sourceApp}</span>
                                  {allDone && (
                                    <span className="px-2 py-0.5 rounded-full text-[10px] font-bold" style={{ backgroundColor: '#D1FAE5', color: '#065F46' }}>
                                      All Verified ✓
                                    </span>
                                  )}
                                </div>
                                <p className="text-xs" style={{ color: COLORS.textLight }}>
                                  {batchDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                  {' · '}
                                  {batchDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                </p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <div className="text-right">
                                <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>{total}</p>
                                <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.textLight }}>
                                  combinations · {batch.uniqueDesigns} designs
                                </p>
                              </div>
                              <ChevronDown size={16} style={{ color: COLORS.textLight, transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </div>
                          </div>

                          {/* Progress bar */}
                          <div className="h-2 rounded-full bg-gray-100 overflow-hidden mb-2">
                            <div className="h-full flex">
                              {verifiedPct > 0 && (
                                <div style={{ width: `${verifiedPct}%`, backgroundColor: '#10B981' }} className="transition-all duration-500" />
                              )}
                              {pendingPct > 0 && (
                                <div style={{ width: `${pendingPct}%`, backgroundColor: '#F59E0B' }} className="transition-all duration-500" />
                              )}
                              {errorPct > 0 && (
                                <div style={{ width: `${errorPct}%`, backgroundColor: '#EF4444' }} className="transition-all duration-500" />
                              )}
                            </div>
                          </div>

                          {/* Status breakdown */}
                          <div className="flex items-center gap-4 text-xs">
                            {verified > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#10B981' }} />
                                <span style={{ color: COLORS.textSecondary }}>Verified: {verified}</span>
                              </span>
                            )}
                            {pending > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#F59E0B' }} />
                                <span style={{ color: COLORS.textSecondary }}>Pending: {pending}</span>
                              </span>
                            )}
                            {processing > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full bg-gray-300" />
                                <span style={{ color: COLORS.textSecondary }}>Processing: {processing}</span>
                              </span>
                            )}
                            {errors > 0 && (
                              <span className="flex items-center gap-1">
                                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#EF4444' }} />
                                <span style={{ color: '#EF4444', fontWeight: 600 }}>Failed: {errors}</span>
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Expanded: show designs in this batch */}
                        {isExpanded && (
                          <div className="ml-6 mt-2 mb-2 border-l-2 pl-4 space-y-2" style={{ borderColor: COLORS.maroon + '30' }}>
                            {batchDesignsLoading ? (
                              <div className="py-4 text-center">
                                <RefreshCw size={16} className="animate-spin mx-auto mb-2" style={{ color: COLORS.textLight }} />
                                <p className="text-xs" style={{ color: COLORS.textLight }}>Loading designs...</p>
                              </div>
                            ) : batchDesigns.length === 0 ? (
                              <p className="text-xs py-3" style={{ color: COLORS.textLight }}>No designs in this batch</p>
                            ) : (
                              batchDesigns.map((d) => (
                                <div key={d.combinationNumber} className="bg-white rounded-lg border px-4 py-3 flex items-center gap-4" style={{ borderColor: COLORS.border }}>
                                  {d.imageUrl ? (
                                    <img src={d.imageUrl} alt={d.designNumber} className="w-12 h-12 rounded-lg object-cover border" style={{ borderColor: COLORS.border }}
                                      onError={(e) => { e.target.style.display = 'none'; }} />
                                  ) : (
                                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-lg">💎</div>
                                  )}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                      <span className="text-xs font-bold" style={{ color: COLORS.textPrimary }}>
                                        {d.combinationNumber}
                                      </span>
                                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100" style={{ color: COLORS.textSecondary }}>
                                        Design {d.designNumber}
                                      </span>
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${
                                        d.attributesStatus === 'verified' ? 'bg-green-50 text-green-700' :
                                        d.attributesStatus === 'pending' ? 'bg-amber-50 text-amber-700' :
                                        d.attributesStatus === 'error' ? 'bg-red-50 text-red-700' :
                                        'bg-gray-50 text-gray-500'
                                      }`}>
                                        {d.attributesStatus === 'verified' ? '✓ Verified' :
                                         d.attributesStatus === 'pending' ? '⏳ Pending' :
                                         d.attributesStatus === 'error' ? '✕ Failed' : '○ Processing'}
                                      </span>
                                    </div>
                                    <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                                      {d.productType} · {d.baseMaterial}
                                      {d.itemName && ` · ${d.itemName}`}
                                      {d.mrp && ` · ₹${Number(d.mrp).toLocaleString()}`}
                                    </p>
                                    {d.processingError && (
                                      <p className="text-[10px] mt-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-red-600 truncate" title={d.processingError}>
                                        ⚠ {d.processingError}
                                      </p>
                                    )}
                                    {d.missingFields && d.missingFields.length > 0 && (
                                      <p className="text-[10px] mt-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-amber-700 truncate"
                                        title={`Missing: ${d.missingFields.join(', ')}`}>
                                        Missing: {d.missingFields.join(', ')}
                                      </p>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 flex-shrink-0">
                                    {d.attributesStatus === 'needs_processing' && (
                                      <button
                                        onClick={async (e) => {
                                          e.stopPropagation();
                                          const btn = e.currentTarget;
                                          btn.disabled = true;
                                          btn.innerHTML = '<span class="animate-spin">↻</span>';
                                          try {
                                            const res = await config.apiFetch(config.buildUrl('/api/batch-history/reprocess'), {
                                              method: 'POST',
                                              body: JSON.stringify({ designNumbers: [d.designNumber] })
                                            });
                                            const data = await res.json();
                                            if (data.success && data.triggered > 0) {
                                              alert(`Reprocessing triggered for design ${d.designNumber}`);
                                            } else if (data.skippedReasons && data.skippedReasons.length > 0) {
                                              alert(data.skippedReasons.map(s => s.reason).join('\n'));
                                            }
                                          } catch (err) {
                                            alert('Reprocess failed: ' + err.message);
                                          }
                                          btn.disabled = false;
                                          btn.innerHTML = '↻ Retry';
                                        }}
                                        className="px-2 py-1 text-[10px] font-semibold bg-white border rounded hover:bg-gray-50 transition-all"
                                        style={{ color: COLORS.maroon, borderColor: '#E5E7EB' }}
                                      >
                                        ↻ Retry
                                      </button>
                                    )}
                                    <p className="text-[10px]" style={{ color: COLORS.textLight }}>
                                      {new Date(d.createdAt).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                  </div>
                                </div>
                              ))
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Occasion Review Page */}
          {currentPage === 'occasion_review' && <OccasionReview />}

          {/* Category Review Page */}
          {currentPage === 'category_review' && <CategoryReview />}

          {/* Pending Page */}
          {currentPage === 'pending' && (
            <div className="max-w-7xl mx-auto space-y-8">
              <div className="flex justify-between items-center">
                <PageSizeSelector
                  pageSize={pendingPageSize}
                  setPageSize={setPendingPageSize}
                  totalItems={(pendingTotalItems || 0)}
                />
                <button
                  onClick={fetchPending}
                  className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
                  style={{ color: COLORS.textSecondary }}
                >
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>

              <DateRangeFilter
                label="Date Created"
                currentPreset={pendingDateFilter.preset}
                startDate={pendingDateFilter.startDate}
                endDate={pendingDateFilter.endDate}
                onFilterChange={setPendingDateFilter}
              />

              <DesignGrid
                items={paginatedPendingItems}
                onItemClick={openDesignDetails}
                clickable={true}
                onRetry={handleRetry}
                retryingDesign={retryingDesign}
                onMoveToEnrichment={handleMoveToEnrichment}
                movingDesign={movingDesign}
              />
              
              <PaginationControls
                currentPage={pendingCurrentPage}
                totalPages={pendingTotalPages}
                totalItems={(pendingTotalItems || 0)}
                pageSize={pendingPageSize}
                onPageChange={setPendingCurrentPage}
              />
            </div>
          )}


          {/* Verified Page */}
          {currentPage === 'verified' && (
            <div className="max-w-7xl mx-auto space-y-8">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-4">
                  <PageSizeSelector
                    pageSize={verifiedPageSize}
                    setPageSize={setVerifiedPageSize}
                    totalItems={(verifiedItems?.length || 0)}
                  />
                  {selectedDesignsForPush.length > 0 && (
                    <span className="text-xs font-semibold px-3 py-1 rounded-full" style={{ backgroundColor: COLORS.maroon + '15', color: COLORS.maroon }}>
                      {selectedDesignsForPush.length} selected
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  {selectedDesignsForPush.length > 0 && (
                    <>
                      <button
                        onClick={() => setSelectedDesignsForPush([])}
                        className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
                        style={{ color: COLORS.textSecondary }}
                      >
                        <X size={14} />
                        Clear
                      </button>
                      <button
                        onClick={() => setShowPushModal(true)}
                        className="px-4 py-2 text-xs font-semibold tracking-wider uppercase text-white transition-colors hover:opacity-90 flex items-center gap-2 rounded-lg shadow-md"
                        style={{ backgroundColor: COLORS.maroon }}
                      >
                        <Send size={14} />
                        Push to Channels
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => {
                      // Toggle select mode: select all / deselect all
                      if (selectedDesignsForPush.length === (verifiedItems?.length || 0)) {
                        setSelectedDesignsForPush([]);
                      } else {
                        setSelectedDesignsForPush((verifiedItems || []).map(d => d.designNumber));
                      }
                    }}
                    className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    style={{ color: COLORS.textSecondary }}
                  >
                    <CheckCircle size={14} />
                    {selectedDesignsForPush.length === (verifiedItems?.length || 0) && selectedDesignsForPush.length > 0 ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    onClick={fetchVerified}
                    className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border border-gray-200 hover:bg-gray-50 transition-colors flex items-center gap-2"
                    style={{ color: COLORS.textSecondary }}
                  >
                    <RefreshCw size={14} />
                    Refresh
                  </button>
                  {(verifiedItems?.length || 0) > 0 && (
                    <button
                      onClick={handleExport}
                      className="px-4 py-2 text-xs font-semibold tracking-wider uppercase text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: COLORS.maroon }}
                    >
                      Export to CSV
                    </button>
                  )}
                </div>
              </div>

              <DateRangeFilter
                label="Date Verified"
                currentPreset={verifiedDateFilter.preset}
                startDate={verifiedDateFilter.startDate}
                endDate={verifiedDateFilter.endDate}
                onFilterChange={setVerifiedDateFilter}
              />

              <DesignGrid
                items={paginatedVerifiedItems}
                verified={true}
                selectable={true}
                selectedItems={selectedDesignsForPush}
                onToggleSelect={(dn) => {
                  setSelectedDesignsForPush(prev =>
                    prev.includes(dn) ? prev.filter(d => d !== dn) : [...prev, dn]
                  );
                }}
              />

              <PaginationControls
                currentPage={verifiedCurrentPage}
                totalPages={verifiedTotalPages}
                totalItems={(verifiedItems?.length || 0)}
                pageSize={verifiedPageSize}
                onPageChange={setVerifiedCurrentPage}
              />

              {/* Push to Channels Modal */}
              {showPushModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden">
                    <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                          Push to Channels
                        </h3>
                        <button onClick={() => setShowPushModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                          <X size={18} />
                        </button>
                      </div>
                      <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
                        {selectedDesignsForPush.length} design(s) selected
                      </p>
                    </div>
                    <div className="p-6 space-y-3">
                      <p className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: COLORS.textSecondary }}>
                        Select Platforms
                      </p>
                      {platforms.map((platform) => (
                        <label
                          key={platform.id}
                          className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:bg-gray-50"
                          style={{
                            borderColor: selectedPlatformsForPush.includes(platform.id) ? COLORS.maroon : COLORS.border,
                            backgroundColor: selectedPlatformsForPush.includes(platform.id) ? COLORS.maroon + '08' : 'transparent'
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedPlatformsForPush.includes(platform.id)}
                            onChange={() => {
                              setSelectedPlatformsForPush(prev =>
                                prev.includes(platform.id)
                                  ? prev.filter(p => p !== platform.id)
                                  : [...prev, platform.id]
                              );
                            }}
                            className="w-4 h-4 rounded"
                          />
                          <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: platform.color }} />
                          <span className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                            {platform.name}
                          </span>
                          <span className="text-xs ml-auto" style={{ color: COLORS.textSecondary }}>
                            {platform.designCount || 0} designs
                          </span>
                        </label>
                      ))}
                    </div>
                    <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: COLORS.border }}>
                      <button
                        onClick={() => setShowPushModal(false)}
                        className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                        style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handlePushToChannels}
                        disabled={selectedPlatformsForPush.length === 0 || isPushing}
                        className="px-6 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                        style={{ backgroundColor: COLORS.maroon }}
                      >
                        <Send size={14} />
                        {isPushing ? 'Pushing...' : `Push ${selectedDesignsForPush.length} Design(s)`}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}


          {/* Channel Platform Page */}
          {currentPage.startsWith('channel_') && (() => {
            const activePlatformId = currentPage.replace('channel_', '');
            const activePlatform = platforms.find(p => p.id === activePlatformId);
            if (!activePlatform) return <div className="p-12 text-center" style={{ color: COLORS.textSecondary }}>Platform not found</div>;

            const getEffectiveStatus = (d) => (d.channelStatus === 'assigned' || !d.channelStatus) ? 'draft' : d.channelStatus;
            const filteredDesigns = platformDesigns.filter(d => getEffectiveStatus(d) === platformStatusFilter);

            const statusCounts = {
              draft: platformDesigns.filter(d => getEffectiveStatus(d) === 'draft').length,
              reviewed: platformDesigns.filter(d => getEffectiveStatus(d) === 'reviewed').length,
              published: platformDesigns.filter(d => getEffectiveStatus(d) === 'published').length,
            };

            return (
              <div className="max-w-7xl mx-auto space-y-6">
                {/* Platform Header */}
                <div className="bg-white rounded-xl border p-6 pb-4" style={{ borderColor: COLORS.border, borderLeft: `4px solid ${activePlatform.color}` }}>
                  {/* Row 1: Identity + Primary Actions */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: activePlatform.color }}>
                        {getPlatformIcon(activePlatform.icon, 22)}
                      </div>
                      <div>
                        <h2 className="text-xl font-bold" style={{ color: COLORS.textPrimary }}>{activePlatform.name} Channel</h2>
                        <div className="flex items-center gap-3 mt-1">
                          <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                            {activePlatform.designCount || 0} designs
                          </p>
                          {activePlatform.hasApi ? (
                            activePlatform.hasCredentials ? (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-green-50 text-green-600 flex items-center gap-1">
                                <CheckCircle size={10} />
                                Connected
                              </span>
                            ) : (
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-50 text-amber-600 flex items-center gap-1">
                                <ExternalLink size={10} />
                                API — Not Configured
                              </span>
                            )
                          ) : (
                            <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                              CSV Export Only
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <button
                        onClick={() => setShowAddDesignsModal(true)}
                        className="px-5 py-2.5 text-xs font-bold tracking-wider uppercase text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2"
                        style={{ backgroundColor: activePlatform.color }}
                      >
                        <Plus size={14} />
                        Add Designs
                      </button>
                      {activePlatform.hasApi && activePlatform.hasCredentials && (
                        <button
                          disabled={isPublishing}
                          onClick={() => {
                            const reviewedDesigns = platformDesigns.filter(d => d.channelStatus === 'reviewed');
                            if (reviewedDesigns.length === 0) {
                              alert('No designs in "Reviewed" status to publish. Review designs first.');
                              return;
                            }
                            if (window.confirm(`Publish ${reviewedDesigns.length} reviewed design(s) to ${activePlatform.name}?`)) {
                              handleBulkPublish(activePlatformId, reviewedDesigns.map(d => d.designNumber));
                            }
                          }}
                          className="px-5 py-2.5 text-xs font-bold tracking-wider uppercase text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
                          style={{ backgroundColor: '#10B981' }}
                        >
                          <Send size={14} />
                          {isPublishing ? 'Publishing...' : `Publish (${statusCounts.reviewed || 0})`}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Row 2: Status Tabs + Secondary Actions */}
                  <div className="flex items-center justify-between mt-5 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                    <div className="flex items-center gap-1.5">
                      {[
                        { key: 'draft', label: 'Draft', color: activePlatform.color },
                        { key: 'reviewed', label: 'Reviewed', color: '#3B82F6' },
                        { key: 'published', label: 'Published', color: '#10B981' },
                      ].map(tab => (
                        <button
                          key={tab.key}
                          onClick={() => { setShowCollectionsTab(false); setPlatformStatusFilter(tab.key); }}
                          className={`px-3.5 py-1.5 text-[11px] font-bold tracking-wider uppercase rounded-lg transition-all flex items-center gap-1.5 ${
                            !showCollectionsTab && platformStatusFilter === tab.key ? 'text-white shadow-sm' : 'hover:bg-gray-50'
                          }`}
                          style={{
                            backgroundColor: !showCollectionsTab && platformStatusFilter === tab.key ? tab.color : 'transparent',
                            color: !showCollectionsTab && platformStatusFilter === tab.key ? '#FFFFFF' : COLORS.textSecondary
                          }}
                        >
                          {tab.label}
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${
                            !showCollectionsTab && platformStatusFilter === tab.key ? 'bg-white/20' : 'bg-gray-100'
                          }`}>
                            {statusCounts[tab.key] || 0}
                          </span>
                        </button>
                      ))}
                      <div className="w-px h-5 mx-1 self-center" style={{ backgroundColor: COLORS.border }} />
                      <button
                        onClick={() => {
                          setShowCollectionsTab(!showCollectionsTab);
                          if (!showCollectionsTab) fetchChannelCollections(activePlatformId);
                        }}
                        className={`px-3.5 py-1.5 text-[11px] font-bold tracking-wider uppercase rounded-lg transition-all flex items-center gap-1.5 ${
                          showCollectionsTab ? 'text-white shadow-sm' : 'hover:bg-gray-50'
                        }`}
                        style={{
                          backgroundColor: showCollectionsTab ? '#8B5CF6' : 'transparent',
                          color: showCollectionsTab ? '#FFFFFF' : COLORS.textSecondary
                        }}
                      >
                        <Folder size={12} />
                        Collections
                        {channelCollections.length > 0 && (
                          <span className={`px-1.5 py-0.5 rounded-full text-[10px] ${showCollectionsTab ? 'bg-white/20' : 'bg-gray-100'}`}>
                            {channelCollections.length}
                          </span>
                        )}
                      </button>
                      {activePlatformId === 'shopify' && (
                        <button
                          onClick={() => setShopifyProductsTab(t => !t)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 18px',
                            borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                            background: shopifyProductsTab
                              ? `linear-gradient(135deg, ${COLORS.maroon} 0%, #6B1530 100%)`
                              : 'white',
                            color: shopifyProductsTab ? 'white' : COLORS.maroon,
                            boxShadow: shopifyProductsTab ? '0 4px 20px rgba(139,29,60,0.35)' : '0 1px 6px rgba(0,0,0,0.08)',
                            border: `1.5px solid ${shopifyProductsTab ? 'transparent' : 'rgba(139,29,60,0.2)'}`,
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <span>🛍️</span> Shopify Catalog
                          {shopifyProductsTotal > 0 && (
                            <span style={{ background: shopifyProductsTab ? 'rgba(255,255,255,0.25)' : COLORS.maroon,
                              color: 'white', borderRadius: 99, padding: '2px 8px', fontSize: 11 }}>
                              {formatNumber(shopifyProductsTotal)}
                            </span>
                          )}
                        </button>
                      )}
                    </div>
                    <div className="flex gap-1">
                      {activePlatform.hasApi && (
                        <button
                          onClick={() => {
                            fetchPlatformCredentials(activePlatformId);
                            setConnectionStatus(null);
                            setShowPlatformSettings(true);
                          }}
                          className="p-2 text-[10px] font-semibold tracking-wider uppercase hover:bg-gray-100 rounded-lg flex items-center gap-1.5 transition-all"
                          style={{ color: activePlatform.hasCredentials ? COLORS.textLight : '#F59E0B' }}
                          title={activePlatform.hasCredentials ? 'Platform Settings' : 'Connect API'}
                        >
                          <Settings size={15} />
                          <span className="hidden xl:inline">{activePlatform.hasCredentials ? 'Settings' : 'Connect'}</span>
                        </button>
                      )}
                      <button
                        onClick={() => {
                          fetchPlatformConfig(activePlatformId);
                          setShowPlatformConfigModal(true);
                        }}
                        className="p-2 text-[10px] font-semibold tracking-wider uppercase hover:bg-gray-100 rounded-lg flex items-center gap-1.5 transition-all"
                        style={{ color: COLORS.textLight }}
                        title="Configure Fields"
                      >
                        <GripVertical size={15} />
                        <span className="hidden xl:inline">Fields</span>
                      </button>
                      <div className="relative" ref={exportDropdownRef}>
                        <button
                          onClick={() => setShowExportDropdown(!showExportDropdown)}
                          className="p-2 text-[10px] font-semibold tracking-wider uppercase hover:bg-gray-100 rounded-lg flex items-center gap-1.5 transition-all"
                          style={{ color: COLORS.textLight }}
                          title="Export Data"
                        >
                          <Download size={15} />
                          <span className="hidden xl:inline">Export</span>
                          <ChevronDown size={10} />
                        </button>
                        {showExportDropdown && (
                          <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg z-50 min-w-[240px]"
                               style={{ borderColor: COLORS.border }}>
                            {exportTemplates.map(tmpl => (
                              <button
                                key={tmpl.key}
                                className="block w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg border-b last:border-b-0"
                                style={{ color: COLORS.textPrimary, borderColor: COLORS.border }}
                                onClick={async () => {
                                  setShowExportDropdown(false);
                                  try {
                                    // Fetch the export file with mark_published flag
                                    const exportUrl = config.endpoints.channelExport(activePlatformId, tmpl.key)
                                      + (tmpl.key ? '&' : '?') + 'mark_published=true';
                                    const res = await config.apiFetch(exportUrl);
                                    if (!res.ok) {
                                      const err = await res.json();
                                      alert('Export failed: ' + (err.error || 'Unknown error'));
                                      return;
                                    }
                                    // Trigger file download from response blob
                                    const blob = await res.blob();
                                    const filename = res.headers.get('content-disposition')
                                      ?.match(/filename="?([^"]+)"?/)?.[1] || `${activePlatformId}_export.${tmpl.extension || 'csv'}`;
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = filename;
                                    document.body.appendChild(a);
                                    a.click();
                                    a.remove();
                                    window.URL.revokeObjectURL(url);
                                    // Refresh designs list and switch to Published tab
                                    fetchPlatformDesigns(activePlatformId);
                                    setPlatformStatusFilter('published');
                                  } catch (e) {
                                    console.error('Export error:', e);
                                    alert('Failed to export designs');
                                  }
                                }}
                              >
                                <div className="font-medium text-xs uppercase tracking-wider">{tmpl.label}</div>
                                <div className="text-[11px] mt-0.5" style={{ color: COLORS.textSecondary }}>
                                  {tmpl.description} &middot; .{tmpl.extension}
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <DateRangeFilter
                  label={platformStatusFilter === 'published' ? 'Date Published' : platformStatusFilter === 'reviewed' ? 'Date Reviewed' : 'Date Assigned'}
                  currentPreset={channelDateFilter.preset}
                  startDate={channelDateFilter.startDate}
                  endDate={channelDateFilter.endDate}
                  onFilterChange={setChannelDateFilter}
                />

                {/* ═══ Shopify Products Dashboard ═══ */}
                {shopifyProductsTab && (() => {
                  const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
                  return (
                    <div style={{ marginTop: 24 }}>
                      {/* Hero Summary Cards */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20, marginBottom: 28 }}>
                        {[
                          { label: 'Total Products', value: formatNumber(shopifyProductsTotal), sub: 'on Shopify store', icon: '🏷️', gradient: 'linear-gradient(135deg, #8B1D3C 0%, #6B1530 100%)' },
                          { label: 'Total Revenue', value: formatCurrency(shopifyAnalyticsSummary?.totalRevenue, shopifyAnalyticsSummary?.currency), sub: `Last ${shopifyAnalyticsDays} days`, icon: '₹', gradient: 'linear-gradient(135deg, #D4AF37 0%, #B8962E 100%)' },
                          { label: 'Units Sold', value: formatNumber(shopifyAnalyticsSummary?.totalUnitsSold), sub: `Last ${shopifyAnalyticsDays} days`, icon: '📦', gradient: 'linear-gradient(135deg, #7C3AED 0%, #5B21B6 100%)' },
                          { label: 'Total Orders', value: formatNumber(shopifyAnalyticsSummary?.totalOrders), sub: `Last ${shopifyAnalyticsDays} days`, icon: '🛒', gradient: 'linear-gradient(135deg, #0891B2 0%, #0E7490 100%)' },
                        ].map(card => (
                          <div key={card.label} style={{ background: card.gradient, borderRadius: 18, padding: '22px 24px', color: 'white', boxShadow: '0 8px 32px rgba(0,0,0,0.12)', position: 'relative', overflow: 'hidden' }}>
                            <div style={{ position: 'absolute', top: -10, right: -10, fontSize: 52, opacity: 0.15 }}>{card.icon}</div>
                            <div style={{ fontSize: 13, fontWeight: 500, opacity: 0.85, marginBottom: 8 }}>{card.label}</div>
                            <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1, marginBottom: 6 }}>{shopifyAnalyticsLoading ? '…' : card.value}</div>
                            <div style={{ fontSize: 11, opacity: 0.7 }}>{card.sub}</div>
                          </div>
                        ))}
                      </div>

                      {/* Controls Bar */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 220, position: 'relative' }}>
                          <input value={shopifyProductsSearch} onChange={e => { setShopifyProductsSearch(e.target.value); setShopifyCurrentPage(1); }}
                            placeholder="Search by title, SKU, design number…"
                            style={{ width: '100%', padding: '10px 16px 10px 40px', borderRadius: 10, fontSize: 13, border: '1.5px solid rgba(212,175,55,0.3)', background: 'white', outline: 'none', boxSizing: 'border-box' }} />
                          <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 16, color: COLORS.textLight }}>🔍</span>
                        </div>
                        {['ACTIVE', 'DRAFT', 'ALL'].map(s => (
                          <button key={s} onClick={() => { setShopifyProductsFilter(s); setShopifyProducts([]); setShopifyCurrentPage(1); fetchShopifyProducts(shopifyPlatformId, null, false, s); }}
                            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                              background: shopifyProductsFilter === s ? COLORS.maroon : 'white', color: shopifyProductsFilter === s ? 'white' : COLORS.textSecondary,
                              boxShadow: '0 1px 4px rgba(0,0,0,0.08)' }}>{s}</button>
                        ))}
                        <select value={shopifyAnalyticsDays} onChange={e => setShopifyAnalyticsDays(Number(e.target.value))}
                          style={{ padding: '8px 12px', borderRadius: 8, border: '1.5px solid rgba(212,175,55,0.3)', fontSize: 12, fontWeight: 600, color: COLORS.textPrimary, background: 'white' }}>
                          {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}
                        </select>
                        <button onClick={() => fetchShopifyProducts(shopifyPlatformId)} disabled={shopifyProductsLoading}
                          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600, background: COLORS.gold, color: 'white', opacity: shopifyProductsLoading ? 0.6 : 1 }}>
                          {shopifyProductsLoading ? '⟳ Loading…' : '⟳ Refresh'}
                        </button>
                        <button onClick={() => fetchShopifyAnalytics(shopifyPlatformId, shopifyProducts.map(p => p.id), shopifyAnalyticsDays)}
                          disabled={shopifyAnalyticsLoading || shopifyProducts.length === 0}
                          style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 600,
                            background: `linear-gradient(135deg, ${COLORS.maroon} 0%, #6B1530 100%)`, color: 'white', opacity: shopifyAnalyticsLoading ? 0.6 : 1 }}>
                          {shopifyAnalyticsLoading ? '⟳ Loading Analytics…' : '📊 Load Sales Data'}
                        </button>
                      </div>

                      {/* Scope Error Banner */}
                      {shopifyAnalyticsScopeError && (
                        <div style={{ background: '#FEF3C7', border: '1px solid #F59E0B', borderRadius: 12, padding: '14px 20px', marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12, fontSize: 13 }}>
                          <span style={{ fontSize: 20 }}>⚠️</span>
                          <div>
                            <strong style={{ color: '#92400E' }}>Sales data requires additional permission.</strong>
                            <div style={{ color: '#78350F', marginTop: 2 }}>Add <code style={{ background: '#FDE68A', padding: '1px 6px', borderRadius: 4 }}>read_orders</code> scope to your Shopify access token, then regenerate it.</div>
                          </div>
                        </div>
                      )}

                      {/* Products Grid */}
                      {shopifyProductsLoading && shopifyProducts.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: COLORS.textLight }}>
                          <div style={{ fontSize: 32, marginBottom: 12 }}>⟳</div>
                          <div>Fetching products from Shopify…</div>
                        </div>
                      ) : shopifyProducts.length === 0 ? (
                        <div style={{ textAlign: 'center', padding: '60px 0', color: COLORS.textLight }}>
                          <div style={{ fontSize: 40, marginBottom: 12 }}>🛍️</div>
                          <div style={{ fontSize: 16, fontWeight: 600, color: COLORS.textSecondary }}>No products found</div>
                          <div style={{ fontSize: 13, marginTop: 6 }}>Click Refresh to load your Shopify catalog</div>
                          <button onClick={() => fetchShopifyProducts(shopifyPlatformId)}
                            style={{ marginTop: 16, padding: '10px 24px', borderRadius: 10, border: 'none', cursor: 'pointer', background: COLORS.maroon, color: 'white', fontWeight: 600, fontSize: 13 }}>
                            Load Products
                          </button>
                        </div>
                      ) : (() => {
                        const filteredShopifyProducts = shopifyProducts.filter(p => {
                          if (!shopifyProductsSearch) return true;
                          const q = shopifyProductsSearch.toLowerCase();
                          return p.title.toLowerCase().includes(q) || p.handle?.toLowerCase().includes(q) ||
                            p.designNumber?.toLowerCase().includes(q) || p.variants?.some(v => v.sku?.toLowerCase().includes(q));
                        });
                        const shopifyTotalPages = Math.ceil(filteredShopifyProducts.length / shopifyPageSize);
                        const shopifyPaginatedProducts = filteredShopifyProducts.slice(
                          (shopifyCurrentPage - 1) * shopifyPageSize,
                          shopifyCurrentPage * shopifyPageSize
                        );
                        return (
                          <>
                            {/* Pagination header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                              <div style={{ fontSize: 13, color: COLORS.textSecondary }}>
                                Showing {filteredShopifyProducts.length > 0 ? (shopifyCurrentPage - 1) * shopifyPageSize + 1 : 0}–{Math.min(shopifyCurrentPage * shopifyPageSize, filteredShopifyProducts.length)} of {filteredShopifyProducts.length} loaded
                                {shopifyProductsTotal > 0 && ` (${formatNumber(shopifyProductsTotal)} total on Shopify)`}
                              </div>
                              <PageSizeSelector pageSize={shopifyPageSize} setPageSize={(v) => { setShopifyPageSize(v); setShopifyCurrentPage(1); }} totalItems={filteredShopifyProducts.length} />
                            </div>

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 20 }}>
                              {shopifyPaginatedProducts.map(product => {
                                  const a = shopifyProductsAnalytics[product.id] || {};
                                  return (
                                    <div key={product.id} onClick={() => { setShopifySelectedProduct(product); fetchShopifyTrends(shopifyPlatformId, product.id, shopifyAnalyticsDays); fetchProductMetafields(product.id); }}
                                      style={{ background: 'white', borderRadius: 16, overflow: 'hidden', cursor: 'pointer',
                                        boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1.5px solid rgba(212,175,55,0.15)', transition: 'box-shadow 0.2s' }}>
                                      <div style={{ height: 160, background: 'linear-gradient(135deg, #FDF8F0 0%, #F3E8D0 100%)', position: 'relative', overflow: 'hidden' }}>
                                        {product.image
                                          ? <img src={product.image} alt={product.imageAlt} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                          : <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 40, opacity: 0.3 }}>💎</div>}
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newStatus = product.status === 'ACTIVE' ? 'DRAFT' : 'ACTIVE';
                                            updateShopifyProductStatus(product.id, newStatus);
                                          }}
                                          disabled={statusUpdatingProductId === product.id}
                                          style={{
                                            position: 'absolute', top: 10, left: 10, padding: '3px 10px', borderRadius: 99,
                                            fontSize: 10, fontWeight: 700, border: 'none', cursor: 'pointer',
                                            background: product.status === 'ACTIVE' ? '#D1FAE5' : '#FEF3C7',
                                            color: product.status === 'ACTIVE' ? '#065F46' : '#92400E',
                                            opacity: statusUpdatingProductId === product.id ? 0.6 : 1,
                                            display: 'flex', alignItems: 'center', gap: 4,
                                            transition: 'all 0.2s',
                                          }}
                                          title={`Click to ${product.status === 'ACTIVE' ? 'set as Draft' : 'set as Active'}`}
                                        >
                                          {statusUpdatingProductId === product.id ? '⏳' : product.status === 'ACTIVE' ? '●' : '○'}
                                          {' '}{product.status}
                                        </button>
                                        {a.revenue > 0 && (
                                          <span style={{ position: 'absolute', top: 10, right: 10, padding: '3px 10px', borderRadius: 99, fontSize: 11, fontWeight: 700, background: 'rgba(212,175,55,0.9)', color: 'white' }}>
                                            {formatCurrency(a.revenue, a.currency)}
                                          </span>
                                        )}
                                      </div>
                                      <div style={{ padding: '16px 18px 18px' }}>
                                        <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary, marginBottom: 4, lineHeight: 1.3 }}>{product.title}</div>
                                        <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 10 }}>
                                          {product.designNumber && <span style={{ color: COLORS.gold, fontWeight: 600 }}>#{product.designNumber} · </span>}
                                          {product.variantCount} variant{product.variantCount !== 1 ? 's' : ''}
                                          {product.totalInventory != null && ` · ${product.totalInventory} in stock`}
                                        </div>
                                        <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.maroon, marginBottom: 12 }}>
                                          {product.minPrice === product.maxPrice
                                            ? formatCurrency(product.minPrice, product.currency)
                                            : `${formatCurrency(product.minPrice, product.currency)} – ${formatCurrency(product.maxPrice, product.currency)}`}
                                        </div>
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, borderTop: '1px solid rgba(212,175,55,0.15)', paddingTop: 12, marginBottom: 10 }}>
                                          {[{ label: 'Units', value: a.unitsSold ?? '—' }, { label: 'Orders', value: a.orderCount ?? '—' }, { label: 'AOV', value: a.avgOrderValue ? formatCurrency(a.avgOrderValue, a.currency) : '—' }].map(m => (
                                            <div key={m.label} style={{ textAlign: 'center' }}>
                                              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>{shopifyAnalyticsLoading ? '…' : m.value}</div>
                                              <div style={{ fontSize: 10, color: COLORS.textLight, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{m.label}</div>
                                            </div>
                                          ))}
                                        </div>
                                        {a.revenue > 0 && (
                                          <div style={{ borderTop: '1px solid rgba(212,175,55,0.1)', paddingTop: 8 }}>
                                            <div style={{ fontSize: 10, color: COLORS.textLight, marginBottom: 4 }}>REVENUE TREND</div>
                                            <Sparkline data={Array(14).fill(null).map((_, i) => ({ revenue: (a.revenue / 14) * (0.7 + Math.abs(Math.sin(i * 0.8)) * 0.6) }))} color={COLORS.gold} width={240} height={28} />
                                          </div>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                            </div>

                            {/* Pagination Controls */}
                            <div style={{ marginTop: 24 }}>
                              <PaginationControls
                                currentPage={shopifyCurrentPage}
                                totalPages={shopifyTotalPages}
                                totalItems={filteredShopifyProducts.length}
                                pageSize={shopifyPageSize}
                                onPageChange={(page) => { setShopifyCurrentPage(page); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                              />
                            </div>

                            {/* Load More from Shopify */}
                            {shopifyProductsPagination.hasNextPage && (
                              <div style={{ textAlign: 'center', marginTop: 20, padding: '16px', background: 'rgba(139,29,60,0.03)', borderRadius: 12, border: `1px dashed ${COLORS.border}` }}>
                                <div style={{ fontSize: 12, color: COLORS.textSecondary, marginBottom: 8 }}>
                                  {shopifyProducts.length} of {formatNumber(shopifyProductsTotal)} products loaded from Shopify
                                </div>
                                <button onClick={() => fetchShopifyProducts(shopifyPlatformId, shopifyProductsPagination.endCursor, true)} disabled={shopifyProductsLoading}
                                  style={{ padding: '10px 28px', borderRadius: 10, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                                    background: `linear-gradient(135deg, ${COLORS.maroon} 0%, #6B1530 100%)`, color: 'white', boxShadow: '0 4px 20px rgba(139,29,60,0.3)', opacity: shopifyProductsLoading ? 0.6 : 1 }}>
                                  {shopifyProductsLoading ? '⟳ Loading…' : `Load Next ${Math.min(50, shopifyProductsTotal - shopifyProducts.length)} Products`}
                                </button>
                              </div>
                            )}
                          </>
                        );
                      })()}
                    </div>
                  );
                })()}

                {/* Designs View OR Collections View */}
                {!showCollectionsTab ? (
                <>

                {/* Selection Action Bar */}
                {selectedCardDesigns.size > 0 && (
                  <div className="flex items-center justify-between px-4 py-2.5 rounded-lg border" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                        {selectedCardDesigns.size} design{selectedCardDesigns.size > 1 ? 's' : ''} selected
                      </span>
                      <button
                        onClick={() => setSelectedCardDesigns(new Set())}
                        className="text-xs underline hover:no-underline"
                        style={{ color: COLORS.textSecondary }}
                      >
                        Clear
                      </button>
                      <span className="text-xs" style={{ color: COLORS.textLight }}>|</span>
                      <button
                        onClick={() => {
                          if (selectedCardDesigns.size === filteredDesigns.length) {
                            setSelectedCardDesigns(new Set());
                          } else {
                            setSelectedCardDesigns(new Set(filteredDesigns.map(d => d.designNumber)));
                          }
                        }}
                        className="text-xs underline hover:no-underline"
                        style={{ color: COLORS.textSecondary }}
                      >
                        {selectedCardDesigns.size === filteredDesigns.length && filteredDesigns.length > 0 ? 'Deselect all' : `Select all (${filteredDesigns.length})`}
                      </button>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Move to Draft / Reviewed — shown on Published or Reviewed tabs */}
                      {platformStatusFilter === 'published' && (
                        <>
                          <button
                            onClick={() => {
                              if (window.confirm(`Move ${selectedCardDesigns.size} design(s) back to Draft?`)) {
                                const designNumbers = [...selectedCardDesigns];
                                Promise.all(
                                  designNumbers.map(dn =>
                                    config.apiFetch(config.endpoints.channelDesignDetail(activePlatformId, dn), {
                                      method: 'PUT',
                                      body: JSON.stringify({ status: 'draft' })
                                    }).then(r => r.json())
                                  )
                                ).then(() => {
                                  fetchPlatformDesigns(activePlatformId);
                                  setSelectedCardDesigns(new Set());
                                  setPlatformStatusFilter('draft');
                                });
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-semibold bg-white border rounded-lg hover:bg-amber-50 transition-all flex items-center gap-1.5"
                            style={{ color: '#D97706', borderColor: '#FCD34D' }}
                          >
                            <RotateCcw size={12} />
                            Move to Draft
                          </button>
                          <button
                            onClick={() => {
                              if (window.confirm(`Move ${selectedCardDesigns.size} design(s) back to Reviewed?`)) {
                                const designNumbers = [...selectedCardDesigns];
                                Promise.all(
                                  designNumbers.map(dn =>
                                    config.apiFetch(config.endpoints.channelDesignDetail(activePlatformId, dn), {
                                      method: 'PUT',
                                      body: JSON.stringify({ status: 'reviewed' })
                                    }).then(r => r.json())
                                  )
                                ).then(() => {
                                  fetchPlatformDesigns(activePlatformId);
                                  setSelectedCardDesigns(new Set());
                                  setPlatformStatusFilter('reviewed');
                                });
                              }
                            }}
                            className="px-3 py-1.5 text-xs font-semibold bg-white border rounded-lg hover:bg-blue-50 transition-all flex items-center gap-1.5"
                            style={{ color: '#3B82F6', borderColor: '#93C5FD' }}
                          >
                            <RotateCcw size={12} />
                            Move to Reviewed
                          </button>
                        </>
                      )}
                      {platformStatusFilter === 'reviewed' && (
                        <button
                          onClick={() => {
                            if (window.confirm(`Move ${selectedCardDesigns.size} design(s) back to Draft?`)) {
                              const designNumbers = [...selectedCardDesigns];
                              Promise.all(
                                designNumbers.map(dn =>
                                  config.apiFetch(config.endpoints.channelDesignDetail(activePlatformId, dn), {
                                    method: 'PUT',
                                    body: JSON.stringify({ status: 'draft' })
                                  }).then(r => r.json())
                                )
                              ).then(() => {
                                fetchPlatformDesigns(activePlatformId);
                                setSelectedCardDesigns(new Set());
                                setPlatformStatusFilter('draft');
                              });
                            }
                          }}
                          className="px-3 py-1.5 text-xs font-semibold bg-white border rounded-lg hover:bg-amber-50 transition-all flex items-center gap-1.5"
                          style={{ color: '#D97706', borderColor: '#FCD34D' }}
                        >
                          <RotateCcw size={12} />
                          Move to Draft
                        </button>
                      )}
                      {/* Publish — shown on Draft and Reviewed tabs */}
                      {platformStatusFilter !== 'published' && (
                        <button
                          disabled={isPublishing}
                          onClick={() => {
                            if (window.confirm(`Publish ${selectedCardDesigns.size} design(s) to ${activePlatform.name}?`)) {
                              handleBulkPublish(activePlatformId, [...selectedCardDesigns]);
                              setSelectedCardDesigns(new Set());
                            }
                          }}
                          className="px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90 transition-all flex items-center gap-1.5 disabled:opacity-50"
                          style={{ backgroundColor: COLORS.maroon }}
                        >
                          <Send size={12} />
                          {isPublishing ? 'Publishing...' : 'Publish'}
                        </button>
                      )}
                      <button
                        disabled={isBulkRemoving}
                        onClick={() => {
                          if (window.confirm(`Remove ${selectedCardDesigns.size} design(s) from ${activePlatform.name}?`)) {
                            const designNumbers = [...selectedCardDesigns];
                            setIsBulkRemoving(true);
                            Promise.all(
                              designNumbers.map(dn =>
                                config.apiFetch(config.endpoints.channelDesignDetail(activePlatformId, dn), { method: 'DELETE' })
                                  .then(r => r.json())
                                  .then(data => ({ dn, success: data.success, error: data.error }))
                                  .catch(err => ({ dn, success: false, error: err.message }))
                              )
                            ).then((results) => {
                              const failed = results.filter(r => !r.success);
                              if (failed.length > 0) {
                                alert(`Removed ${results.length - failed.length}/${results.length} design(s). ${failed.length} failed.`);
                              }
                              fetchPlatformDesigns(activePlatformId);
                              fetchPlatforms();
                              setSelectedCardDesigns(new Set());
                              setIsBulkRemoving(false);
                            });
                          }
                        }}
                        className="px-3 py-1.5 text-xs font-semibold text-red-600 bg-white border border-red-300 rounded-lg hover:bg-red-50 transition-all flex items-center gap-1.5 disabled:opacity-50"
                      >
                        <Trash2 size={12} />
                        {isBulkRemoving ? 'Removing...' : 'Remove'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Designs Grid */}
                {filteredDesigns.length === 0 ? (
                  <div className="bg-white border rounded-lg p-16 text-center" style={{ borderColor: COLORS.border }}>
                    <Send size={48} className="mx-auto mb-4" style={{ color: COLORS.textLight }} />
                    <p className="font-medium mb-2" style={{ color: COLORS.textSecondary }}>
                      {platformDesigns.length === 0 ? `No designs on ${activePlatform.name}` : `No designs with "${platformStatusFilter}" status`}
                    </p>
                    <p className="text-sm" style={{ color: COLORS.textLight }}>
                      {platformDesigns.length === 0
                        ? 'Verified designs are auto-assigned here. Verify designs to see them appear.'
                        : 'Try a different status filter.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredDesigns.map((design) => (
                      <div
                        key={design.designNumber}
                        className={`rounded-xl overflow-hidden transition-all duration-300 hover:shadow-lg flex flex-col group ${
                          selectedCardDesigns.has(design.designNumber) ? 'ring-2 ring-red-300 border-2' : 'border'
                        }`}
                        style={{
                          borderColor: selectedCardDesigns.has(design.designNumber) ? '#FCA5A5'
                            : getEffectiveStatus(design) === 'published' ? '#86EFAC' : COLORS.border,
                          backgroundColor: getEffectiveStatus(design) === 'published' ? '#F0FDF4' : '#FFFFFF',
                        }}
                      >
                        {/* Image */}
                        <div className="relative aspect-[4/5] overflow-hidden">
                          <img
                            src={design.imageUrl}
                            alt={design.designNumber}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                            onError={(e) => {
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f9fafb" width="400" height="500"/%3E%3Ctext fill="%23d1d5db" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          {/* Selection Checkbox */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCardDesigns(prev => {
                                const next = new Set(prev);
                                if (next.has(design.designNumber)) next.delete(design.designNumber);
                                else next.add(design.designNumber);
                                return next;
                              });
                            }}
                            className={`absolute top-3 right-3 w-6 h-6 rounded-md border-2 flex items-center justify-center transition-all z-10 ${
                              selectedCardDesigns.has(design.designNumber)
                                ? 'bg-red-500 border-red-500 text-white'
                                : 'bg-white/80 border-gray-300 text-transparent opacity-0 group-hover:opacity-100'
                            }`}
                          >
                            <CheckCircle size={14} />
                          </button>
                          {/* Status Badge */}
                          <div className="absolute top-3 left-3">
                            <span
                              className="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider text-white backdrop-blur-sm"
                              style={{
                                backgroundColor: getEffectiveStatus(design) === 'published' ? 'rgba(16,185,129,0.85)' :
                                  getEffectiveStatus(design) === 'reviewed' ? 'rgba(59,130,246,0.85)' :
                                  getEffectiveStatus(design) === 'draft' ? 'rgba(245,158,11,0.85)' : activePlatform.color
                              }}
                            >
                              {getEffectiveStatus(design)}
                            </span>
                          </div>
                        </div>

                        {/* Card Content */}
                        <div className="p-4 pt-3 flex flex-col gap-2.5 flex-1">
                          {/* Title Row */}
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-sm leading-tight" style={{ color: COLORS.textPrimary }}>
                                {design.productType || 'Necklace'}
                              </h3>
                              <p className="text-[10px] font-medium uppercase tracking-widest mt-0.5" style={{ color: COLORS.textLight }}>
                                {design.designNumber}
                              </p>
                            </div>
                            {design.platformData?.variants && design.platformData.variants.filter(v => v.include !== false).length > 0 && (
                              <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold flex items-center gap-1 bg-gray-100"
                                style={{ color: COLORS.textSecondary }}>
                                <Package size={10} />
                                {design.platformData.variants.filter(v => v.include !== false).length}
                              </span>
                            )}
                          </div>

                          {/* Platform Data Preview */}
                          {design.platformData && Object.keys(design.platformData).length > 0 && (() => {
                            const hiddenKeys = ['variants', 'variantOptions', 'metafields', 'images'];
                            const previewEntries = Object.entries(design.platformData)
                              .filter(([key, val]) => !hiddenKeys.includes(key) && val != null && typeof val !== 'object')
                              .filter(([, val]) => String(val).trim() !== '');
                            if (previewEntries.length === 0) return null;
                            return (
                              <div className="text-[10px] flex gap-2 flex-wrap">
                                {previewEntries.slice(0, 1).map(([key, val]) => (
                                  <span key={key} className="px-2 py-0.5 rounded bg-gray-50 truncate max-w-[200px]"
                                    style={{ color: COLORS.textSecondary }}>
                                    <span className="font-medium text-gray-400">{key}:</span> {Array.isArray(val) ? val.join(', ') : String(val).substring(0, 30)}
                                  </span>
                                ))}
                                {previewEntries.length > 1 && (
                                  <span className="px-2 py-0.5 rounded bg-gray-50 text-gray-400">+{previewEntries.length - 1}</span>
                                )}
                              </div>
                            );
                          })()}

                          {/* Metafield status indicator */}
                          {design.platformData?.metafields && (() => {
                            const mf = design.platformData.metafields;
                            const total = Object.keys(mf).length;
                            const filled = Object.values(mf).filter(m => m.include && m.value).length;
                            const allGood = filled === total;
                            return (
                              <div className={`text-[10px] flex items-center gap-1.5 px-2 py-1 rounded-md ${
                                allGood ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'
                              }`}>
                                <Tag size={10} />
                                <span className="font-medium">Metafields {filled}/{total}</span>
                              </div>
                            );
                          })()}

                          {/* Listing URL for published designs */}
                          {design.listingUrl && getEffectiveStatus(design) === 'published' && (
                            <a
                              href={design.listingUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] font-medium flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg hover:bg-green-100 transition-colors"
                              style={{ color: '#059669', backgroundColor: '#ECFDF5' }}
                            >
                              <ExternalLink size={11} />
                              View on {activePlatform.name}
                            </a>
                          )}

                          {/* Actions */}
                          {(() => {
                            const hasImages = design.platformData?.images?.primaryImageId;
                            const showPublish = activePlatform.hasApi && activePlatform.hasCredentials && getEffectiveStatus(design) === 'reviewed';
                            return (
                              <>
                                <div className="flex gap-2 mt-auto pt-3 border-t" style={{ borderColor: COLORS.border }}>
                                  <button
                                    onClick={() => {
                                      setEditingPlatformDesign(design);
                                      fetchPlatformConfig(activePlatformId);
                                      fetchDesignVariants(design.designNumber, design.platformData);
                                      setShowPlatformFieldEditor(true);
                                    }}
                                    className="flex-1 px-3 py-2 text-[11px] font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5"
                                    style={{ color: COLORS.textPrimary }}
                                  >
                                    <Edit2 size={12} style={{ color: COLORS.maroon }} />
                                    Fields
                                  </button>
                                  <button
                                    onClick={() => openImageModal(design)}
                                    className="flex-1 px-3 py-2 text-[11px] font-semibold rounded-lg hover:bg-gray-50 transition-all flex items-center justify-center gap-1.5"
                                    style={{ color: COLORS.textPrimary }}
                                  >
                                    <ImageIcon size={12} style={{ color: COLORS.gold }} />
                                    Images
                                    {hasImages && <CheckCircle size={9} className="text-green-500" />}
                                  </button>
                                </div>

                                {/* Publish button — only for reviewed status */}
                                {showPublish && (
                                  <button
                                    disabled={isPublishing && publishingDesign === design.designNumber}
                                    onClick={() => {
                                      if (window.confirm(`Publish design ${design.designNumber} to ${activePlatform.name}?`)) {
                                        handlePublishDesign(activePlatformId, design.designNumber);
                                      }
                                    }}
                                    className="w-full px-3 py-2 text-[11px] font-bold text-white rounded-lg hover:opacity-90 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
                                    style={{ backgroundColor: '#10B981' }}
                                  >
                                    <Send size={11} />
                                    {isPublishing && publishingDesign === design.designNumber ? 'Publishing...' : `Publish to ${activePlatform.name}`}
                                  </button>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                </>
                ) : (
                /* ═══ COLLECTIONS VIEW ═══ */
                <div className="space-y-4">
                  {/* Collections header */}
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                      Collections ({channelCollections.length})
                    </h3>
                    <button
                      onClick={() => {
                        setCollectionName(''); setCollectionDescription(''); setCollectionTags([]); setCollectionTagInput('');
                        setShowCreateCollectionModal(true);
                      }}
                      className="px-4 py-2 text-xs font-bold tracking-wider uppercase text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2"
                      style={{ backgroundColor: '#8B5CF6' }}
                    >
                      <Plus size={14} />
                      New Collection
                    </button>
                  </div>

                  {channelCollections.length === 0 ? (
                    <div className="bg-white border rounded-xl p-12 text-center" style={{ borderColor: COLORS.border }}>
                      <Folder size={40} className="mx-auto mb-3 text-gray-300" />
                      <p className="text-sm font-medium mb-1" style={{ color: COLORS.textSecondary }}>No collections yet</p>
                      <p className="text-xs" style={{ color: COLORS.textLight }}>Create a collection to group designs for seasonal campaigns and bulk publishing</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {channelCollections.map(collection => (
                        <div key={collection.id} className="bg-white border rounded-xl overflow-hidden hover:shadow-lg transition-all flex flex-col" style={{ borderColor: COLORS.border }}>
                          <div className="p-5">
                            <div className="flex items-center justify-between mb-2">
                              <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white"
                                style={{ backgroundColor: collection.status === 'published' ? '#10B981' : '#8B5CF6' }}>
                                {collection.status}
                              </span>
                              <span className="text-[10px]" style={{ color: COLORS.textLight }}>{collection.designCount} design{collection.designCount !== 1 ? 's' : ''}</span>
                            </div>
                            <h4 className="font-bold text-base mb-1" style={{ color: COLORS.textPrimary }}>{collection.name}</h4>
                            {collection.description && (
                              <p className="text-xs line-clamp-2 mb-3" style={{ color: COLORS.textSecondary }}>{collection.description}</p>
                            )}
                            <div className="flex flex-wrap gap-1.5">
                              {(collection.tags || []).map(tag => (
                                <span key={tag} className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 border border-purple-200">
                                  {tag}
                                </span>
                              ))}
                              {(!collection.tags || collection.tags.length === 0) && (
                                <span className="text-[10px] italic" style={{ color: COLORS.textLight }}>No tags</span>
                              )}
                            </div>
                          </div>
                          {collection.marketplaceCollectionUrl && (
                            <a href={collection.marketplaceCollectionUrl} target="_blank" rel="noopener noreferrer"
                              className="px-5 py-2 text-[10px] flex items-center gap-1 hover:underline border-t" style={{ borderColor: COLORS.border, color: activePlatform.color }}>
                              <ExternalLink size={10} />
                              View on {activePlatform.name}
                            </a>
                          )}
                          <div className="p-4 pt-2 mt-auto flex gap-2">
                            <button
                              onClick={() => {
                                setEditingCollection(collection);
                                setCollectionName(collection.name);
                                setCollectionDescription(collection.description || '');
                                setCollectionTags(collection.tags || []);
                                setCollectionTagInput('');
                                setShowEditCollectionModal(true);
                              }}
                              className="flex-1 px-3 py-2 text-[11px] font-semibold border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5"
                              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                            >
                              <Edit2 size={12} /> Edit
                            </button>
                            <button
                              onClick={() => {
                                setPickerTargetCollection(collection);
                                setPickerSearch('');
                                fetchCollectionDesignNumbers(collection.id);
                                setShowCollectionDesignPicker(true);
                              }}
                              className="flex-1 px-3 py-2 text-[11px] font-semibold border rounded-lg hover:bg-gray-50 flex items-center justify-center gap-1.5"
                              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                            >
                              <Plus size={12} /> Designs
                            </button>
                            {activePlatform.hasApi && activePlatform.hasCredentials && collection.status !== 'published' && (
                              <button
                                disabled={isPublishingCollection && publishingCollectionId === collection.id}
                                onClick={() => {
                                  if (collection.designCount === 0) {
                                    alert('Add designs to this collection before publishing.');
                                    return;
                                  }
                                  if (window.confirm(`Publish collection "${collection.name}" (${collection.designCount} designs) to ${activePlatform.name}?\n\nThis will:\n1. Publish unpublished designs\n2. Add tags to all designs\n3. Create a marketplace collection`)) {
                                    handlePublishCollection(collection.id);
                                  }
                                }}
                                className="flex-1 px-3 py-2 text-[11px] font-bold text-white rounded-lg hover:opacity-90 flex items-center justify-center gap-1.5 disabled:opacity-50"
                                style={{ backgroundColor: '#10B981' }}
                              >
                                <Send size={12} />
                                {isPublishingCollection && publishingCollectionId === collection.id ? '...' : 'Publish'}
                              </button>
                            )}
                            {!activePlatform.hasApi && (
                              <span className="flex-1 px-3 py-2 text-[10px] text-center rounded-lg bg-gray-50" style={{ color: COLORS.textLight }}>
                                CSV export only
                              </span>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                )}

                {/* ═══ Create/Edit Collection Modal ═══ */}
                {(showCreateCollectionModal || showEditCollectionModal) && (() => {
                  const isEdit = showEditCollectionModal;
                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => { setShowCreateCollectionModal(false); setShowEditCollectionModal(false); }}>
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[80vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                              {isEdit ? 'Edit Collection' : 'New Collection'}
                            </h3>
                            <button onClick={() => { setShowCreateCollectionModal(false); setShowEditCollectionModal(false); }} className="p-2 hover:bg-gray-100 rounded-lg">
                              <X size={18} />
                            </button>
                          </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-6 space-y-5">
                          <div>
                            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: COLORS.textSecondary }}>Collection Name *</label>
                            <input type="text" value={collectionName} onChange={e => setCollectionName(e.target.value)}
                              placeholder="e.g., Valentines 2026"
                              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
                              style={{ borderColor: COLORS.border }}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: COLORS.textSecondary }}>Description</label>
                            <textarea value={collectionDescription} onChange={e => setCollectionDescription(e.target.value)}
                              placeholder="Curated for the Valentines season..."
                              rows={3}
                              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 resize-none"
                              style={{ borderColor: COLORS.border }}
                            />
                          </div>
                          <div>
                            <label className="text-xs font-bold uppercase tracking-wider block mb-1.5" style={{ color: COLORS.textSecondary }}>Tags</label>
                            <p className="text-[10px] mb-2" style={{ color: COLORS.textLight }}>
                              These tags will be added to all designs when publishing. Press Enter to add.
                            </p>
                            <div className="flex flex-wrap gap-1.5 mb-2">
                              {collectionTags.map(tag => (
                                <span key={tag} className="px-2 py-1 rounded-full text-xs font-medium bg-purple-50 text-purple-700 border border-purple-200 flex items-center gap-1">
                                  {tag}
                                  <button onClick={() => setCollectionTags(prev => prev.filter(t => t !== tag))} className="hover:text-red-500"><X size={10} /></button>
                                </span>
                              ))}
                            </div>
                            <input type="text" value={collectionTagInput}
                              onChange={e => setCollectionTagInput(e.target.value)}
                              onKeyDown={e => {
                                if (e.key === 'Enter' && collectionTagInput.trim()) {
                                  e.preventDefault();
                                  const newTag = collectionTagInput.trim().toLowerCase().replace(/\s+/g, '-');
                                  if (!collectionTags.includes(newTag)) setCollectionTags(prev => [...prev, newTag]);
                                  setCollectionTagInput('');
                                }
                              }}
                              placeholder="Type a tag and press Enter"
                              className="w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2"
                              style={{ borderColor: COLORS.border }}
                            />
                          </div>
                        </div>
                        <div className="p-6 border-t flex justify-between" style={{ borderColor: COLORS.border }}>
                          {isEdit ? (
                            <button onClick={() => {
                              if (window.confirm('Delete this collection? Designs will NOT be affected.')) {
                                handleDeleteCollection(editingCollection.id);
                                setShowEditCollectionModal(false);
                              }
                            }} className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-lg hover:bg-red-50">
                              Delete
                            </button>
                          ) : <div />}
                          <div className="flex gap-2">
                            <button onClick={() => { setShowCreateCollectionModal(false); setShowEditCollectionModal(false); }}
                              className="px-5 py-2 text-sm font-medium border rounded-lg hover:bg-gray-50"
                              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}>
                              Cancel
                            </button>
                            <button
                              disabled={!collectionName.trim()}
                              onClick={() => isEdit ? handleUpdateCollection(editingCollection.id) : handleCreateCollection(activePlatformId)}
                              className="px-6 py-2 text-sm font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                              style={{ backgroundColor: '#8B5CF6' }}>
                              {isEdit ? 'Save Changes' : 'Create Collection'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ═══ Collection Design Picker Modal ═══ */}
                {showCollectionDesignPicker && pickerTargetCollection && (() => {
                  const existingSet = new Set(collectionDesignNumbers);

                  // Only show reviewed + published designs (not drafts)
                  const eligibleDesigns = platformDesigns.filter(d =>
                    getEffectiveStatus(d) === 'reviewed' || getEffectiveStatus(d) === 'published'
                  );

                  // Build dynamic attribute values from eligible designs (same approach as internal collections)
                  const pickerAttrValues = {};
                  const attrNames = Object.keys(attributeValuesForFilters);
                  eligibleDesigns.forEach(d => {
                    const attrs = d.attributes || {};
                    // Also include productType as a filterable attribute
                    if (d.productType) {
                      if (!pickerAttrValues['Product Type']) pickerAttrValues['Product Type'] = new Set();
                      pickerAttrValues['Product Type'].add(d.productType);
                    }
                    Object.keys(attrs).forEach(attrName => {
                      if (!attrNames.includes(attrName)) return;
                      if (!pickerAttrValues[attrName]) pickerAttrValues[attrName] = new Set();
                      const val = attrs[attrName];
                      if (val) {
                        if (typeof val === 'string' && val.includes(',')) {
                          val.split(',').forEach(v => pickerAttrValues[attrName].add(v.trim()));
                        } else {
                          pickerAttrValues[attrName].add(val);
                        }
                      }
                    });
                  });
                  // Convert sets to sorted arrays
                  const pickerAttrOptions = {};
                  Object.keys(pickerAttrValues).forEach(k => {
                    pickerAttrOptions[k] = Array.from(pickerAttrValues[k]).sort();
                  });

                  // Helper: get filtered values for a picker filter row
                  const getPickerFilteredValues = (filterIndex) => {
                    const filter = pickerFilters[filterIndex];
                    const searchTerm = pickerFilterSearchTerms[filterIndex] || '';
                    const allValues = pickerAttrOptions[filter.attribute] || [];
                    if (!searchTerm) return allValues;
                    return allValues.filter(val => val.toLowerCase().includes(searchTerm.toLowerCase()));
                  };

                  // Apply search + dynamic filters
                  const filteredDesigns = eligibleDesigns.filter(d => {
                    // Text search
                    if (pickerSearch && !d.designNumber.toLowerCase().includes(pickerSearch.toLowerCase()) &&
                        !(d.productType || '').toLowerCase().includes(pickerSearch.toLowerCase())) return false;
                    // Apply each active filter
                    for (const filter of pickerFilters) {
                      if (!filter.values || filter.values.length === 0) continue;
                      // Get the design's value for this attribute
                      let designVal;
                      if (filter.attribute === 'Product Type') {
                        designVal = d.productType || '';
                      } else {
                        designVal = d.attributes?.[filter.attribute] || '';
                      }
                      // Handle comma-separated multi-values
                      const designVals = typeof designVal === 'string' && designVal.includes(',')
                        ? designVal.split(',').map(v => v.trim())
                        : [designVal];
                      const filterVals = filter.values;

                      if (filter.operator === 'equals' || filter.operator === 'in') {
                        if (!designVals.some(dv => filterVals.includes(dv))) return false;
                      } else if (filter.operator === 'not_equals' || filter.operator === 'not_in') {
                        if (designVals.some(dv => filterVals.includes(dv))) return false;
                      }
                    }
                    return true;
                  });

                  // Split into two groups: already in collection vs available
                  const inCollectionDesigns = filteredDesigns.filter(d => existingSet.has(d.designNumber));
                  const availableDesigns = filteredDesigns.filter(d => !existingSet.has(d.designNumber));
                  const newlySelected = [...pickerSelected].filter(dn => !existingSet.has(dn));

                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCollectionDesignPicker(false)}>
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
                        {/* Header */}
                        <div className="p-5 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center justify-between mb-3">
                            <div>
                              <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                                Manage Designs — "{pickerTargetCollection.name}"
                              </h3>
                              <p className="text-xs mt-0.5" style={{ color: COLORS.textLight }}>
                                {existingSet.size} in collection · {newlySelected.length} newly selected · Only reviewed/published designs shown
                              </p>
                            </div>
                            <button onClick={() => setShowCollectionDesignPicker(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={18} /></button>
                          </div>
                          {/* Search row */}
                          <div className="flex gap-2 items-center mb-3">
                            <div className="flex-1 relative">
                              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                              <input type="text" value={pickerSearch} onChange={e => setPickerSearch(e.target.value)}
                                placeholder="Search by design number or product type..."
                                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
                                style={{ borderColor: COLORS.border }}
                              />
                            </div>
                            <button
                              onClick={() => {
                                const firstAttr = Object.keys(pickerAttrOptions)[0];
                                if (!firstAttr) return;
                                setPickerFilters(prev => [...prev, { attribute: firstAttr, operator: 'in', values: [] }]);
                              }}
                              disabled={Object.keys(pickerAttrOptions).length === 0}
                              className="flex items-center gap-1 px-3 py-2 text-xs font-semibold rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
                              style={{ backgroundColor: '#8B5CF6', color: 'white' }}
                            >
                              <Plus size={14} />
                              Add Filter
                            </button>
                          </div>
                          {/* Dynamic Filter rows */}
                          {pickerFilters.length > 0 && (
                            <div className="space-y-2">
                              {pickerFilters.map((filter, index) => (
                                <div key={index} className="relative">
                                  <div className="flex items-center gap-2 p-2 border rounded-lg" style={{ borderColor: COLORS.border, backgroundColor: 'white' }}>
                                    {/* Attribute select */}
                                    <select
                                      value={filter.attribute}
                                      onChange={(e) => {
                                        const updated = [...pickerFilters];
                                        updated[index] = { ...updated[index], attribute: e.target.value, values: [] };
                                        setPickerFilters(updated);
                                      }}
                                      className="px-3 py-1.5 border rounded-lg text-xs font-semibold"
                                      style={{ borderColor: COLORS.border, color: '#8B5CF6' }}
                                    >
                                      {Object.keys(pickerAttrOptions).map(attr => (
                                        <option key={attr} value={attr}>{attr}</option>
                                      ))}
                                    </select>
                                    <span className="text-gray-300">|</span>
                                    {/* Operator select */}
                                    <select
                                      value={filter.operator}
                                      onChange={(e) => {
                                        const updated = [...pickerFilters];
                                        updated[index] = { ...updated[index], operator: e.target.value };
                                        setPickerFilters(updated);
                                      }}
                                      className="px-3 py-1.5 border rounded-lg text-xs font-medium"
                                      style={{ borderColor: COLORS.border, minWidth: '100px' }}
                                    >
                                      <option value="equals">Equals (=)</option>
                                      <option value="not_equals">Not Equals (≠)</option>
                                      <option value="in">In (∈)</option>
                                      <option value="not_in">Not In (∉)</option>
                                    </select>
                                    <span className="text-gray-300">|</span>
                                    {/* Values display / search */}
                                    <div className="flex-1 min-w-0">
                                      {pickerExpandedFilter === index ? (
                                        <input
                                          type="text"
                                          value={pickerFilterSearchTerms[index] || ''}
                                          onChange={(e) => setPickerFilterSearchTerms({ ...pickerFilterSearchTerms, [index]: e.target.value })}
                                          placeholder="Search values..."
                                          className="w-full px-3 py-1.5 border rounded-lg text-xs focus:outline-none"
                                          style={{ borderColor: COLORS.border }}
                                          autoFocus
                                          onBlur={(e) => {
                                            setTimeout(() => {
                                              if (!e.relatedTarget?.closest(`[data-picker-filter="${index}"]`)) {
                                                setPickerExpandedFilter(null);
                                              }
                                            }, 100);
                                          }}
                                        />
                                      ) : (
                                        <div onClick={() => setPickerExpandedFilter(index)}
                                          className="px-3 py-1.5 text-xs cursor-pointer hover:bg-gray-50 rounded-lg">
                                          {filter.values.length > 0 ? (
                                            <span className="font-medium text-gray-700">{filter.values.join(', ')}</span>
                                          ) : (
                                            <span className="text-gray-400 italic">Click to select values</span>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    {/* Toggle */}
                                    <button onClick={() => setPickerExpandedFilter(pickerExpandedFilter === index ? null : index)}
                                      className="p-1.5 hover:bg-gray-100 rounded-lg" type="button">
                                      {pickerExpandedFilter === index ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                                    </button>
                                    {/* Delete filter */}
                                    <button onClick={() => {
                                      setPickerFilters(pickerFilters.filter((_, i) => i !== index));
                                      setPickerExpandedFilter(null);
                                    }} className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg" type="button">
                                      <Trash2 size={14} />
                                    </button>
                                  </div>
                                  {/* Dropdown with checkboxes */}
                                  {pickerExpandedFilter === index && (
                                    <div data-picker-filter={index}
                                      className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-[200px] overflow-y-auto"
                                      style={{ borderColor: COLORS.border }}>
                                      <div className="p-2 grid grid-cols-3 gap-1">
                                        {getPickerFilteredValues(index).length > 0 ? (
                                          getPickerFilteredValues(index).map(value => (
                                            <label key={value} className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer text-xs">
                                              <input type="checkbox" checked={filter.values.includes(value)}
                                                onChange={() => {
                                                  const updated = [...pickerFilters];
                                                  const vals = updated[index].values;
                                                  updated[index] = {
                                                    ...updated[index],
                                                    values: vals.includes(value) ? vals.filter(v => v !== value) : [...vals, value]
                                                  };
                                                  setPickerFilters(updated);
                                                }}
                                                className="rounded" style={{ accentColor: '#8B5CF6' }} />
                                              <span className="flex-1 truncate">{value}</span>
                                            </label>
                                          ))
                                        ) : (
                                          <p className="col-span-3 text-xs text-center py-3" style={{ color: COLORS.textSecondary }}>No matching values</p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Body */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-5">
                          {/* Section 1: Already in collection */}
                          {inCollectionDesigns.length > 0 && (
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                  In Collection ({inCollectionDesigns.length})
                                </span>
                                <div className="flex-1 h-px bg-purple-100" />
                              </div>
                              <div className="grid grid-cols-5 gap-2">
                                {inCollectionDesigns.map(d => (
                                  <div key={d.designNumber}
                                    className="relative border-2 rounded-lg overflow-hidden opacity-80"
                                    style={{ borderColor: '#8B5CF6', backgroundColor: '#FAF5FF' }}
                                  >
                                    <img src={d.imageUrl} alt={d.designNumber} className="w-full aspect-square object-cover" />
                                    <div className="p-1.5">
                                      <p className="text-[10px] font-bold" style={{ color: COLORS.textPrimary }}>{d.designNumber}</p>
                                      <p className="text-[8px]" style={{ color: COLORS.textLight }}>{d.productType}</p>
                                    </div>
                                    <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-purple-500 text-white flex items-center justify-center">
                                      <CheckCircle size={11} />
                                    </div>
                                    <button
                                      onClick={() => {
                                        if (window.confirm(`Remove ${d.designNumber} from this collection?`)) {
                                          handleRemoveDesignFromCollection(pickerTargetCollection.id, d.designNumber);
                                          setCollectionDesignNumbers(prev => prev.filter(dn => dn !== d.designNumber));
                                          setPickerSelected(prev => { const s = new Set(prev); s.delete(d.designNumber); return s; });
                                        }
                                      }}
                                      className="absolute bottom-1 right-1 p-1 rounded-full bg-red-100 hover:bg-red-200 text-red-600 opacity-0 hover:opacity-100 transition-opacity"
                                      title="Remove from collection"
                                    >
                                      <X size={10} />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Section 2: Available to add */}
                          <div>
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-gray-100" style={{ color: COLORS.textSecondary }}>
                                Available ({availableDesigns.length})
                              </span>
                              <div className="flex-1 h-px bg-gray-200" />
                              {availableDesigns.length > 0 && (
                                <button
                                  onClick={() => {
                                    const allDns = new Set([...pickerSelected, ...availableDesigns.map(d => d.designNumber)]);
                                    setPickerSelected(allDns);
                                  }}
                                  className="text-[10px] underline" style={{ color: '#8B5CF6' }}>Select all filtered</button>
                              )}
                            </div>
                            <div className="grid grid-cols-5 gap-2">
                              {availableDesigns.map(d => {
                                const isSelected = pickerSelected.has(d.designNumber);
                                return (
                                  <div key={d.designNumber}
                                    onClick={() => {
                                      setPickerSelected(prev => {
                                        const updated = new Set(prev);
                                        if (updated.has(d.designNumber)) updated.delete(d.designNumber);
                                        else updated.add(d.designNumber);
                                        return updated;
                                      });
                                    }}
                                    className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${isSelected ? 'ring-2 ring-green-400' : 'hover:shadow-md'}`}
                                    style={{ borderColor: isSelected ? '#10B981' : COLORS.border }}
                                  >
                                    <img src={d.imageUrl} alt={d.designNumber} className="w-full aspect-square object-cover" />
                                    <div className="p-1.5">
                                      <p className="text-[10px] font-bold" style={{ color: COLORS.textPrimary }}>{d.designNumber}</p>
                                      <div className="flex items-center gap-1">
                                        <p className="text-[8px]" style={{ color: COLORS.textLight }}>{d.productType}</p>
                                        <span className="text-[7px] px-1 py-0.5 rounded-full" style={{
                                          backgroundColor: getEffectiveStatus(d) === 'published' ? '#D1FAE5' : '#DBEAFE',
                                          color: getEffectiveStatus(d) === 'published' ? '#065F46' : '#1E40AF'
                                        }}>{getEffectiveStatus(d)}</span>
                                      </div>
                                    </div>
                                    {isSelected && (
                                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-green-500 text-white flex items-center justify-center">
                                        <CheckCircle size={11} />
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                            {availableDesigns.length === 0 && (
                              <div className="py-6 text-center">
                                <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                                  {eligibleDesigns.length === 0
                                    ? 'No reviewed or published designs on this platform yet'
                                    : 'No designs match the current filters'}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="p-4 border-t flex justify-between items-center" style={{ borderColor: COLORS.border }}>
                          <p className="text-xs" style={{ color: COLORS.textLight }}>
                            {newlySelected.length} new design{newlySelected.length !== 1 ? 's' : ''} to add
                            {existingSet.size > 0 && ` · ${existingSet.size} already in collection`}
                          </p>
                          <div className="flex gap-2">
                            <button onClick={() => setShowCollectionDesignPicker(false)}
                              className="px-5 py-2 text-sm font-medium border rounded-lg hover:bg-gray-50"
                              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}>
                              Cancel
                            </button>
                            <button
                              onClick={() => handleAddDesignsToCollection(pickerTargetCollection.id, [...pickerSelected])}
                              disabled={pickerSelected.size === 0}
                              className="px-6 py-2 text-sm font-bold text-white rounded-lg hover:opacity-90 disabled:opacity-50"
                              style={{ backgroundColor: '#8B5CF6' }}>
                              Save ({pickerSelected.size})
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Add Designs Modal */}
                {showAddDesignsModal && (() => {
                  const assignedNumbers = new Set(platformDesigns.map(d => d.designNumber));
                  const availableDesigns = (verifiedItems || []).filter(d => !assignedNumbers.has(d.designNumber));
                  const [searchTerm, setSearchTerm] = [manualInput, setManualInput]; // reuse existing state
                  const filtered = searchTerm
                    ? availableDesigns.filter(d =>
                        d.designNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        (d.productType || '').toLowerCase().includes(searchTerm.toLowerCase())
                      )
                    : availableDesigns;

                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col overflow-hidden">
                        <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: COLORS.border }}>
                          <div>
                            <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                              Add Designs to {activePlatform.name}
                            </h3>
                            <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
                              {availableDesigns.length} verified designs available
                            </p>
                          </div>
                          <button onClick={() => { setShowAddDesignsModal(false); setManualInput(''); }} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X size={18} />
                          </button>
                        </div>

                        {/* Search */}
                        <div className="px-6 pt-4">
                          <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textLight }} />
                            <input
                              type="text"
                              placeholder="Search by design number or product type..."
                              value={manualInput}
                              onChange={(e) => setManualInput(e.target.value)}
                              className="w-full pl-10 pr-4 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2"
                              style={{ borderColor: COLORS.border, focusRingColor: activePlatform.color }}
                            />
                          </div>
                        </div>

                        {/* Design List */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-2">
                          {filtered.length === 0 ? (
                            <p className="text-center py-8 text-sm" style={{ color: COLORS.textSecondary }}>
                              {searchTerm ? 'No matching designs found' : 'All verified designs are already assigned'}
                            </p>
                          ) : (
                            filtered.slice(0, 50).map((design) => {
                              const isSelected = selectedDesignsForPush.includes(design.designNumber);
                              return (
                                <label
                                  key={design.designNumber}
                                  className="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all hover:bg-gray-50"
                                  style={{
                                    borderColor: isSelected ? activePlatform.color : COLORS.border,
                                    backgroundColor: isSelected ? activePlatform.color + '08' : 'transparent'
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() => {
                                      setSelectedDesignsForPush(prev =>
                                        prev.includes(design.designNumber)
                                          ? prev.filter(d => d !== design.designNumber)
                                          : [...prev, design.designNumber]
                                      );
                                    }}
                                    className="w-4 h-4 rounded"
                                  />
                                  <img
                                    src={design.imageUrl}
                                    alt={design.designNumber}
                                    className="w-10 h-10 rounded object-cover"
                                    onError={(e) => { e.target.style.display = 'none'; }}
                                  />
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{design.designNumber}</p>
                                    <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>{design.productType || 'Unknown'}</p>
                                  </div>
                                </label>
                              );
                            })
                          )}
                        </div>

                        {/* Actions */}
                        <div className="p-6 border-t flex justify-between items-center" style={{ borderColor: COLORS.border }}>
                          <span className="text-xs" style={{ color: COLORS.textSecondary }}>
                            {selectedDesignsForPush.length} selected
                          </span>
                          <div className="flex gap-3">
                            <button
                              onClick={() => { setShowAddDesignsModal(false); setSelectedDesignsForPush([]); setManualInput(''); }}
                              className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={() => {
                                handleAddDesignsToPlatform(activePlatformId, selectedDesignsForPush);
                                setSelectedDesignsForPush([]);
                                setManualInput('');
                              }}
                              disabled={selectedDesignsForPush.length === 0}
                              className="px-6 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                              style={{ backgroundColor: activePlatform.color }}
                            >
                              <Plus size={14} />
                              Add {selectedDesignsForPush.length} Design(s)
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Platform Field Editor Modal */}
                {showPlatformFieldEditor && editingPlatformDesign && platformConfig && (() => {
                  const fields = platformConfig.fields || {};
                  const sortedFields = Object.entries(fields).sort((a, b) => (a[1].order || 0) - (b[1].order || 0));
                  const currentData = { ...(editingPlatformDesign.platformData || {}) };
                  const aiAttrs = editingPlatformDesign.attributes || {};

                  // Helper: format attribute value for display
                  const formatAttrVal = (val) => {
                    if (Array.isArray(val)) return val.join(', ');
                    if (typeof val === 'string') return val;
                    return String(val || '');
                  };

                  // Copy text to clipboard and briefly flash the button
                  const copyToClipboard = (text, btnId) => {
                    navigator.clipboard.writeText(text).then(() => {
                      const btn = document.getElementById(btnId);
                      if (btn) {
                        btn.textContent = 'Copied!';
                        setTimeout(() => { btn.textContent = 'Copy'; }, 1200);
                      }
                    });
                  };


                  // Group AI attributes by category for the reference panel
                  const attrCategories = [
                    { label: 'Product Info', keys: ['Product Type', 'Product Style', 'Pieces', 'Base Material'] },
                    { label: 'Design', keys: ['Theme', 'Motif', 'Design Shape', 'Design Complexity', 'Traditional vs Western', 'Closure Type'] },
                    { label: 'Stones', keys: ['Stone Type', 'Stone Color', 'Stone Setting'] },
                    { label: 'Styling', keys: ['Primary Occasion', 'Occasion Type', 'Glam Level', 'Style Persona', 'Cultural / Ethnic Region'] },
                    { label: 'AI Suggestions', keys: ['Outfit Suggestion', 'Recommended Colors', 'Makeup Tip', 'Hair Style', 'Accessories to Pair'] },
                    { label: 'Marketing', keys: ['Attitude', 'SEO_Keywords', 'Product_Description', 'Inventory_Tags'] },
                  ];

                  // Build metafield editing state from platform_data.metafields
                  const metafieldMappings = platformConfig.metafieldMappings || {};
                  const designMetafields = currentData.metafields || {};

                  // Map of top-level design fields that aren't in attributes JSON
                  const extraFields = {
                    'Base Material': editingPlatformDesign.baseMaterial || '',
                    'Product Type': editingPlatformDesign.productType || '',
                  };

                  // Derive a sensible AI-based default for a platform field when
                  // platformData has no saved value yet (e.g. manually-added designs).
                  const getAiDefault = (fieldKey, fieldDef) => {
                    const label = ((fieldDef && fieldDef.label) || fieldKey).toLowerCase();
                    const pt  = aiAttrs['Product Type']  || editingPlatformDesign.productType || '';
                    const ps  = aiAttrs['Product Style'] || '';
                    const th  = aiAttrs['Theme']         || '';
                    if (label.includes('seo title') || label.includes('meta title') || label.includes('a+ content title')) {
                      const parts = [pt, ps, th].filter(Boolean);
                      return parts.length ? `Kushal's ${parts.join(' - ')}` : '';
                    }
                    if (label.includes('seo description') || label.includes('meta description')) {
                      return formatAttrVal(aiAttrs['Product_Description'] || '');
                    }
                    if (label.includes('tag')) {
                      const kw  = formatAttrVal(aiAttrs['SEO_Keywords']    || '');
                      const inv = formatAttrVal(aiAttrs['Inventory_Tags']  || '');
                      return [kw, inv].filter(Boolean).join(', ');
                    }
                    if (label.includes('vendor') || label.includes('brand')) {
                      return "Kushal's Fashion Jewellery";
                    }
                    if (label.includes('product type')) {
                      return pt || formatAttrVal(aiAttrs['Product Style'] || '');
                    }
                    return '';
                  };

                  // Group enabled metafields by category for the left panel
                  const mfEditCategories = {};
                  Object.entries(metafieldMappings).forEach(([attrKey, mapping]) => {
                    if (!mapping.enabled) return; // Only show enabled metafields
                    const cat = mapping.category || 'Other';
                    if (!mfEditCategories[cat]) mfEditCategories[cat] = [];
                    const designMf = designMetafields[attrKey] || {};
                    // Resolve value: design-level metafield → AI attribute → top-level design field
                    const fallbackVal = formatAttrVal(aiAttrs[attrKey]) || extraFields[attrKey] || '';
                    mfEditCategories[cat].push({
                      attrKey,
                      namespace: mapping.namespace || 'kushals',
                      key: mapping.key || attrKey.toLowerCase().replace(/ /g, '_').replace(/\//g, '_'),
                      value: designMf.value !== undefined ? designMf.value : fallbackVal,
                      include: designMf.include !== undefined ? designMf.include : true,
                    });
                  });
                  const mfEditCategoryOrder = ['Product Info', 'Design', 'Stones', 'Styling', 'Marketing', 'AI Suggestions', 'Other'];

                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                              {editingPlatformDesign.imageUrl && (
                                <img
                                  src={editingPlatformDesign.imageUrl}
                                  alt={editingPlatformDesign.designNumber}
                                  className="w-12 h-12 rounded-lg object-cover border"
                                  style={{ borderColor: COLORS.border }}
                                />
                              )}
                              <div>
                                <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                                  Edit {activePlatform.name} Fields
                                </h3>
                                <p className="text-sm mt-0.5" style={{ color: COLORS.textSecondary }}>
                                  Design: {editingPlatformDesign.designNumber} &middot; {editingPlatformDesign.productType || 'Unknown'}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => { setShowPlatformFieldEditor(false); setEditingPlatformDesign(null); setEditingVariants(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                              <X size={18} />
                            </button>
                          </div>
                        </div>

                        {/* Two-column layout: AI Reference | Platform Fields */}
                        <div className="flex-1 flex overflow-hidden">
                          {/* Left Panel: AI-Generated Attributes — select any as metafield */}
                          <div className="w-[340px] flex-shrink-0 border-r overflow-y-auto bg-gray-50 p-4 space-y-4" style={{ borderColor: COLORS.border }}>
                            <div className="flex items-center gap-2 mb-1">
                              <Sparkles size={14} style={{ color: COLORS.gold }} />
                              <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.maroon }}>
                                AI-Generated Data
                              </h4>
                            </div>
                            <p className="text-[10px] mb-3" style={{ color: COLORS.textLight }}>
                              Select attributes to publish as Shopify metafields. Edit values inline.
                            </p>

                            {attrCategories.map((cat) => {
                              const hasValues = cat.keys.some(k => aiAttrs[k] && formatAttrVal(aiAttrs[k]).trim());
                              if (!hasValues) return null;
                              return (
                                <div key={cat.label}>
                                  <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                                    {cat.label}
                                  </p>
                                  <div className="space-y-1.5">
                                    {cat.keys.map((attrKey) => {
                                      const val = aiAttrs[attrKey];
                                      if (!val || !formatAttrVal(val).trim()) return null;
                                      const displayVal = formatAttrVal(val);
                                      // Check global Fields config + per-design override
                                      const globalMapping = metafieldMappings[attrKey];
                                      const isGloballyEnabled = globalMapping?.enabled ?? false;
                                      const existingMf = designMetafields[attrKey] || {};
                                      // Default: use per-design override if set, otherwise fall back to global Fields config
                                      const isSelected = existingMf.include !== undefined ? existingMf.include : isGloballyEnabled;
                                      const mfKey = (globalMapping?.key) || attrKey.toLowerCase().replace(/ /g, '_').replace(/\//g, '_');
                                      const mfNamespace = (globalMapping?.namespace) || 'kushals';
                                      return (
                                        <div
                                          key={attrKey}
                                          className="bg-white rounded-lg border px-3 py-2 transition-all"
                                          style={{
                                            borderColor: isSelected ? COLORS.gold : COLORS.border,
                                            boxShadow: isSelected ? '0 0 0 1px rgba(212,175,55,0.3)' : 'none',
                                          }}
                                        >
                                          <div className="flex items-center gap-2 mb-1">
                                            <input
                                              type="checkbox"
                                              defaultChecked={isSelected}
                                              className="rounded flex-shrink-0 accent-amber-600"
                                              style={{ width: 15, height: 15 }}
                                              data-metafield-include={attrKey}
                                            />
                                            <p className="text-[10px] font-semibold uppercase tracking-wider flex-1" style={{ color: COLORS.textLight }}>
                                              {attrKey}
                                            </p>
                                            <span className="text-[8px] font-mono px-1.5 py-0.5 rounded" style={{ background: isSelected ? 'rgba(212,175,55,0.12)' : 'transparent', color: COLORS.textLight }}>
                                              {mfNamespace}.{mfKey}
                                            </span>
                                          </div>
                                          <input
                                            type="text"
                                            defaultValue={existingMf.value !== undefined ? existingMf.value : displayVal}
                                            className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                            style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                                            placeholder={displayVal}
                                            data-metafield-value={attrKey}
                                          />
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}

                            {/* Base material */}
                            {editingPlatformDesign.baseMaterial && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                                  Material
                                </p>
                                <div className="bg-white rounded-lg border px-3 py-2 transition-all" style={{ borderColor: COLORS.border }}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <input
                                      type="checkbox"
                                      defaultChecked={(designMetafields['Base Material'] || {}).include || false}
                                      className="rounded flex-shrink-0 accent-amber-600"
                                      style={{ width: 15, height: 15 }}
                                      data-metafield-include="Base Material"
                                    />
                                    <p className="text-[10px] font-semibold uppercase tracking-wider flex-1" style={{ color: COLORS.textLight }}>
                                      Base Material
                                    </p>
                                    <span className="text-[8px] font-mono px-1.5 py-0.5 rounded" style={{ color: COLORS.textLight }}>
                                      kushals.base_material
                                    </span>
                                  </div>
                                  <input
                                    type="text"
                                    defaultValue={(designMetafields['Base Material'] || {}).value || editingPlatformDesign.baseMaterial}
                                    className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                    style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                                    data-metafield-value="Base Material"
                                  />
                                </div>
                              </div>
                            )}

                            {/* Custom Metafields from global Fields config */}
                            {Object.entries(metafieldMappings).filter(([_, m]) => m.source === 'custom' && m.enabled).length > 0 && (
                              <div>
                                <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>Custom</p>
                                <div className="space-y-1.5">
                                  {Object.entries(metafieldMappings).filter(([_, m]) => m.source === 'custom' && m.enabled).map(([attrKey, mapping]) => {
                                    const existingMf = designMetafields[attrKey] || {};
                                    const isSelected = existingMf.include !== undefined ? existingMf.include : true;
                                    return (
                                      <div key={attrKey} className="bg-white rounded-lg border px-3 py-2 transition-all"
                                        style={{ borderColor: isSelected ? COLORS.gold : COLORS.border, boxShadow: isSelected ? '0 0 0 1px rgba(212,175,55,0.3)' : 'none' }}>
                                        <div className="flex items-center gap-2 mb-1">
                                          <input type="checkbox" defaultChecked={isSelected} className="rounded flex-shrink-0 accent-amber-600"
                                            style={{ width: 15, height: 15 }} data-metafield-include={attrKey} />
                                          <p className="text-[10px] font-semibold uppercase tracking-wider flex-1" style={{ color: COLORS.textLight }}>{attrKey}</p>
                                          <span className="text-[8px] font-mono px-1.5 py-0.5 rounded" style={{ background: isSelected ? 'rgba(212,175,55,0.12)' : 'transparent', color: COLORS.textLight }}>
                                            {mapping.namespace}.{mapping.key}
                                          </span>
                                        </div>
                                        <input type="text" defaultValue={existingMf.value !== undefined ? existingMf.value : (mapping.defaultValue || '')}
                                          className="w-full px-2 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                          style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                                          placeholder={mapping.defaultValue || `Enter ${attrKey}`}
                                          data-metafield-value={attrKey} />
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>

                          {/* Right Panel: Platform Fields */}
                          <div className="flex-1 overflow-y-auto p-6 space-y-4" id="platform-field-editor">
                            {/* Status Selector */}
                            <div>
                              <label className="block text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                                Status
                              </label>
                              <select
                                defaultValue={editingPlatformDesign.channelStatus || 'assigned'}
                                id="platform-status-select"
                                className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                                style={{ borderColor: COLORS.border }}
                              >
                                <option value="draft">Draft</option>
                                <option value="reviewed">Reviewed</option>
                                <option value="published">Published</option>
                              </select>
                            </div>

                            {sortedFields.length === 0 ? (
                              <p className="text-center py-8 text-sm" style={{ color: COLORS.textSecondary }}>
                                No fields configured. Use "Fields" button to add platform fields.
                              </p>
                            ) : (
                              sortedFields.map(([fieldKey, fieldDef]) => {
                                return (
                                  <div key={fieldKey}>
                                    <label className="block text-xs font-bold uppercase tracking-wider mb-1" style={{ color: COLORS.textSecondary }}>
                                      {fieldDef.label || fieldKey}
                                      {fieldDef.required && <span className="text-red-500 ml-1">*</span>}
                                    </label>
                                    {fieldDef.description && (
                                      <p className="text-[10px] mb-1.5" style={{ color: COLORS.textLight }}>{fieldDef.description}</p>
                                    )}
                                    {fieldDef.type === 'textarea' ? (
                                      <textarea
                                        defaultValue={currentData[fieldKey] || getAiDefault(fieldKey, fieldDef)}
                                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none resize-none"
                                        style={{ borderColor: COLORS.border }}
                                        rows={3}
                                        maxLength={fieldDef.maxLength}
                                        data-field-key={fieldKey}
                                      />
                                    ) : fieldDef.type === 'tags' ? (
                                      <input
                                        type="text"
                                        defaultValue={Array.isArray(currentData[fieldKey])
                                          ? (currentData[fieldKey].length > 0 ? currentData[fieldKey].join(', ') : getAiDefault(fieldKey, fieldDef))
                                          : (currentData[fieldKey] || getAiDefault(fieldKey, fieldDef))}
                                        placeholder="Comma-separated values"
                                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                                        style={{ borderColor: COLORS.border }}
                                        data-field-key={fieldKey}
                                        data-field-type="tags"
                                      />
                                    ) : (
                                      <input
                                        type="text"
                                        defaultValue={currentData[fieldKey] || getAiDefault(fieldKey, fieldDef)}
                                        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                                        style={{ borderColor: COLORS.border }}
                                        maxLength={fieldDef.maxLength}
                                        data-field-key={fieldKey}
                                      />
                                    )}
                                    {fieldDef.maxLength && (
                                      <p className="text-[10px] mt-1 text-right" style={{ color: COLORS.textLight }}>
                                        Max {fieldDef.maxLength} chars
                                      </p>
                                    )}
                                  </div>
                                );
                              })
                            )}

                            {/* ─── Variants Section ─── */}
                            {editingVariants && editingVariants.variants && editingVariants.variants.length > 0 && (
                              <div className="mt-6 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                                <div className="flex items-center justify-between mb-3">
                                  <div className="flex items-center gap-2">
                                    <Package size={14} style={{ color: COLORS.maroon }} />
                                    <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.maroon }}>
                                      Variants
                                    </h4>
                                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                                      {editingVariants.variants.filter(v => v.include !== false).length} of {editingVariants.variants.length}
                                    </span>
                                  </div>
                                  {editingVariants.variantOptions.length > 0 && (
                                    <span className="text-[10px] font-medium px-2 py-1 rounded-lg bg-gray-100" style={{ color: COLORS.textSecondary }}>
                                      By: {editingVariants.variantOptions.join(' × ')}
                                    </span>
                                  )}
                                </div>

                                <div className="space-y-2" id="variant-editor-list">
                                  {editingVariants.variants.map((variant, idx) => (
                                    <div
                                      key={variant.combinationId || idx}
                                      className="border rounded-lg p-3 transition-all"
                                      style={{
                                        borderColor: variant.include !== false ? COLORS.border : '#E5E7EB',
                                        backgroundColor: variant.include !== false ? 'white' : '#F9FAFB',
                                        opacity: variant.include !== false ? 1 : 0.6
                                      }}
                                      data-variant-idx={idx}
                                    >
                                      {/* Variant header with toggle */}
                                      <div className="flex items-center justify-between mb-2">
                                        <div className="flex items-center gap-2">
                                          <input
                                            type="checkbox"
                                            checked={variant.include !== false}
                                            onChange={(e) => {
                                              const updated = { ...editingVariants };
                                              updated.variants = [...updated.variants];
                                              updated.variants[idx] = { ...updated.variants[idx], include: e.target.checked };
                                              setEditingVariants(updated);
                                            }}
                                            className="rounded border-gray-300"
                                          />
                                          <span className="text-xs font-bold" style={{ color: COLORS.textPrimary }}>
                                            Variant {idx + 1}
                                          </span>
                                        </div>
                                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-100" style={{ color: COLORS.textSecondary }}>
                                          {variant.combinationId}
                                        </span>
                                      </div>

                                      {/* Variant fields in a grid */}
                                      <div className="grid grid-cols-2 gap-2">
                                        {variant.color && (
                                          <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>Color</label>
                                            <p className="text-xs mt-0.5" style={{ color: COLORS.textPrimary }}>{variant.color}</p>
                                          </div>
                                        )}
                                        {variant.polish && (
                                          <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>Polish</label>
                                            <p className="text-xs mt-0.5" style={{ color: COLORS.textPrimary }}>{variant.polish}</p>
                                          </div>
                                        )}
                                        {variant.size && (
                                          <div>
                                            <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>Size</label>
                                            <p className="text-xs mt-0.5" style={{ color: COLORS.textPrimary }}>{variant.size}</p>
                                          </div>
                                        )}
                                        <div>
                                          <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>MRP</label>
                                          <p className="text-xs mt-0.5 font-semibold" style={{ color: COLORS.textPrimary }}>₹{variant.mrp}</p>
                                        </div>
                                      </div>

                                      {/* Editable fields: SKU, Compare-at Price, Cost */}
                                      <div className="mt-2 grid grid-cols-3 gap-2">
                                        <div>
                                          <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>SKU</label>
                                          <input
                                            type="text"
                                            defaultValue={variant.sku || ''}
                                            className="w-full px-2 py-1.5 border rounded text-xs mt-0.5 focus:outline-none font-mono"
                                            style={{ borderColor: COLORS.border }}
                                            data-variant-sku={idx}
                                          />
                                        </div>
                                        <div>
                                          <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#DC2626' }}>Compare-at ₹</label>
                                          <input
                                            type="number"
                                            step="0.01"
                                            defaultValue={variant.compareAtPrice || ''}
                                            placeholder={variant.mrp || ''}
                                            className="w-full px-2 py-1.5 border rounded text-xs mt-0.5 focus:outline-none"
                                            style={{ borderColor: '#FCA5A5', color: '#DC2626' }}
                                            data-variant-compare-price={idx}
                                          />
                                        </div>
                                        <div>
                                          <label className="text-[10px] font-bold uppercase tracking-wider" style={{ color: COLORS.textLight }}>Cost ₹</label>
                                          <input
                                            type="number"
                                            step="0.01"
                                            defaultValue={variant.cost || ''}
                                            placeholder="Cost per item"
                                            className="w-full px-2 py-1.5 border rounded text-xs mt-0.5 focus:outline-none"
                                            style={{ borderColor: COLORS.border }}
                                            data-variant-cost={idx}
                                          />
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: COLORS.border }}>
                          <button
                            onClick={() => { setShowPlatformFieldEditor(false); setEditingPlatformDesign(null); setEditingVariants(null); }}
                            className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                            style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              // Collect form data
                              const editor = document.getElementById('platform-field-editor');
                              const newData = {};
                              const inputs = editor.querySelectorAll('[data-field-key]');
                              inputs.forEach(input => {
                                const key = input.getAttribute('data-field-key');
                                const type = input.getAttribute('data-field-type');
                                let val = input.value;
                                if (type === 'tags') {
                                  val = val.split(',').map(v => v.trim()).filter(Boolean);
                                }
                                newData[key] = val;
                              });
                              // Collect metafield data from all [data-metafield-*] elements in the modal
                              const mfData = {};
                              const mfIncludes = document.querySelectorAll('[data-metafield-include]');
                              const mfValues = document.querySelectorAll('[data-metafield-value]');
                              mfIncludes.forEach(cb => {
                                const attrKey = cb.getAttribute('data-metafield-include');
                                if (!mfData[attrKey]) mfData[attrKey] = {};
                                mfData[attrKey].include = cb.checked;
                              });
                              mfValues.forEach(inp => {
                                const attrKey = inp.getAttribute('data-metafield-value');
                                if (!mfData[attrKey]) mfData[attrKey] = {};
                                mfData[attrKey].value = inp.value;
                              });
                              if (Object.keys(mfData).length > 0) {
                                newData.metafields = mfData;
                              }
                              // Collect variant data (include flags + edited SKUs)
                              if (editingVariants && editingVariants.variants) {
                                const updatedVariants = editingVariants.variants.map((v, idx) => {
                                  const skuInput = document.querySelector(`[data-variant-sku="${idx}"]`);
                                  const comparePriceInput = document.querySelector(`[data-variant-compare-price="${idx}"]`);
                                  const costInput = document.querySelector(`[data-variant-cost="${idx}"]`);
                                  return {
                                    ...v,
                                    sku: skuInput ? skuInput.value : v.sku,
                                    compareAtPrice: comparePriceInput && comparePriceInput.value ? parseFloat(comparePriceInput.value) : v.compareAtPrice,
                                    cost: costInput && costInput.value ? parseFloat(costInput.value) : v.cost,
                                  };
                                });
                                newData.variants = updatedVariants;
                                newData.variantOptions = editingVariants.variantOptions || [];
                              }
                              const statusSelect = document.getElementById('platform-status-select');
                              const newStatus = statusSelect ? statusSelect.value : editingPlatformDesign.channelStatus;

                              // Block transition to 'reviewed' if no images selected
                              if (newStatus === 'reviewed' && editingPlatformDesign.channelStatus !== 'reviewed') {
                                const savedImages = editingPlatformDesign.platformData?.images;
                                if (!savedImages || !savedImages.primaryImageId) {
                                  alert('Please select images before marking as Reviewed.\n\nUse the "Images" button on the design card to select a primary image.');
                                  return;
                                }
                              }

                              handleUpdateDesignPlatformData(activePlatformId, editingPlatformDesign.designNumber, newData, newStatus);
                            }}
                            className="px-6 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2"
                            style={{ backgroundColor: activePlatform.color }}
                          >
                            <Save size={14} />
                            Save Changes
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* ── Image Selection Modal ── */}
                {showImageModal && imageModalDesign && (() => {
                  // Build the combined image list: shoot images + AI analysis image
                  const aiImage = {
                    id: 'ai',
                    imageUrl: imageModalDesign.imageUrl,
                    imageType: 'ai-analysis',
                    altText: 'AI Analysis Image (current)',
                  };
                  const allImages = [aiImage, ...designShootImages];
                  const hasSelections = selectedPrimaryImage !== null;

                  // Get primary image URL for Shopify preview
                  const primaryImg = allImages.find(img => img.id === selectedPrimaryImage) || aiImage;
                  const galleryImgs = selectedGalleryImages
                    .map(gid => allImages.find(img => img.id === gid))
                    .filter(Boolean);
                  const previewImages = [primaryImg, ...galleryImgs];

                  // Design data for preview
                  const pData = imageModalDesign.platformData || {};
                  const previewTitle = pData.seo_title || pData.title || `${imageModalDesign.productType || 'Jewellery'} ${imageModalDesign.designNumber}`;
                  const previewPrice = pData.price || pData.mrp || '—';
                  const previewDesc = pData.seo_description || pData.body_html || '';
                  const attrs = imageModalDesign.attributes || {};

                  return (
                    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50" onClick={() => setShowImageModal(false)}>
                      <div
                        className="bg-white rounded-2xl shadow-2xl w-full max-w-7xl mx-4 max-h-[95vh] flex flex-col overflow-hidden"
                        onClick={e => e.stopPropagation()}
                      >
                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center gap-3">
                            <ImageIcon size={20} style={{ color: COLORS.maroon }} />
                            <div>
                              <h2 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                                Image Selection — {imageModalDesign.designNumber}
                              </h2>
                              <p className="text-xs" style={{ color: COLORS.textLight }}>
                                Select primary and gallery images for {activePlatform?.name || 'publishing'}
                              </p>
                            </div>
                          </div>
                          <button onClick={() => setShowImageModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                            <X size={18} />
                          </button>
                        </div>

                        {/* Body: Two panels */}
                        <div className="flex flex-1 overflow-hidden">
                          {/* LEFT: Image Grid */}
                          <div className="w-1/2 p-6 overflow-y-auto border-r" style={{ borderColor: COLORS.border }}>
                            <div className="mb-4">
                              <h3 className="text-sm font-bold mb-1" style={{ color: COLORS.textPrimary }}>Available Images</h3>
                              <p className="text-xs" style={{ color: COLORS.textLight }}>
                                Click star to set as primary. Click image to toggle gallery selection.
                                {designShootImages.length === 0 && ' No shoot images uploaded yet — only the AI analysis image is available.'}
                              </p>
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                              {allImages.map((img) => {
                                const isPrimary = selectedPrimaryImage === img.id;
                                const isGallery = selectedGalleryImages.includes(img.id);
                                const isSelected = isPrimary || isGallery;

                                return (
                                  <div
                                    key={img.id}
                                    className={`relative rounded-lg overflow-hidden border-2 cursor-pointer transition-all group ${
                                      isPrimary ? 'ring-2 ring-amber-400' : isGallery ? 'ring-2 ring-blue-400' : 'hover:shadow-md'
                                    }`}
                                    style={{ borderColor: isPrimary ? '#F59E0B' : isGallery ? '#3B82F6' : COLORS.border }}
                                  >
                                    <img
                                      src={img.imageUrl}
                                      alt={img.altText || img.imageType}
                                      className="w-full aspect-square object-cover"
                                      onClick={() => toggleGalleryImage(img.id)}
                                    />

                                    {/* Star button for primary */}
                                    <button
                                      onClick={(e) => { e.stopPropagation(); setPrimaryImage(img.id); }}
                                      className={`absolute top-1.5 right-1.5 p-1 rounded-full transition-all ${
                                        isPrimary ? 'bg-amber-400 text-white' : 'bg-white/80 text-gray-400 opacity-0 group-hover:opacity-100'
                                      }`}
                                      title="Set as primary image"
                                    >
                                      <Star size={14} fill={isPrimary ? 'white' : 'none'} />
                                    </button>

                                    {/* Type badge */}
                                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                                      <span className="text-[9px] font-bold uppercase text-white tracking-wider">
                                        {img.imageType === 'ai-analysis' ? 'AI Analysis' : img.imageType}
                                      </span>
                                    </div>

                                    {/* Selection badge */}
                                    {isPrimary && (
                                      <div className="absolute top-1.5 left-1.5 bg-amber-400 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">
                                        Primary
                                      </div>
                                    )}
                                    {isGallery && (
                                      <div className="absolute top-1.5 left-1.5 bg-blue-500 text-white text-[8px] font-bold px-1.5 py-0.5 rounded-full uppercase">
                                        Gallery {selectedGalleryImages.indexOf(img.id) + 1}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>

                            {/* Selection summary */}
                            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                              <p className="text-xs font-medium" style={{ color: COLORS.textSecondary }}>
                                <span className="font-bold">Primary:</span>{' '}
                                {selectedPrimaryImage
                                  ? selectedPrimaryImage === 'ai' ? 'AI Analysis Image' : `Image #${selectedPrimaryImage}`
                                  : 'None selected'}
                                {' | '}
                                <span className="font-bold">Gallery:</span>{' '}
                                {selectedGalleryImages.length > 0 ? `${selectedGalleryImages.length} image(s)` : 'None'}
                              </p>
                            </div>

                            {/* ── Variant Image Assignment ── */}
                            {(() => {
                              const variants = imageModalDesign.platformData?.variants?.filter(v => v.include !== false) || [];
                              const variantOptions = imageModalDesign.platformData?.variantOptions || [];
                              if (variants.length <= 1) return null; // skip for single/no variants

                              return (
                                <div className="mt-5 border-t pt-4" style={{ borderColor: COLORS.border }}>
                                  <div className="mb-3">
                                    <h3 className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>Variant Images</h3>
                                    <p className="text-xs" style={{ color: COLORS.textLight }}>
                                      Assign a specific image to each variant. On Shopify, this image shows when a customer selects that variant.
                                    </p>
                                  </div>
                                  <div className="space-y-2">
                                    {variants.map((variant) => {
                                      const variantId = variant.combinationId || variant.sku || '';
                                      const variantLabel = variantOptions.length > 0
                                        ? variantOptions.map(opt => variant[opt.toLowerCase()] || '—').join(' / ')
                                        : variant.sku || variantId;
                                      const assignedImgId = variantImageMap[variantId];
                                      const assignedImg = assignedImgId ? allImages.find(img => img.id === assignedImgId) : null;

                                      return (
                                        <div key={variantId} className="flex items-center gap-3 p-2 rounded-lg border bg-white" style={{ borderColor: COLORS.border }}>
                                          {/* Variant thumbnail */}
                                          <div className="w-10 h-10 rounded border overflow-hidden flex-shrink-0" style={{ borderColor: assignedImg ? '#3B82F6' : COLORS.border }}>
                                            {assignedImg ? (
                                              <img src={assignedImg.imageUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                              <div className="w-full h-full bg-gray-50 flex items-center justify-center">
                                                <ImageIcon size={14} className="text-gray-300" />
                                              </div>
                                            )}
                                          </div>
                                          {/* Variant label */}
                                          <div className="flex-1 min-w-0">
                                            <p className="text-xs font-semibold truncate" style={{ color: COLORS.textPrimary }}>{variantLabel}</p>
                                            <p className="text-[10px] truncate" style={{ color: COLORS.textLight }}>
                                              {variant.sku ? `SKU: ${variant.sku}` : ''} {variant.mrp ? `₹${variant.mrp}` : ''}
                                            </p>
                                          </div>
                                          {/* Image picker dropdown */}
                                          <select
                                            value={assignedImgId || ''}
                                            onChange={(e) => {
                                              const val = e.target.value;
                                              setVariantImageMap(prev => {
                                                const next = { ...prev };
                                                if (val) {
                                                  next[variantId] = val === 'ai' ? 'ai' : (isNaN(val) ? val : parseInt(val));
                                                } else {
                                                  delete next[variantId];
                                                }
                                                return next;
                                              });
                                            }}
                                            className="text-xs border rounded-lg px-2 py-1.5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-400"
                                            style={{ borderColor: COLORS.border, color: COLORS.textPrimary, maxWidth: '140px' }}
                                          >
                                            <option value="">None</option>
                                            {allImages.map(img => (
                                              <option key={img.id} value={img.id}>
                                                {img.imageType === 'ai-analysis' ? 'AI Image' : `#${img.id} ${img.imageType || 'shoot'}`}
                                              </option>
                                            ))}
                                          </select>
                                        </div>
                                      );
                                    })}
                                  </div>
                                  {/* Quick assign helpers */}
                                  <div className="mt-2 flex gap-2">
                                    <button
                                      onClick={() => {
                                        // Auto-assign: primary image to all variants
                                        if (!selectedPrimaryImage) return;
                                        const map = {};
                                        variants.forEach(v => { map[v.combinationId || v.sku || ''] = selectedPrimaryImage; });
                                        setVariantImageMap(map);
                                      }}
                                      className="text-[10px] px-2 py-1 rounded border hover:bg-gray-50 transition-colors"
                                      style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                                      title="Set the primary image as the image for all variants"
                                    >
                                      Assign primary to all
                                    </button>
                                    <button
                                      onClick={() => setVariantImageMap({})}
                                      className="text-[10px] px-2 py-1 rounded border hover:bg-gray-50 transition-colors"
                                      style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                                    >
                                      Clear all
                                    </button>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>

                          {/* RIGHT: Platform Preview */}
                          <div className="w-1/2 p-6 overflow-y-auto bg-gray-50">
                            <div className="mb-4">
                              <h3 className="text-sm font-bold mb-1" style={{ color: COLORS.textPrimary }}>{activePlatform?.name || 'Platform'} Preview</h3>
                              <p className="text-xs" style={{ color: COLORS.textLight }}>
                                Preview how this product will appear on {activePlatform?.name || 'the marketplace'}
                              </p>
                            </div>

                            {/* ═══ SHOPIFY PREVIEW ═══ */}
                            {activePlatformId === 'shopify' && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                              <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#FFF5F7', borderBottom: '1px solid #F3E8EB' }}>
                                <span className="text-sm font-bold italic" style={{ color: COLORS.maroon, fontFamily: 'serif' }}>Kushals</span>
                                <div className="flex gap-3 text-[9px]" style={{ color: COLORS.textLight }}>
                                  <span>Necklaces</span><span>Earrings</span><span>Bangles</span>
                                </div>
                              </div>
                              <div className="flex">
                                <div className="w-1/2 p-3">
                                  <div className="relative rounded-lg overflow-hidden bg-gray-100 aspect-[4/5]">
                                    {primaryImg?.imageUrl ? (
                                      <img src={primaryImg.imageUrl} alt="Primary" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={48} /></div>
                                    )}
                                    <div className="absolute top-2 left-2 px-2 py-0.5 rounded text-[8px] font-bold text-white" style={{ backgroundColor: '#2D8B4E' }}>Handpicked</div>
                                  </div>
                                  {previewImages.length > 1 && (
                                    <div className="flex gap-1.5 mt-2 overflow-x-auto">
                                      {previewImages.slice(0, 5).map((img, idx) => (
                                        <div key={idx} className="w-12 h-12 rounded border flex-shrink-0 overflow-hidden" style={{ borderColor: idx === 0 ? COLORS.maroon : COLORS.border }}>
                                          <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <div className="w-1/2 p-4">
                                  <div className="flex items-center gap-2 mb-1">
                                    <Star size={10} fill="#F59E0B" className="text-amber-400" />
                                    <span className="text-[10px] font-medium">5.0</span>
                                  </div>
                                  <h3 className="text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>{previewTitle}</h3>
                                  <div className="mb-3">
                                    <p className="text-[9px]" style={{ color: COLORS.textLight }}>MRP</p>
                                    <p className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>₹ {typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}</p>
                                    <p className="text-[8px]" style={{ color: COLORS.textLight }}>(Incl. of all taxes)</p>
                                  </div>
                                  <div className="flex gap-2 mb-3">
                                    <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{ borderColor: '#2D8B4E', color: '#2D8B4E' }}>Available At Store</span>
                                    <span className="text-[9px] px-2 py-0.5 rounded-full border" style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}>Free Delivery</span>
                                  </div>
                                  {attrs['Stone Color'] && (
                                    <p className="text-[10px] mb-2" style={{ color: COLORS.textSecondary }}>Stone Color : <span className="font-bold" style={{ color: COLORS.textPrimary }}>{attrs['Stone Color']}</span></p>
                                  )}
                                  <button className="w-full py-2 rounded-lg text-white text-xs font-bold mt-2" style={{ background: 'linear-gradient(135deg, #C2185B, #E91E63)' }} disabled>Add to Bag</button>
                                  <div className="mt-3 p-2 rounded-lg grid grid-cols-4 gap-1 text-center" style={{ backgroundColor: '#FFF0F3' }}>
                                    {[{ l: '20 Lakh+', s: 'Customers' }, { l: '100', s: 'Stores' }, { l: '14+', s: 'Quality Checks' }, { l: '15 Days', s: 'Free Return' }].map((b, i) => (
                                      <div key={i}><p className="text-[8px] font-bold" style={{ color: COLORS.maroon }}>{b.l}</p><p className="text-[7px]" style={{ color: COLORS.textLight }}>{b.s}</p></div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                            </div>
                            )}

                            {/* ═══ MYNTRA PREVIEW ═══ */}
                            {activePlatformId === 'myntra' && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                              {/* Myntra header - dark with pink M logo */}
                              <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#FAFAFA', borderBottom: '1px solid #E5E7EB' }}>
                                <span className="text-lg font-bold" style={{ color: '#FF3F6C' }}>M</span>
                                <div className="flex gap-4 text-[9px] font-medium" style={{ color: '#282C3F' }}>
                                  <span>MEN</span><span>WOMEN</span><span>KIDS</span><span>BEAUTY</span>
                                </div>
                              </div>
                              {/* Breadcrumb */}
                              <div className="px-4 py-1.5 text-[8px]" style={{ color: COLORS.textLight, borderBottom: '1px solid #F5F5F5' }}>
                                Home / Accessories / {imageModalDesign.productType || 'Jewellery'} / Kushal's Fashion Jewellery
                              </div>
                              <div className="flex">
                                {/* Left: Two-column image grid like Myntra */}
                                <div className="w-1/2 p-2">
                                  <div className="grid grid-cols-2 gap-1">
                                    {previewImages.slice(0, 4).map((img, idx) => (
                                      <div key={idx} className="aspect-[3/4] bg-gray-100 overflow-hidden">
                                        <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                    {previewImages.length < 2 && (
                                      <div className="aspect-[3/4] bg-gray-50 flex items-center justify-center"><ImageIcon size={24} className="text-gray-200" /></div>
                                    )}
                                  </div>
                                </div>
                                {/* Right: Product info - Myntra style */}
                                <div className="w-1/2 p-4">
                                  <p className="text-xs font-bold" style={{ color: '#282C3F' }}>Kushal's Fashion Jewellery</p>
                                  <p className="text-[11px] mt-0.5 mb-2" style={{ color: '#535766' }}>{previewTitle}</p>
                                  {/* Rating */}
                                  <div className="flex items-center gap-1.5 mb-2">
                                    <span className="text-[10px] px-1.5 py-0.5 rounded font-bold text-white" style={{ backgroundColor: '#14958F' }}>4.5 ★</span>
                                    <span className="text-[9px]" style={{ color: '#878B94' }}>| 14 Ratings</span>
                                  </div>
                                  <div className="border-t pt-2 mb-2" style={{ borderColor: '#F5F5F5' }}>
                                    <p className="text-[9px]" style={{ color: '#535766' }}>MRP</p>
                                    <p className="text-base font-bold" style={{ color: '#282C3F' }}>₹ {typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}</p>
                                    <p className="text-[8px]" style={{ color: '#03A685' }}>inclusive of all taxes</p>
                                  </div>
                                  {/* Size */}
                                  <div className="mb-3">
                                    <p className="text-[10px] font-bold mb-1" style={{ color: '#282C3F' }}>SELECT SIZE</p>
                                    <span className="text-[9px] px-3 py-1 rounded-full border" style={{ borderColor: '#FF3F6C', color: '#FF3F6C' }}>Onesize</span>
                                  </div>
                                  {/* Buttons - Myntra pink ADD TO BAG + outline WISHLIST */}
                                  <div className="flex gap-2">
                                    <button className="flex-1 py-2 rounded text-white text-[10px] font-bold flex items-center justify-center gap-1" style={{ backgroundColor: '#FF3F6C' }} disabled>
                                      🛍 ADD TO BAG
                                    </button>
                                    <button className="flex-1 py-2 rounded border text-[10px] font-bold" style={{ borderColor: '#D4D5D9', color: '#282C3F' }} disabled>
                                      ♡ WISHLIST
                                    </button>
                                  </div>
                                  <div className="mt-3 border-t pt-2" style={{ borderColor: '#F5F5F5' }}>
                                    <p className="text-[9px]" style={{ color: '#282C3F' }}>Seller: <span className="font-bold" style={{ color: '#FF3F6C' }}>KUSHAL'S RETAIL PRIVATE LIMITED</span></p>
                                  </div>
                                  <div className="mt-2">
                                    <p className="text-[10px] font-bold mb-1" style={{ color: '#282C3F' }}>DELIVERY OPTIONS</p>
                                    <div className="flex items-center gap-2 border rounded px-2 py-1.5" style={{ borderColor: '#D4D5D9' }}>
                                      <span className="text-[9px] flex-1" style={{ color: '#878B94' }}>Enter pincode</span>
                                      <span className="text-[9px] font-bold" style={{ color: '#FF3F6C' }}>Check</span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                            )}

                            {/* ═══ AMAZON PREVIEW ═══ */}
                            {activePlatformId === 'amazon' && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                              {/* Amazon header */}
                              <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#131921' }}>
                                <span className="text-sm font-bold text-white">amazon.in</span>
                                <div className="flex items-center gap-3 text-[9px] text-gray-300">
                                  <span>🔍</span><span>🛒</span>
                                </div>
                              </div>
                              <div className="flex">
                                {/* Left: Image with vertical thumbnails like Amazon */}
                                <div className="w-1/2 p-3 flex gap-2">
                                  <div className="flex flex-col gap-1">
                                    {previewImages.slice(0, 4).map((img, idx) => (
                                      <div key={idx} className="w-9 h-9 border rounded overflow-hidden flex-shrink-0" style={{ borderColor: idx === 0 ? '#E77600' : '#E5E7EB' }}>
                                        <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                  </div>
                                  <div className="flex-1 aspect-square bg-white border rounded overflow-hidden flex items-center justify-center" style={{ borderColor: '#E5E7EB' }}>
                                    {primaryImg?.imageUrl ? (
                                      <img src={primaryImg.imageUrl} alt="Primary" className="max-w-full max-h-full object-contain" />
                                    ) : (
                                      <ImageIcon size={48} className="text-gray-200" />
                                    )}
                                  </div>
                                </div>
                                {/* Right: Product info - Amazon style */}
                                <div className="w-1/2 p-3">
                                  <p className="text-[9px] mb-1" style={{ color: '#007185' }}>Visit the KUSHAL'S Store</p>
                                  <h3 className="text-[11px] font-normal leading-tight mb-2" style={{ color: '#0F1111' }}>
                                    KUSHAL'S Fashion Jewellery {previewTitle}
                                  </h3>
                                  {/* Price */}
                                  <div className="mb-2">
                                    <p className="text-lg font-normal" style={{ color: '#0F1111' }}>
                                      <span className="text-[10px] align-top">₹</span>{typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}<span className="text-[10px] align-top">00</span>
                                    </p>
                                    <p className="text-[9px]" style={{ color: '#565959' }}>Inclusive of all taxes</p>
                                  </div>
                                  {/* Trust icons row */}
                                  <div className="flex gap-2 mb-2 pb-2 border-b" style={{ borderColor: '#E5E7EB' }}>
                                    {['Free Delivery', '10 days Return', 'Top Brand', 'Secure'].map((t, i) => (
                                      <div key={i} className="text-center flex-1">
                                        <p className="text-[7px]" style={{ color: '#565959' }}>{t}</p>
                                      </div>
                                    ))}
                                  </div>
                                  {/* Product details table */}
                                  <p className="text-[10px] font-bold mb-1" style={{ color: '#0F1111' }}>Product details</p>
                                  <div className="space-y-1">
                                    {[
                                      ['Material', attrs['Base Material'] || 'Copper'],
                                      ['Type', imageModalDesign.productType || '—'],
                                      ['Occasion', attrs['Primary Occasion'] || '—'],
                                      ['Origin', 'India'],
                                    ].map(([k, v], i) => (
                                      <div key={i} className="flex text-[9px]">
                                        <span className="w-20 font-bold" style={{ color: '#565959' }}>{k}</span>
                                        <span style={{ color: '#0F1111' }}>{v}</span>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {/* Buy box on right side */}
                              <div className="border-t p-3 flex gap-2" style={{ borderColor: '#E5E7EB' }}>
                                <button className="flex-1 py-1.5 rounded-full text-[10px] font-bold text-center" style={{ backgroundColor: '#FFD814', color: '#0F1111', border: '1px solid #FCD200' }} disabled>Add to cart</button>
                                <button className="flex-1 py-1.5 rounded-full text-[10px] font-bold text-center" style={{ backgroundColor: '#FFA41C', color: '#0F1111', border: '1px solid #FF8F00' }} disabled>Buy Now</button>
                              </div>
                            </div>
                            )}

                            {/* ═══ FLIPKART PREVIEW ═══ */}
                            {activePlatformId === 'flipkart' && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                              {/* Flipkart header - blue */}
                              <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#2874F0' }}>
                                <span className="text-sm font-bold italic text-white">Flipkart</span>
                                <div className="flex items-center gap-3">
                                  <div className="flex items-center bg-white rounded px-2 py-0.5"><span className="text-[8px]" style={{ color: '#878787' }}>Search...</span></div>
                                  <span className="text-[9px] text-white">🛒 Cart</span>
                                </div>
                              </div>
                              {/* Breadcrumb */}
                              <div className="px-4 py-1.5 text-[8px]" style={{ color: '#878787', borderBottom: '1px solid #F0F0F0' }}>
                                Home / Jewellery / {imageModalDesign.productType || 'Jewellery'} / Kushals Fashion Jewellery
                              </div>
                              <div className="flex">
                                {/* Left: 2x2 image grid like Flipkart */}
                                <div className="w-1/2 p-2">
                                  <div className="grid grid-cols-2 gap-1">
                                    {previewImages.slice(0, 4).map((img, idx) => (
                                      <div key={idx} className="aspect-square bg-white overflow-hidden">
                                        <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                      </div>
                                    ))}
                                    {previewImages.length < 2 && (
                                      <div className="aspect-square bg-gray-50 flex items-center justify-center"><ImageIcon size={24} className="text-gray-200" /></div>
                                    )}
                                  </div>
                                </div>
                                {/* Right: Product info - Flipkart style */}
                                <div className="w-1/2 p-3">
                                  {/* Color selection thumbnails */}
                                  <div className="mb-2">
                                    <p className="text-[9px] mb-1" style={{ color: '#878787' }}>Selected Color: <span className="font-medium" style={{ color: '#212121' }}>Gold</span></p>
                                    <div className="flex gap-1.5">
                                      {previewImages.slice(0, 3).map((img, idx) => (
                                        <div key={idx} className="w-10 h-10 border-2 rounded overflow-hidden" style={{ borderColor: idx === 0 ? '#2874F0' : '#E0E0E0' }}>
                                          <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                  <p className="text-xs font-medium" style={{ color: '#212121' }}>Kushals Fashion Jewellery</p>
                                  <p className="text-[10px] mb-1" style={{ color: '#878787' }}>{previewTitle}</p>
                                  <p className="text-lg font-bold mb-1" style={{ color: '#212121' }}>₹{typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}</p>
                                  {/* Offers section - blue banner */}
                                  <div className="rounded overflow-hidden mb-2">
                                    <div className="px-2 py-1.5 flex items-center gap-1" style={{ backgroundColor: '#2874F0' }}>
                                      <span className="text-[9px] font-bold text-white">Apply offers for maximum savings</span>
                                    </div>
                                    <div className="p-2 border border-t-0 rounded-b" style={{ borderColor: '#E0E0E0' }}>
                                      <div className="flex gap-2 text-[8px]">
                                        <div><span className="font-bold" style={{ color: '#212121' }}>Bank Offers</span><br /><span style={{ color: '#878787' }}>₹190 off Flipkart Axis</span></div>
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* Bottom buy bar like Flipkart */}
                              <div className="border-t p-3 flex gap-2" style={{ borderColor: '#E5E7EB' }}>
                                <button className="flex-1 py-2 rounded text-[10px] font-bold flex items-center justify-center gap-1" style={{ backgroundColor: '#FF9F00', color: 'white' }} disabled>
                                  🛒 Buy with EMI
                                </button>
                                <button className="flex-1 py-2 rounded text-[10px] font-bold flex items-center justify-center gap-1" style={{ backgroundColor: '#FB641B', color: 'white' }} disabled>
                                  Buy at ₹{typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}
                                </button>
                              </div>
                            </div>
                            )}

                            {/* ═══ TATACLIQ PREVIEW ═══ */}
                            {activePlatformId === 'tatacliq' && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border" style={{ borderColor: '#E5E7EB' }}>
                              {/* TataCliq header - dark */}
                              <div className="px-4 py-2 flex items-center justify-between" style={{ backgroundColor: '#1A1A2E' }}>
                                <div>
                                  <span className="text-[8px] text-gray-400">TATA</span>
                                  <span className="text-sm font-bold text-white ml-0.5">CLI</span>
                                  <span className="text-sm font-bold" style={{ color: '#E91E63' }}>Q</span>
                                  <span className="text-[8px] text-gray-400 ml-1">FASHION</span>
                                </div>
                                <div className="flex gap-3 text-[9px] text-gray-300">
                                  <span>Categories</span><span>Brands</span>
                                </div>
                              </div>
                              {/* Breadcrumb */}
                              <div className="px-4 py-1.5 text-[8px]" style={{ color: '#999', borderBottom: '1px solid #F0F0F0' }}>
                                Home &gt; Jewellery &gt; Fashion &gt; Kushal's Fashi...
                              </div>
                              <div className="flex">
                                {/* Left: Single large image with arrows like TataCliq */}
                                <div className="w-1/2 p-3">
                                  <div className="relative aspect-[4/5] bg-gray-50 overflow-hidden rounded">
                                    {primaryImg?.imageUrl ? (
                                      <img src={primaryImg.imageUrl} alt="Primary" className="w-full h-full object-cover" />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center text-gray-300"><ImageIcon size={48} /></div>
                                    )}
                                    {/* Navigation arrows */}
                                    <div className="absolute left-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-white/80 rounded-full flex items-center justify-center text-gray-500 text-xs">&lt;</div>
                                    <div className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 bg-white/80 rounded-full flex items-center justify-center text-gray-500 text-xs">&gt;</div>
                                  </div>
                                  {/* Dot thumbnails */}
                                  {previewImages.length > 1 && (
                                    <div className="flex gap-1.5 mt-2 justify-center">
                                      {previewImages.slice(0, 5).map((img, idx) => (
                                        <div key={idx} className="w-10 h-10 border rounded overflow-hidden" style={{ borderColor: idx === 0 ? '#E91E63' : '#E5E7EB' }}>
                                          <img src={img.imageUrl} alt="" className="w-full h-full object-cover" />
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                {/* Right: Product info - TataCliq style */}
                                <div className="w-1/2 p-4">
                                  <div className="flex items-center justify-between mb-1">
                                    <p className="text-xs font-bold" style={{ color: '#333' }}>Kushal's Fashion Jewellery</p>
                                    <div className="flex gap-2">
                                      <span className="text-gray-400 text-sm">↗</span>
                                      <span className="text-gray-400 text-sm">♡</span>
                                    </div>
                                  </div>
                                  <p className="text-[10px] mb-2" style={{ color: '#666' }}>{previewTitle}</p>
                                  <div className="mb-2">
                                    <p className="text-base font-bold" style={{ color: '#333' }}>MRP: ₹{typeof previewPrice === 'number' ? previewPrice.toLocaleString('en-IN') : previewPrice}</p>
                                    <p className="text-[8px]" style={{ color: '#999' }}>Inclusive of all taxes</p>
                                  </div>
                                  <p className="text-[9px] mb-2" style={{ color: '#E91E63' }}>Share your opinion</p>
                                  <p className="text-[10px] mb-3" style={{ color: '#333' }}>
                                    {attrs['Theme'] || 'Wedding'} {attrs['Base Material'] || 'Copper'} {imageModalDesign.productType || 'Set'} for Women
                                  </p>
                                  {/* EMI info */}
                                  <p className="text-[9px] mb-3" style={{ color: '#666' }}>
                                    EMI Starting from ₹{Math.round((typeof previewPrice === 'number' ? previewPrice : 1000) / 6)}/month | <span className="font-bold">Debit Card EMI Available</span>
                                  </p>
                                  {/* Size selector */}
                                  <div className="mb-3">
                                    <p className="text-[10px] font-bold mb-1" style={{ color: '#333' }}>Select a Size</p>
                                    <select className="text-[9px] border rounded px-3 py-1.5" style={{ borderColor: '#DDD' }} disabled>
                                      <option>Size</option>
                                    </select>
                                  </div>
                                  {/* Offers */}
                                  <div className="p-2 rounded border" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFDF0' }}>
                                    <p className="text-[9px] font-bold mb-1" style={{ color: '#333' }}>Available Offers</p>
                                    <p className="text-[8px]" style={{ color: '#666' }}>🎁 Flat 15% OFF. Offer Price <span className="font-bold" style={{ color: '#E91E63' }}>₹{Math.round((typeof previewPrice === 'number' ? previewPrice : 1000) * 0.85)}</span></p>
                                  </div>
                                </div>
                              </div>
                            </div>
                            )}

                            {/* ═══ GENERIC PREVIEW (Myntra or unknown) ═══ */}
                            {!['shopify', 'myntra', 'amazon', 'flipkart', 'tatacliq'].includes(activePlatformId) && (
                            <div className="bg-white rounded-xl shadow-sm overflow-hidden border p-4" style={{ borderColor: '#E5E7EB' }}>
                              <div className="text-center py-8">
                                <ImageIcon size={40} className="mx-auto mb-3 text-gray-300" />
                                <p className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Preview not available for {activePlatform?.name}</p>
                                <p className="text-xs mt-1" style={{ color: COLORS.textLight }}>Selected images will be used when publishing</p>
                              </div>
                            </div>
                            )}

                            {/* Description preview */}
                            {previewDesc && (
                              <div className="mt-3 p-3 bg-white rounded-lg border" style={{ borderColor: '#E5E7EB' }}>
                                <p className="text-[10px] font-bold mb-1" style={{ color: COLORS.textPrimary }}>Product Description</p>
                                <p className="text-[9px] line-clamp-3" style={{ color: COLORS.textSecondary }}>{previewDesc}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: COLORS.border, backgroundColor: '#FAFAFA' }}>
                          <p className="text-xs" style={{ color: COLORS.textLight }}>
                            {designShootImages.length} shoot image{designShootImages.length !== 1 ? 's' : ''} + 1 AI image available
                          </p>
                          <div className="flex gap-3">
                            <button
                              onClick={() => setShowImageModal(false)}
                              className="px-5 py-2 text-sm font-medium border rounded-lg hover:bg-gray-50"
                              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                            >
                              Cancel
                            </button>
                            <button
                              onClick={handleSaveImageSelections}
                              disabled={isSavingImages}
                              className="px-5 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
                              style={{ backgroundColor: activePlatform?.color || COLORS.maroon }}
                            >
                              <Save size={14} />
                              {isSavingImages ? 'Saving...' : 'Save Selections'}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Platform Config Modal — Standard Fields + AI Attribute Metafield Toggles */}
                {showPlatformConfigModal && platformConfig && (() => {
                  const fields = { ...(platformConfig.fields || {}) };
                  const metafieldMappings = { ...(platformConfig.metafieldMappings || {}) };

                  // Auto-populate all known AI attributes into metafieldMappings if missing
                  const allAttrCategories = [
                    { label: 'Product Info', keys: ['Product Type', 'Product Style', 'Pieces', 'Base Material'] },
                    { label: 'Design', keys: ['Theme', 'Motif', 'Design Shape', 'Design Complexity', 'Traditional vs Western', 'Closure Type'] },
                    { label: 'Stones', keys: ['Stone Type', 'Stone Color', 'Stone Setting'] },
                    { label: 'Styling', keys: ['Primary Occasion', 'Occasion Type', 'Glam Level', 'Style Persona', 'Cultural / Ethnic Region'] },
                    { label: 'AI Suggestions', keys: ['Outfit Suggestion', 'Recommended Colors', 'Makeup Tip', 'Hair Style', 'Accessories to Pair'] },
                    { label: 'Marketing', keys: ['Attitude', 'SEO_Keywords', 'Product_Description', 'Inventory_Tags'] },
                  ];
                  let needsSync = false;
                  allAttrCategories.forEach(cat => {
                    cat.keys.forEach(key => {
                      if (!metafieldMappings[key]) {
                        metafieldMappings[key] = {
                          enabled: false,
                          namespace: 'kushals',
                          key: key.toLowerCase().replace(/ /g, '_').replace(/\//g, '_'),
                          category: cat.label,
                        };
                        needsSync = true;
                      }
                    });
                  });
                  // Sync auto-populated keys into platformConfig state so toggles & save work
                  if (needsSync && platformConfig) {
                    const synced = { ...platformConfig, metafieldMappings: { ...metafieldMappings } };
                    setTimeout(() => setPlatformConfig(synced), 0);
                  }

                  // Group metafields by category
                  const mfCategories = {};
                  Object.entries(metafieldMappings).forEach(([attrKey, mapping]) => {
                    const cat = mapping.category || 'Other';
                    if (!mfCategories[cat]) mfCategories[cat] = [];
                    mfCategories[cat].push({ attrKey, ...mapping });
                  });

                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl mx-4 max-h-[90vh] flex flex-col overflow-hidden">
                        <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                                Configure {activePlatform.name} Fields
                              </h3>
                              <p className="text-sm mt-1" style={{ color: COLORS.textSecondary }}>
                                Standard fields + AI attributes that get pushed to {activePlatform.name}
                              </p>
                            </div>
                            <button onClick={() => { setShowPlatformConfigModal(false); setSelectedMfForDelete(new Set()); }} className="p-2 hover:bg-gray-100 rounded-lg">
                              <X size={18} />
                            </button>
                          </div>
                        </div>

                        <div className="flex-1 overflow-y-auto p-6 space-y-6" id="platform-config-editor">
                          {/* Section 1: Standard Platform Fields */}
                          <div>
                            <div className="flex items-center gap-2 mb-3">
                              <GripVertical size={14} style={{ color: activePlatform.color }} />
                              <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: activePlatform.color }}>
                                Standard Fields
                              </h4>
                              <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100" style={{ color: COLORS.textSecondary }}>
                                Maps to {activePlatform.name} product fields
                              </span>
                            </div>
                            <div className="space-y-2">
                              {Object.entries(fields).sort((a, b) => (a[1].order || 0) - (b[1].order || 0)).map(([fieldKey, fieldDef]) => (
                                <div key={fieldKey} className="border rounded-lg px-4 py-3 flex items-center justify-between" style={{ borderColor: COLORS.border }}>
                                  <div className="flex items-center gap-3">
                                    <span className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                                      {fieldDef.label || fieldKey}
                                    </span>
                                    <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 rounded font-mono" style={{ color: COLORS.textLight }}>
                                      {fieldDef.type}
                                    </span>
                                    {fieldDef.required && (
                                      <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-500 rounded font-bold">Required</span>
                                    )}
                                  </div>
                                  {fieldDef.description && (
                                    <span className="text-[10px] max-w-[200px] truncate" style={{ color: COLORS.textLight }}>
                                      {fieldDef.description}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Section 2: AI Attribute Metafield Mappings (Shopify only) */}
                          {Object.keys(metafieldMappings).length > 0 ? (
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <Sparkles size={14} style={{ color: COLORS.gold }} />
                              <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.maroon }}>
                                AI Attributes → Metafields
                              </h4>
                              <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                                {Object.values(metafieldMappings).filter(m => m.enabled).length} / {Object.keys(metafieldMappings).length} enabled
                              </span>
                            </div>
                            <p className="text-[10px] mb-2" style={{ color: COLORS.textLight }}>
                              Toggle which AI-generated attributes to include when publishing. Enabled attributes become custom fields on {activePlatform.name}.
                            </p>
                            <div className="flex gap-2 mb-3">
                              <button
                                onClick={() => {
                                  const updated = { ...platformConfig };
                                  updated.metafieldMappings = { ...updated.metafieldMappings };
                                  Object.keys(updated.metafieldMappings).forEach(key => {
                                    updated.metafieldMappings[key] = { ...updated.metafieldMappings[key], enabled: true };
                                  });
                                  setPlatformConfig(updated);
                                }}
                                className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border transition-all hover:bg-green-50"
                                style={{ borderColor: '#86EFAC', color: '#166534' }}
                              >
                                ✓ Select All
                              </button>
                              <button
                                onClick={() => {
                                  const updated = { ...platformConfig };
                                  updated.metafieldMappings = { ...updated.metafieldMappings };
                                  Object.keys(updated.metafieldMappings).forEach(key => {
                                    updated.metafieldMappings[key] = { ...updated.metafieldMappings[key], enabled: false };
                                  });
                                  setPlatformConfig(updated);
                                }}
                                className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border transition-all hover:bg-red-50"
                                style={{ borderColor: '#FCA5A5', color: '#991B1B' }}
                              >
                                ✕ Deselect All
                              </button>
                              {selectedMfForDelete.size > 0 && (
                                <button
                                  disabled={bulkDeleting}
                                  onClick={async () => {
                                    const items = [...selectedMfForDelete].map(k => {
                                      const m = metafieldMappings[k];
                                      return { namespace: m?.namespace || 'kushals', key: m?.key || k.toLowerCase().replace(/ /g, '_'), attrKey: k };
                                    });
                                    const names = items.map(i => `${i.namespace}.${i.key}`).join('\n');
                                    if (!window.confirm(`Delete ${items.length} metafield(s) from Shopify?\n\nThis removes definitions + values from ALL products:\n${names}`)) return;
                                    setBulkDeleting(true);
                                    try {
                                      const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
                                      const resp = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/metafield-definitions/bulk-delete`), {
                                        method: 'POST',
                                        headers: { 'Content-Type': 'application/json' },
                                        body: JSON.stringify({ definitions: items.map(i => ({ namespace: i.namespace, key: i.key })) }),
                                      });
                                      const result = await resp.json();
                                      if (result.success || result.deleted_count > 0) {
                                        const updated = { ...platformConfig };
                                        updated.metafieldMappings = { ...updated.metafieldMappings };
                                        items.forEach(i => { delete updated.metafieldMappings[i.attrKey]; });
                                        setPlatformConfig(updated);
                                        setSelectedMfForDelete(new Set());
                                        window.alert(`Deleted ${result.deleted_count}/${items.length} metafields from Shopify.${result.failed_count > 0 ? ` ${result.failed_count} failed.` : ''}`);
                                      } else {
                                        window.alert(`Failed: ${result.error || 'Unknown error'}`);
                                      }
                                    } catch (err) { console.error(err); window.alert('Bulk delete failed'); }
                                    finally { setBulkDeleting(false); }
                                  }}
                                  className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border transition-all hover:bg-red-100"
                                  style={{ borderColor: '#EF4444', color: '#DC2626', opacity: bulkDeleting ? 0.5 : 1 }}
                                >
                                  {bulkDeleting ? '⏳ Deleting…' : `🗑 Delete ${selectedMfForDelete.size} Selected`}
                                </button>
                              )}
                            </div>

                            {/* Add Custom Metafield */}
                            <div className="mb-3">
                              {!showAddCustomMf ? (
                                <button
                                  onClick={() => setShowAddCustomMf(true)}
                                  className="w-full px-4 py-2.5 text-xs font-bold uppercase tracking-wider rounded-lg border-2 border-dashed transition-all hover:bg-amber-50 flex items-center justify-center gap-2"
                                  style={{ borderColor: COLORS.gold + '60', color: COLORS.gold }}
                                >
                                  + Add Custom Metafield
                                </button>
                              ) : (
                                <div className="border-2 rounded-lg p-4 space-y-3" style={{ borderColor: COLORS.gold, backgroundColor: 'rgba(212,175,55,0.03)' }}>
                                  <div className="flex items-center justify-between">
                                    <p className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.maroon }}>New Custom Metafield</p>
                                    <button onClick={() => setShowAddCustomMf(false)} className="text-gray-400 hover:text-gray-600"><X size={14} /></button>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3">
                                    <div>
                                      <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: COLORS.textSecondary }}>Display Name *</label>
                                      <input type="text" placeholder="e.g. Discount Percentage" value={customMfForm.name}
                                        onChange={(e) => {
                                          const name = e.target.value;
                                          const autoKey = name.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');
                                          setCustomMfForm(prev => ({ ...prev, name, key: autoKey }));
                                        }}
                                        className="w-full px-3 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                        style={{ borderColor: COLORS.border }}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: COLORS.textSecondary }}>Namespace</label>
                                      <input type="text" value={customMfForm.namespace}
                                        onChange={(e) => setCustomMfForm(prev => ({ ...prev, namespace: e.target.value }))}
                                        className="w-full px-3 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                        style={{ borderColor: COLORS.border }}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: COLORS.textSecondary }}>Key (auto-generated)</label>
                                      <input type="text" value={customMfForm.key}
                                        onChange={(e) => setCustomMfForm(prev => ({ ...prev, key: e.target.value }))}
                                        className="w-full px-3 py-1.5 border rounded-lg text-xs font-mono focus:outline-none focus:ring-1 focus:ring-amber-300"
                                        style={{ borderColor: COLORS.border }}
                                      />
                                    </div>
                                    <div>
                                      <label className="text-[10px] font-semibold uppercase tracking-wider block mb-1" style={{ color: COLORS.textSecondary }}>Default Value</label>
                                      <input type="text" placeholder="Optional" value={customMfForm.defaultValue}
                                        onChange={(e) => setCustomMfForm(prev => ({ ...prev, defaultValue: e.target.value }))}
                                        className="w-full px-3 py-1.5 border rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-amber-300"
                                        style={{ borderColor: COLORS.border }}
                                      />
                                    </div>
                                  </div>
                                  <div className="flex gap-2 justify-end">
                                    <button onClick={() => setShowAddCustomMf(false)}
                                      className="px-3 py-1.5 text-xs font-medium rounded-lg border hover:bg-gray-50"
                                      style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}>Cancel</button>
                                    <button
                                      onClick={() => {
                                        if (!customMfForm.name || !customMfForm.key) { alert('Name and Key are required'); return; }
                                        const attrKey = customMfForm.name;
                                        if (platformConfig.metafieldMappings?.[attrKey]) { alert('A metafield with this name already exists'); return; }
                                        const updated = { ...platformConfig };
                                        updated.metafieldMappings = { ...updated.metafieldMappings };
                                        updated.metafieldMappings[attrKey] = {
                                          enabled: true,
                                          namespace: customMfForm.namespace || 'kushals',
                                          key: customMfForm.key,
                                          category: 'Custom',
                                          source: 'custom',
                                          defaultValue: customMfForm.defaultValue || '',
                                        };
                                        setPlatformConfig(updated);
                                        setCustomMfForm({ name: '', namespace: 'kushals', key: '', defaultValue: '' });
                                        setShowAddCustomMf(false);
                                      }}
                                      className="px-4 py-1.5 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90"
                                      style={{ backgroundColor: COLORS.gold }}>
                                      Add Metafield
                                    </button>
                                  </div>
                                </div>
                              )}
                            </div>

                            <div className="space-y-4">
                              {Object.entries(mfCategories).map(([category, attrs]) => (
                                <div key={category}>
                                  <p className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                                    {category}
                                  </p>
                                  <div className="space-y-1">
                                    {attrs.map((attr) => (
                                      <div
                                        key={attr.attrKey}
                                        className="border rounded-lg px-4 py-2.5 flex items-center justify-between transition-all"
                                        style={{
                                          borderColor: selectedMfForDelete.has(attr.attrKey) ? '#EF4444' : attr.enabled ? activePlatform.color + '40' : COLORS.border,
                                          backgroundColor: selectedMfForDelete.has(attr.attrKey) ? '#FEF2F2' : attr.enabled ? activePlatform.color + '05' : 'white',
                                        }}
                                      >
                                        <div className="flex items-center gap-3">
                                          {/* Bulk-delete selection checkbox (custom metafields only) */}
                                          {attr.source === 'custom' && (
                                            <input
                                              type="checkbox"
                                              checked={selectedMfForDelete.has(attr.attrKey)}
                                              onChange={(e) => {
                                                const next = new Set(selectedMfForDelete);
                                                if (e.target.checked) next.add(attr.attrKey); else next.delete(attr.attrKey);
                                                setSelectedMfForDelete(next);
                                              }}
                                              className="rounded flex-shrink-0"
                                              style={{ accentColor: '#EF4444', width: 14, height: 14 }}
                                              title="Select for bulk delete"
                                            />
                                          )}
                                          {/* Enable/disable toggle */}
                                          <input
                                            type="checkbox"
                                            checked={attr.enabled}
                                            onChange={(e) => {
                                              const updated = { ...platformConfig };
                                              updated.metafieldMappings = { ...updated.metafieldMappings };
                                              updated.metafieldMappings[attr.attrKey] = {
                                                ...updated.metafieldMappings[attr.attrKey],
                                                enabled: e.target.checked,
                                              };
                                              setPlatformConfig(updated);
                                            }}
                                            className="rounded"
                                            style={{ accentColor: activePlatform.color }}
                                          />
                                          <span className="text-sm font-medium" style={{ color: attr.enabled ? COLORS.textPrimary : COLORS.textLight }}>
                                            {attr.attrKey}
                                          </span>
                                          {attr.source === 'custom' && (
                                            <span className="text-[9px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>Custom</span>
                                          )}
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-gray-50" style={{ color: COLORS.textLight }}>
                                            {attr.namespace}.{attr.key}
                                          </span>
                                          {attr.source === 'custom' && (
                                            <button
                                              onClick={async (e) => {
                                                e.stopPropagation();
                                                if (!window.confirm(`Delete "${attr.attrKey}" metafield?\n\nThis will remove the metafield definition from Shopify and delete values from ALL products.`)) return;
                                                try {
                                                  const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
                                                  const resp = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/metafield-definition`), {
                                                    method: 'DELETE',
                                                    headers: { 'Content-Type': 'application/json' },
                                                    body: JSON.stringify({ namespace: attr.namespace, key: attr.key }),
                                                  });
                                                  const result = await resp.json();
                                                  if (!result.success) { alert(`Failed: ${result.error}`); return; }
                                                } catch (err) { console.error(err); }
                                                const updated = { ...platformConfig };
                                                updated.metafieldMappings = { ...updated.metafieldMappings };
                                                delete updated.metafieldMappings[attr.attrKey];
                                                setPlatformConfig(updated);
                                              }}
                                              className="p-1 rounded hover:bg-red-50 transition-all flex-shrink-0"
                                              style={{ color: '#EF4444' }}
                                              title="Delete metafield from Shopify & remove from config"
                                            >
                                              <Trash2 size={12} />
                                            </button>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>

                            {/* ─── Live Shopify Metafield Definitions ─── */}
                            <div className="mt-6 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                              <div className="flex items-center justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Tag size={14} style={{ color: '#0891B2' }} />
                                  <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#0891B2' }}>
                                    Live on Shopify
                                  </h4>
                                </div>
                                <button
                                  id="fetch-shopify-defs-btn"
                                  onClick={async () => {
                                    const btn = document.getElementById('fetch-shopify-defs-btn');
                                    btn.disabled = true;
                                    btn.textContent = '⏳ Fetching…';
                                    try {
                                      const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
                                      const resp = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/metafield-definitions`));
                                      const result = await resp.json();
                                      if (result.success) {
                                        const container = document.getElementById('shopify-live-defs');
                                        if (container) {
                                          container.innerHTML = '';
                                          if (result.definitions.length === 0) {
                                            container.innerHTML = '<p style="text-align:center;color:#9CA3AF;font-size:12px;padding:12px 0">No metafield definitions found on Shopify</p>';
                                          } else {
                                            result.definitions.forEach(def => {
                                              const row = document.createElement('div');
                                              row.className = 'border rounded-lg px-4 py-2.5 flex items-center justify-between transition-all';
                                              row.style.cssText = 'border-color: #E5E7EB; margin-bottom: 4px;';
                                              row.innerHTML = `
                                                <div style="display:flex;align-items:center;gap:10px">
                                                  <input type="checkbox" class="shopify-def-select rounded" data-ns="${def.namespace}" data-key="${def.key}" data-id="${def.id}" style="accent-color:#EF4444;width:14px;height:14px" />
                                                  <div>
                                                    <span style="font-size:13px;font-weight:500;color:#1F2937">${def.name || def.key}</span>
                                                    <span style="font-size:10px;font-family:monospace;color:#9CA3AF;margin-left:8px">${def.namespace}.${def.key}</span>
                                                  </div>
                                                </div>
                                                <span style="font-size:10px;color:#9CA3AF;background:#F3F4F6;padding:2px 8px;border-radius:4px">${def.type}</span>
                                              `;
                                              container.appendChild(row);
                                            });
                                            // Show delete button
                                            const delBtn = document.getElementById('delete-shopify-defs-btn');
                                            if (delBtn) delBtn.style.display = 'inline-flex';
                                          }
                                        }
                                      } else {
                                        window.alert('Failed to fetch: ' + (result.error || 'Unknown'));
                                      }
                                    } catch (err) { console.error(err); window.alert('Failed to fetch metafield definitions'); }
                                    finally { btn.disabled = false; btn.textContent = '↻ Fetch from Shopify'; }
                                  }}
                                  className="px-3 py-1 text-[10px] font-bold uppercase tracking-wider rounded-md border transition-all hover:bg-cyan-50"
                                  style={{ borderColor: '#0891B2', color: '#0891B2' }}
                                >
                                  ↻ Fetch from Shopify
                                </button>
                              </div>
                              <p className="text-[10px] mb-3" style={{ color: COLORS.textLight }}>
                                Fetch all metafield definitions currently live on Shopify. Select and delete ones you no longer need.
                              </p>
                              <div id="shopify-live-defs" className="space-y-1">
                                <p className="text-center text-xs py-3" style={{ color: COLORS.textLight }}>Click "Fetch from Shopify" to load definitions</p>
                              </div>
                              <button
                                id="delete-shopify-defs-btn"
                                style={{ display: 'none', marginTop: 8, borderColor: '#EF4444', color: '#DC2626' }}
                                className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider rounded-md border transition-all hover:bg-red-50 items-center gap-1"
                                onClick={async () => {
                                  const checks = document.querySelectorAll('.shopify-def-select:checked');
                                  if (checks.length === 0) { window.alert('Select metafields to delete first'); return; }
                                  const items = [...checks].map(cb => ({ namespace: cb.dataset.ns, key: cb.dataset.key }));
                                  const names = items.map(i => `${i.namespace}.${i.key}`).join('\n');
                                  if (!window.confirm(`Delete ${items.length} metafield definition(s) from Shopify?\n\nThis removes values from ALL products:\n${names}`)) return;
                                  const btn = document.getElementById('delete-shopify-defs-btn');
                                  btn.disabled = true; btn.textContent = '⏳ Deleting…';
                                  try {
                                    const shopifyPlatformId = platforms?.find(p => p.id === 'shopify')?.id || 'shopify';
                                    const resp = await config.apiFetch(config.buildUrl(`/api/channels/${shopifyPlatformId}/metafield-definitions/bulk-delete`), {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify({ definitions: items }),
                                    });
                                    const result = await resp.json();
                                    window.alert(`Deleted ${result.deleted_count || 0}/${items.length} definitions.${result.failed_count > 0 ? ` ${result.failed_count} failed.` : ''}`);
                                    // Re-fetch to refresh list
                                    document.getElementById('fetch-shopify-defs-btn')?.click();
                                  } catch (err) { console.error(err); window.alert('Delete failed'); }
                                  finally { btn.disabled = false; btn.textContent = '🗑 Delete Selected'; }
                                }}
                              >
                                🗑 Delete Selected
                              </button>
                            </div>
                          </div>
                          ) : (
                          <div className="p-4 rounded-lg border" style={{ backgroundColor: '#FFFBEB', borderColor: '#FDE68A' }}>
                            <div className="flex items-start gap-2">
                              <Info size={14} className="mt-0.5 flex-shrink-0" style={{ color: '#D97706' }} />
                              <div>
                                <p className="text-xs font-semibold" style={{ color: '#92400E' }}>
                                  Metafields are not supported on {activePlatform.name}
                                </p>
                                <p className="text-[10px] mt-0.5" style={{ color: '#B45309' }}>
                                  Metafields (custom product attributes) are a Shopify-only feature. {activePlatform.name} uses its own structured catalog attributes which are handled through the standard fields above.
                                </p>
                              </div>
                            </div>
                          </div>
                          )}
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3" style={{ borderColor: COLORS.border }}>
                          <button
                            onClick={() => setShowPlatformConfigModal(false)}
                            className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                            style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              handleSavePlatformConfig(activePlatformId, platformConfig);
                            }}
                            className="px-6 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2"
                            style={{ backgroundColor: activePlatform.color }}
                          >
                            <Save size={14} />
                            Save Configuration
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {/* Platform Settings / Credentials Modal */}
                {showPlatformSettings && activePlatform && (() => {
                  const credentialFields = {
                    shopify: [
                      { key: 'store_url', label: 'Store URL', placeholder: 'your-store.myshopify.com', type: 'text', sensitive: false },
                      { key: 'access_token', label: 'Admin API Access Token', placeholder: 'shpat_xxxxxxxxxxxxxxxxxx', type: 'password', sensitive: true },
                      { key: 'api_version', label: 'API Version', placeholder: '2024-10', type: 'text', sensitive: false },
                    ],
                    amazon: [
                      { key: 'seller_id', label: 'Seller ID', placeholder: 'AXXXXXXXXXXXXXXX', type: 'text', sensitive: false },
                      { key: 'marketplace_id', label: 'Marketplace ID (India)', placeholder: 'A21TJRUUN4KGV', type: 'text', sensitive: false, defaultValue: 'A21TJRUUN4KGV' },
                      { key: 'client_id', label: 'LWA Client ID', placeholder: 'amzn1.application-oa2-client...', type: 'text', sensitive: true },
                      { key: 'client_secret', label: 'LWA Client Secret', placeholder: 'amzn1.oa2-cs...', type: 'password', sensitive: true },
                      { key: 'refresh_token', label: 'Refresh Token', placeholder: 'Atzr|...', type: 'password', sensitive: true },
                    ],
                    flipkart: [
                      { key: 'app_id', label: 'App ID', placeholder: 'your-app-id', type: 'text', sensitive: false },
                      { key: 'app_secret', label: 'App Secret', placeholder: 'your-app-secret', type: 'password', sensitive: true },
                    ],
                    myntra: [
                      { key: 'api_key', label: 'API Key', placeholder: 'your-myntra-api-key', type: 'text', sensitive: true },
                      { key: 'api_secret', label: 'API Secret', placeholder: 'your-myntra-api-secret', type: 'password', sensitive: true },
                      { key: 'seller_id', label: 'Seller ID', placeholder: 'your-seller-id', type: 'text', sensitive: false },
                    ],
                  };

                  const fields = credentialFields[activePlatformId] || [];

                  return (
                    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[85vh] flex flex-col overflow-hidden">
                        {/* Header */}
                        <div className="p-6 border-b" style={{ borderColor: COLORS.border }}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white" style={{ backgroundColor: activePlatform.color }}>
                                {getPlatformIcon(activePlatform.icon, 18)}
                              </div>
                              <div>
                                <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                                  {activePlatform.name} Settings
                                </h3>
                                <p className="text-xs mt-0.5" style={{ color: COLORS.textSecondary }}>
                                  API Credentials &middot; {activePlatform.apiType || 'Custom API'}
                                </p>
                              </div>
                            </div>
                            <button onClick={() => setShowPlatformSettings(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                              <X size={18} />
                            </button>
                          </div>
                        </div>

                        {/* Credential Fields */}
                        <div className="flex-1 overflow-y-auto p-6 space-y-4" id="credentials-form">
                          {fields.length === 0 ? (
                            <div className="py-6 px-4 text-center">
                              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-amber-50 flex items-center justify-center">
                                <Info size={28} className="text-amber-500" />
                              </div>
                              <h4 className="text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                                No API Available
                              </h4>
                              <p className="text-xs leading-relaxed mb-4" style={{ color: COLORS.textSecondary }}>
                                {activePlatform.name} does not provide a public seller API for product listing.
                                Use the <strong>Export CSV</strong> button to download your designs and upload them
                                via the {activePlatform.name} Seller Portal.
                              </p>
                              {activePlatformId === 'tatacliq' && (
                                <div className="text-left bg-gray-50 rounded-lg p-3 text-[10px] space-y-1" style={{ color: COLORS.textSecondary }}>
                                  <p><strong>How to list on TataCliq:</strong></p>
                                  <p>1. Export your designs as CSV from this page</p>
                                  <p>2. Log in to Seller Zone (sellerzone.tatacliq.com)</p>
                                  <p>3. Upload via Bulk Upload or create listings manually</p>
                                  <p>4. Upload product images via SFTP (FileZilla)</p>
                                </div>
                              )}
                            </div>
                          ) : (
                            fields.map((field) => (
                              <div key={field.key}>
                                <label className="block text-xs font-bold uppercase tracking-wider mb-1.5" style={{ color: COLORS.textSecondary }}>
                                  {field.label}
                                </label>
                                <input
                                  type={field.type}
                                  placeholder={field.placeholder}
                                  defaultValue={platformCredentials[field.key] || ''}
                                  className="w-full px-3 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-opacity-50"
                                  style={{ borderColor: COLORS.border, focusRing: activePlatform.color }}
                                  data-cred-key={field.key}
                                />
                                {field.sensitive && platformCredentials[field.key] && platformCredentials[field.key].includes('••') && (
                                  <p className="text-[10px] mt-1" style={{ color: COLORS.textLight }}>
                                    Currently set (masked). Enter a new value to update.
                                  </p>
                                )}
                              </div>
                            ))
                          )}

                          {/* Connection Status */}
                          {connectionStatus && (
                            <div className={`p-4 rounded-lg border ${connectionStatus.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'}`}>
                              <div className="flex items-center gap-2">
                                {connectionStatus.success ? (
                                  <CheckCircle size={16} className="text-green-600" />
                                ) : (
                                  <X size={16} className="text-red-600" />
                                )}
                                <span className={`text-sm font-medium ${connectionStatus.success ? 'text-green-700' : 'text-red-700'}`}>
                                  {connectionStatus.message}
                                </span>
                              </div>
                              {connectionStatus.details && (
                                <div className="mt-2 space-y-1">
                                  {Object.entries(connectionStatus.details).map(([k, v]) => (
                                    <div key={k} className="flex gap-2 text-xs">
                                      <span className="text-green-600 font-medium">{k}:</span>
                                      <span className="text-green-700">{String(v)}</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}

                          {activePlatform.apiDocsUrl && (
                            <a
                              href={activePlatform.apiDocsUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs flex items-center gap-1 hover:underline"
                              style={{ color: activePlatform.color }}
                            >
                              <ExternalLink size={12} />
                              View {activePlatform.name} API Documentation
                            </a>
                          )}

                          {/* Default Publish Status (Shopify only) */}
                          {activePlatformId === 'shopify' && (
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                              <div className="flex items-center gap-2 mb-2">
                                <Send size={14} style={{ color: COLORS.maroon }} />
                                <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.maroon }}>
                                  Publish Settings
                                </h4>
                              </div>
                              <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3 border" style={{ borderColor: COLORS.border }}>
                                <div>
                                  <p className="text-sm font-medium" style={{ color: COLORS.textPrimary }}>Default Publish Status</p>
                                  <p className="text-[10px] mt-0.5" style={{ color: COLORS.textLight }}>
                                    New products will be created with this status on Shopify
                                  </p>
                                </div>
                                <select
                                  id="default-publish-status"
                                  defaultValue={platformConfig?.defaultPublishStatus || 'ACTIVE'}
                                  className="px-3 py-1.5 border rounded-lg text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-amber-300"
                                  style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                                >
                                  <option value="ACTIVE">Active (visible on store)</option>
                                  <option value="DRAFT">Draft (hidden until activated)</option>
                                </select>
                              </div>
                            </div>
                          )}

                          {/* Auto-Deploy Toggle (Shopify + Admin only) */}
                          {activePlatformId === 'shopify' && authUser?.role === 'admin' && (
                            <div className="mt-4 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                              <div className="flex items-center gap-2 mb-2">
                                <Zap size={14} style={{ color: '#F59E0B' }} />
                                <h4 className="text-xs font-bold uppercase tracking-wider" style={{ color: '#F59E0B' }}>
                                  Auto-Deploy
                                </h4>
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold" style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}>
                                  Admin Only
                                </span>
                              </div>
                              <div className="flex items-center justify-between bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg px-4 py-4 border" style={{ borderColor: '#FDE68A' }}>
                                <div>
                                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>Full Automation Pipeline</p>
                                  <p className="text-[10px] mt-1 max-w-sm" style={{ color: COLORS.textLight }}>
                                    When ON: External pushes → auto-generate attributes → auto-verify → auto-publish to Shopify (first image, pre-configured metafields). Zero human intervention.
                                  </p>
                                </div>
                                <button
                                  id="auto-deploy-toggle"
                                  onClick={() => {
                                    const current = platformConfig?.autoDeployEnabled || false;
                                    const updated = { ...(platformConfig || {}), autoDeployEnabled: !current };
                                    setPlatformConfig(updated);
                                    handleSavePlatformConfig(activePlatformId, updated);
                                  }}
                                  className="relative inline-flex h-7 w-14 items-center rounded-full transition-colors duration-300 focus:outline-none flex-shrink-0"
                                  style={{ backgroundColor: platformConfig?.autoDeployEnabled ? '#10B981' : '#D1D5DB' }}
                                >
                                  <span
                                    className="inline-block h-5 w-5 rounded-full bg-white shadow-lg transform transition-transform duration-300"
                                    style={{ transform: platformConfig?.autoDeployEnabled ? 'translateX(30px)' : 'translateX(4px)' }}
                                  />
                                </button>
                              </div>
                              {platformConfig?.autoDeployEnabled && (
                                <div className="mt-2 px-3 py-2 rounded-lg bg-green-50 border border-green-200">
                                  <p className="text-[10px] font-semibold text-green-700 flex items-center gap-1">
                                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                                    Auto-Deploy is ACTIVE — all incoming designs will be published automatically
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Footer */}
                        <div className="p-6 border-t flex justify-between" style={{ borderColor: COLORS.border }}>
                          {fields.length > 0 ? (
                            <>
                              <button
                                disabled={isTestingConnection}
                                onClick={() => {
                                  // First save, then test
                                  const form = document.getElementById('credentials-form');
                                  const creds = {};
                                  form.querySelectorAll('[data-cred-key]').forEach(input => {
                                    const key = input.getAttribute('data-cred-key');
                                    const val = input.value.trim();
                                    if (val && !val.includes('••••')) {
                                      creds[key] = val;
                                    }
                                  });
                                  if (Object.keys(creds).length > 0) {
                                    handleSaveCredentials(activePlatformId, creds).then(() => {
                                      handleTestConnection(activePlatformId);
                                    });
                                  } else {
                                    handleTestConnection(activePlatformId);
                                  }
                                }}
                                className="px-4 py-2 text-sm font-bold rounded-lg border hover:bg-gray-50 flex items-center gap-2 disabled:opacity-50"
                                style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                              >
                                <Activity size={14} />
                                {isTestingConnection ? 'Testing...' : 'Test Connection'}
                              </button>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => setShowPlatformSettings(false)}
                                  className="px-4 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                                  style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                                >
                                  Cancel
                                </button>
                                <button
                                  disabled={isSavingCredentials}
                                  onClick={() => {
                                    const form = document.getElementById('credentials-form');
                                    const creds = {};
                                    form.querySelectorAll('[data-cred-key]').forEach(input => {
                                      const key = input.getAttribute('data-cred-key');
                                      const val = input.value.trim();
                                      if (val && !val.includes('••••')) {
                                        creds[key] = val;
                                      }
                                    });
                                    if (Object.keys(creds).length === 0) {
                                      alert('Enter at least one credential field.');
                                      return;
                                    }
                                    handleSaveCredentials(activePlatformId, creds);
                                    // Also save publish status if changed
                                    const statusSelect = document.getElementById('default-publish-status');
                                    if (statusSelect) {
                                      const updated = { ...(platformConfig || {}), defaultPublishStatus: statusSelect.value };
                                      handleSavePlatformConfig(activePlatformId, updated);
                                    }
                                  }}
                                  className="px-6 py-2 text-sm font-bold text-white rounded-lg transition-all hover:opacity-90 flex items-center gap-2 disabled:opacity-50"
                                  style={{ backgroundColor: activePlatform.color }}
                                >
                                  <Save size={14} />
                                  {isSavingCredentials ? 'Saving...' : 'Save Settings'}
                                </button>
                              </div>
                            </>
                          ) : (
                            <div className="w-full flex justify-end">
                              <button
                                onClick={() => setShowPlatformSettings(false)}
                                className="px-6 py-2 text-sm font-medium rounded-lg border hover:bg-gray-50"
                                style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                              >
                                Close
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            );
          })()}


          {/* Analytics Page */}
          {currentPage === 'analytics' && (
            <div className="space-y-8">
              <div className="flex justify-end items-center">
                <button
                  onClick={fetchAnalytics}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg border transition-all hover:bg-gray-50"
                  style={{ color: COLORS.textPrimary, borderColor: COLORS.border }}
                >
                  <RefreshCw size={14} />
                  Refresh
                </button>
              </div>

              {analyticsData ? (
                <div className="space-y-6">
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Total Cost</span>
                        <DollarSign size={20} style={{ color: COLORS.gold }} />
                      </div>
                      <div className="text-3xl font-bold" style={{ color: COLORS.textPrimary }}>
                        ${analyticsData.totals.totalCost.toFixed(4)}
                      </div>
                      <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        {analyticsData.totals.apiCalls} API calls
                      </div>
                    </div>

                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Tokens Used</span>
                        <Activity size={20} style={{ color: COLORS.maroon }} />
                      </div>
                      <div className="text-3xl font-bold" style={{ color: COLORS.textPrimary }}>
                        {analyticsData.totals.tokensUsed.toLocaleString()}
                      </div>
                      <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        {analyticsData.averages.tokensPerDesign.toFixed(0)} avg/design
                      </div>
                    </div>

                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Designs Processed</span>
                        <Database size={20} style={{ color: COLORS.verified }} />
                      </div>
                      <div className="text-3xl font-bold" style={{ color: COLORS.textPrimary }}>
                        {analyticsData.totals.designsProcessed}
                      </div>
                      <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        {analyticsData.averages.successRate.toFixed(1)}% success rate
                      </div>
                    </div>

                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Reruns</span>
                        <RefreshCw size={20} style={{ color: COLORS.pending }} />
                      </div>
                      <div className="flex items-baseline gap-2">
                        <div className="text-3xl font-bold" style={{ color: COLORS.verified }}>
                          {analyticsData.totals.successfulReruns || 0}
                        </div>
                        <div className="text-lg font-medium" style={{ color: COLORS.error }}>
                          / {analyticsData.totals.failedReruns || 0}
                        </div>
                      </div>
                      <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        Success / Failed attempts
                      </div>
                    </div>
                  </div>

                  {/* Detailed Stats */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>Token Breakdown</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: COLORS.border }}>
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Input Tokens</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                            {analyticsData.totals.inputTokens.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: COLORS.border }}>
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Output Tokens</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                            {analyticsData.totals.outputTokens.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Avg Cost/Design</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.gold }}>
                            ${analyticsData.averages.costPerDesign.toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                      <h3 className="text-lg font-bold mb-4" style={{ color: COLORS.textPrimary }}>Today's Usage</h3>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: COLORS.border }}>
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>API Calls</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                            {analyticsData.today.calls}
                          </span>
                        </div>
                        <div className="flex justify-between items-center pb-3 border-b" style={{ borderColor: COLORS.border }}>
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Tokens</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                            {analyticsData.today.tokens.toLocaleString()}
                          </span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>Cost</span>
                          <span className="text-sm font-bold" style={{ color: COLORS.gold }}>
                            ${analyticsData.today.cost.toFixed(4)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent History */}
                  <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                        Recent API Calls
                      </h3>
                      <PageSizeSelector 
                        pageSize={analyticsPageSize}
                        setPageSize={setAnalyticsPageSize}
                        totalItems={analyticsData?.recentHistory.length || 0}
                      />
                    </div>
                    
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b" style={{ borderColor: COLORS.border }}>
                            <th className="text-left text-xs font-bold uppercase tracking-wider py-3 px-4" style={{ color: COLORS.textSecondary }}>Time</th>
                            <th className="text-left text-xs font-bold uppercase tracking-wider py-3 px-4" style={{ color: COLORS.textSecondary }}>Design</th>
                            <th className="text-right text-xs font-bold uppercase tracking-wider py-3 px-4" style={{ color: COLORS.textSecondary }}>Tokens</th>
                            <th className="text-right text-xs font-bold uppercase tracking-wider py-3 px-4" style={{ color: COLORS.textSecondary }}>Cost</th>
                            <th className="text-center text-xs font-bold uppercase tracking-wider py-3 px-4" style={{ color: COLORS.textSecondary }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paginatedAnalyticsHistory.map((call, index) => (
                            <tr key={index} className="border-b hover:bg-gray-50 transition-colors" style={{ borderColor: COLORS.border }}>
                              <td className="text-sm py-3 px-4" style={{ color: COLORS.textSecondary }}>
                                {new Date(call.timestamp).toLocaleString()}
                              </td>
                              <td className="text-sm font-medium py-3 px-4" style={{ color: COLORS.textPrimary }}>
                                {call.designNumber}
                              </td>
                              <td className="text-sm text-right py-3 px-4" style={{ color: COLORS.textPrimary }}>
                                {call.totalTokens.toLocaleString()}
                              </td>
                              <td className="text-sm text-right py-3 px-4 font-medium" style={{ color: COLORS.gold }}>
                                ${call.cost.toFixed(6)}
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded-full text-xs font-bold uppercase ${
                                  call.status === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
                                }`}>
                                  {call.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    <PaginationControls
                      currentPage={analyticsCurrentPage}
                      totalPages={analyticsTotalPages}
                      totalItems={analyticsData?.recentHistory.length || 0}
                      pageSize={analyticsPageSize}
                      onPageChange={setAnalyticsCurrentPage}
                    />
                  </div>

                  {/* Pricing Info */}
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                    <h3 className="text-sm font-bold mb-2 text-blue-900">GPT-4o Pricing</h3>
                    <div className="text-xs text-blue-700 space-y-1">
                      <p>• Input: $2.50 per 1M tokens</p>
                      <p>• Output: $10.00 per 1M tokens</p>
                      <p className="mt-2 font-medium">Estimated cost based on OpenAI API pricing as of Jan 2026</p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-white border rounded-xl p-16 text-center" style={{ borderColor: COLORS.border }}>
                  <Activity size={48} className="mx-auto mb-4" style={{ color: COLORS.textLight }} />
                  <p className="font-medium mb-2" style={{ color: COLORS.textSecondary }}>Loading analytics...</p>
                  <button
                    onClick={fetchAnalytics}
                    className="mt-4 px-4 py-2 text-sm font-semibold rounded-lg text-white"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    Load Analytics
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Collections Page */}
          {currentPage === 'collections' && (
            <div className="space-y-8">
              {!showCollectionDetail ? (
                <>
                  {/* Centered Header - matches other pages */}
                  <header className="mb-16">
                    {/* Logo - Top Right */}
                    <div className="flex justify-end mb-8">
                      <div className="flex items-center gap-3">
                        <span className="text-xs font-light tracking-wide" style={{ color: COLORS.textSecondary }}>
                          A PRODUCT BY
                        </span>
                        <img 
                          src="/company-logo.png" 
                          alt="Company Logo" 
                          className="h-8 object-contain"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'block';
                          }}
                        />
                        <div style={{ display: 'none', color: COLORS.textSecondary, fontSize: '12px' }}>
                          [Logo not found]
                        </div>
                      </div>
                    </div>

                    {/* Page Title */}
                    <div className="text-center">
                      <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">
                        Collections
                      </h2>
                      <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: COLORS.maroon }} />
                      <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
                        Organize designs into curated collections with smart filters
                      </p>
                    </div>
                  </header>

                  <div className="flex justify-end items-center mb-8">
                    <button
                      onClick={() => {
                        setSelectedCollection(null);
                        setShowCollectionModal(true);
                      }}
                      className="flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg"
                      style={{ backgroundColor: COLORS.maroon }}
                    >
                      <Plus size={18} />
                      Create Collection
                    </button>
                  </div>

                  {/* Collections Grid */}
                  {(collections?.length || 0) === 0 ? (
                    <div className="text-center py-20">
                      <Database size={64} className="mx-auto mb-4 opacity-20" />
                      <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>No Collections Yet</h3>
                      <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
                        Create your first collection to organize designs with smart filters
                      </p>
                      <button
                        onClick={() => {
                          setSelectedCollection(null);
                          setShowCollectionModal(true);
                        }}
                        className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
                        style={{ backgroundColor: COLORS.maroon }}
                      >
                        <Plus size={18} />
                        Create First Collection
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {(collections || []).filter(c => !c.parentId).map((collection) => (
                    <div
                      key={collection.id}
                      className="bg-white border rounded-xl p-6 hover:shadow-lg transition-all flex flex-col min-h-[320px]"
                      style={{ borderColor: COLORS.border }}
                    >
                      {/* Title & Description */}
                      <div className="mb-4">
                        <h3 className="font-serif text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                          {collection.name}
                        </h3>
                        {collection.description ? (
                          <p className="text-sm leading-relaxed italic line-clamp-2" style={{ color: COLORS.textSecondary }}>
                            "{collection.description}"
                          </p>
                        ) : (
                          <p className="text-xs italic opacity-50" style={{ color: COLORS.textSecondary }}>
                            No description
                          </p>
                        )}
                      </div>

                      {/* <TrendingSection collectionId={collection.id} /> */}

                      {/* Design Count */}
                      <div className="flex items-baseline gap-2 mb-3 pb-3 border-b" style={{ borderColor: COLORS.border }}>
                        <div className="text-4xl font-bold" style={{ color: COLORS.maroon }}>
                          {collection.designCount || 0}
                        </div>
                        <div className="text-sm" style={{ color: COLORS.textSecondary }}>
                          design{collection.designCount !== 1 ? 's' : ''}
                          {(collection.filteredCount > 0 || collection.manualCount > 0) && (
                            <div className="text-xs mt-0.5">
                              {collection.filteredCount > 0 && <span>{collection.filteredCount} filtered</span>}
                              {collection.filteredCount > 0 && collection.manualCount > 0 && <span> • </span>}
                              {collection.manualCount > 0 && <span>{collection.manualCount} manual</span>}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Filters Preview - grows to fill space */}
                      <div className="flex-1 mb-4">
                        {collection.filters.length > 0 ? (
                          <>
                            <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                              Filters ({collection.filters.length})
                            </p>
                            <div className="space-y-1.5">
                              {collection.filters.slice(0, 2).map((filter, idx) => (
                                <div key={idx} className="text-xs" style={{ color: COLORS.textPrimary }}>
                                  <span className="font-semibold">{filter.attribute}</span>
                                  <span className="mx-1" style={{ color: COLORS.textSecondary }}>
                                    {filter.operator === 'equals' && '='}
                                    {filter.operator === 'not_equals' && '≠'}
                                    {filter.operator === 'in' && '∈'}
                                    {filter.operator === 'not_in' && '∉'}
                                  </span>
                                  <span>{filter.values.slice(0, 2).join(', ')}</span>
                                  {filter.values.length > 2 && (
                                    <span style={{ color: COLORS.textSecondary }}> +{filter.values.length - 2} more</span>
                                  )}
                                </div>
                              ))}
                              {collection.filters.length > 2 && (
                                <p className="text-xs italic" style={{ color: COLORS.textSecondary }}>
                                  +{collection.filters.length - 2} more filter{collection.filters.length - 2 !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                          </>
                        ) : collection.manualCount > 0 ? (
                          <>
                            <p className="text-xs font-bold uppercase tracking-wider mb-2" style={{ color: COLORS.textSecondary }}>
                              Manual Selection Only
                            </p>
                            <p className="text-xs italic" style={{ color: COLORS.textSecondary }}>
                              {collection.manualCount} design{collection.manualCount !== 1 ? 's' : ''} added manually
                            </p>
                          </>
                        ) : null}
                      </div>

                      <CollectionMetricsCard collectionId={collection.id} />

                      {/* Action Buttons - always at bottom */}
                      <div className="flex gap-2 mt-auto">
                        <button
                          onClick={() => fetchCollectionDetail(collection.id)}
                          className="flex-1 px-4 py-2 text-white text-xs font-semibold rounded-lg transition-all hover:opacity-90"
                          style={{ backgroundColor: COLORS.maroon }}
                        >
                          Open Collection
                        </button>
                        <button
                          onClick={() => {
                            setSelectedCollection(collection);
                            setShowCollectionModal(true);
                          }}
                          className="px-4 py-2 border text-xs font-semibold rounded-lg transition-all hover:bg-gray-50"
                          style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
                        >
                          <Edit2 size={14} />
                        </button>
                        <button
                          onClick={async () => {
                            if (window.confirm(`Delete "${collection.name}"?`)) {
                              try {
                                await config.apiFetch(config.endpoints.collections + '/' + collection.id, { method: 'DELETE' });  
                                fetchCollections();
                              } catch (error) {
                                console.error('Error deleting collection:', error);
                              }
                            }
                          }}
                          className="px-4 py-2 border text-xs font-semibold rounded-lg transition-all hover:bg-red-50"
                          style={{ borderColor: COLORS.border, color: COLORS.error }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </>
              ) : (
                /* Collection Detail View */
                <CollectionDetailView 
                  collection={collectionDetailData?.collection}
                  designs={collectionDetailData?.designs}
                  filteredDesigns={collectionDetailData?.filteredDesigns}
                  manualDesigns={collectionDetailData?.manualDesigns}
                  onBack={() => {
                    const currentCollection = collectionDetailData?.collection;
                    if (currentCollection?.parentId) {
                      // Sub-collection: go back to parent
                      fetchCollectionDetail(currentCollection.parentId);
                    } else {
                      // Root collection: go back to main page
                      setShowCollectionDetail(false);
                      setCollectionDetailData(null);
                      fetchCollections();
                    }
                  }}
                  onEdit={() => {
                    setSelectedCollection(collectionDetailData?.collection);
                    setShowCollectionModal(true);
                  }}
                  onDelete={async () => {
                    const currentCollection = collectionDetailData?.collection;
                    if (window.confirm(`Delete "${currentCollection?.name}"?`)) {
                      try {
                        await config.apiFetch(config.endpoints.collections + '/' + currentCollection?.id, { method: 'DELETE' });
                        
                        if (currentCollection?.parentId) {
                          // Sub-collection: go back to parent
                          fetchCollectionDetail(currentCollection.parentId);
                        } else {
                          // Root collection: go back to main page
                          setShowCollectionDetail(false);
                          setCollectionDetailData(null);
                        }
                        
                        fetchCollections();
                        alert('✅ Collection deleted successfully');
                      } catch (error) {
                        console.error('Error deleting collection:', error);
                        alert('❌ Failed to delete collection');
                      }
                    }
                  }}
                  onCreateSubCollection={() => {
                    setShowSubCollectionModal(true);
                  }}
                  onViewSubCollection={(subCollectionId) => {
                    fetchCollectionDetail(subCollectionId);
                  }}
                  collections={collections}
                  onRefresh={() => fetchCollectionDetail(collectionDetailData?.collection?.id)}
                />
              )}
            </div>
          )}

          {/* Configuration Page */}
          {currentPage === 'config' && (
            <ConfigurationPage
              attributesConfig={attributesConfig}
              setAttributesConfig={setAttributesConfig}
              isLoading={isConfigLoading}
              onRefresh={fetchAttributesConfig}
              authUser={authUser}
            />
          )}

          {/* Activity Feed Page */}
          {currentPage === 'activity' && (
            <ActivityFeedPage />
          )}

          {/* Trend Predictor Page */}
          {currentPage === 'trend_predictor' && (
            <TrendPredictorPage />
          )}

          {/* Settings Page */}
          {currentPage === 'settings' && (
            <SettingsPage authUser={authUser} />
          )}

          {/* Catalog Enrichment List View */}
          {currentPage === 'enrichment' && (
            <div className="max-w-7xl mx-auto">
              {/* Header */}
              <header className="mb-12">
                <div className="flex justify-end mb-8">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-light tracking-wide" style={{ color: COLORS.textSecondary }}>
                      A PRODUCT BY
                    </span>
                    <img
                      src="/company-logo.png"
                      alt="Company Logo"
                      className="h-8 object-contain"
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  </div>
                </div>
                <div className="text-center">
                  <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">
                    Catalog Enrichment
                  </h2>
                  <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: COLORS.maroon }} />
                  <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
                    Complete product attributes for AI processing
                  </p>
                </div>
              </header>

              {/* Source Tabs */}
              <div className="flex items-center justify-center gap-1 mb-8 bg-gray-100 rounded-xl p-1 max-w-md mx-auto">
                {[
                  { key: 'all', label: 'All' },
                  { key: 'erp', label: 'ERP / DITOS' },
                  { key: 'uploads', label: 'Uploads' },
                ].map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => setEnrichmentSourceTab(tab.key)}
                    className="flex-1 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
                    style={{
                      backgroundColor: enrichmentSourceTab === tab.key ? 'white' : 'transparent',
                      color: enrichmentSourceTab === tab.key ? COLORS.maroon : COLORS.textSecondary,
                      boxShadow: enrichmentSourceTab === tab.key ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                      fontWeight: enrichmentSourceTab === tab.key ? 600 : 400,
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Controls Bar */}
              <div className="bg-white rounded-xl border p-4 mb-6 flex items-center justify-start" style={{ borderColor: COLORS.border }}>
                <div className="flex flex-wrap items-center gap-3">
                  <div className="relative">
                    <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textSecondary }} />
                    <input
                      type="text"
                      placeholder="Search by design or combo number..."
                      value={enrichmentSearchInput}
                      onChange={(e) => setEnrichmentSearchInput(e.target.value)}
                      className="pl-10 pr-4 py-2 border rounded-lg w-80 focus:outline-none focus:ring-2 transition-all"
                      style={{ borderColor: COLORS.border, '--tw-ring-color': COLORS.maroon }}
                    />
                  </div>
                  {/* Brand / Category / Product Type filters — options from attributes config */}
                  {[
                    { label: 'All Brands',        val: enrichmentFilterBrand,       set: setEnrichmentFilterBrand,       opts: enrichmentFacets.brands },
                    { label: 'All Categories',    val: enrichmentFilterCategory,    set: setEnrichmentFilterCategory,    opts: enrichmentFacets.categories },
                    { label: 'All Product Types', val: enrichmentFilterProductType, set: setEnrichmentFilterProductType, opts: enrichmentFacets.productTypes },
                    { label: 'All POs',           val: enrichmentFilterPoNumber,    set: setEnrichmentFilterPoNumber,    opts: enrichmentFacets.poNumbers, fmt: (v) => `PO ${v}` },
                  ].map(f => (
                    <select
                      key={f.label}
                      value={f.val}
                      onChange={(e) => f.set(e.target.value)}
                      className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2 transition-all cursor-pointer"
                      style={{
                        borderColor: f.val ? COLORS.maroon : COLORS.border,
                        color: f.val ? COLORS.maroon : COLORS.textSecondary,
                        fontWeight: f.val ? 600 : 400,
                        '--tw-ring-color': COLORS.maroon,
                      }}
                    >
                      <option value="">{f.label}</option>
                      {f.opts.map(v => <option key={v} value={v}>{f.fmt ? f.fmt(v) : v}</option>)}
                    </select>
                  ))}
                  {(enrichmentFilterBrand || enrichmentFilterCategory || enrichmentFilterProductType || enrichmentFilterPoNumber) && (
                    <button
                      onClick={() => { setEnrichmentFilterBrand(''); setEnrichmentFilterCategory(''); setEnrichmentFilterProductType(''); setEnrichmentFilterPoNumber(''); }}
                      className="text-sm underline transition-opacity hover:opacity-70"
                      style={{ color: COLORS.maroon }}
                    >
                      Clear
                    </button>
                  )}
                  <button
                    onClick={() => { fetchEnrichmentCounts(); fetchEnrichmentDesigns(); }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors hover:bg-gray-50"
                    style={{ borderColor: COLORS.border }}
                  >
                    <RefreshCw size={16} style={{ color: COLORS.textSecondary }} />
                    <span style={{ color: COLORS.textSecondary }}>Refresh</span>
                  </button>
                  <button
                    onClick={() => setShowUploadModal(true)}
                    className="flex items-center gap-2 px-4 py-2.5 text-xs font-semibold tracking-wider uppercase text-white rounded-lg transition-colors hover:opacity-90"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    <Upload size={14} />
                    Upload CSV / Excel
                  </button>
                </div>
              </div>

              {/* Card Grid — one card per design (design-grouped) */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {enrichmentDesigns.map(design => (
                  <div
                    key={design.designNumber}
                    className="bg-white rounded-xl border overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer group"
                    style={{ borderColor: COLORS.border }}
                    onClick={() => setSelectedEnrichmentDesignNumber(design.designNumber)}
                  >
                    {/* Image */}
                    <div className="relative aspect-square overflow-hidden bg-gray-100">
                      <img
                        src={design.imageUrl}
                        alt={design.itemName || design.designNumber}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        onError={(e) => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="14">No Image</text></svg>'; }}
                      />
                      {/* Variant count badge */}
                      {design.variantCount > 1 && (
                        <div className="absolute top-3 right-3 px-2 py-1 rounded-full text-xs font-semibold bg-black/60 text-white">
                          {design.variantCount} variants
                        </div>
                      )}
                      {/* Hover overlay */}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white px-4 py-2 rounded-lg text-sm font-medium shadow-lg flex items-center gap-2" style={{ color: COLORS.maroon }}>
                          <Edit2 size={16} /> View Details
                        </span>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="p-4">
                      <p className="text-xs font-medium mb-0.5" style={{ color: COLORS.textSecondary }}>
                        Design {design.designNumber}
                      </p>
                      <h3 className="font-semibold text-base truncate mb-1" style={{ color: COLORS.textPrimary }}>
                        {design.itemName || 'Untitled'}
                      </h3>
                      <p className="text-xs truncate mb-1" style={{ color: COLORS.textSecondary }}>
                        {[design.productType, design.category].filter(Boolean).join(' · ') || 'Unknown'}
                      </p>
                      <p className="text-xs mb-2" style={{ color: COLORS.textSecondary }}>
                        {[design.brand, design.mrp ? `₹${design.mrp}` : null].filter(Boolean).join(' · ')}
                      </p>
                      {design.processingError && (
                        <div
                          className="mb-2 px-2 py-1.5 rounded-md text-[11px] leading-tight"
                          style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B' }}
                          title={design.processingError}
                        >
                          <span className="font-semibold">⚠ Last run failed: </span>
                          <span className="line-clamp-2">{design.processingError}</span>
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t flex items-center justify-between" style={{ borderColor: COLORS.border }}>
                        <span className="text-xs" style={{ color: COLORS.textSecondary }}>
                          {design.variantCount || 1} {design.variantCount === 1 ? 'variant' : 'variants'}
                        </span>
                        <ChevronRight size={16} style={{ color: COLORS.textSecondary }} className="group-hover:translate-x-1 transition-transform" />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Empty State — pure inbox: nothing here = nothing to enrich */}
              {!enrichmentLoading && enrichmentDesigns.length === 0 && (
                <div className="text-center py-20 px-6">
                  <CheckCircle size={64} className="mx-auto mb-5" style={{ color: COLORS.verified }} />
                  <h3 className="text-2xl font-semibold mb-2" style={{ color: COLORS.textPrimary }}>
                    {enrichmentSearch ? 'No matching items need enrichment' : 'All catalog data is enriched'}
                  </h3>
                  <p className="max-w-md mx-auto leading-relaxed" style={{ color: COLORS.textSecondary }}>
                    {enrichmentSearch
                      ? 'Nothing matched your search in the enrichment queue. Items already enriched live on the Verification page.'
                      : 'Nothing to do here right now. Upload new items above, or head to Verification to review what is queued for sign-off.'}
                  </p>
                  {!enrichmentSearch && (
                    <button
                      onClick={() => setCurrentPage('pending')}
                      className="mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold tracking-wide text-white transition-colors hover:opacity-90"
                      style={{ backgroundColor: COLORS.maroon }}
                    >
                      Go to Verification
                      <ChevronRight size={16} />
                    </button>
                  )}
                </div>
              )}

              {/* Loading State */}
              {enrichmentLoading && enrichmentDesigns.length === 0 && (
                <div className="text-center py-16">
                  <RefreshCw size={48} className="mx-auto mb-4 animate-spin" style={{ color: COLORS.maroon }} />
                  <p style={{ color: COLORS.textSecondary }}>Loading designs…</p>
                </div>
              )}

              {/* Pagination Controls */}
              {enrichmentTotal > 0 && (() => {
                const totalPages = Math.max(1, Math.ceil(enrichmentTotal / enrichmentPerPage));
                const curPage = Math.min(enrichmentPage, totalPages);
                const startIdx = (curPage - 1) * enrichmentPerPage + 1;
                const endIdx = Math.min(curPage * enrichmentPerPage, enrichmentTotal);
                const canPrev = curPage > 1;
                const canNext = curPage < totalPages;
                return (
                  <div className="bg-white rounded-xl border p-4 mt-8 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderColor: COLORS.border }}>
                    {/* Range info */}
                    <div className="text-sm" style={{ color: COLORS.textSecondary }}>
                      Showing <span className="font-semibold" style={{ color: COLORS.textPrimary }}>{startIdx.toLocaleString()}</span>
                      {' '}–{' '}
                      <span className="font-semibold" style={{ color: COLORS.textPrimary }}>{endIdx.toLocaleString()}</span>
                      {' '}of{' '}
                      <span className="font-semibold" style={{ color: COLORS.textPrimary }}>{enrichmentTotal.toLocaleString()}</span>
                      {' '}items
                    </div>

                    {/* Page navigation */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => canPrev && setEnrichmentPage(1)}
                        disabled={!canPrev}
                        className="px-3 py-2 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                        style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                        title="First page"
                      >
                        «
                      </button>
                      <button
                        onClick={() => canPrev && setEnrichmentPage(curPage - 1)}
                        disabled={!canPrev}
                        className="px-3 py-2 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                        style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                      >
                        <ChevronLeft size={16} /> Prev
                      </button>
                      <span className="px-4 py-2 text-sm font-medium" style={{ color: COLORS.textPrimary }}>
                        Page {curPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => canNext && setEnrichmentPage(curPage + 1)}
                        disabled={!canNext}
                        className="px-3 py-2 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 flex items-center gap-1"
                        style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                      >
                        Next <ChevronRight size={16} />
                      </button>
                      <button
                        onClick={() => canNext && setEnrichmentPage(totalPages)}
                        disabled={!canNext}
                        className="px-3 py-2 rounded-lg border text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
                        style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                        title="Last page"
                      >
                        »
                      </button>
                    </div>

                    {/* Per-page selector */}
                    <div className="flex items-center gap-2">
                      <label className="text-sm" style={{ color: COLORS.textSecondary }}>Per page:</label>
                      <select
                        value={enrichmentPerPage}
                        onChange={(e) => {
                          setEnrichmentPerPage(parseInt(e.target.value, 10));
                          setEnrichmentPage(1);
                        }}
                        className="px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
                        style={{ borderColor: COLORS.border, '--tw-ring-color': COLORS.maroon }}
                      >
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                        <option value={200}>200</option>
                        <option value={500}>500</option>
                      </select>
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>

        {/* Footer */}
        <footer className="max-w-6xl mx-auto mt-16 mb-8 pt-8 border-t border-gray-100 flex justify-between items-center text-[10px] tracking-widest uppercase" style={{ color: COLORS.textSecondary }}>
          <span>&copy; 2024 Kushals Fashion Jewellery</span>
          <div className="flex gap-8">
            <span className="cursor-pointer hover:text-[#8B1D3C]">Support</span>
            <span className="cursor-pointer hover:text-[#8B1D3C]">Privacy Policy</span>
          </div>
        </footer>
      </main>

      {/* Detail Modal */}
      {selectedDesign && (
        <DesignDetailModal
          design={selectedDesign}
          attributes={attributes}
          attributesConfig={attributesConfig}
          onUpdateAttribute={updateAttribute}
          onVerify={handleVerify}
          onRerun={handleRerun}
          onClose={() => setSelectedDesign(null)}
          isRerunning={isRerunning}
          onEditDetails={(dn) => setVerifyEditDesignNumber(dn)}
        />
      )}

      {/* Design Enrichment Modal (Phase G) */}
      {selectedEnrichmentDesignNumber && (
        <DesignEnrichmentModal
          designNumber={selectedEnrichmentDesignNumber}
          designs={enrichmentDesigns}
          coreAttributes={coreAttributes}
          onClose={() => setSelectedEnrichmentDesignNumber(null)}
          onNavigate={(dn) => { fetchEnrichmentCounts(); fetchEnrichmentDesigns(); setSelectedEnrichmentDesignNumber(dn); }}
          onSaved={() => { fetchEnrichmentCounts(); fetchEnrichmentDesigns(); }}
        />
      )}

      {/* Enrichment editor opened from the Verification Queue (Edit details).
          Reuses DesignEnrichmentModal (product/core/combination tabs); on save it
          refreshes the pending list so edited fields (item_name, image, etc.) show. */}
      {verifyEditDesignNumber && (
        <DesignEnrichmentModal
          designNumber={verifyEditDesignNumber}
          designs={[]}
          coreAttributes={coreAttributes}
          onClose={() => setVerifyEditDesignNumber(null)}
          onNavigate={() => {}}
          onSaved={() => { fetchPending(); }}
        />
      )}

      {/* Catalog Upload Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: COLORS.border }}>
              <div>
                <h3 className="text-xl font-bold" style={{ color: COLORS.textPrimary }}>Bulk Operations</h3>
                <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                  Import new designs or update existing ones via CSV / Excel.
                </p>
              </div>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setUploadResult(null); setUploadFile(null);
                  setUpdateResult(null); setUpdateFile(null);
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              ><X size={20} /></button>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* ---------------- Card 1: Import new designs ---------------- */}
              <div className="border rounded-xl p-5 space-y-4" style={{ borderColor: COLORS.border }}>
                <div>
                  <h4 className="text-base font-bold" style={{ color: COLORS.textPrimary }}>Import new designs</h4>
                  <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                    Adds new designs and combinations. Existing rows with the same combination_number are overwritten.
                  </p>
                </div>
                <div className="text-xs space-y-1" style={{ color: COLORS.textSecondary }}>
                  <p><strong>Required:</strong> design_number, combination_number</p>
                  <p><strong>Optional:</strong> image_url, item_name, product_type, base_material, + core attributes</p>
                </div>
                <button
                  onClick={() => {
                    const baseOrder = ['design_number', 'combination_number', 'item_name', 'product_type',
                      'base_material', 'image_url'];
                    const toSnake = (s) => s
                      .replace(/([A-Z])/g, '_$1')
                      .replace(/\s+/g, '_')
                      .replace(/^_+/, '')
                      .toLowerCase();
                    const coreFields = Object.entries(coreAttributes).map(([key]) => toSnake(key));
                    const headers = Array.from(new Set([...baseOrder, ...coreFields]));
                    const exampleRow = headers.map(h => {
                      if (h === 'design_number') return '147422';
                      if (h === 'combination_number') return '600045';
                      if (h === 'item_name') return 'Silver Temple Necklace';
                      if (h === 'product_type') return 'Necklace';
                      if (h === 'base_material') return 'SILVER';
                      if (h === 'image_url') return 'https://cdn.example.com/image.jpg';
                      return '';
                    });
                    const csv = headers.join(',') + '\n' + exampleRow.join(',') + '\n';
                    const blob = new Blob([csv], { type: 'text/csv' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'catalog_import_template.csv'; a.click();
                  }}
                  className="flex items-center gap-2 px-3 py-2 border rounded-lg text-xs font-medium transition-colors hover:bg-gray-50"
                  style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                >
                  <Download size={14} /> Download import template
                </button>
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:bg-gray-50"
                  style={{ borderColor: uploadFile ? COLORS.verified : COLORS.border }}
                  onClick={() => document.getElementById('catalog-upload-input').click()}
                >
                  <input
                    id="catalog-upload-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => { setUploadFile(e.target.files[0]); setUploadResult(null); }}
                  />
                  {uploadFile ? (
                    <div>
                      <CheckCircle size={28} className="mx-auto mb-2" style={{ color: COLORS.verified }} />
                      <p className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>{uploadFile.name}</p>
                      <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        {(uploadFile.size / 1024).toFixed(1)} KB — Click to change
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={28} className="mx-auto mb-2" style={{ color: COLORS.textSecondary }} />
                      <p className="text-sm" style={{ color: COLORS.textSecondary }}>Click to select a file</p>
                    </div>
                  )}
                </div>
                {uploadResult && (
                  <div className={`p-3 rounded-lg text-xs ${uploadResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'}`}>
                    {uploadResult.success ? (
                      <p><CheckCircle size={14} className="inline mr-1" />{uploadResult.message}</p>
                    ) : (
                      <p><AlertTriangle size={14} className="inline mr-1" />{uploadResult.error}</p>
                    )}
                    {uploadResult.warnings && (
                      <p className="mt-1">Skipped {uploadResult.warnings.skipped} rows with errors</p>
                    )}
                  </div>
                )}
                <button
                  onClick={async () => {
                    if (!uploadFile) return;
                    setUploadResult(null);
                    setUploading(true);
                    const formData = new FormData();
                    formData.append('file', uploadFile);
                    try {
                      const token = localStorage.getItem('authToken');
                      const resp = await fetch(config.endpoints.catalogUpload, {
                        method: 'POST',
                        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                        body: formData,
                      });
                      const data = await resp.json();
                      setUploadResult(data);
                      if (data.success) {
                        notify.success(data.message || 'Catalog imported');
                        fetchEnrichmentCounts();
                        fetchEnrichmentDesigns();
                      } else {
                        notify.error(data.error || 'Upload failed');
                      }
                    } catch (err) {
                      setUploadResult({ success: false, error: err.message });
                      notify.error('Upload failed: ' + err.message);
                    } finally {
                      setUploading(false);
                    }
                  }}
                  disabled={!uploadFile || uploading}
                  className="w-full py-3 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: COLORS.maroon }}
                >
                  {uploading ? 'Uploading…' : 'Upload & Import'}
                </button>
              </div>

              {/* ---------------- Card 2: Bulk update existing ---------------- */}
              <div className="border rounded-xl p-5 space-y-4" style={{ borderColor: COLORS.border, backgroundColor: '#FFFBF5' }}>
                <div>
                  <h4 className="text-base font-bold" style={{ color: COLORS.textPrimary }}>Update existing designs</h4>
                  <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                    Only updates rows already in PIMS. Empty cells are ignored. Unknown combination numbers are reported back, never auto-created.
                  </p>
                </div>
                <div className="text-xs space-y-1" style={{ color: COLORS.textSecondary }}>
                  <p><strong>Required:</strong> combination_number</p>
                  <p><strong>Updatable:</strong> item_name, product_type, base_material, image_url, color, polish, size, weight, mrp, sku, + custom attributes</p>
                </div>
                <a
                  href={config.endpoints.catalogUpdateTemplate}
                  className="inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-xs font-medium transition-colors hover:bg-white"
                  style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                >
                  <Download size={14} /> Download update template
                </a>
                <div
                  className="border-2 border-dashed rounded-xl p-6 text-center cursor-pointer transition-colors hover:bg-white"
                  style={{ borderColor: updateFile ? COLORS.verified : COLORS.border }}
                  onClick={() => document.getElementById('catalog-update-input').click()}
                >
                  <input
                    id="catalog-update-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => { setUpdateFile(e.target.files[0]); setUpdateResult(null); }}
                  />
                  {updateFile ? (
                    <div>
                      <CheckCircle size={28} className="mx-auto mb-2" style={{ color: COLORS.verified }} />
                      <p className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>{updateFile.name}</p>
                      <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                        {(updateFile.size / 1024).toFixed(1)} KB — Click to change
                      </p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={28} className="mx-auto mb-2" style={{ color: COLORS.textSecondary }} />
                      <p className="text-sm" style={{ color: COLORS.textSecondary }}>Click to select a file</p>
                    </div>
                  )}
                </div>

                {updateResult && (
                  <div className={`p-3 rounded-lg text-xs ${updateResult.success ? 'bg-green-50 text-green-800' : (updateResult.conflicts && updateResult.conflicts.length > 0 ? 'bg-amber-50 text-amber-900' : 'bg-red-50 text-red-800')}`}>
                    <p>
                      {updateResult.success
                        ? <CheckCircle size={14} className="inline mr-1" />
                        : <AlertTriangle size={14} className="inline mr-1" />}
                      {updateResult.message || updateResult.error || 'Update finished'}
                    </p>
                    {updateResult.aiTriggered && updateResult.aiTriggered.length > 0 && (
                      <p className="mt-1">AI re-enrichment queued for {updateResult.aiTriggered.length} design(s).</p>
                    )}
                    {updateResult.conflicts && updateResult.conflicts.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer font-semibold">
                          {updateResult.conflicts.length} row(s) with disagreeing variants — needs confirmation
                        </summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {updateResult.conflicts.slice(0, 10).map((c, i) => (
                            <li key={i}>
                              <code>{c.combination_number}</code> (row {c.row}):{' '}
                              {Object.entries(c.disagreements || {}).map(([f, vals]) =>
                                `${f} = [${(vals || []).join(' | ')}]`
                              ).join('; ')}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                    {updateResult.errors && updateResult.errors.length > 0 && (
                      <details className="mt-2">
                        <summary className="cursor-pointer font-semibold">
                          {updateResult.errors.length} skipped row(s)
                        </summary>
                        <ul className="mt-2 space-y-1 list-disc list-inside">
                          {updateResult.errors.slice(0, 10).map((e, i) => (
                            <li key={i}>
                              row {e.row}{e.combination_number ? ` (${e.combination_number})` : ''}: {e.reason}
                            </li>
                          ))}
                        </ul>
                      </details>
                    )}
                  </div>
                )}

                <button
                  onClick={async () => {
                    if (!updateFile) return;
                    const submit = async (force) => {
                      setUpdating(true);
                      const formData = new FormData();
                      formData.append('file', updateFile);
                      try {
                        const token = localStorage.getItem('authToken');
                        const url = config.endpoints.catalogUpdate + (force ? '?force=true' : '');
                        const resp = await fetch(url, {
                          method: 'POST',
                          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                          body: formData,
                        });
                        const data = await resp.json();
                        setUpdateResult(data);
                        if (data.updated > 0) {
                          notify.success(`${data.updated} row(s) updated`);
                          fetchEnrichmentCounts();
                          fetchEnrichmentDesigns();
                        } else if (data.conflicts && data.conflicts.length > 0) {
                          notify.error('Conflicts detected — review and force-overwrite if intended');
                        } else {
                          notify.error(data.error || 'No rows updated');
                        }
                      } catch (err) {
                        setUpdateResult({ success: false, error: err.message });
                        notify.error('Update failed: ' + err.message);
                      } finally {
                        setUpdating(false);
                      }
                    };
                    setUpdateResult(null);
                    submit(false);
                  }}
                  disabled={!updateFile || updating}
                  className="w-full py-3 rounded-lg text-xs font-bold uppercase tracking-wider text-white transition-all disabled:opacity-50"
                  style={{ backgroundColor: COLORS.maroon }}
                >
                  {updating ? 'Updating…' : 'Update existing rows'}
                </button>

                {updateResult && updateResult.conflicts && updateResult.conflicts.length > 0 && (
                  <button
                    onClick={async () => {
                      if (!updateFile) return;
                      setUpdating(true);
                      const formData = new FormData();
                      formData.append('file', updateFile);
                      try {
                        const token = localStorage.getItem('authToken');
                        const resp = await fetch(config.endpoints.catalogUpdate + '?force=true', {
                          method: 'POST',
                          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
                          body: formData,
                        });
                        const data = await resp.json();
                        setUpdateResult(data);
                        if (data.updated > 0) {
                          notify.success(`${data.updated} row(s) force-updated`);
                          fetchEnrichmentCounts();
                          fetchEnrichmentDesigns();
                        } else {
                          notify.error(data.error || 'Force update produced no changes');
                        }
                      } catch (err) {
                        setUpdateResult({ success: false, error: err.message });
                        notify.error('Force update failed: ' + err.message);
                      } finally {
                        setUpdating(false);
                      }
                    }}
                    disabled={updating}
                    className="w-full py-2 rounded-lg text-xs font-semibold border transition-colors hover:bg-amber-100 disabled:opacity-50"
                    style={{ borderColor: '#B45309', color: '#92400E', backgroundColor: '#FEF3C7' }}
                  >
                    Force-overwrite all variants
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Collection Modal */}
      <CollectionModal
        show={showCollectionModal}
        onClose={() => {
          setShowCollectionModal(false);
          setSelectedCollection(null);
        }}
        collection={selectedCollection}
        attributeValues={attributeValuesForFilters}
        onSave={handleSaveCollection}
        availableDesigns={pendingItems}
      />

      {/* Sub-Collection Modal */}
      <SubCollectionModal
        show={showSubCollectionModal}
        onClose={() => setShowSubCollectionModal(false)}
        parentCollection={collectionDetailData?.collection}
        parentDesigns={collectionDetailData?.designs}
        configurableAttributeNames={Object.keys(attributeValuesForFilters)}
        onSave={async (subCollectionData) => {
          try {
            const response = await config.apiFetch(config.endpoints.collections, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(subCollectionData)
            });
            const data = await response.json();
            if (data.success) {
              setShowSubCollectionModal(false);
              fetchCollections();
              if (collectionDetailData?.collection?.id) {
                fetchCollectionDetail(collectionDetailData.collection.id);
              }
              alert('✅ Sub-collection created successfully!');
            } else {
              alert('❌ Failed to create sub-collection: ' + data.error);
            }
          } catch (error) {
            console.error('Error creating sub-collection:', error);
            alert('❌ Failed to create sub-collection');
          }
        }}
      />

      {/* Collection Designs Modal */}
      <CollectionDesignsModal
        show={showCollectionDesigns}
        onClose={() => {
          setShowCollectionDesigns(false);
          setCollectionDesigns([]);
        }}
        designs={collectionDesigns}
        collectionName={collections.find(c => collectionDesigns.length > 0)?.name || 'Collection'}
      />

      {/* Shopify Product Detail Modal */}
      {shopifySelectedProduct && (() => {
        const a = shopifyProductsAnalytics[shopifySelectedProduct.id] || {};
        const trendMax = Math.max(...(shopifyTrends.map(t => t.revenue || 0)), 1);
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', backdropFilter: 'blur(6px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
            <div style={{ background: 'linear-gradient(135deg, #fff 0%, #FDF8F0 100%)', borderRadius: 20, width: '100%', maxWidth: 860, maxHeight: '90vh', overflow: 'auto', boxShadow: '0 32px 80px rgba(139,29,60,0.18)', padding: 36 }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 }}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  {shopifySelectedProduct.image && <img src={shopifySelectedProduct.image} alt="" style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 12, boxShadow: '0 4px 16px rgba(0,0,0,0.12)', border: '2px solid rgba(212,175,55,0.3)' }} />}
                  <div>
                    <div style={{ fontSize: 20, fontWeight: 700, color: COLORS.maroon }}>{shopifySelectedProduct.title}</div>
                    <div style={{ fontSize: 13, color: COLORS.textSecondary, marginTop: 4 }}>{shopifySelectedProduct.vendor} · {shopifySelectedProduct.variantCount} variants</div>
                    {shopifySelectedProduct.designNumber && <div style={{ fontSize: 12, color: COLORS.gold, fontWeight: 600, marginTop: 4 }}>Design #{shopifySelectedProduct.designNumber}</div>}
                    <div style={{ display: 'flex', gap: 8, marginTop: 8, alignItems: 'center' }}>
                      {/* Status toggle buttons */}
                      <div style={{ display: 'flex', borderRadius: 99, overflow: 'hidden', border: '1px solid #E5E7EB' }}>
                        <button
                          onClick={() => updateShopifyProductStatus(shopifySelectedProduct.id, 'ACTIVE')}
                          disabled={shopifySelectedProduct.status === 'ACTIVE' || statusUpdatingProductId === shopifySelectedProduct.id}
                          style={{
                            padding: '3px 12px', border: 'none', fontSize: 11, fontWeight: 600, cursor: shopifySelectedProduct.status === 'ACTIVE' ? 'default' : 'pointer',
                            background: shopifySelectedProduct.status === 'ACTIVE' ? '#D1FAE5' : 'white',
                            color: shopifySelectedProduct.status === 'ACTIVE' ? '#065F46' : '#9CA3AF',
                            opacity: statusUpdatingProductId === shopifySelectedProduct.id ? 0.5 : 1,
                          }}
                        >
                          Active
                        </button>
                        <button
                          onClick={() => updateShopifyProductStatus(shopifySelectedProduct.id, 'DRAFT')}
                          disabled={shopifySelectedProduct.status === 'DRAFT' || statusUpdatingProductId === shopifySelectedProduct.id}
                          style={{
                            padding: '3px 12px', border: 'none', borderLeft: '1px solid #E5E7EB', fontSize: 11, fontWeight: 600, cursor: shopifySelectedProduct.status === 'DRAFT' ? 'default' : 'pointer',
                            background: shopifySelectedProduct.status === 'DRAFT' ? '#FEF3C7' : 'white',
                            color: shopifySelectedProduct.status === 'DRAFT' ? '#92400E' : '#9CA3AF',
                            opacity: statusUpdatingProductId === shopifySelectedProduct.id ? 0.5 : 1,
                          }}
                        >
                          Draft
                        </button>
                      </div>
                      {statusUpdatingProductId === shopifySelectedProduct.id && <span style={{ fontSize: 11, color: COLORS.textLight }}>Updating…</span>}
                      <a href={shopifySelectedProduct.adminUrl} target="_blank" rel="noreferrer" style={{ padding: '2px 10px', borderRadius: 99, fontSize: 11, fontWeight: 600, background: 'rgba(139,29,60,0.08)', color: COLORS.maroon, textDecoration: 'none' }}>View in Shopify ↗</a>
                    </div>
                  </div>
                </div>
                <button onClick={() => { setShopifySelectedProduct(null); setShopifyProductMetafields([]); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 24, color: COLORS.textLight }}>✕</button>
              </div>
              {/* KPIs */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 28 }}>
                {[
                  { label: 'Revenue', value: formatCurrency(a.revenue, a.currency), icon: '₹', color: COLORS.gold },
                  { label: 'Units Sold', value: a.unitsSold ?? '—', icon: '📦', color: '#7C3AED' },
                  { label: 'Orders', value: a.orderCount ?? '—', icon: '🛒', color: '#0891B2' },
                  { label: 'Avg Order Value', value: formatCurrency(a.avgOrderValue, a.currency), icon: '⚡', color: COLORS.maroon },
                ].map(kpi => (
                  <div key={kpi.label} style={{ background: 'white', borderRadius: 14, padding: '16px 18px', boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid rgba(212,175,55,0.15)' }}>
                    <div style={{ fontSize: 20, marginBottom: 6 }}>{kpi.icon}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                    <div style={{ fontSize: 12, color: COLORS.textSecondary, marginTop: 2 }}>{kpi.label}</div>
                  </div>
                ))}
              </div>
              {/* Trend Chart */}
              <div style={{ background: 'white', borderRadius: 16, padding: '20px 24px', border: '1px solid rgba(212,175,55,0.2)', marginBottom: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>Revenue Trend (Daily)</div>
                  {shopifyTrendsLoading && <span style={{ fontSize: 12, color: COLORS.textLight }}>Loading…</span>}
                </div>
                {shopifyTrends.length > 0 ? (
                  <div>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 80 }}>
                      {shopifyTrends.map((t, i) => (
                        <div key={i} title={`${t.date}: ${formatCurrency(t.revenue)}`}
                          style={{ flex: 1, minWidth: 4, height: `${Math.max(4, (t.revenue / trendMax) * 80)}px`,
                            background: t.revenue > 0 ? `linear-gradient(180deg, ${COLORS.gold} 0%, rgba(212,175,55,0.4) 100%)` : 'rgba(212,175,55,0.15)',
                            borderRadius: '3px 3px 0 0' }} />
                      ))}
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, fontSize: 11, color: COLORS.textLight }}>
                      <span>{shopifyTrends[0]?.date}</span><span>{shopifyTrends[shopifyTrends.length - 1]?.date}</span>
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center', color: COLORS.textLight, fontSize: 13, padding: '20px 0' }}>
                    {shopifyTrendsLoading ? 'Fetching trend data…' : 'No sales data for this period'}
                  </div>
                )}
              </div>
              {/* Variants */}
              {shopifySelectedProduct.variants?.length > 0 && (
                <div>
                  <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 12 }}>Variants ({shopifySelectedProduct.variants.length})</div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
                    {shopifySelectedProduct.variants.map(v => (
                      <div key={v.id} style={{ background: 'white', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(212,175,55,0.15)', fontSize: 13 }}>
                        <div style={{ fontWeight: 600 }}>{v.title}</div>
                        <div style={{ color: COLORS.textSecondary, fontSize: 12, marginTop: 2 }}>SKU: {v.sku || '—'} · Stock: {v.inventory ?? '—'}</div>
                        <div style={{ color: COLORS.maroon, fontWeight: 600, marginTop: 4 }}>{formatCurrency(v.price, shopifySelectedProduct.currency)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Live Metafields Section */}
              <div style={{ marginTop: 24 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ fontSize: 14, fontWeight: 600 }}>
                    Metafields {shopifyMfLoading && <span style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 400 }}>Loading…</span>}
                  </div>
                  <button onClick={() => fetchProductMetafields(shopifySelectedProduct.id)}
                    style={{ fontSize: 11, color: COLORS.maroon, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}>
                    ↻ Refresh
                  </button>
                </div>
                {shopifyProductMetafields.length > 0 ? (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 10 }}>
                    {shopifyProductMetafields.map((mf, idx) => (
                      <div key={mf.id || idx} style={{ background: 'white', borderRadius: 10, padding: '10px 14px', border: '1px solid rgba(212,175,55,0.15)', fontSize: 13 }}>
                        <div style={{ fontWeight: 600, fontSize: 11, color: COLORS.textSecondary, marginBottom: 4, fontFamily: 'monospace' }}>
                          {mf.namespace}.{mf.key}
                        </div>
                        <input
                          type="text"
                          defaultValue={mf.value}
                          data-mf-idx={idx}
                          style={{ width: '100%', padding: '4px 8px', border: '1px solid #E5E7EB', borderRadius: 6, fontSize: 12, color: COLORS.textPrimary }}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  !shopifyMfLoading && <div style={{ color: COLORS.textLight, fontSize: 12, textAlign: 'center', padding: '16px 0' }}>No metafields found</div>
                )}
                {shopifyProductMetafields.length > 0 && (
                  <button
                    onClick={() => {
                      const inputs = document.querySelectorAll('[data-mf-idx]');
                      const updates = [];
                      inputs.forEach(inp => {
                        const idx = parseInt(inp.getAttribute('data-mf-idx'));
                        const mf = shopifyProductMetafields[idx];
                        if (mf && inp.value !== mf.value) {
                          updates.push({ namespace: mf.namespace, key: mf.key, value: inp.value, type: mf.type });
                        }
                      });
                      if (updates.length === 0) { alert('No changes to save'); return; }
                      saveProductMetafields(shopifySelectedProduct.id, updates);
                    }}
                    disabled={shopifyMfSaving}
                    style={{ marginTop: 12, padding: '8px 20px', borderRadius: 8, border: 'none', cursor: 'pointer',
                      background: `linear-gradient(135deg, ${COLORS.gold} 0%, #B8860B 100%)`, color: 'white',
                      fontWeight: 600, fontSize: 12, opacity: shopifyMfSaving ? 0.6 : 1 }}>
                    {shopifyMfSaving ? 'Saving…' : 'Save Metafield Changes'}
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

// Platform icon helper
const PLATFORM_ICONS = {
  ShoppingBag: ShoppingBag,
  Tag: Tag,
  Package: Package,
  ShoppingCart: ShoppingCart,
  Store: Store,
};

const getPlatformIcon = (iconName, size = 14) => {
  const Icon = PLATFORM_ICONS[iconName] || Store;
  return <Icon size={size} />;
};

// Sidebar Component
const Sidebar = ({ currentPage, setCurrentPage, counts, enrichmentCounts = { needsEnrichment: 0, complete: 0, total: 0 }, platforms = [], channelsExpanded, setChannelsExpanded, authUser, onLogout }) => {
  const sections = [
    {
      id: 'generate',
      label: 'BATCH HISTORY',
      icon: <Activity className="w-5 h-5" />,
      count: counts.generate
    },
    {
      id: 'enrichment',
      label: 'CATALOG ENRICHMENT',
      icon: <Package className="w-5 h-5" />,
      count: enrichmentCounts.needsEnrichment > 0 ? enrichmentCounts.needsEnrichment : null
    },
    {
      id: 'pending',
      label: 'VERIFICATION',
      icon: <Eye className="w-5 h-5" />,
      count: counts.pending
    },
    {
      id: 'occasion_review',
      label: 'OCCASION REVIEW',
      icon: <Calendar className="w-5 h-5" />,
      count: null
    },
    {
      id: 'category_review',
      label: 'CATEGORY REVIEW',
      icon: <Tag className="w-5 h-5" />,
      count: null
    },
    {
      id: 'verified',
      label: 'VERIFIED',
      icon: <Shield className="w-5 h-5" />,
      count: counts.verified
    },
  ];

  const allBottomSections = [
    {
      id: 'collections',
      label: 'COLLECTIONS',
      icon: <Database className="w-5 h-5" />,
      count: null
    },
    {
      id: 'analytics',
      label: 'ANALYTICS',
      icon: <TrendingUp className="w-5 h-5" />,
      count: null
    },
    {
      id: 'trend_predictor',
      label: 'TREND PREDICTOR',
      icon: <BarChart2 className="w-5 h-5" />,
      count: null
    },
    {
      id: 'activity',
      label: 'ACTIVITY LOG',
      icon: <Activity className="w-5 h-5" />,
      count: null,
      roles: ['admin']
    },
    {
      id: 'config',
      label: 'CONFIGURATION',
      icon: <Settings className="w-5 h-5" />,
      count: null,
      roles: ['admin']
    },
    {
      id: 'settings',
      label: 'SETTINGS',
      icon: <Users className="w-5 h-5" />,
      count: null,
      roles: ['admin']
    }
  ];

  // Role-based filtering
  const bottomSections = allBottomSections.filter(s =>
    !s.roles || (authUser && s.roles.includes(authUser.role))
  );
  const showChannels = !authUser || ['admin', 'publisher'].includes(authUser.role);

  const isChannelActive = currentPage.startsWith('channel_');

  const renderNavButton = (section) => (
    <button
      key={section.id}
      onClick={() => setCurrentPage(section.id)}
      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all text-xs font-bold tracking-wider uppercase ${
        currentPage === section.id
          ? 'text-white shadow-md'
          : 'hover:bg-gray-50'
      }`}
      style={{
        backgroundColor: currentPage === section.id ? COLORS.maroon : 'transparent',
        color: currentPage === section.id ? '#FFFFFF' : COLORS.textSecondary
      }}
    >
      <div className="flex items-center gap-3">
        {section.icon}
        <span className="text-[11px]">{section.label}</span>
      </div>
      {section.count !== null && section.count > 0 && (
        <span className={`px-2.5 py-0.5 text-[10px] rounded-full font-bold ${
          currentPage === section.id ? 'bg-white' : 'bg-gray-100'
        }`}
        style={{ color: currentPage === section.id ? COLORS.maroon : COLORS.textSecondary }}>
          {section.count}
        </span>
      )}
    </button>
  );

  return (
    <aside className="w-64 flex flex-col h-screen bg-white border-r fixed left-0 top-0 overflow-y-auto" style={{ borderColor: COLORS.border }}>
      {/* Kushal's Branding */}
      <div className="p-8 border-b" style={{ borderColor: COLORS.border }}>
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg" style={{ backgroundColor: COLORS.maroon }}>
            K
          </div>
          <div>
            <h1 className="font-serif text-2xl font-bold tracking-tight" style={{ color: COLORS.textPrimary }}>
              Kushals
            </h1>
            <p className="text-[9px] tracking-[0.25em] uppercase font-semibold" style={{ color: COLORS.gold }}>
              Fashion Jewellery
            </p>
          </div>
        </div>
        <p className="text-xs font-medium mt-3" style={{ color: COLORS.textSecondary }}>
          AI Attribute Manager
        </p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {sections.map(renderNavButton)}

        {/* CHANNELS - Expandable Section (admin + publisher only) */}
        {showChannels && <div className="mt-1">
          <button
            onClick={() => setChannelsExpanded(!channelsExpanded)}
            className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all text-xs font-bold tracking-wider uppercase ${
              isChannelActive ? 'text-white shadow-md' : 'hover:bg-gray-50'
            }`}
            style={{
              backgroundColor: isChannelActive ? COLORS.maroon : 'transparent',
              color: isChannelActive ? '#FFFFFF' : COLORS.textSecondary
            }}
          >
            <div className="flex items-center gap-3">
              <Send className="w-5 h-5" />
              <span className="text-[11px]">CHANNELS</span>
            </div>
            <div className="flex items-center gap-2">
              {platforms.reduce((sum, p) => sum + (p.designCount || 0), 0) > 0 && (
                <span className={`px-2 py-0.5 text-[10px] rounded-full font-bold ${
                  isChannelActive ? 'bg-white' : 'bg-gray-100'
                }`}
                style={{ color: isChannelActive ? COLORS.maroon : COLORS.textSecondary }}>
                  {platforms.reduce((sum, p) => sum + (p.designCount || 0), 0)}
                </span>
              )}
              {channelsExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            </div>
          </button>

          {/* Platform Sub-Items */}
          {channelsExpanded && (
            <div className="ml-4 mt-1 space-y-0.5">
              {platforms.map((platform) => {
                const pageId = `channel_${platform.id}`;
                const isActive = currentPage === pageId;
                return (
                  <button
                    key={platform.id}
                    onClick={() => setCurrentPage(pageId)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-all text-[11px] font-semibold ${
                      isActive ? 'bg-gray-100' : 'hover:bg-gray-50'
                    }`}
                    style={{ color: isActive ? COLORS.maroon : COLORS.textSecondary }}
                  >
                    <div className="flex items-center gap-2.5">
                      <div
                        className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                        style={{ backgroundColor: platform.color }}
                      />
                      <span>{platform.name}</span>
                    </div>
                    {(platform.designCount || 0) > 0 && (
                      <span className="px-2 py-0.5 text-[10px] rounded-full font-bold bg-gray-100"
                        style={{ color: COLORS.textSecondary }}>
                        {platform.designCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>}

        {/* Divider */}
        <div className="my-2 border-t" style={{ borderColor: COLORS.border }} />

        {bottomSections.map(renderNavButton)}
      </nav>

      {/* User Info & Logout */}
      {authUser && (
        <div className="p-4 border-t" style={{ borderColor: COLORS.border }}>
          <div className="flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-xs font-semibold truncate" style={{ color: COLORS.textPrimary }}>
                {authUser.fullName}
              </p>
              <p className="text-[10px] uppercase tracking-wider" style={{ color: COLORS.gold }}>
                {authUser.role}
              </p>
            </div>
            <button
              onClick={onLogout}
              className="p-2 rounded-lg hover:bg-red-50 transition-colors flex-shrink-0"
              title="Sign out"
            >
              <ArrowLeft size={16} style={{ color: COLORS.textSecondary }} />
            </button>
          </div>
        </div>
      )}
    </aside>
  );
};

// Design Grid Component
const DesignGrid = ({ items, selectedItems = [], onToggleSelect, onItemClick, selectable, clickable, verified, onRetry, retryingDesign, onMoveToEnrichment, movingDesign }) => {
  if (items.length === 0) {
    return (
      <div className="bg-white border rounded-lg p-16 text-center" style={{ borderColor: COLORS.border }}>
        <Database size={48} className="mx-auto mb-4" style={{ color: COLORS.textLight }} />
        <p className="font-medium" style={{ color: COLORS.textSecondary }}>No designs available</p>
      </div>
    );
  }

  return (
    <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
      {items.map((item) => {
        const isSelected = selectable && selectedItems.includes(item.designNumber);
        const isError = item.status === 'error';

        return (
          <div
            key={item.designNumber}
            className="bg-white border rounded-xl overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col group"
            style={{ 
              borderColor: isSelected ? COLORS.maroon : COLORS.border,
              boxShadow: isSelected ? '0 4px 6px -1px rgba(139, 29, 60, 0.1)' : '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}
          >
            {/* Image Container */}
            <div
              onClick={() => {
                // Failed designs are clickable too — open details to inspect
                // what data exists on the design before the AI run failed.
                // The Retry button lives in a sibling container, so it
                // doesn't trigger this handler.
                if (clickable) onItemClick(item);
                if (selectable) onToggleSelect(item.designNumber);
              }}
              className={`relative aspect-[4/5] overflow-hidden ${
                clickable || selectable ? 'cursor-pointer' : ''
              }`}
            >
              <img
                src={item.imageUrl}
                alt={item.designNumber}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f9fafb" width="400" height="500"/%3E%3Ctext fill="%23d1d5db" font-family="sans-serif" font-size="14" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                }}
              />
              
              {/* Status Badge - Top Left */}
              {!selectable && (
                <div className="absolute top-3 left-3">
                  <span
                    className="px-3 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider text-white shadow-lg flex items-center gap-1.5"
                    style={{
                      backgroundColor: isError ? COLORS.errorDark : (verified ? COLORS.verified : COLORS.pending)
                    }}
                  >
                    {isError ? (
                      <>
                        <X size={12} />
                        Failed
                      </>
                    ) : verified ? (
                      <>
                        <CheckCircle size={12} />
                        Verified
                      </>
                    ) : (
                      <>
                        <Eye size={12} />
                        Pending Review
                      </>
                    )}
                  </span>
                </div>
              )}
              
              {/* Selection Indicator */}
              {selectable && isSelected && (
                <div className="absolute top-3 right-3 rounded-full p-1.5 text-white shadow-lg" style={{ backgroundColor: COLORS.maroon }}>
                  <CheckCircle size={16} />
                </div>
              )}

              {/* Hover Overlay */}
              {clickable && (
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center pb-6">
                  <span className="text-white text-sm font-bold flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-lg">
                    <Eye size={16} />
                    View Details
                  </span>
                </div>
              )}
            </div>

            {/* Card Content */}
            <div className="p-4 flex flex-col gap-2">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: COLORS.textLight }}>
                  Design {item.designNumber}
                </p>
                <h3 className="font-semibold text-base leading-tight" style={{ color: COLORS.textPrimary }}>
                  {item.productType || 'Necklace'}
                </h3>
              </div>
              
              <div className="flex items-center gap-2 text-[11px] font-medium" style={{ color: COLORS.textSecondary }}>
                <span className="px-2 py-0.5 bg-gray-50 rounded uppercase text-[10px] font-bold" style={{ color: COLORS.textSecondary }}>
                  {item.baseMaterial}
                </span>
                {item.attributes?.Theme && (
                  <span className="px-2 py-0.5 rounded uppercase text-[10px] font-bold"
                    style={{ backgroundColor: COLORS.bgLight, color: COLORS.gold }}>
                    {item.attributes.Theme.split(',').map(v => v.trim()).join(', ')}
                  </span>
                )}
              </div>

              {isError && (
                <div className="mt-1 rounded-lg p-3" style={{ backgroundColor: COLORS.errorBg }}>
                  <p className="text-[11px] font-semibold mb-2 leading-snug" style={{ color: COLORS.errorText }}>
                    {item.error || 'AI processing failed.'}
                  </p>
                  <button
                    onClick={(e) => { e.stopPropagation(); if (onRetry) onRetry(item.designNumber); }}
                    disabled={retryingDesign === item.designNumber}
                    className="w-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-white rounded-lg transition-opacity hover:opacity-90 disabled:opacity-50 flex items-center justify-center gap-1.5"
                    style={{ backgroundColor: COLORS.errorDark }}
                  >
                    <RefreshCw size={12} />
                    {retryingDesign === item.designNumber ? 'Retrying…' : 'Retry'}
                  </button>
                </div>
              )}

              {/* Verification-only: discard AI attributes and send back to Catalog
                  Enrichment. Shown for pending-review and failed cards alike. */}
              {onMoveToEnrichment && !selectable && !verified && (
                <button
                  onClick={(e) => { e.stopPropagation(); onMoveToEnrichment(item.designNumber); }}
                  disabled={movingDesign === item.designNumber}
                  title="Discard AI attributes and return this design to Catalog Enrichment"
                  className="mt-1 w-full px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-lg border bg-white transition-all hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 flex items-center justify-center gap-1.5"
                  style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                >
                  <ArrowLeft size={12} />
                  {movingDesign === item.designNumber ? 'Moving…' : 'Move to Enrichment'}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </section>
  );
};

// Collection Detail View Component - Shows collection with designs and sub-collections
const CollectionDetailView = ({ collection, designs, filteredDesigns, manualDesigns, onBack, onEdit, onDelete, onCreateSubCollection, onViewSubCollection, collections, onRefresh }) => {
  if (!collection) return null;

  // Get sub-collections
  const subCollections = collections.filter(c => c.parentId === collection.id);
  
  // Get parent collection if this is a sub-collection
  const parentCollection = collection.parentId 
    ? collections.find(c => c.id === collection.parentId) 
    : null;

  return (
    <div className="space-y-6">
      {/* Breadcrumb Navigation */}
      {parentCollection && (
        <div className="flex items-center gap-2 text-sm" style={{ color: COLORS.textSecondary }}>
          <button
            onClick={onBack}
            className="hover:underline"
            style={{ color: COLORS.maroon }}
          >
            {parentCollection.name}
          </button>
          <ChevronRight size={16} />
          <span className="font-semibold" style={{ color: COLORS.textPrimary }}>
            {collection.name}
          </span>
        </div>
      )}

      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-all"
          style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
        >
          <ArrowLeft size={18} />
          {parentCollection ? `Back to ${parentCollection.name}` : 'Back to Collections'}
        </button>
      </div>

      {/* Collection Header Card */}
      <div className="bg-gradient-to-br from-white to-gray-50 border-2 rounded-2xl p-8 shadow-lg" style={{ borderColor: COLORS.maroon }}>
        {/* Sub-collection badge */}
        {parentCollection && (
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-4 text-xs font-semibold" style={{ backgroundColor: '#E0F2FE', color: '#0369A1' }}>
            <Database size={14} />
            Sub-Collection
          </div>
        )}
        
        <div className="flex items-start justify-between mb-6">
          <div className="flex-1">
            <h1 className="font-serif text-4xl font-bold mb-3" style={{ color: COLORS.textPrimary }}>
              {collection.name}
            </h1>
            {collection.description && (
              <p className="text-lg mb-4" style={{ color: COLORS.textSecondary }}>
                {collection.description}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 ml-4">
            <button
              onClick={onEdit}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-gray-50 transition-all"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
              title="Edit Collection"
            >
              <Edit2 size={16} />
              <span className="text-sm font-semibold">Edit</span>
            </button>
            <button
              onClick={onDelete}
              className="flex items-center gap-2 px-4 py-2 border rounded-lg hover:bg-red-50 transition-all"
              style={{ borderColor: COLORS.border, color: COLORS.error }}
              title="Delete Collection"
            >
              <Trash2 size={16} />
              <span className="text-sm font-semibold">Delete</span>
            </button>
          </div>
        </div>
            
        {/* Stats Row */}
        <div className="flex items-center gap-8 mt-6">
          <div>
            <div className="text-5xl font-bold mb-1" style={{ color: COLORS.maroon }}>
              {designs?.length || 0}
            </div>
            <div className="text-sm font-semibold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
              Total Designs
            </div>
          </div>
          
          {filteredDesigns && filteredDesigns.length > 0 && (
            <div className="border-l-2 pl-8" style={{ borderColor: COLORS.border }}>
              <div className="text-3xl font-bold mb-1" style={{ color: COLORS.gold }}>
                {filteredDesigns.length}
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                From Filters
              </div>
            </div>
          )}
          
          {manualDesigns && manualDesigns.length > 0 && (
            <div className="border-l-2 pl-8" style={{ borderColor: COLORS.border }}>
              <div className="text-3xl font-bold mb-1" style={{ color: COLORS.pending }}>
                {manualDesigns.length}
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                Manual Picks
              </div>
            </div>
          )}
          
          {(subCollections?.length || 0) > 0 && (
            <div className="border-l-2 pl-8" style={{ borderColor: COLORS.border }}>
              <div className="text-3xl font-bold mb-1" style={{ color: COLORS.verified }}>
                {(subCollections?.length || 0)}
              </div>
              <div className="text-xs font-semibold uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                Sub-Collections
              </div>
            </div>
          )}
        </div>

        {/* Filters Display */}
        {collection.filters && collection.filters.length > 0 && (
          <div className="mt-6 pt-6 border-t" style={{ borderColor: COLORS.border }}>
            <h3 className="text-sm font-bold uppercase tracking-wider mb-3" style={{ color: COLORS.textSecondary }}>
              Active Filters
            </h3>
            <div className="flex flex-wrap gap-2">
              {collection.filters.map((filter, idx) => (
                <div
                  key={idx}
                  className="px-4 py-2 bg-white border rounded-lg text-sm"
                  style={{ borderColor: COLORS.maroon }}
                >
                  <span className="font-semibold" style={{ color: COLORS.maroon }}>{filter.attribute}</span>
                  <span className="mx-2 text-gray-400">
                    {filter.operator === 'equals' && '='}
                    {filter.operator === 'not_equals' && '≠'}
                    {filter.operator === 'in' && '∈'}
                    {filter.operator === 'not_in' && '∉'}
                  </span>
                  <span style={{ color: COLORS.textPrimary }}>{filter.values.join(', ')}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Sub-Collections Section */}
      {(subCollections?.length || 0) > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold" style={{ color: COLORS.textPrimary }}>
              Sub-Collections ({(subCollections?.length || 0)})
            </h2>
            <button
              onClick={onCreateSubCollection}
              className="flex items-center gap-2 px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: COLORS.maroon }}
            >
              <Plus size={16} />
              Create Sub-Collection
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {subCollections.map(subCol => (
              <div
                key={subCol.id}
                onClick={() => onViewSubCollection(subCol.id)}
                className="bg-white border-2 rounded-xl p-4 hover:shadow-lg transition-all cursor-pointer group"
                style={{ borderColor: COLORS.border }}
              >
                <h3 className="font-serif text-lg font-bold mb-2 group-hover:text-opacity-80 transition-all" style={{ color: COLORS.maroon }}>
                  {subCol.name}
                </h3>
                <div className="text-3xl font-bold mb-1" style={{ color: COLORS.textPrimary }}>
                  {subCol.designCount || 0}
                </div>
                <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                  design{subCol.designCount !== 1 ? 's' : ''}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Create Sub-Collection CTA (if no sub-collections) */}
      {(subCollections?.length || 0) === 0 && (
        <div className="text-center py-12 border-2 border-dashed rounded-xl" style={{ borderColor: COLORS.border }}>
          <Database size={48} className="mx-auto mb-4 opacity-30" style={{ color: COLORS.maroon }} />
          <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            No Sub-Collections Yet
          </h3>
          <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
            Create sub-collections to further organize these {designs?.length || 0} designs
          </p>
          <button
            onClick={onCreateSubCollection}
            className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg"
            style={{ backgroundColor: COLORS.maroon }}
          >
            <Plus size={18} />
            Create First Sub-Collection
          </button>
        </div>
      )}

      <TrendingSection collectionId={collection.id} />

      {/* Designs Grid */}
      <div>
        <h2 className="text-2xl font-bold mb-4" style={{ color: COLORS.textPrimary }}>
          All Designs ({designs?.length || 0})
        </h2>
        
        {!designs || designs.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-xl">
            <p className="text-lg" style={{ color: COLORS.textSecondary }}>
              No designs in this collection
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {designs.map(design => (
              <div
                key={design.designNumber}
                className="bg-white border rounded-lg overflow-hidden hover:shadow-xl transition-all group cursor-pointer"
                style={{ borderColor: COLORS.border }}
              >
                <div className="aspect-[4/5] bg-gray-100 relative overflow-hidden">
                  <img
                    src={design.imageUrl}
                    alt={design.designNumber}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="250"%3E%3Crect fill="%23f5f5f5" width="200" height="250"/%3E%3Ctext fill="%23999" font-family="serif" font-size="14" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                    }}
                  />

                  {/* ADD THIS */}
                  <TrendingBadge 
                    score={design.trendScore}
                    attributes={design.trendingAttributes}
                    isHiddenGem={design.isHiddenGem}
                  />
                </div>
                <div className="p-2">
                  <p className="font-semibold text-xs truncate" style={{ color: COLORS.textPrimary }}>
                    {design.designNumber}
                  </p>
                  <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                    {design.productType}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Collection Modal Component - Create/Edit ROOT Collections Only
const CollectionModal = ({ show, onClose, collection, attributeValues, onSave, availableDesigns }) => {
  const [name, setName] = useState(collection?.name || '');
  const [description, setDescription] = useState(collection?.description || '');
  const [filters, setFilters] = useState(collection?.filters || []);
  const [expandedFilterIndex, setExpandedFilterIndex] = useState(null);
  const [filterSearchTerms, setFilterSearchTerms] = useState({});
  const [manualDesigns, setManualDesigns] = useState(collection?.manualDesigns || []);
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [designSearchTerm, setDesignSearchTerm] = useState('');

  useEffect(() => {
    if (collection) {
      setName(collection.name);
      setDescription(collection.description || '');
      setFilters(collection.filters || []);
      setManualDesigns(collection.manualDesigns || []);
    } else {
      setName('');
      setDescription('');
      setFilters([]);
      setManualDesigns([]);
    }
    setExpandedFilterIndex(null);
    setFilterSearchTerms({});
    setShowDesignPicker(false);
    setDesignSearchTerm('');
  }, [collection, show]);

  const addFilter = () => {
    setFilters([...filters, {
      attribute: Object.keys(attributeValues)[0] || '',
      operator: 'in',
      values: []
    }]);
  };

  const updateFilter = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index][field] = value;
    setFilters(newFilters);
  };

  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
    setExpandedFilterIndex(null);
  };

  const toggleValue = (filterIndex, value) => {
    const newFilters = [...filters];
    const filter = newFilters[filterIndex];
    if (filter.values.includes(value)) {
      filter.values = filter.values.filter(v => v !== value);
    } else {
      filter.values = [...filter.values, value];
    }
    setFilters(newFilters);
  };

  const getFilteredValues = (filterIndex) => {
    const filter = filters[filterIndex];
    const searchTerm = filterSearchTerms[filterIndex] || '';
    const allValues = attributeValues[filter.attribute] || [];
    
    if (!searchTerm) return allValues;
    
    return allValues.filter(val => 
      val.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const toggleManualDesign = (designNumber) => {
    if (manualDesigns.includes(designNumber)) {
      setManualDesigns(manualDesigns.filter(d => d !== designNumber));
    } else {
      setManualDesigns([...manualDesigns, designNumber]);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a collection name');
      return;
    }
    onSave({ name, description, filters, parentId: null, manualDesigns });
  };

  if (!show) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl">
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: COLORS.border, backgroundColor: COLORS.maroon }}>
            <h2 className="font-serif text-xl text-white">
              {collection ? 'Edit Collection' : 'Create New Collection'}
            </h2>
            <button onClick={onClose} className="text-white hover:opacity-70">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
            {/* Collection Name */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Collection Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., Temple Collection, Bridal Necklaces"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:border-2"
                style={{ borderColor: COLORS.border }}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description of this collection"
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none resize-none"
                style={{ borderColor: COLORS.border }}
              />
            </div>

            {/* Filters */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                  Filters (Optional)
                </label>
                <button
                  onClick={addFilter}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg transition-all hover:opacity-90"
                  style={{ backgroundColor: COLORS.maroon, color: 'white' }}
                >
                  <Plus size={14} />
                  Add Filter
                </button>
              </div>

              {filters.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-lg" style={{ borderColor: COLORS.border }}>
                  <p className="text-sm mb-2" style={{ color: COLORS.textSecondary }}>
                    No filters added
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filters.map((filter, index) => (
                    <div key={index} className="relative">
                      {/* Compact Filter Bar */}
                      <div className="flex items-center gap-2 p-2 border rounded-lg" style={{ borderColor: COLORS.border, backgroundColor: 'white' }}>
                        {/* Attribute */}
                        <select
                          value={filter.attribute}
                          onChange={(e) => {
                            updateFilter(index, 'attribute', e.target.value);
                            updateFilter(index, 'values', []);
                          }}
                          className="px-3 py-2 border rounded-lg text-sm font-semibold"
                          style={{ borderColor: COLORS.border, color: COLORS.maroon }}
                        >
                          {Object.keys(attributeValues).map((attr) => (
                            <option key={attr} value={attr}>{attr}</option>
                          ))}
                        </select>

                        <span className="text-gray-300">|</span>

                        {/* Operator */}
                        <select
                          value={filter.operator}
                          onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                          className="px-3 py-2 border rounded-lg text-sm font-medium"
                          style={{ borderColor: COLORS.border, minWidth: '120px' }}
                        >
                          <option value="equals">Equals (=)</option>
                          <option value="not_equals">Not Equals (≠)</option>
                          <option value="in">In (∈)</option>
                          <option value="not_in">Not In (∉)</option>
                        </select>

                        <span className="text-gray-300">|</span>

                        {/* Selected Values Display / Search Input */}
                        <div className="flex-1 min-w-0">
                          {expandedFilterIndex === index ? (
                            <input
                              type="text"
                              value={filterSearchTerms[index] || ''}
                              onChange={(e) => setFilterSearchTerms({...filterSearchTerms, [index]: e.target.value})}
                              placeholder="Search values..."
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-2"
                              style={{ borderColor: COLORS.border }}
                              autoFocus
                              onBlur={(e) => {
                                // Don't close if clicking inside dropdown
                                setTimeout(() => {
                                  if (!e.relatedTarget?.closest(`[data-filter-dropdown="${index}"]`)) {
                                    setExpandedFilterIndex(null);
                                  }
                                }, 100);
                              }}
                            />
                          ) : (
                            <div 
                              onClick={() => setExpandedFilterIndex(index)}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 rounded-lg transition-all"
                            >
                              {filter.values.length > 0 ? (
                                <span className="font-medium text-gray-700">
                                  {filter.values.join(', ')}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">Click to select values</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Toggle Chevron */}
                        <button
                          onClick={() => setExpandedFilterIndex(expandedFilterIndex === index ? null : index)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                          type="button"
                        >
                          {expandedFilterIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => removeFilter(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Dropdown with Values */}
                      {expandedFilterIndex === index && (
                        <div 
                          data-filter-dropdown={index}
                          className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-[250px] overflow-y-auto"
                          style={{ borderColor: COLORS.border }}
                        >
                          <div className="p-2 grid grid-cols-2 gap-1">
                            {getFilteredValues(index).length > 0 ? (
                              getFilteredValues(index).map((value) => (
                                <label
                                  key={value}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={filter.values.includes(value)}
                                    onChange={() => toggleValue(index, value)}
                                    className="rounded"
                                    style={{ accentColor: COLORS.maroon }}
                                  />
                                  <span className="flex-1 truncate">{value}</span>
                                </label>
                              ))
                            ) : (
                              <p className="col-span-2 text-sm text-center py-4" style={{ color: COLORS.textSecondary }}>
                                No matching values
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                  Manual Selection ({manualDesigns.length} selected)
                </label>
                <button
                  onClick={() => setShowDesignPicker(!showDesignPicker)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg border"
                  style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                  type="button"
                >
                  {showDesignPicker ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showDesignPicker ? 'Hide' : 'Pick Designs'}
                </button>
              </div>

              {manualDesigns.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg" style={{ borderColor: COLORS.border, backgroundColor: COLORS.bgLight }}>
                    {manualDesigns.map(designNum => {
                      const design = availableDesigns?.find(d => d.designNumber === designNum);
                      return (
                        <div
                          key={designNum}
                          className="inline-flex items-center gap-2 px-3 py-1 bg-white border rounded-lg text-xs"
                          style={{ borderColor: COLORS.maroon }}
                        >
                          <span className="font-medium">{designNum}</span>
                          <button
                            onClick={() => toggleManualDesign(designNum)}
                            className="text-red-600 hover:text-red-800"
                            type="button"
                          >
                            <X size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {showDesignPicker && (
                <div className="border rounded-lg p-4" style={{ borderColor: COLORS.border }}>
                  <input
                    type="text"
                    value={designSearchTerm}
                    onChange={(e) => setDesignSearchTerm(e.target.value)}
                    placeholder="Search by design number or product type..."
                    className="w-full px-4 py-2 border rounded-lg mb-4 text-sm focus:outline-none focus:border-2"
                    style={{ borderColor: COLORS.border }}
                  />
                  
                  <div className="grid grid-cols-3 gap-3 max-h-[300px] overflow-y-auto">
                    {(availableDesigns || [])
                      .filter(design => {
                        if (!designSearchTerm) return true;
                        return design.designNumber.toLowerCase().includes(designSearchTerm.toLowerCase()) ||
                               (design.attributes?.['Product Type'] || '').toLowerCase().includes(designSearchTerm.toLowerCase());
                      })
                      .map(design => (
                        <div
                          key={design.designNumber}
                          onClick={() => toggleManualDesign(design.designNumber)}
                          className={`relative border-2 rounded-lg overflow-hidden cursor-pointer transition-all ${
                            manualDesigns.includes(design.designNumber) ? 'ring-2' : 'hover:shadow-lg'
                          }`}
                          style={{
                            borderColor: manualDesigns.includes(design.designNumber) ? COLORS.maroon : COLORS.border,
                            ringColor: COLORS.maroon
                          }}
                        >
                          <div className="aspect-[4/5] bg-gray-100">
                            <img
                              src={design.imageUrl}
                              alt={design.designNumber}
                              className="w-full h-full object-cover"
                              onError={(e) => {
                                e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="250"%3E%3Crect fill="%23f5f5f5" width="200" height="250"/%3E%3Ctext fill="%23999" font-family="serif" font-size="14" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                              }}
                            />
                          </div>
                          <div className="p-2 bg-white">
                            <p className="text-xs font-semibold truncate" style={{ color: COLORS.textPrimary }}>
                              {design.designNumber}
                            </p>
                            <p className="text-xs truncate" style={{ color: COLORS.textSecondary }}>
                              {design.attributes?.['Product Type'] || 'Unknown'}
                            </p>
                          </div>
                          {manualDesigns.includes(design.designNumber) && (
                            <div className="absolute top-2 right-2 bg-white rounded-full p-1 shadow-lg" style={{ color: COLORS.maroon }}>
                              <CheckCircle size={16} />
                            </div>
                          )}
                        </div>
                      ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex gap-3 justify-end" style={{ borderColor: COLORS.border }}>
            <button
              onClick={onClose}
              className="px-6 py-2 border text-sm font-semibold rounded-lg transition-all hover:bg-gray-50"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {collection ? 'Update Collection' : 'Create Collection'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-Collection Modal - Create sub-collections scoped to parent
const SubCollectionModal = ({ show, onClose, parentCollection, parentDesigns, configurableAttributeNames, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [filters, setFilters] = useState([]);
  const [manualDesigns, setManualDesigns] = useState([]);
  const [showDesignPicker, setShowDesignPicker] = useState(false);
  const [designSearchTerm, setDesignSearchTerm] = useState('');
  const [expandedFilterIndex, setExpandedFilterIndex] = useState(null);
  const [filterSearchTerms, setFilterSearchTerms] = useState({});

  // Extract unique attribute values from parent collection's designs
  const [attributeValues, setAttributeValues] = useState({});

  useEffect(() => {
    if (!show || !parentDesigns || !configurableAttributeNames) return;

    // Extract all unique values for each attribute from parent's designs
    // BUT only for configurable attributes
    const values = {};
    parentDesigns.forEach(design => {
      const attrs = design.attributes || {};
      Object.keys(attrs).forEach(attrName => {
        // FILTER: Only include configurable attributes
        if (!configurableAttributeNames.includes(attrName)) return;
        
        if (!values[attrName]) values[attrName] = new Set();
        const attrValue = attrs[attrName];
        if (attrValue) {
          // Handle multi-select (comma-separated)
          if (typeof attrValue === 'string' && attrValue.includes(',')) {
            attrValue.split(',').forEach(v => values[attrName].add(v.trim()));
          } else {
            values[attrName].add(attrValue);
          }
        }
      });
    });

    // Convert sets to arrays
    const valuesObj = {};
    Object.keys(values).forEach(key => {
      valuesObj[key] = Array.from(values[key]).sort();
    });
    setAttributeValues(valuesObj);

    // Reset form
    setName('');
    setDescription('');
    setFilters([]);
    setManualDesigns([]);
    setShowDesignPicker(false);
    setDesignSearchTerm('');
    setExpandedFilterIndex(null);
    setFilterSearchTerms({});
  }, [show, parentDesigns, configurableAttributeNames]);

  const addFilter = () => {
    const firstAttr = Object.keys(attributeValues)[0];
    if (!firstAttr) return;
    setFilters([...filters, {
      attribute: firstAttr,
      operator: 'in',
      values: []
    }]);
  };

  const updateFilter = (index, field, value) => {
    const newFilters = [...filters];
    newFilters[index][field] = value;
    // Reset values if attribute changes
    if (field === 'attribute') {
      newFilters[index].values = [];
    }
    setFilters(newFilters);
  };

  const removeFilter = (index) => {
    setFilters(filters.filter((_, i) => i !== index));
    setExpandedFilterIndex(null);
  };

  const toggleValue = (filterIndex, value) => {
    const newFilters = [...filters];
    const filter = newFilters[filterIndex];
    if (filter.values.includes(value)) {
      filter.values = filter.values.filter(v => v !== value);
    } else {
      filter.values = [...filter.values, value];
    }
    setFilters(newFilters);
  };

  const getFilteredValues = (filterIndex) => {
    const filter = filters[filterIndex];
    const searchTerm = filterSearchTerms[filterIndex] || '';
    const allValues = attributeValues[filter.attribute] || [];
    
    if (!searchTerm) return allValues;
    
    return allValues.filter(val => 
      val.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const toggleManualDesign = (designNumber) => {
    if (manualDesigns.includes(designNumber)) {
      setManualDesigns(manualDesigns.filter(d => d !== designNumber));
    } else {
      setManualDesigns([...manualDesigns, designNumber]);
    }
  };

  const handleSave = () => {
    if (!name.trim()) {
      alert('Please enter a sub-collection name');
      return;
    }
    onSave({
      name,
      description,
      filters,
      parentId: parentCollection.id,
      manualDesigns
    });
  };

  if (!show) return null;

  const filteredDesignsForPicker = (parentDesigns || []).filter(design => {
    if (!designSearchTerm) return true;
    return design.designNumber.toLowerCase().includes(designSearchTerm.toLowerCase()) ||
           (design.productType || '').toLowerCase().includes(designSearchTerm.toLowerCase());
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0" style={{ borderColor: COLORS.border, backgroundColor: COLORS.maroon }}>
            <div>
              <h2 className="font-serif text-xl text-white">
                Create Sub-Collection
              </h2>
              <p className="text-sm text-white opacity-75">
                Under: {parentCollection?.name}
              </p>
            </div>
            <button onClick={onClose} className="text-white hover:opacity-70">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-6 overflow-y-auto flex-1">
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm" style={{ color: COLORS.textPrimary }}>
                <strong>Scoped to Parent:</strong> You can only select from {parentDesigns?.length || 0} designs in "{parentCollection?.name}". 
                Filters will show only values present in these designs.
              </p>
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Sub-Collection Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., South Indian Temple, Premium Pieces"
                className="w-full px-4 py-2 border rounded-lg focus:outline-none"
                style={{ borderColor: COLORS.border }}
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Description (Optional)
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Brief description"
                rows={2}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none resize-none"
                style={{ borderColor: COLORS.border }}
              />
            </div>

            {/* Filters */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                  Filters (Optional)
                </label>
                <button
                  onClick={addFilter}
                  disabled={Object.keys(attributeValues).length === 0}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
                  style={{ backgroundColor: COLORS.maroon, color: 'white' }}
                >
                  <Plus size={14} />
                  Add Filter
                </button>
              </div>

              {filters.length === 0 ? (
                <div className="text-center py-6 border-2 border-dashed rounded-lg" style={{ borderColor: COLORS.border }}>
                  <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                    {Object.keys(attributeValues).length === 0 
                      ? 'No filterable attributes in parent collection'
                      : 'No filters added'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filters.map((filter, index) => (
                    <div key={index} className="relative">
                      {/* Compact Filter Bar */}
                      <div className="flex items-center gap-2 p-2 border rounded-lg" style={{ borderColor: COLORS.border, backgroundColor: 'white' }}>
                        {/* Attribute */}
                        <select
                          value={filter.attribute}
                          onChange={(e) => {
                            updateFilter(index, 'attribute', e.target.value);
                            updateFilter(index, 'values', []);
                          }}
                          className="px-3 py-2 border rounded-lg text-sm font-semibold"
                          style={{ borderColor: COLORS.border, color: COLORS.maroon }}
                        >
                          {Object.keys(attributeValues).map(attr => (
                            <option key={attr} value={attr}>{attr}</option>
                          ))}
                        </select>

                        <span className="text-gray-300">|</span>

                        {/* Operator */}
                        <select
                          value={filter.operator}
                          onChange={(e) => updateFilter(index, 'operator', e.target.value)}
                          className="px-3 py-2 border rounded-lg text-sm font-medium"
                          style={{ borderColor: COLORS.border, minWidth: '120px' }}
                        >
                          <option value="equals">Equals (=)</option>
                          <option value="not_equals">Not Equals (≠)</option>
                          <option value="in">In (∈)</option>
                          <option value="not_in">Not In (∉)</option>
                        </select>

                        <span className="text-gray-300">|</span>

                        {/* Selected Values Display / Search Input */}
                        <div className="flex-1 min-w-0">
                          {expandedFilterIndex === index ? (
                            <input
                              type="text"
                              value={filterSearchTerms[index] || ''}
                              onChange={(e) => setFilterSearchTerms({...filterSearchTerms, [index]: e.target.value})}
                              placeholder="Search values..."
                              className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-2"
                              style={{ borderColor: COLORS.border }}
                              autoFocus
                              onBlur={(e) => {
                                setTimeout(() => {
                                  if (!e.relatedTarget?.closest(`[data-filter-dropdown="${index}"]`)) {
                                    setExpandedFilterIndex(null);
                                  }
                                }, 100);
                              }}
                            />
                          ) : (
                            <div 
                              onClick={() => setExpandedFilterIndex(index)}
                              className="px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 rounded-lg transition-all"
                            >
                              {filter.values.length > 0 ? (
                                <span className="font-medium text-gray-700">
                                  {filter.values.join(', ')}
                                </span>
                              ) : (
                                <span className="text-gray-400 italic">Click to select values</span>
                              )}
                            </div>
                          )}
                        </div>

                        {/* Toggle Chevron */}
                        <button
                          onClick={() => setExpandedFilterIndex(expandedFilterIndex === index ? null : index)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-all"
                          type="button"
                        >
                          {expandedFilterIndex === index ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>

                        {/* Delete */}
                        <button
                          onClick={() => removeFilter(index)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all"
                          type="button"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>

                      {/* Dropdown with Values */}
                      {expandedFilterIndex === index && (
                        <div 
                          data-filter-dropdown={index}
                          className="absolute z-10 left-0 right-0 mt-1 bg-white border rounded-lg shadow-lg max-h-[250px] overflow-y-auto"
                          style={{ borderColor: COLORS.border }}
                        >
                          <div className="p-2 grid grid-cols-2 gap-1">
                            {getFilteredValues(index).length > 0 ? (
                              getFilteredValues(index).map((value) => (
                                <label
                                  key={value}
                                  className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 rounded cursor-pointer text-sm"
                                >
                                  <input
                                    type="checkbox"
                                    checked={filter.values.includes(value)}
                                    onChange={() => toggleValue(index, value)}
                                    className="rounded"
                                    style={{ accentColor: COLORS.maroon }}
                                  />
                                  <span className="flex-1 truncate">{value}</span>
                                </label>
                              ))
                            ) : (
                              <p className="col-span-2 text-sm text-center py-4" style={{ color: COLORS.textSecondary }}>
                                No matching values
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Manual Selection */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                  Manual Selection ({manualDesigns.length} selected)
                </label>
                <button
                  onClick={() => setShowDesignPicker(!showDesignPicker)}
                  className="flex items-center gap-1 px-3 py-1 text-xs font-semibold rounded-lg border"
                  style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                >
                  {showDesignPicker ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                  {showDesignPicker ? 'Hide' : 'Pick Designs'}
                </button>
              </div>

              {manualDesigns.length > 0 && (
                <div className="mb-3">
                  <div className="flex flex-wrap gap-2 p-3 border rounded-lg" style={{ borderColor: COLORS.border }}>
                    {manualDesigns.map(designNum => (
                      <div key={designNum} className="flex items-center gap-2 px-3 py-1 bg-white border rounded-lg text-sm" style={{ borderColor: COLORS.maroon }}>
                        <span style={{ color: COLORS.maroon }}>{designNum}</span>
                        <button onClick={() => toggleManualDesign(designNum)} className="text-gray-400 hover:text-red-600">
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {showDesignPicker && (
                <div className="border rounded-lg p-4" style={{ borderColor: COLORS.border }}>
                  <input
                    type="text"
                    value={designSearchTerm}
                    onChange={(e) => setDesignSearchTerm(e.target.value)}
                    placeholder="Search designs..."
                    className="w-full px-4 py-2 border rounded-lg mb-3 text-sm"
                    style={{ borderColor: COLORS.border }}
                  />
                  <div className="max-h-[300px] overflow-y-auto grid grid-cols-4 gap-2">
                    {filteredDesignsForPicker.slice(0, 50).map(design => (
                      <button
                        key={design.designNumber}
                        onClick={() => toggleManualDesign(design.designNumber)}
                        className={`relative aspect-[4/5] border-2 rounded-lg overflow-hidden transition-all ${
                          manualDesigns.includes(design.designNumber) ? 'ring-2 ring-offset-2' : 'hover:border-gray-400'
                        }`}
                        style={{
                          borderColor: manualDesigns.includes(design.designNumber) ? COLORS.maroon : COLORS.border,
                          ringColor: COLORS.maroon
                        }}
                      >
                        <img
                          src={design.imageUrl}
                          alt={design.designNumber}
                          className="w-full h-full object-cover"
                          onError={(e) => e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="125"%3E%3Crect fill="%23f5f5f5" width="100" height="125"/%3E%3C/svg%3E'}
                        />
                        {manualDesigns.includes(design.designNumber) && (
                          <div className="absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold" style={{ backgroundColor: COLORS.maroon }}>✓</div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-70 text-white text-xs p-1 text-center truncate">
                          {design.designNumber}
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t flex gap-3 justify-end flex-shrink-0" style={{ borderColor: COLORS.border }}>
            <button
              onClick={onClose}
              className="px-6 py-2 border text-sm font-semibold rounded-lg hover:bg-gray-50"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-6 py-2 text-white text-sm font-semibold rounded-lg hover:opacity-90"
              style={{ backgroundColor: COLORS.maroon }}
            >
              Create Sub-Collection
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Collection Designs Modal - View designs in a collection
const CollectionDesignsModal = ({ show, onClose, designs, collectionName }) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl mx-auto">
          {/* Header */}
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: COLORS.border, backgroundColor: COLORS.maroon }}>
            <div>
              <h2 className="font-serif text-xl text-white">{collectionName}</h2>
              <p className="text-sm text-white opacity-75">{designs.length} design{designs.length !== 1 ? 's' : ''}</p>
            </div>
            <button onClick={onClose} className="text-white hover:opacity-70">
              <X size={24} />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 max-h-[70vh] overflow-y-auto">
            {designs.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg" style={{ color: COLORS.textSecondary }}>No designs match this collection's filters</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {designs.map((design) => (
                  <div
                    key={design.designNumber}
                    className="bg-white border rounded-lg overflow-hidden hover:shadow-lg transition-all group"
                    style={{ borderColor: COLORS.border }}
                  >
                    <div className="aspect-[4/5] bg-gray-100 relative overflow-hidden">
                      <img
                        src={design.imageUrl}
                        alt={design.designNumber}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="500"%3E%3Crect fill="%23f5f5f5" width="400" height="500"/%3E%3Ctext fill="%23999" font-family="serif" font-size="16" x="50%25" y="50%25" text-anchor="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
                        }}
                      />
                    </div>
                    <div className="p-3">
                      <p className="font-semibold text-sm mb-1" style={{ color: COLORS.textPrimary }}>
                        {design.designNumber}
                      </p>
                      <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                        {design.productType}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// =============================================================================
// USERS TAB (Admin only - User Management)
// =============================================================================

// ── Trend Predictor Page ────────────────────────────────────────────────
const TrendPredictorPage = () => {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [productType, setProductType] = useState('Necklace');
  const [baseMaterial, setBaseMaterial] = useState('Gold Plated');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showAllAttrs, setShowAllAttrs] = useState(false);
  const fileInputRef = useRef(null);

  const productTypes = ['Necklace', 'Earrings', 'Bangles', 'Bracelet', 'Ring', 'Pendant', 'Anklet', 'Mangalsutra', 'Chain', 'Nose Pin'];
  const baseMaterials = ['Gold Plated', 'Silver', 'Oxidised Silver', 'Rose Gold', 'Brass', 'German Silver', 'Copper'];

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
    setResult(null);
    setError(null);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.type.startsWith('image/')) handleFile(f);
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('productType', productType);
      formData.append('baseMaterial', baseMaterial);

      const token = localStorage.getItem('authToken');
      const res = await fetch(config.endpoints.trendingPredict, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        body: formData
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Analysis failed');
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setError(null);
    setShowAllAttrs(false);
  };

  const scoreColor = (score) => {
    if (score >= 70) return '#10B981';
    if (score >= 45) return '#F59E0B';
    return '#EF4444';
  };

  const verdictBg = (verdict) => {
    if (verdict === 'Hot Trend') return { bg: '#ECFDF5', border: '#10B981', text: '#065F46' };
    if (verdict === 'Moderate Potential') return { bg: '#FFFBEB', border: '#F59E0B', text: '#92400E' };
    return { bg: '#FEF2F2', border: '#EF4444', text: '#991B1B' };
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Upload Section */}
      <div className="bg-white rounded-xl border p-6" style={{ borderColor: COLORS.border }}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left - Drop Zone */}
          <div
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
            className="relative border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors min-h-[280px]"
            style={{
              borderColor: preview ? COLORS.maroon : COLORS.border,
              backgroundColor: preview ? '#FDF2F4' : '#FAFAFA'
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              className="hidden"
              onChange={(e) => handleFile(e.target.files[0])}
            />
            {preview ? (
              <img src={preview} alt="Preview" className="max-h-[260px] rounded-lg object-contain" />
            ) : (
              <>
                <Upload className="w-10 h-10 mb-3" style={{ color: COLORS.textLight }} />
                <p className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                  Drop an image here or click to upload
                </p>
                <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                  JPG, PNG, or WEBP — max 10 MB
                </p>
              </>
            )}
          </div>

          {/* Right - Options */}
          <div className="flex flex-col justify-between">
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                  Product Type
                </label>
                <select
                  value={productType}
                  onChange={(e) => setProductType(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.border, focusRingColor: COLORS.maroon }}
                >
                  {productTypes.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>
                  Base Material
                </label>
                <select
                  value={baseMaterial}
                  onChange={(e) => setBaseMaterial(e.target.value)}
                  className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2"
                  style={{ borderColor: COLORS.border }}
                >
                  {baseMaterials.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            </div>

            <button
              onClick={analyze}
              disabled={!file || loading}
              className="w-full mt-6 flex items-center justify-center gap-2 py-3 rounded-lg text-white font-bold text-sm tracking-wider transition-all disabled:opacity-40"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {loading ? (
                <>
                  <RefreshCw className="w-4 h-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Zap className="w-4 h-4" />
                  Analyze Trend Potential
                </>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="space-y-6">
          {/* Score Card */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: COLORS.border }}>
            <div className="flex items-center gap-8">
              {/* Big Score */}
              <div className="text-center flex-shrink-0">
                <div
                  className="text-5xl font-black"
                  style={{ color: scoreColor(result.compositeScore) }}
                >
                  {result.compositeScore}
                </div>
                <div className="text-xs font-bold mt-1" style={{ color: COLORS.textSecondary }}>
                  / 100
                </div>
              </div>

              {/* Progress Bar + Verdict */}
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <span
                    className="px-3 py-1 rounded-full text-xs font-bold border"
                    style={{
                      backgroundColor: verdictBg(result.verdict).bg,
                      borderColor: verdictBg(result.verdict).border,
                      color: verdictBg(result.verdict).text
                    }}
                  >
                    {result.verdict === 'Hot Trend' && '🔥 '}
                    {result.verdict === 'Moderate Potential' && '⚡ '}
                    {result.verdict === 'Low Trend' && '❄️ '}
                    {result.verdict}
                  </span>
                </div>
                <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-700"
                    style={{
                      width: `${result.compositeScore}%`,
                      backgroundColor: scoreColor(result.compositeScore)
                    }}
                  />
                </div>
                <div className="flex justify-between mt-1 text-[10px] font-bold" style={{ color: COLORS.textLight }}>
                  <span>Low Trend</span>
                  <span>Moderate</span>
                  <span>Hot Trend</span>
                </div>
              </div>
            </div>
          </div>

          {/* Attribute Breakdown */}
          <div className="bg-white rounded-xl border p-6" style={{ borderColor: COLORS.border }}>
            <h3 className="text-sm font-bold mb-4 uppercase tracking-wider" style={{ color: COLORS.textPrimary }}>
              Attribute Breakdown
            </h3>
            <div className="space-y-4">
              {result.breakdown.map((item) => (
                <div key={item.attribute} className="border rounded-lg p-4" style={{ borderColor: COLORS.border }}>
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <span className="text-sm font-bold" style={{ color: COLORS.textPrimary }}>
                        {item.attribute}
                      </span>
                      <span className="text-xs ml-2 px-2 py-0.5 rounded-full bg-gray-100" style={{ color: COLORS.textSecondary }}>
                        {Math.round(item.weight * 100)}% weight
                      </span>
                    </div>
                    <div className="text-right">
                      <span className="text-lg font-black" style={{ color: scoreColor(item.score) }}>
                        {item.score}
                      </span>
                      {item.rank && (
                        <span className="text-[10px] ml-1" style={{ color: COLORS.textSecondary }}>
                          #{item.rank}
                        </span>
                      )}
                    </div>
                  </div>

                  {item.extracted ? (
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs px-2 py-1 rounded font-semibold" style={{ backgroundColor: '#F3E8FF', color: COLORS.maroon }}>
                        {item.extracted}
                      </span>
                    </div>
                  ) : (
                    <p className="text-xs italic mb-2" style={{ color: COLORS.textLight }}>Not detected</p>
                  )}

                  {/* Score bar */}
                  <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${item.score}%`,
                        backgroundColor: scoreColor(item.score)
                      }}
                    />
                  </div>

                  {/* Internet trend reason */}
                  {item.reason && (
                    <p className="text-[11px] mt-2 italic" style={{ color: COLORS.textSecondary }}>
                      {item.reason}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Suggestions */}
          {result.suggestions && result.suggestions.length > 0 && (
            <div className="rounded-xl border p-5" style={{ backgroundColor: '#FFFBEB', borderColor: '#FCD34D' }}>
              <h3 className="text-sm font-bold mb-3 flex items-center gap-2" style={{ color: '#92400E' }}>
                <AlertTriangle className="w-4 h-4" />
                Improvement Suggestions
              </h3>
              <ul className="space-y-2">
                {result.suggestions.map((s, i) => (
                  <li key={i} className="text-xs flex items-start gap-2" style={{ color: '#78350F' }}>
                    <span className="mt-0.5">•</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* All Extracted Attributes (collapsible) */}
          <div className="bg-white rounded-xl border" style={{ borderColor: COLORS.border }}>
            <button
              onClick={() => setShowAllAttrs(!showAllAttrs)}
              className="w-full flex items-center justify-between p-4 text-sm font-bold uppercase tracking-wider"
              style={{ color: COLORS.textPrimary }}
            >
              <span>All Extracted Attributes ({Object.keys(result.allAttributes).length})</span>
              {showAllAttrs ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showAllAttrs && (
              <div className="px-4 pb-4">
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  {Object.entries(result.allAttributes).map(([key, val]) => (
                    <div key={key} className="border rounded-lg p-2" style={{ borderColor: COLORS.border }}>
                      <div className="text-[10px] font-bold uppercase" style={{ color: COLORS.textSecondary }}>{key}</div>
                      <div className="text-xs mt-0.5" style={{ color: COLORS.textPrimary }}>{val || '—'}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Analyze Another */}
          <div className="text-center">
            <button
              onClick={reset}
              className="px-6 py-2 rounded-lg border text-sm font-bold transition-all hover:bg-gray-50"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              <RefreshCw className="w-4 h-4 inline mr-2" />
              Analyze Another Image
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Activity Feed / Change Log Page ─────────────────────────────────────
const ActivityFeedPage = () => {
  const [activities, setActivities] = useState([]);
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [filterAction, setFilterAction] = useState('');
  const [filterResource, setFilterResource] = useState('');
  const [searchDesign, setSearchDesign] = useState('');
  const pageSize = 30;

  const fetchActivities = async (offset = 0) => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({ limit: pageSize, offset });
      if (filterAction) params.append('action', filterAction);
      if (filterResource) params.append('resourceType', filterResource);
      if (searchDesign.trim()) params.append('designNumber', searchDesign.trim());
      const res = await config.apiFetch(`${config.endpoints.activity}?${params}`);
      const data = await res.json();
      if (data.success) {
        setActivities(data.activities);
        setTotal(data.total);
      }
    } catch (err) { console.error('Error fetching activity:', err); }
    finally { setIsLoading(false); }
  };

  const fetchStats = async () => {
    try {
      const res = await config.apiFetch(config.endpoints.activityStats);
      const data = await res.json();
      if (data.success) setStats(data.stats);
    } catch (err) { console.error('Error fetching stats:', err); }
  };

  useEffect(() => { fetchStats(); }, []);
  useEffect(() => { fetchActivities(page * pageSize); }, [page, filterAction, filterResource, searchDesign]);

  const actionMeta = {
    LOGIN: { icon: '🔑', label: 'Logged In', color: '#6366F1', bg: '#EEF2FF' },
    GENERATE: { icon: '✨', label: 'Generated Attributes', color: '#8B5CF6', bg: '#F5F3FF' },
    VERIFY: { icon: '✅', label: 'Verified Design', color: '#10B981', bg: '#ECFDF5' },
    RERUN: { icon: '🔄', label: 'Reran Generation', color: '#F59E0B', bg: '#FFFBEB' },
    PUBLISH: { icon: '🚀', label: 'Published to Channel', color: '#3B82F6', bg: '#EFF6FF' },
    BULK_PUBLISH: { icon: '📦', label: 'Bulk Published', color: '#2563EB', bg: '#DBEAFE' },
    PUBLISH_COLLECTION: { icon: '📤', label: 'Published Collection', color: '#0891B2', bg: '#ECFEFF' },
    CREATE_USER: { icon: '👤', label: 'Created User', color: '#8B1D3C', bg: '#FDF2F8' },
    UPDATE_USER: { icon: '✏️', label: 'Updated User', color: '#D97706', bg: '#FFFBEB' },
    DELETE_USER: { icon: '🗑️', label: 'Deleted User', color: '#EF4444', bg: '#FEF2F2' },
    CREATE_COLLECTION: { icon: '📁', label: 'Created Collection', color: '#059669', bg: '#ECFDF5' },
    DELETE_COLLECTION: { icon: '🗑️', label: 'Deleted Collection', color: '#EF4444', bg: '#FEF2F2' },
    UPDATE_CONFIG: { icon: '⚙️', label: 'Updated Configuration', color: '#6B7280', bg: '#F3F4F6' },
    SAVE_CREDENTIALS: { icon: '🔐', label: 'Saved Credentials', color: '#7C3AED', bg: '#F5F3FF' },
  };

  const uniqueActions = [...new Set(activities.map(a => a.action))];
  const uniqueResources = [...new Set(activities.map(a => a.resourceType))];

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const date = new Date(dateStr);
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const totalPages = Math.ceil(total / pageSize);

  // Max bar height for the daily chart
  const maxDaily = stats?.daily?.length ? Math.max(...stats.daily.map(d => d.count)) : 1;

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">Activity Feed</h2>
        <div className="w-16 h-[2px] mb-4" style={{ backgroundColor: COLORS.maroon }} />
        <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
          Complete audit trail of all actions — who did what and when
        </p>
      </div>

      {/* Stats Row */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: COLORS.border }}>
            <p className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: COLORS.textSecondary }}>Total Events</p>
            <p className="text-3xl font-bold" style={{ color: COLORS.maroon }}>{stats.totalEvents.toLocaleString()}</p>
          </div>
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: COLORS.border }}>
            <p className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: COLORS.textSecondary }}>Active Users (30d)</p>
            <p className="text-3xl font-bold" style={{ color: '#3B82F6' }}>{stats.byUser?.length || 0}</p>
          </div>
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: COLORS.border }}>
            <p className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: COLORS.textSecondary }}>Top Action (30d)</p>
            <p className="text-lg font-bold truncate" style={{ color: '#10B981' }}>
              {stats.byAction?.[0]?.action || '—'}
            </p>
            <p className="text-[10px]" style={{ color: COLORS.textSecondary }}>
              {stats.byAction?.[0]?.count || 0} events
            </p>
          </div>
          <div className="bg-white border rounded-xl p-5" style={{ borderColor: COLORS.border }}>
            <p className="text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: COLORS.textSecondary }}>Last 14 Days</p>
            {stats.daily?.length > 0 ? (
              <div className="flex items-end gap-[3px] h-10">
                {stats.daily.map((d, i) => (
                  <div
                    key={i}
                    className="flex-1 rounded-t transition-all"
                    style={{
                      height: `${Math.max(4, (d.count / maxDaily) * 100)}%`,
                      backgroundColor: COLORS.maroon,
                      opacity: 0.3 + (d.count / maxDaily) * 0.7,
                    }}
                    title={`${d.date}: ${d.count} events`}
                  />
                ))}
              </div>
            ) : (
              <p className="text-sm" style={{ color: COLORS.textSecondary }}>No data yet</p>
            )}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border rounded-xl p-4" style={{ borderColor: COLORS.border }}>
        <div className="flex flex-col md:flex-row items-start md:items-center gap-3">
          {/* Design Search */}
          <div className="relative flex-1 max-w-xs">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textSecondary }} />
            <input
              type="text"
              placeholder="Search by design number..."
              value={searchDesign}
              onChange={e => { setSearchDesign(e.target.value); setPage(0); }}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
              style={{ borderColor: COLORS.border }}
            />
          </div>

          {/* Action Filter */}
          <select
            value={filterAction}
            onChange={e => { setFilterAction(e.target.value); setPage(0); }}
            className="px-3 py-2 border rounded-lg text-sm"
            style={{ borderColor: COLORS.border }}
          >
            <option value="">All Actions</option>
            {Object.keys(actionMeta).map(a => (
              <option key={a} value={a}>{actionMeta[a]?.label || a}</option>
            ))}
          </select>

          {/* Resource Filter */}
          <select
            value={filterResource}
            onChange={e => { setFilterResource(e.target.value); setPage(0); }}
            className="px-3 py-2 border rounded-lg text-sm"
            style={{ borderColor: COLORS.border }}
          >
            <option value="">All Resources</option>
            {['design', 'collection', 'user', 'auth', 'config', 'platform'].map(r => (
              <option key={r} value={r}>{r.charAt(0).toUpperCase() + r.slice(1)}</option>
            ))}
          </select>

          {/* Refresh */}
          <button
            onClick={() => { fetchActivities(page * pageSize); fetchStats(); }}
            className="p-2 border rounded-lg hover:bg-gray-50 transition-colors"
            style={{ borderColor: COLORS.border }}
            title="Refresh"
          >
            <RefreshCw size={16} style={{ color: COLORS.textSecondary }} />
          </button>

          <span className="text-[11px] ml-auto" style={{ color: COLORS.textSecondary }}>
            {total.toLocaleString()} event{total !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Activity Timeline */}
      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: COLORS.border }}>
        {isLoading ? (
          <div className="py-16 text-center">
            <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-3" style={{ borderColor: COLORS.border, borderTopColor: COLORS.maroon }} />
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>Loading activity...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="py-16 text-center">
            <Activity size={40} className="mx-auto mb-3 opacity-20" />
            <p className="text-sm font-medium" style={{ color: COLORS.textSecondary }}>No activity recorded yet</p>
            <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>Actions like generating, verifying, and publishing will appear here</p>
          </div>
        ) : (
          <div className="divide-y" style={{ borderColor: COLORS.border }}>
            {activities.map((a, idx) => {
              const meta = actionMeta[a.action] || { icon: '📋', label: a.action, color: '#6B7280', bg: '#F3F4F6' };
              return (
                <div key={a.id || idx} className="flex items-start gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors">
                  {/* Icon */}
                  <div className="w-10 h-10 rounded-full flex items-center justify-center text-lg flex-shrink-0 mt-0.5"
                    style={{ backgroundColor: meta.bg }}>
                    {meta.icon}
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                          {a.username || 'System'}
                        </span>
                        {a.userRole && (
                          <span className="ml-1.5 text-[9px] px-1.5 py-0.5 rounded font-bold uppercase text-white"
                            style={{ backgroundColor: a.userRole === 'admin' ? '#8B1D3C' : a.userRole === 'publisher' ? '#3B82F6' : a.userRole === 'reviewer' ? '#10B981' : '#6B7280' }}>
                            {a.userRole}
                          </span>
                        )}
                        <span className="text-sm ml-1" style={{ color: COLORS.textSecondary }}>
                          {meta.label.toLowerCase()}
                        </span>
                      </div>
                      <span className="text-[10px] whitespace-nowrap flex-shrink-0" style={{ color: COLORS.textSecondary }}>
                        {formatTimeAgo(a.createdAt)}
                      </span>
                    </div>

                    {/* Resource info */}
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {a.resourceId && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-semibold bg-gray-100" style={{ color: COLORS.textPrimary }}>
                          {a.resourceType === 'design' && '🎨'}
                          {a.resourceType === 'collection' && '📁'}
                          {a.resourceType === 'user' && '👤'}
                          {a.resourceType === 'platform' && '🔌'}
                          {a.resourceId}
                        </span>
                      )}
                      {a.resourceName && (
                        <span className="text-[11px]" style={{ color: COLORS.textSecondary }}>
                          {a.resourceName}
                        </span>
                      )}
                      {a.status === 'failure' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-50 text-red-600 font-semibold">Failed</span>
                      )}
                    </div>

                    {/* Details */}
                    {a.details && (
                      <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
                        {Object.entries(a.details).map(([k, v]) => (
                          <span key={k} className="text-[10px]" style={{ color: COLORS.textSecondary }}>
                            <span className="font-semibold">{k}:</span>{' '}
                            {Array.isArray(v) ? `${v.length} items` : String(v)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50/50" style={{ borderColor: COLORS.border }}>
            <button
              onClick={() => setPage(p => Math.max(0, p - 1))}
              disabled={page === 0}
              className="px-3 py-1.5 text-xs font-semibold border rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
            >
              ← Previous
            </button>
            <span className="text-[11px]" style={{ color: COLORS.textSecondary }}>
              Page {page + 1} of {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
              className="px-3 py-1.5 text-xs font-semibold border rounded-lg disabled:opacity-40 hover:bg-white transition-colors"
              style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Action Breakdown + User Leaderboard */}
      {stats && (stats.byAction?.length > 0 || stats.byUser?.length > 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Action Breakdown */}
          {stats.byAction?.length > 0 && (
            <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
              <h3 className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: COLORS.textSecondary }}>Action Breakdown (30 Days)</h3>
              <div className="space-y-3">
                {stats.byAction.map(item => {
                  const meta = actionMeta[item.action] || { icon: '📋', label: item.action, color: '#6B7280' };
                  const pct = stats.byAction[0]?.count ? (item.count / stats.byAction[0].count * 100) : 0;
                  return (
                    <div key={item.action} className="flex items-center gap-3">
                      <span className="text-sm w-5 text-center">{meta.icon}</span>
                      <span className="text-xs font-medium w-32 truncate" style={{ color: COLORS.textPrimary }}>{meta.label}</span>
                      <div className="flex-1 h-5 bg-gray-100 rounded-full overflow-hidden">
                        <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: meta.color, opacity: 0.8 }} />
                      </div>
                      <span className="text-xs font-bold w-10 text-right" style={{ color: COLORS.textPrimary }}>{item.count}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* User Leaderboard */}
          {stats.byUser?.length > 0 && (
            <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
              <h3 className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: COLORS.textSecondary }}>Most Active Users (30 Days)</h3>
              <div className="space-y-3">
                {stats.byUser.map((u, i) => {
                  const roleColor = u.role === 'admin' ? '#8B1D3C' : u.role === 'publisher' ? '#3B82F6' : u.role === 'reviewer' ? '#10B981' : '#6B7280';
                  return (
                    <div key={u.username} className="flex items-center gap-3">
                      <span className="text-xs font-bold w-5 text-center" style={{ color: COLORS.textSecondary }}>#{i + 1}</span>
                      <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-bold"
                        style={{ backgroundColor: roleColor }}>
                        {u.username.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-xs font-semibold" style={{ color: COLORS.textPrimary }}>@{u.username}</span>
                        <span className="ml-1.5 text-[9px] px-1 py-0.5 rounded text-white font-bold uppercase"
                          style={{ backgroundColor: roleColor }}>{u.role}</span>
                      </div>
                      <span className="text-xs font-bold" style={{ color: COLORS.textPrimary }}>{u.count} actions</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ── API Keys Management Section ─────────────────────────────────────
const ApiKeysSection = () => {
  const [apiKeys, setApiKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newKeyData, setNewKeyData] = useState({ name: '', sourceApp: '', rateLimit: 60 });
  const [creating, setCreating] = useState(false);
  const [generatedKey, setGeneratedKey] = useState(null);
  const [copied, setCopied] = useState(false);

  const fetchKeys = async () => {
    try {
      const res = await config.apiFetch(config.endpoints.apiKeys);
      const data = await res.json();
      if (data.success) setApiKeys(data.apiKeys || []);
    } catch (err) { console.error('Error fetching API keys:', err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchKeys(); }, []);

  const handleCreate = async () => {
    if (!newKeyData.name.trim()) {
      alert('Key name is required');
      return;
    }
    setCreating(true);
    try {
      const res = await config.apiFetch(config.endpoints.apiKeys, {
        method: 'POST',
        body: JSON.stringify(newKeyData),
      });
      const data = await res.json();
      if (data.success) {
        setGeneratedKey(data.apiKey);
        fetchKeys();
      } else {
        alert(data.error || 'Failed to create API key');
      }
    } catch (err) { alert('Error creating API key'); }
    finally { setCreating(false); }
  };

  const handleRevoke = async (key) => {
    if (!window.confirm(`Revoke API key "${key.name}" (${key.sourceApp})? External apps using this key will lose access immediately.`)) return;
    try {
      const res = await config.apiFetch(config.endpoints.apiKeyById(key.id), { method: 'DELETE' });
      const data = await res.json();
      if (data.success) fetchKeys();
      else alert(data.error);
    } catch (err) { alert('Error revoking key'); }
  };

  const copyToClipboard = (text) => {
    // navigator.clipboard only works on HTTPS — use execCommand fallback for HTTP
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(text).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const el = document.createElement('textarea');
    el.value = text;
    el.style.position = 'fixed';
    el.style.opacity = '0';
    document.body.appendChild(el);
    el.select();
    document.execCommand('copy');
    document.body.removeChild(el);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setGeneratedKey(null);
    setNewKeyData({ name: '', sourceApp: '', rateLimit: 60 });
    setCopied(false);
  };

  return (
    <div className="bg-white border rounded-xl" style={{ borderColor: COLORS.border }}>
      <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: COLORS.border }}>
        <div>
          <h3 className="text-sm font-bold tracking-wider uppercase flex items-center gap-2" style={{ color: COLORS.textPrimary }}>
            <Key size={16} style={{ color: COLORS.maroon }} />
            External API Keys
          </h3>
          <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
            Generate API keys for external applications to push designs into the catalog
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="px-4 py-2 text-white text-xs font-semibold tracking-wider uppercase rounded-lg hover:opacity-90 flex items-center gap-2"
          style={{ backgroundColor: COLORS.maroon }}
        >
          <Plus size={14} /> Generate Key
        </button>
      </div>

      <div className="p-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-6 h-6 border-2 rounded-full mx-auto mb-2" style={{ borderColor: COLORS.border, borderTopColor: COLORS.maroon }} />
            <p className="text-xs" style={{ color: COLORS.textSecondary }}>Loading API keys...</p>
          </div>
        ) : apiKeys.length === 0 ? (
          <div className="text-center py-8">
            <Key size={32} className="mx-auto mb-2 opacity-20" />
            <p className="text-sm" style={{ color: COLORS.textSecondary }}>No API keys created yet</p>
            <p className="text-xs mt-1" style={{ color: COLORS.textLight }}>Generate a key to allow external apps to push designs</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-bold tracking-wider uppercase" style={{ color: COLORS.textSecondary }}>
                <th className="text-left pb-3">Name</th>
                <th className="text-left pb-3">Source App</th>
                <th className="text-left pb-3">Key Prefix</th>
                <th className="text-left pb-3">Rate Limit</th>
                <th className="text-left pb-3">Status</th>
                <th className="text-left pb-3">Last Used</th>
                <th className="text-right pb-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map(key => (
                <tr key={key.id} className="border-t hover:bg-gray-50/50" style={{ borderColor: COLORS.border }}>
                  <td className="py-3 font-semibold" style={{ color: COLORS.textPrimary }}>{key.name}</td>
                  <td className="py-3">
                    <span className="text-xs px-2 py-1 rounded-full bg-blue-50 text-blue-700 font-medium">{key.sourceApp}</span>
                  </td>
                  <td className="py-3 font-mono text-xs" style={{ color: COLORS.textSecondary }}>{key.keyPrefix}••••••••</td>
                  <td className="py-3 text-xs" style={{ color: COLORS.textSecondary }}>{key.rateLimit}/min</td>
                  <td className="py-3">
                    {key.isActive ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-600">
                        <ToggleRight size={16} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-500">
                        <ToggleLeft size={16} /> Revoked
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-[11px]" style={{ color: COLORS.textSecondary }}>
                    {key.lastUsedAt ? new Date(key.lastUsedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                  </td>
                  <td className="py-3 text-right">
                    {key.isActive && (
                      <button
                        onClick={() => handleRevoke(key)}
                        className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Create API Key Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={closeCreateModal}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: COLORS.border }}>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                  {generatedKey ? 'API Key Generated' : 'Generate API Key'}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: COLORS.textSecondary }}>
                  {generatedKey ? 'Copy and save this key — it will not be shown again' : 'Create a key for an external application'}
                </p>
              </div>
              <button onClick={closeCreateModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} style={{ color: COLORS.textSecondary }} />
              </button>
            </div>

            <div className="p-6">
              {generatedKey ? (
                <div className="space-y-4">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <p className="text-xs font-bold text-green-800 mb-2 flex items-center gap-1">
                      <CheckCircle size={14} /> Key created successfully
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-white border border-green-300 rounded px-3 py-2 font-mono break-all select-all">
                        {generatedKey}
                      </code>
                      <button
                        onClick={() => copyToClipboard(generatedKey)}
                        className="flex-shrink-0 p-2 rounded-lg border border-green-300 hover:bg-green-100 transition-colors"
                        title="Copy to clipboard"
                      >
                        {copied ? <CheckCircle size={16} className="text-green-600" /> : <Copy size={16} className="text-green-700" />}
                      </button>
                    </div>
                  </div>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                    <p className="text-xs text-amber-800 flex items-center gap-1">
                      <AlertTriangle size={13} />
                      <strong>Save this key now.</strong> It cannot be retrieved after closing this dialog.
                    </p>
                  </div>
                  <button
                    onClick={closeCreateModal}
                    className="w-full py-2.5 text-sm font-semibold text-white rounded-lg hover:opacity-90"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    Done
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                      Key Name <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={newKeyData.name}
                      onChange={e => setNewKeyData(p => ({ ...p, name: e.target.value }))}
                      placeholder='e.g. "GoFrugal ERP Integration"'
                      className="w-full px-4 py-2.5 border rounded-lg text-sm"
                      style={{ borderColor: COLORS.border }}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                      Source App <span className="text-[10px] font-normal normal-case tracking-normal">(optional)</span>
                    </label>
                    <input
                      type="text"
                      value={newKeyData.sourceApp}
                      onChange={e => setNewKeyData(p => ({ ...p, sourceApp: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))}
                      placeholder="Auto-generated from key name if left blank"
                      className="w-full px-4 py-2.5 border rounded-lg text-sm"
                      style={{ borderColor: COLORS.border }}
                    />
                    <p className="text-[10px] mt-1" style={{ color: COLORS.textSecondary }}>Optional tag to identify the source — purely for your reference</p>
                  </div>
                  <div>
                    <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                      Rate Limit (requests/minute)
                    </label>
                    <input
                      type="number"
                      value={newKeyData.rateLimit}
                      onChange={e => setNewKeyData(p => ({ ...p, rateLimit: parseInt(e.target.value) || 60 }))}
                      min={1}
                      max={1000}
                      className="w-full px-4 py-2.5 border rounded-lg text-sm"
                      style={{ borderColor: COLORS.border }}
                    />
                  </div>
                  <div className="flex justify-end gap-3 pt-2">
                    <button
                      onClick={closeCreateModal}
                      className="px-5 py-2.5 text-sm border rounded-lg hover:bg-gray-50"
                      style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleCreate}
                      disabled={creating || !newKeyData.name.trim()}
                      className="px-5 py-2.5 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90 flex items-center gap-2"
                      style={{ backgroundColor: COLORS.maroon }}
                    >
                      {creating ? (
                        <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Generating...</>
                      ) : (
                        <><Key size={14} /> Generate Key</>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const SettingsPage = ({ authUser }) => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({ username: '', email: '', fullName: '', password: '', role: 'viewer' });
  const [saving, setSaving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [showPassword, setShowPassword] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const fetchUsers = async () => {
    try {
      const res = await config.apiFetch(config.endpoints.users);
      const data = await res.json();
      if (data.success) setUsers(data.users);
    } catch (err) { console.error('Error fetching users:', err); }
    finally { setIsLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleSave = async () => {
    if (!formData.username.trim() || !formData.fullName.trim() || !formData.email.trim()) {
      alert('Username, full name, and email are required');
      return;
    }
    if (!editingUser && (!formData.password || formData.password.length < 6)) {
      alert('Password must be at least 6 characters');
      return;
    }
    if (editingUser && formData.password && formData.password.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    setSaving(true);
    try {
      const url = editingUser ? config.endpoints.userById(editingUser.id) : config.endpoints.users;
      const method = editingUser ? 'PUT' : 'POST';
      const body = { ...formData };
      if (editingUser && !body.password) delete body.password;
      const res = await config.apiFetch(url, { method, body: JSON.stringify(body) });
      const data = await res.json();
      if (data.success) {
        setShowModal(false);
        setEditingUser(null);
        setFormData({ username: '', email: '', fullName: '', password: '', role: 'viewer' });
        setShowPassword(false);
        fetchUsers();
        showSuccess(editingUser ? `Updated ${formData.fullName}` : `Created user ${formData.username}`);
      } else { alert(data.error || 'Failed to save user'); }
    } catch (err) { alert('Error saving user'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (user) => {
    if (user.id === authUser?.id) {
      alert("You can't delete your own account");
      return;
    }
    if (!window.confirm(`Delete user "${user.fullName}" (@${user.username})? This cannot be undone.`)) return;
    try {
      const res = await config.apiFetch(config.endpoints.userById(user.id), { method: 'DELETE' });
      const data = await res.json();
      if (data.success) { fetchUsers(); showSuccess(`Deleted ${user.fullName}`); }
      else alert(data.error);
    } catch (err) { alert('Error deleting user'); }
  };

  const handleToggleActive = async (user) => {
    if (user.id === authUser?.id) {
      alert("You can't disable your own account");
      return;
    }
    try {
      const res = await config.apiFetch(config.endpoints.userById(user.id), {
        method: 'PUT',
        body: JSON.stringify({ is_active: !user.isActive })
      });
      const data = await res.json();
      if (data.success) { fetchUsers(); showSuccess(`${user.isActive ? 'Disabled' : 'Enabled'} ${user.fullName}`); }
      else alert(data.error);
    } catch (err) { alert('Error updating user'); }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({ username: user.username, email: user.email, fullName: user.fullName, password: '', role: user.role });
    setShowPassword(false);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingUser(null);
    setFormData({ username: '', email: '', fullName: '', password: '', role: 'viewer' });
    setShowPassword(false);
    setShowModal(true);
  };

  const roleColors = { admin: '#8B1D3C', reviewer: '#10B981', publisher: '#3B82F6', viewer: '#6B7280' };
  const roleDescriptions = {
    admin: 'Full access to all features including configuration and user management',
    reviewer: 'Can verify designs and manage collections',
    publisher: 'Can manage channels and publish designs',
    viewer: 'Read-only access to catalog and verified designs'
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = searchQuery === '' ||
      u.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = filterRole === 'all' || u.role === filterRole;
    return matchesSearch && matchesRole;
  });

  const roleCounts = { all: users.length };
  users.forEach(u => { roleCounts[u.role] = (roleCounts[u.role] || 0) + 1; });

  if (isLoading) {
    return (
      <div className="max-w-6xl mx-auto py-16 text-center">
        <div className="animate-spin w-8 h-8 border-2 rounded-full mx-auto mb-4" style={{ borderColor: COLORS.border, borderTopColor: COLORS.maroon }} />
        <p className="text-sm" style={{ color: COLORS.textSecondary }}>Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      {/* Page Header */}
      <div>
        <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">Settings</h2>
        <div className="w-16 h-[2px] mb-4" style={{ backgroundColor: COLORS.maroon }} />
        <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
          Manage user accounts, roles, and access permissions
        </p>
      </div>

      {/* Success Toast */}
      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm animate-pulse">
          <CheckCircle size={16} /> {successMessage}
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        {[
          { label: 'Total Users', count: users.length, color: COLORS.maroon },
          { label: 'Admins', count: roleCounts.admin || 0, color: roleColors.admin },
          { label: 'Reviewers', count: roleCounts.reviewer || 0, color: roleColors.reviewer },
          { label: 'Publishers', count: roleCounts.publisher || 0, color: roleColors.publisher },
          { label: 'Viewers', count: roleCounts.viewer || 0, color: roleColors.viewer },
        ].map(stat => (
          <div key={stat.label} className="bg-white border rounded-xl p-4" style={{ borderColor: COLORS.border }}>
            <p className="text-[10px] font-bold tracking-wider uppercase mb-1" style={{ color: COLORS.textSecondary }}>{stat.label}</p>
            <p className="text-2xl font-bold" style={{ color: stat.color }}>{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="bg-white border rounded-xl p-4" style={{ borderColor: COLORS.border }}>
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <div className="relative flex-1 max-w-sm">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textSecondary }} />
              <input
                type="text"
                placeholder="Search users..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border rounded-lg text-sm"
                style={{ borderColor: COLORS.border }}
              />
            </div>
            <div className="flex items-center gap-1 border rounded-lg overflow-hidden" style={{ borderColor: COLORS.border }}>
              {['all', 'admin', 'reviewer', 'publisher', 'viewer'].map(role => (
                <button
                  key={role}
                  onClick={() => setFilterRole(role)}
                  className={`px-3 py-2 text-[10px] font-bold tracking-wider uppercase transition-all ${
                    filterRole === role ? 'text-white' : 'hover:bg-gray-50'
                  }`}
                  style={filterRole === role ? { backgroundColor: role === 'all' ? COLORS.maroon : roleColors[role], color: '#fff' } : { color: COLORS.textSecondary }}
                >
                  {role === 'all' ? 'All' : role}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={handleCreate}
            className="px-5 py-2.5 text-white text-xs font-semibold tracking-wider uppercase rounded-lg hover:opacity-90 flex items-center gap-2 shadow-sm"
            style={{ backgroundColor: COLORS.maroon }}
          >
            <UserPlus size={15} /> Create User
          </button>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: COLORS.border }}>
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50/80 text-[10px] font-bold tracking-wider uppercase" style={{ color: COLORS.textSecondary }}>
              <th className="text-left px-5 py-3.5">User</th>
              <th className="text-left px-5 py-3.5">Email</th>
              <th className="text-left px-5 py-3.5">Role</th>
              <th className="text-left px-5 py-3.5">Status</th>
              <th className="text-left px-5 py-3.5">Last Login</th>
              <th className="text-right px-5 py-3.5">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-5 py-12 text-center" style={{ color: COLORS.textSecondary }}>
                  <Users size={32} className="mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No users found</p>
                </td>
              </tr>
            ) : (
              filteredUsers.map(u => (
                <tr key={u.id} className="border-t hover:bg-gray-50/50 transition-colors" style={{ borderColor: COLORS.border }}>
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-xs font-bold"
                        style={{ backgroundColor: roleColors[u.role] || '#6B7280' }}>
                        {u.fullName.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="font-semibold" style={{ color: COLORS.textPrimary }}>
                          {u.fullName}
                          {u.id === authUser?.id && (
                            <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-gray-100 font-normal" style={{ color: COLORS.textSecondary }}>You</span>
                          )}
                        </div>
                        <div className="text-[11px]" style={{ color: COLORS.textSecondary }}>@{u.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4" style={{ color: COLORS.textSecondary }}>{u.email}</td>
                  <td className="px-5 py-4">
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase text-white"
                      style={{ backgroundColor: roleColors[u.role] || '#6B7280' }}>
                      {u.role === 'admin' && <Lock size={10} />}
                      {u.role}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <button
                      onClick={() => handleToggleActive(u)}
                      disabled={u.id === authUser?.id}
                      className={`inline-flex items-center gap-1.5 text-[11px] font-semibold ${u.id === authUser?.id ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:opacity-80'}`}
                      title={u.id === authUser?.id ? "Can't disable yourself" : `Click to ${u.isActive ? 'disable' : 'enable'}`}
                    >
                      {u.isActive ? (
                        <><ToggleRight size={18} className="text-green-500" /> <span className="text-green-600">Active</span></>
                      ) : (
                        <><ToggleLeft size={18} className="text-gray-400" /> <span className="text-red-500">Disabled</span></>
                      )}
                    </button>
                  </td>
                  <td className="px-5 py-4 text-[11px]" style={{ color: COLORS.textSecondary }}>
                    {u.lastLogin ? new Date(u.lastLogin).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                  </td>
                  <td className="px-5 py-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button onClick={() => handleEdit(u)} className="p-2 hover:bg-gray-100 rounded-lg transition-colors" title="Edit user">
                        <Edit2 size={14} style={{ color: COLORS.textSecondary }} />
                      </button>
                      {u.id !== authUser?.id && (
                        <button onClick={() => handleDelete(u)} className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Delete user">
                          <Trash2 size={14} className="text-red-400" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Role Permissions Reference */}
      <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
        <h3 className="text-xs font-bold tracking-wider uppercase mb-4" style={{ color: COLORS.textSecondary }}>Role Permissions Reference</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(roleDescriptions).map(([role, desc]) => (
            <div key={role} className="border rounded-lg p-4" style={{ borderColor: COLORS.border }}>
              <div className="flex items-center gap-2 mb-2">
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase text-white"
                  style={{ backgroundColor: roleColors[role] }}>
                  {role === 'admin' && <Lock size={9} />}
                  {role}
                </span>
              </div>
              <p className="text-[11px] leading-relaxed" style={{ color: COLORS.textSecondary }}>{desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* API Keys Management */}
      {authUser?.role === 'admin' && <ApiKeysSection />}

      {/* Create/Edit User Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => { setShowModal(false); setEditingUser(null); }}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg" onClick={e => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: COLORS.border }}>
              <div>
                <h3 className="text-lg font-semibold" style={{ color: COLORS.textPrimary }}>
                  {editingUser ? 'Edit User' : 'Create New User'}
                </h3>
                <p className="text-xs mt-0.5" style={{ color: COLORS.textSecondary }}>
                  {editingUser ? `Editing @${editingUser.username}` : 'Set up a new user account with role-based access'}
                </p>
              </div>
              <button onClick={() => { setShowModal(false); setEditingUser(null); }} className="p-2 hover:bg-gray-100 rounded-lg">
                <X size={18} style={{ color: COLORS.textSecondary }} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-5">
              {/* Username */}
              <div>
                <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                  Username <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={e => setFormData(p => ({ ...p, username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                  disabled={!!editingUser}
                  placeholder="e.g. john_doe"
                  className="w-full px-4 py-2.5 border rounded-lg text-sm disabled:bg-gray-50 disabled:text-gray-500"
                  style={{ borderColor: COLORS.border }}
                />
                {!editingUser && <p className="text-[10px] mt-1" style={{ color: COLORS.textSecondary }}>Lowercase letters, numbers, and underscores only</p>}
              </div>

              {/* Full Name */}
              <div>
                <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                  Full Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  value={formData.fullName}
                  onChange={e => setFormData(p => ({ ...p, fullName: e.target.value }))}
                  placeholder="e.g. John Doe"
                  className="w-full px-4 py-2.5 border rounded-lg text-sm"
                  style={{ borderColor: COLORS.border }}
                />
              </div>

              {/* Email */}
              <div>
                <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                  Email <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={e => setFormData(p => ({ ...p, email: e.target.value }))}
                  placeholder="e.g. john@kushals.com"
                  className="w-full px-4 py-2.5 border rounded-lg text-sm"
                  style={{ borderColor: COLORS.border }}
                />
              </div>

              {/* Password */}
              <div>
                <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                  Password {!editingUser && <span className="text-red-400">*</span>}
                  {editingUser && <span className="normal-case tracking-normal font-normal">(leave blank to keep current)</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={formData.password}
                    onChange={e => setFormData(p => ({ ...p, password: e.target.value }))}
                    placeholder={editingUser ? 'Leave blank to keep current' : 'Min 6 characters'}
                    className="w-full px-4 py-2.5 border rounded-lg text-sm pr-10"
                    style={{ borderColor: COLORS.border }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                  >
                    <Eye size={14} style={{ color: COLORS.textSecondary }} />
                  </button>
                </div>
              </div>

              {/* Role */}
              <div>
                <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
                  Role <span className="text-red-400">*</span>
                </label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: 'viewer', label: 'Viewer', desc: 'Read-only access' },
                    { value: 'reviewer', label: 'Reviewer', desc: 'Verify designs' },
                    { value: 'publisher', label: 'Publisher', desc: 'Manage channels' },
                    { value: 'admin', label: 'Admin', desc: 'Full access' },
                  ].map(r => (
                    <button
                      key={r.value}
                      type="button"
                      onClick={() => setFormData(p => ({ ...p, role: r.value }))}
                      className={`border rounded-lg p-3 text-left transition-all ${
                        formData.role === r.value ? 'ring-2 bg-gray-50' : 'hover:bg-gray-50'
                      }`}
                      style={{
                        borderColor: formData.role === r.value ? roleColors[r.value] : COLORS.border,
                        ringColor: roleColors[r.value]
                      }}
                    >
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: roleColors[r.value] }} />
                        <span className="text-xs font-semibold" style={{ color: formData.role === r.value ? roleColors[r.value] : COLORS.textPrimary }}>
                          {r.label}
                        </span>
                      </div>
                      <p className="text-[10px] mt-1 ml-[18px]" style={{ color: COLORS.textSecondary }}>{r.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t" style={{ borderColor: COLORS.border }}>
              <button
                onClick={() => { setShowModal(false); setEditingUser(null); }}
                className="px-5 py-2.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors"
                style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={saving || !formData.username.trim() || !formData.fullName.trim() || !formData.email.trim()}
                className="px-5 py-2.5 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90 transition-colors flex items-center gap-2"
                style={{ backgroundColor: COLORS.maroon }}
              >
                {saving ? (
                  <><div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Saving...</>
                ) : (
                  <>{editingUser ? <Save size={14} /> : <UserPlus size={14} />} {editingUser ? 'Update User' : 'Create User'}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// CONFIGURATION PAGE - SECTION 1: SKELETON + STATE MANAGEMENT
// =============================================================================

const ConfigurationPage = ({ attributesConfig, setAttributesConfig, isLoading: parentLoading, onRefresh, authUser }) => {
  const confirm = useConfirm();
  // ==================== STATE ====================
  const [activeTab, setActiveTab] = useState('attributes'); // 'categories', 'attributes', 'prompt', 'users'
  const [categories, setCategories] = useState({});
  const [attributes, setAttributes] = useState({});
  const [expandedCategories, setExpandedCategories] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  // Modal states
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showAttributeModal, setShowAttributeModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedAttribute, setSelectedAttribute] = useState(null);
  const [editMode, setEditMode] = useState(false); // true = edit, false = create
  
  // Prompt preview
  const [promptPreview, setPromptPreview] = useState('');
  const [promptLoading, setPromptLoading] = useState(false);
  const [estimatedTokens, setEstimatedTokens] = useState(0);
  const [estimatedCost, setEstimatedCost] = useState(0);

  // Item-name template (global setting; auto-composes item_name on Save & Process).
  // Edited inside the Item Name attribute's modal (see AttributeModal).
  const [itemNameTemplate, setItemNameTemplate] = useState('');

  // ==================== FETCH CONFIG ====================
  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setIsLoading(true);
      const response = await config.apiFetch(config.endpoints.attributesConfig);
      const data = await response.json();
      
      if (data.success) {
        setCategories(data.data.categories || {});
        setAttributes(data.data.attributes || {});
        setItemNameTemplate(data.data.settings?.itemNameTemplate || '');

        // Auto-expand all categories initially
        const expanded = {};
        Object.keys(data.data.categories || {}).forEach(catId => {
          expanded[catId] = true;
        });
        setExpandedCategories(expanded);
      } else {
        alert('Failed to load configuration');
      }
    } catch (error) {
      console.error('Error loading config:', error);
      alert('Error loading configuration');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveItemNameTemplate = async (value) => {
    const tmpl = (value !== undefined ? value : itemNameTemplate).trim();
    try {
      const resp = await config.apiFetch(config.buildUrl('/api/settings'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ settings: { itemNameTemplate: tmpl } }),
      });
      const data = await resp.json();
      if (data.success) {
        setItemNameTemplate(tmpl);
        const rc = data.recompose;
        notify.success(
          rc
            ? `Item name format saved. Updated ${rc.updated} of ${rc.total} designs`
            : 'Item name format saved'
        );
      } else {
        notify.error(data.error || 'Failed to save item name format');
      }
    } catch (e) {
      notify.error('Failed to save item name format: ' + e.message);
    }
  };

  // ==================== CATEGORY HANDLERS ====================
  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setEditMode(false);
    setShowCategoryModal(true);
  };

  const handleEditCategory = (categoryId) => {
    setSelectedCategory({ id: categoryId, ...categories[categoryId] });
    setEditMode(true);
    setShowCategoryModal(true);
  };

  const handleDeleteCategory = async (categoryId) => {
    const cat = categories[categoryId];
    if (!cat) return;

    const attrCount = getAttributesByCategory(categoryId).length;
    const ok = await confirm({
      title: `Delete category "${cat.name}"?`,
      message: attrCount > 0
        ? `This category still has ${attrCount} attribute(s). The delete will be rejected until you move or delete them.`
        : 'This cannot be undone.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const response = await config.apiFetch(config.endpoints.categoryById(categoryId), {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        notify.success(`Category "${cat.name}" deleted`);
        fetchConfig();
        return;
      }

      if (data.attributesInUse && data.attributesInUse.length) {
        const sample = data.attributesInUse.slice(0, 5).join(', ');
        const tail = data.attributesInUse.length > 5 ? ', …' : '';
        notify.error(
          `Cannot delete "${cat.name}": ${data.attributesInUse.length} attribute(s) still belong to it — ${sample}${tail}. Move or delete them first.`
        );
      } else {
        notify.error(data.error || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      notify.error('Network error while deleting category');
    }
  };

  const handleToggleCategoryFlag = async (categoryId, flagKey) => {
    const cat = categories[categoryId];
    if (!cat) return;

    const nextVal = !cat[flagKey];
    const payload = { [flagKey]: nextVal };

    // XOR: Modal Tab and Per-Variant are mutually exclusive.
    if (nextVal && flagKey === 'editableInEnrichment' && cat.perVariant) {
      payload.perVariant = false;
    }
    if (nextVal && flagKey === 'perVariant' && cat.editableInEnrichment) {
      payload.editableInEnrichment = false;
    }

    try {
      const response = await config.apiFetch(config.buildUrl(`/api/categories/${categoryId}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        fetchConfig();
      } else {
        notify.error(data.error || 'Failed to update category');
      }
    } catch (error) {
      console.error('Error toggling category flag:', error);
      notify.error('Network error while updating category');
    }
  };

  const handleSaveCategory = async (categoryData) => {
    try {
      const url = editMode 
        ? config.buildUrl(`/api/categories/${selectedCategory.id}`)
        : config.endpoints.categories;
      
      const method = editMode ? 'PUT' : 'POST';

      const response = await config.apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(categoryData)
      });

      const data = await response.json();

      if (data.success) {
        setShowCategoryModal(false);
        fetchConfig();
        notify.success(editMode ? 'Category updated' : 'Category created');
      } else {
        notify.error(data.error || 'Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      notify.error('Network error while saving category');
    }
  };

  const handleMoveCategoryUp = async (categoryId) => {
    const sortedCategories = Object.entries(categories)
      .sort((a, b) => a[1].order - b[1].order);
    
    const index = sortedCategories.findIndex(([id]) => id === categoryId);
    if (index <= 0) return;

    const orders = {};
    sortedCategories.forEach(([id, cat], i) => {
      if (i === index - 1) {
        orders[id] = cat.order + 1;
      } else if (i === index) {
        orders[id] = sortedCategories[index - 1][1].order;
      } else {
        orders[id] = cat.order;
      }
    });

    try {
      const response = await config.apiFetch(config.endpoints.categoriesReorder, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orders })
      });

      if (response.ok) {
        fetchConfig();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  const handleMoveCategoryDown = async (categoryId) => {
    const sortedCategories = Object.entries(categories)
      .sort((a, b) => a[1].order - b[1].order);
    
    const index = sortedCategories.findIndex(([id]) => id === categoryId);
    if (index < 0 || index >= sortedCategories.length - 1) return;

    const orders = {};
    sortedCategories.forEach(([id, cat], i) => {
      if (i === index) {
        orders[id] = sortedCategories[index + 1][1].order;
      } else if (i === index + 1) {
        orders[id] = cat.order - 1;
      } else {
        orders[id] = cat.order;
      }
    });

    try {
      const response = await config.apiFetch(config.endpoints.categoriesReorder, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ orders })
      });

      if (response.ok) {
        fetchConfig();
      }
    } catch (error) {
      console.error('Error reordering:', error);
    }
  };

  // ==================== ATTRIBUTE HANDLERS ====================
  const handleCreateAttribute = (categoryId) => {
    setSelectedAttribute(null);
    setSelectedCategory({ id: categoryId });
    setEditMode(false);
    setShowAttributeModal(true);
  };

  const handleEditAttribute = (attributeName) => {
    setSelectedAttribute({ name: attributeName, ...attributes[attributeName] });
    setEditMode(true);
    setShowAttributeModal(true);
  };

  const handleDeleteAttribute = async (attributeName) => {
    const attrCfg = attributes[attributeName];
    if (attrCfg?.system) {
      notify.error(
        `"${attributeName}" is a system attribute and cannot be deleted. You can disable it instead.`
      );
      return;
    }
    const ok = await confirm({
      title: `Delete attribute "${attributeName}"?`,
      message: 'This cannot be undone and will remove the attribute from every category and design-level record.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      const response = await config.apiFetch(config.buildUrl(`/api/attributes/${attributeName}`), {
        method: 'DELETE'
      });
      const data = await response.json();

      if (data.success) {
        notify.success(`Deleted "${attributeName}"`);
        fetchConfig();
      } else {
        notify.error(data.error || 'Failed to delete attribute');
      }
    } catch (error) {
      console.error('Error deleting attribute:', error);
      notify.error('Network error while deleting attribute');
    }
  };

  const handleSaveAttribute = async (attributeData) => {
    try {
      const url = editMode
        ? config.buildUrl(`/api/attributes/${selectedAttribute.name}`)
        : config.endpoints.attributes;

      const method = editMode ? 'PUT' : 'POST';

      const response = await config.apiFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(attributeData)
      });

      const data = await response.json();

      if (data.success) {
        setShowAttributeModal(false);
        fetchConfig();
        notify.success(editMode ? 'Attribute updated' : 'Attribute created');
      } else {
        notify.error(data.error || 'Failed to save attribute');
      }
    } catch (error) {
      console.error('Error saving attribute:', error);
      notify.error('Network error while saving attribute');
    }
  };

  const handleToggleAttribute = async (attributeName) => {
    try {
      const currentStatus = attributes[attributeName].enabled;
      
      const response = await config.apiFetch(config.buildUrl(`/api/attributes/${attributeName}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !currentStatus })
      });

      if (response.ok) {
        fetchConfig();
      }
    } catch (error) {
      console.error('Error toggling attribute:', error);
    }
  };

  const handleBulkSetEnabled = async (attributeNames, enabled) => {
    if (!attributeNames || attributeNames.length === 0) return;
    const toastId = notify.loading(`${enabled ? 'Enabling' : 'Disabling'} ${attributeNames.length} attribute${attributeNames.length === 1 ? '' : 's'}…`);
    // Serialize writes — each PUT triggers load_attributes_config + save_attributes_config
    // on the server, so parallel PUTs lost-update each other.
    let failures = 0;
    for (const name of attributeNames) {
      try {
        const res = await config.apiFetch(config.buildUrl(`/api/attributes/${encodeURIComponent(name)}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ enabled })
        });
        if (!res.ok) failures += 1;
      } catch (_) {
        failures += 1;
      }
    }
    notify.dismiss(toastId);
    if (failures === 0) {
      notify.success(`${enabled ? 'Enabled' : 'Disabled'} ${attributeNames.length} attribute${attributeNames.length === 1 ? '' : 's'}`);
    } else {
      notify.error(`${failures} of ${attributeNames.length} failed to ${enabled ? 'enable' : 'disable'}`);
    }
    fetchConfig();
  };

  const handleToggleMultiSelect = async (attributeName) => {
    try {
      const response = await config.apiFetch(config.buildUrl(`/api/attributes/${attributeName}/toggle-multiselect`), {
        method: 'PUT'
      });

      if (response.ok) {
        fetchConfig();
      }
    } catch (error) {
      console.error('Error toggling multiselect:', error);
    }
  };

  const toggleCategoryExpanded = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  // ==================== PROMPT PREVIEW HANDLER ====================
  const generatePromptPreview = async () => {
    try {
      setPromptLoading(true);
      const response = await config.apiFetch(config.endpoints.promptPreview, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          productType: 'Earring',
          baseMaterial: 'BRASS',
          designNumber: 'PREVIEW'
        })
      });

      const data = await response.json();

      if (data.success) {
        setPromptPreview(data.prompt);
        setEstimatedTokens(data.estimatedTokens);
        setEstimatedCost(data.estimatedCost);
      } else {
        alert('Failed to generate preview');
      }
    } catch (error) {
      console.error('Error generating preview:', error);
      alert('Error generating preview');
    } finally {
      setPromptLoading(false);
    }
  };

  // ==================== HELPERS ====================
  const getAttributesByCategory = (categoryId) => {
    const categoryAttrs = Object.entries(attributes)
      .filter(([name, config]) => config.category === categoryId)
      .sort((a, b) => (a[1].categoryOrder || 999) - (b[1].categoryOrder || 999));
    return categoryAttrs;
  };

  const getSortedCategories = () => {
    return Object.entries(categories)
      .sort((a, b) => a[1].order - b[1].order);
  };

  // ==================== RENDER ====================
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <RefreshCw className="animate-spin" size={48} style={{ color: COLORS.maroon }} />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-8">
      {/* Header */}
      <header className="text-center mb-12">
        {/* <h2 className="font-sans text-4xl font-bold text-gray-800 mb-2 tracking-tight">
          Configuration
        </h2> */}
        {/* <div className="w-16 h-[2px] mx-auto mb-6" style={{ backgroundColor: COLORS.maroon }} />
        <p className="font-light tracking-wide text-sm" style={{ color: COLORS.textSecondary }}>
          Manage attribute categories and AI generation settings
        </p> */}
      </header>

      {/* Tabs */}
      <div className="flex gap-2 border-b" style={{ borderColor: COLORS.border }}>
        <button
          onClick={() => setActiveTab('categories')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'categories'
              ? 'border-b-2 text-gray-800'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'categories' ? { borderColor: COLORS.maroon, color: COLORS.maroon } : {}}
        >
          📦 Categories
        </button>
        <button
          onClick={() => setActiveTab('attributes')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'attributes'
              ? 'border-b-2 text-gray-800'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'attributes' ? { borderColor: COLORS.maroon, color: COLORS.maroon } : {}}
        >
          🏷️ Attributes
        </button>
        <button
          onClick={() => setActiveTab('prompt')}
          className={`px-6 py-3 font-semibold transition-all ${
            activeTab === 'prompt'
              ? 'border-b-2 text-gray-800'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          style={activeTab === 'prompt' ? { borderColor: COLORS.maroon, color: COLORS.maroon } : {}}
        >
          👁️ Prompt Preview
        </button>
      </div>

      {/* Tab Content */}
      <div className="min-h-[600px]">
        {activeTab === 'categories' && (
          <CategoriesTab
            categories={getSortedCategories()}
            onCreateCategory={handleCreateCategory}
            onEditCategory={handleEditCategory}
            onDeleteCategory={handleDeleteCategory}
            onMoveUp={handleMoveCategoryUp}
            onMoveDown={handleMoveCategoryDown}
            onToggleCategoryFlag={handleToggleCategoryFlag}
            getAttributesByCategory={getAttributesByCategory}
          />
        )}

        {activeTab === 'attributes' && (
          <AttributesTab
            categories={getSortedCategories()}
            attributes={attributes}
            expandedCategories={expandedCategories}
            onToggleCategory={toggleCategoryExpanded}
            onCreateAttribute={handleCreateAttribute}
            onEditAttribute={handleEditAttribute}
            onDeleteAttribute={handleDeleteAttribute}
            onToggleAttribute={handleToggleAttribute}
            onToggleMultiSelect={handleToggleMultiSelect}
            onBulkSetEnabled={handleBulkSetEnabled}
            getAttributesByCategory={getAttributesByCategory}
          />
        )}

        {activeTab === 'prompt' && (
          <PromptPreviewTab
            promptPreview={promptPreview}
            promptLoading={promptLoading}
            estimatedTokens={estimatedTokens}
            estimatedCost={estimatedCost}
            onGeneratePreview={generatePromptPreview}
          />
        )}

      </div>

      {/* Modals */}
      {showCategoryModal && (
        <CategoryModal
          show={showCategoryModal}
          category={selectedCategory}
          editMode={editMode}
          onClose={() => setShowCategoryModal(false)}
          onSave={handleSaveCategory}
        />
      )}

      {showAttributeModal && (
        <AttributeModal
          show={showAttributeModal}
          attribute={selectedAttribute}
          attributes={attributes}
          categories={categories}
          selectedCategory={selectedCategory}
          editMode={editMode}
          itemNameTemplate={itemNameTemplate}
          onSaveItemNameTemplate={handleSaveItemNameTemplate}
          onClose={() => setShowAttributeModal(false)}
          onSave={handleSaveAttribute}
        />
      )}
    </div>
  );
};

// =============================================================================
// CONFIGURATION PAGE - SECTION 2: CATEGORIES TAB
// =============================================================================
// Add this component after the ConfigurationPage component

const CategoriesTab = ({
  categories,
  onCreateCategory,
  onEditCategory,
  onDeleteCategory,
  onMoveUp,
  onMoveDown,
  onToggleCategoryFlag,
  getAttributesByCategory
}) => {
  return (
    <div className="space-y-6">
      {/* Create Button */}
      <div className="flex justify-end">
        <button
          onClick={onCreateCategory}
          className="flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg"
          style={{ backgroundColor: COLORS.maroon }}
        >
          <Plus size={18} />
          Create New Category
        </button>
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {categories.map(([categoryId, categoryData], index) => {
          const attributeCount = getAttributesByCategory(categoryId).length;
          const isFirst = index === 0;
          const isLast = index === categories.length - 1;

          return (
            <div
              key={categoryId}
              className="bg-white border-2 rounded-xl p-6 hover:shadow-lg transition-all"
              style={{ borderColor: categoryData.enabled ? COLORS.maroon : COLORS.border }}
            >
              {/* Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">{categoryData.emoji}</span>
                  <div>
                    <h3 className="font-serif text-xl font-bold" style={{ color: COLORS.textPrimary }}>
                      {categoryData.name}
                    </h3>
                    <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                      {categoryData.description}
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-1">
                  {/* Move Up */}
                  <button
                    onClick={() => onMoveUp(categoryId)}
                    disabled={isFirst}
                    className={`p-2 rounded transition-all ${
                      isFirst
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-gray-100'
                    }`}
                    style={{ color: isFirst ? undefined : COLORS.textSecondary }}
                    title="Move Up"
                  >
                    <ChevronUp size={18} />
                  </button>

                  {/* Move Down */}
                  <button
                    onClick={() => onMoveDown(categoryId)}
                    disabled={isLast}
                    className={`p-2 rounded transition-all ${
                      isLast
                        ? 'text-gray-300 cursor-not-allowed'
                        : 'hover:bg-gray-100'
                    }`}
                    style={{ color: isLast ? undefined : COLORS.textSecondary }}
                    title="Move Down"
                  >
                    <ChevronDown size={18} />
                  </button>

                  {/* Edit */}
                  <button
                    onClick={() => onEditCategory(categoryId)}
                    className="p-2 hover:bg-gray-100 rounded transition-all"
                    style={{ color: COLORS.textSecondary }}
                    title="Edit Category"
                  >
                    <Edit2 size={18} />
                  </button>

                  {/* Delete (admin can delete any category; backend refuses if attributes still attached) */}
                  <button
                    onClick={() => onDeleteCategory(categoryId)}
                    className="p-2 hover:bg-red-50 rounded transition-all text-red-600"
                    title="Delete Category"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>

              {/* Stats */}
              <div className="flex items-center gap-6 pt-4 border-t" style={{ borderColor: COLORS.border }}>
                <div>
                  <p className="text-2xl font-bold" style={{ color: COLORS.maroon }}>
                    {attributeCount}
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                    Attribute{attributeCount !== 1 ? 's' : ''}
                  </p>
                </div>

                <div>
                  <p className="text-2xl font-bold" style={{ color: COLORS.textSecondary }}>
                    #{categoryData.order}
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                    Order in Prompt
                  </p>
                </div>

                <div className="ml-auto">
                  {categoryData.enabled ? (
                    <span className="px-3 py-1 bg-green-50 text-green-700 text-xs font-semibold rounded-full">
                      ✓ Enabled
                    </span>
                  ) : (
                    <span className="px-3 py-1 bg-gray-100 text-gray-500 text-xs font-semibold rounded-full">
                      Disabled
                    </span>
                  )}
                </div>
              </div>

              {/* Behavior flags — click to toggle */}
              <div className="mt-3 pt-3 border-t flex flex-wrap gap-2" style={{ borderColor: COLORS.border }}>
                {[
                  { key: 'aiGenerated',          label: 'AI Generated', emoji: '🤖', activeBg: '#DCFCE7', activeFg: '#15803D' },
                  { key: 'editableInEnrichment', label: 'Modal Tab',    emoji: '✍️',  activeBg: '#DBEAFE', activeFg: '#1D4ED8' },
                  { key: 'perVariant',           label: 'Per-Variant',  emoji: '🧩', activeBg: '#FEF3C7', activeFg: '#B45309' },
                ].map(flag => {
                  const active = !!categoryData[flag.key];
                  return (
                    <button
                      key={flag.key}
                      type="button"
                      onClick={() => onToggleCategoryFlag && onToggleCategoryFlag(categoryId, flag.key)}
                      className="text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors hover:opacity-90"
                      style={{
                        backgroundColor: active ? flag.activeBg : 'white',
                        color:           active ? flag.activeFg : COLORS.textSecondary,
                        borderColor:     active ? flag.activeFg : COLORS.border,
                      }}
                      title={active ? `Disable ${flag.label}` : `Enable ${flag.label}`}
                    >
                      {flag.emoji} {flag.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed rounded-xl" style={{ borderColor: COLORS.border }}>
          <Database size={64} className="mx-auto mb-4 opacity-20" />
          <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            No Categories
          </h3>
          <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
            Create your first category to organize attributes
          </p>
          <button
            onClick={onCreateCategory}
            className="inline-flex items-center gap-2 px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
            style={{ backgroundColor: COLORS.maroon }}
          >
            <Plus size={18} />
            Create First Category
          </button>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// CONFIGURATION PAGE - SECTION 3: ATTRIBUTES TAB
// =============================================================================
// Add this component after the CategoriesTab component

const AttributesTab = ({
  categories,
  attributes,
  expandedCategories,
  onToggleCategory,
  onCreateAttribute,
  onEditAttribute,
  onDeleteAttribute,
  onToggleAttribute,
  onToggleMultiSelect,
  onBulkSetEnabled,
  getAttributesByCategory
}) => {
  const [recheckLoading, setRecheckLoading] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const [filters, setFilters] = React.useState({ enabled: false, disabled: false, required: false, aiContext: false });
  const [selected, setSelected] = React.useState(() => new Set());

  const toggleFilter = (key) => setFilters(prev => ({ ...prev, [key]: !prev[key] }));
  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search.trim() ? 1 : 0);

  const matchesFilters = React.useCallback(([attrName, attrConfig]) => {
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      const hay = [
        attrName,
        attrConfig.fieldKey,
        attrConfig.dbColumn,
        attrConfig.promptInstruction,
        ...(attrConfig.values || []),
      ].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (filters.enabled && !attrConfig.enabled) return false;
    if (filters.disabled && attrConfig.enabled) return false;
    if (filters.required && !attrConfig.required) return false;
    if (filters.aiContext && !attrConfig.ai_input) return false;
    return true;
  }, [search, filters]);

  const getFilteredAttributes = React.useCallback((categoryId) => {
    return getAttributesByCategory(categoryId).filter(matchesFilters);
  }, [getAttributesByCategory, matchesFilters]);

  const toggleSelect = (name) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      return next;
    });
  };

  const selectAllInCategory = (categoryId, checked) => {
    const names = getFilteredAttributes(categoryId).map(([n]) => n);
    setSelected(prev => {
      const next = new Set(prev);
      if (checked) names.forEach(n => next.add(n));
      else names.forEach(n => next.delete(n));
      return next;
    });
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulk = async (enabled) => {
    const names = Array.from(selected);
    await onBulkSetEnabled(names, enabled);
    clearSelection();
  };

  const filterChip = (key, label) => (
    <button
      key={key}
      onClick={() => toggleFilter(key)}
      className="px-3 py-1.5 text-xs font-semibold rounded-full border transition-all"
      style={{
        borderColor: filters[key] ? COLORS.maroon : COLORS.border,
        backgroundColor: filters[key] ? COLORS.maroon : 'white',
        color: filters[key] ? 'white' : COLORS.textPrimary,
      }}
    >
      {label}
    </button>
  );

  return (
    <div className="space-y-6">
      {/* Search + Filter Toolbar */}
      <div
        className="sticky top-0 z-20 bg-white border rounded-xl p-4 flex flex-wrap items-center gap-3"
        style={{ borderColor: COLORS.border, boxShadow: '0 2px 8px rgba(45,42,46,0.04)' }}
      >
        <div className="flex-1 min-w-[240px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: COLORS.textSecondary }} />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search attributes by name, fieldKey, values…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm focus:outline-none focus:border-2"
            style={{ borderColor: COLORS.border }}
          />
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {filterChip('enabled', '● Enabled')}
          {filterChip('disabled', '○ Disabled')}
          {filterChip('required', '⭐ Required')}
          {filterChip('aiContext', '🧠 AI context')}
          {activeFilterCount > 0 && (
            <button
              onClick={() => { setSearch(''); setFilters({ enabled: false, disabled: false, required: false, aiContext: false }); }}
              className="px-3 py-1.5 text-xs font-semibold rounded-full underline"
              style={{ color: COLORS.textSecondary }}
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Bulk Action Bar — shows when any attributes are selected */}
      {selected.size > 0 && (() => {
        const visibleSelectedCount = categories.reduce((acc, [cid]) =>
          acc + getFilteredAttributes(cid).filter(([n]) => selected.has(n)).length, 0);
        const hiddenCount = selected.size - visibleSelectedCount;
        return (
        <div
          className="sticky top-[72px] z-20 flex items-center justify-between rounded-xl border px-4 py-3"
          style={{ borderColor: COLORS.maroon, background: '#FFF8F1' }}
        >
          <div className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
            {selected.size} selected
            {hiddenCount > 0 && (
              <span className="ml-2 font-normal" style={{ color: COLORS.textSecondary }}>
                ({hiddenCount} hidden by filter)
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleBulk(true)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all hover:bg-green-50"
              style={{ borderColor: '#86EFAC', color: '#15803D' }}
            >
              ● Enable
            </button>
            <button
              onClick={() => handleBulk(false)}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg border transition-all hover:bg-gray-100"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              ○ Disable
            </button>
            <button
              onClick={clearSelection}
              className="px-3 py-1.5 text-xs font-semibold rounded-lg transition-all hover:bg-white"
              style={{ color: COLORS.textSecondary }}
            >
              Clear
            </button>
          </div>
        </div>
        );
      })()}

      {/* Category Sections */}
      {categories.map(([categoryId, categoryData]) => {
        const categoryAttributes = getFilteredAttributes(categoryId);
        const isExpanded = expandedCategories[categoryId];
        const filteredCount = categoryAttributes.length;
        const totalCount = getAttributesByCategory(categoryId).length;
        const selectedInCategory = categoryAttributes.filter(([n]) => selected.has(n)).length;
        const allSelectedInCategory = filteredCount > 0 && selectedInCategory === filteredCount;

        // Hide categories with zero matches when filters are active.
        if (activeFilterCount > 0 && filteredCount === 0) return null;

        return (
          <div
            key={categoryId}
            className="bg-white border rounded-xl overflow-hidden"
            style={{ borderColor: COLORS.border }}
          >
            {/* Category Header */}
            <div
              className="flex items-center justify-between p-6 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => onToggleCategory(categoryId)}
              style={{ backgroundColor: isExpanded ? COLORS.bgLight : 'white' }}
            >
              <div className="flex items-center gap-3">
                <span className="text-2xl">{categoryData.emoji}</span>
                <div>
                  <h3 className="font-serif text-lg font-bold" style={{ color: COLORS.textPrimary }}>
                    {categoryData.name}
                  </h3>
                  <p className="text-xs mt-0.5" style={{ color: COLORS.textSecondary }}>
                    {activeFilterCount > 0 && filteredCount !== totalCount
                      ? `${filteredCount} of ${totalCount} attribute${totalCount !== 1 ? 's' : ''}`
                      : `${totalCount} attribute${totalCount !== 1 ? 's' : ''}`}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {categoryData.source === 'manual' && (
                  <button
                    disabled={recheckLoading}
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (!window.confirm('Re-validate all stuck designs against current core field requirements?\n\nThis will trigger AI processing for designs that now meet all requirements.')) return;
                      setRecheckLoading(true);
                      try {
                        const res = await config.apiFetch(config.buildUrl('/api/config/recheck-enrichment'), { method: 'POST' });
                        const data = await res.json();
                        if (data.success) {
                          alert(`Rechecked ${data.rechecked} designs.\n${data.triggered} triggered for AI processing.\n${data.stillIncomplete} still incomplete.`);
                        } else {
                          alert('Recheck failed: ' + (data.error || 'Unknown error'));
                        }
                      } catch (err) {
                        alert('Recheck failed: ' + err.message);
                      }
                      setRecheckLoading(false);
                    }}
                    className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg border transition-all hover:bg-blue-50 disabled:opacity-50"
                    style={{ color: '#3B82F6', borderColor: '#93C5FD' }}
                  >
                    <RefreshCw size={14} className={recheckLoading ? 'animate-spin' : ''} />
                    {recheckLoading ? 'Rechecking...' : 'Auto Recheck Enrichment'}
                  </button>
                )}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onCreateAttribute(categoryId);
                  }}
                  className="flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg border transition-all hover:bg-white"
                  style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                >
                  <Plus size={14} />
                  Add Attribute
                </button>

                {isExpanded ? (
                  <ChevronUp size={20} style={{ color: COLORS.textSecondary }} />
                ) : (
                  <ChevronDown size={20} style={{ color: COLORS.textSecondary }} />
                )}
              </div>
            </div>

            {/* Attributes List */}
            {isExpanded && (
              <div className="border-t" style={{ borderColor: COLORS.border }}>
                {categoryAttributes.length === 0 ? (
                  <div className="text-center py-12" style={{ backgroundColor: COLORS.bgLight }}>
                    <p className="text-sm mb-4" style={{ color: COLORS.textSecondary }}>
                      No attributes in this category
                    </p>
                    <button
                      onClick={() => onCreateAttribute(categoryId)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold rounded-lg border transition-all hover:bg-white"
                      style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
                    >
                      <Plus size={14} />
                      Add First Attribute
                    </button>
                  </div>
                ) : (
                  <div className="divide-y" style={{ borderColor: COLORS.border }}>
                    {/* Select-all row — lets admin flip every visible attribute at once */}
                    <div
                      className="flex items-center gap-3 px-4 py-2 text-xs"
                      style={{ backgroundColor: COLORS.bgLight, color: COLORS.textSecondary }}
                    >
                      <input
                        type="checkbox"
                        checked={allSelectedInCategory}
                        ref={(el) => {
                          if (el) el.indeterminate = selectedInCategory > 0 && !allSelectedInCategory;
                        }}
                        onChange={(e) => selectAllInCategory(categoryId, e.target.checked)}
                        className="w-4 h-4 cursor-pointer"
                        style={{ accentColor: COLORS.maroon }}
                      />
                      <span className="font-semibold">
                        {selectedInCategory > 0 ? `${selectedInCategory} selected` : 'Select all'}
                      </span>
                    </div>
                    {categoryAttributes.map(([attrName, attrConfig]) => (
                      <div
                        key={attrName}
                        className="p-4 hover:bg-gray-50 transition-colors"
                        style={selected.has(attrName) ? { backgroundColor: '#FFF8F1' } : undefined}
                      >
                        <div className="flex items-start justify-between">
                          {/* Selection checkbox */}
                          <input
                            type="checkbox"
                            checked={selected.has(attrName)}
                            onChange={() => toggleSelect(attrName)}
                            className="w-4 h-4 mt-1 mr-3 cursor-pointer"
                            style={{ accentColor: COLORS.maroon }}
                          />

                          {/* Left: Attribute Info */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h4 className="font-semibold text-base" style={{ color: COLORS.textPrimary }}>
                                {attrName}
                              </h4>

                              {/* Badges */}
                              <div className="flex items-center gap-2">
                                {/* Enabled/Disabled Toggle */}
                                <button
                                  onClick={() => onToggleAttribute(attrName)}
                                  className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                                    attrConfig.enabled
                                      ? 'bg-green-50 text-green-700 hover:bg-green-100'
                                      : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                  }`}
                                >
                                  {attrConfig.enabled ? '● Enabled' : '○ Disabled'}
                                </button>

                                {/* Multi-Select Toggle */}
                                {attrConfig.configurable && (
                                  <button
                                    onClick={() => onToggleMultiSelect(attrName)}
                                    className={`px-2 py-1 text-xs font-semibold rounded transition-all ${
                                      attrConfig.multiSelect
                                        ? 'bg-blue-50 text-blue-700 hover:bg-blue-100'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                    }`}
                                  >
                                    {attrConfig.multiSelect ? 'Multi-Select' : 'Single-Select'}
                                  </button>
                                )}

                                {/* Configurable Badge */}
                                {!attrConfig.configurable && (
                                  <span className="px-2 py-1 text-xs font-semibold bg-purple-50 text-purple-700 rounded">
                                    AI-Only
                                  </span>
                                )}

                                {/* Behavior Type Badge */}
                                <span
                                  className="px-2 py-1 text-xs font-semibold rounded"
                                  style={{
                                    backgroundColor: attrConfig.behaviorType === 'free-form' ? '#FFF4E6' : '#E8F5E9',
                                    color: attrConfig.behaviorType === 'free-form' ? '#E65100' : '#2E7D32'
                                  }}
                                >
                                  {attrConfig.behaviorType === 'free-form' ? 'Free-form' : 'Provided List'}
                                </span>

                                {/* AI Context badge — value feeds the AI prompt as reference */}
                                {attrConfig.ai_input && (
                                  <span
                                    className="px-2 py-1 text-xs font-semibold rounded"
                                    style={{ backgroundColor: '#EEF2FF', color: '#4338CA' }}
                                    title="Value is injected into the AI prompt as context"
                                  >
                                    🧠 AI context
                                  </span>
                                )}

                                {/* Required badge */}
                                {attrConfig.required && (
                                  <span
                                    className="px-2 py-1 text-xs font-semibold rounded"
                                    style={{ backgroundColor: '#FEF3C7', color: '#92400E' }}
                                    title="Required in the enrichment modal"
                                  >
                                    ⭐ Required
                                  </span>
                                )}
                              </div>
                            </div>

                            {/* Values or Instruction */}
                            <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                              {attrConfig.behaviorType === 'free-form' ? (
                                <div>
                                  <span className="font-semibold">Instruction:</span>{' '}
                                  {attrConfig.promptInstruction || 'None'}
                                </div>
                              ) : (
                                <div>
                                  <span className="font-semibold">Values ({attrConfig.values?.length || 0}):</span>{' '}
                                  {attrConfig.values?.length > 0
                                    ? attrConfig.values.slice(0, 5).join(', ') +
                                      (attrConfig.values.length > 5 ? ` +${attrConfig.values.length - 5} more` : '')
                                    : 'No values'}
                                </div>
                              )}
                            </div>

                            {/* Category Order */}
                            <div className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                              <span className="font-semibold">Order:</span> #{attrConfig.categoryOrder || 999}
                            </div>
                          </div>

                          {/* Right: Action Buttons */}
                          <div className="flex items-center gap-2 ml-4">
                            <button
                              onClick={() => onEditAttribute(attrName)}
                              className="p-2 hover:bg-gray-100 rounded transition-all"
                              style={{ color: COLORS.textSecondary }}
                              title="Edit"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => onDeleteAttribute(attrName)}
                              className="p-2 hover:bg-red-50 rounded transition-all text-red-600"
                              title="Delete"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}

      {/* Empty State */}
      {categories.length === 0 && (
        <div className="text-center py-20 border-2 border-dashed rounded-xl" style={{ borderColor: COLORS.border }}>
          <Database size={64} className="mx-auto mb-4 opacity-20" />
          <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            No Categories
          </h3>
          <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
            Create a category first before adding attributes
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// CONFIGURATION PAGE - SECTION 4: CATEGORY MODAL
// =============================================================================
// Add this component after the AttributesTab component

const CategoryModal = ({ show, category, editMode, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [emoji, setEmoji] = useState('📌');
  const [description, setDescription] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [aiGenerated, setAiGenerated] = useState(false);
  const [editableInEnrichment, setEditableInEnrichment] = useState(false);
  const [perVariant, setPerVariant] = useState(false);

  // XOR enforcement: Modal Tab and Per-Variant cannot both be on.
  const setEditableInEnrichmentXor = (val) => {
    setEditableInEnrichment(val);
    if (val) setPerVariant(false);
  };
  const setPerVariantXor = (val) => {
    setPerVariant(val);
    if (val) setEditableInEnrichment(false);
  };

  // Common emojis for quick selection
  const commonEmojis = ['📦', '🎯', '💡', '📱', '🎨', '⚙️', '🏷️', '📊', '🎭', '💎', '🌟', '🔧', '📝', '🎪', '🎬'];

  useEffect(() => {
    if (category) {
      setName(category.name || '');
      setEmoji(category.emoji || '📌');
      setDescription(category.description || '');
      setEnabled(category.enabled !== undefined ? category.enabled : true);
      setAiGenerated(!!category.aiGenerated);
      setEditableInEnrichment(!!category.editableInEnrichment);
      setPerVariant(!!category.perVariant);
    } else {
      setName('');
      setEmoji('📌');
      setDescription('');
      setEnabled(true);
      setAiGenerated(true);
      setEditableInEnrichment(false);
      setPerVariant(false);
    }
  }, [category, show]);

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!name.trim()) {
      notify.error('Category name is required');
      return;
    }

    onSave({
      name: name.trim(),
      emoji,
      description: description.trim(),
      enabled,
      aiGenerated,
      editableInEnrichment,
      perVariant,
    });
  };

  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b" style={{ borderColor: COLORS.border }}>
          <h3 className="text-2xl font-bold" style={{ color: COLORS.textPrimary }}>
            {editMode ? 'Edit Category' : 'Create New Category'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
            style={{ color: COLORS.textSecondary }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Category Name */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Category Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Advanced Attributes"
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
              style={{ borderColor: COLORS.border }}
              required
            />
          </div>

          {/* Emoji Selector */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Emoji Icon
            </label>
            <div className="flex items-center gap-4">
              {/* Selected Emoji Display */}
              <div
                className="flex items-center justify-center w-20 h-20 border-2 rounded-lg text-4xl"
                style={{ borderColor: COLORS.maroon }}
              >
                {emoji}
              </div>

              {/* Quick Select Grid */}
              <div className="flex-1">
                <div className="grid grid-cols-10 gap-2">
                  {commonEmojis.map((e) => (
                    <button
                      key={e}
                      type="button"
                      onClick={() => setEmoji(e)}
                      className={`w-10 h-10 rounded-lg text-2xl transition-all hover:scale-110 ${
                        emoji === e ? 'ring-2' : 'hover:bg-gray-100'
                      }`}
                      style={emoji === e ? { ringColor: COLORS.maroon } : {}}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Custom Emoji Input */}
            <div className="mt-3">
              <input
                type="text"
                value={emoji}
                onChange={(e) => setEmoji(e.target.value)}
                placeholder="Or type any emoji"
                className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-2"
                style={{ borderColor: COLORS.border }}
                maxLength={2}
              />
              <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                Select from above or paste any emoji
              </p>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of what this category contains"
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2 resize-none"
              style={{ borderColor: COLORS.border }}
              rows={3}
            />
          </div>

          {/* Enabled Toggle — master on/off for the category. Whether it's
              injected into the AI prompt is the separate "🤖 AI Generated"
              flag in the Category Behavior section below. */}
          <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: COLORS.border }}>
            <input
              type="checkbox"
              id="category-enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded"
              style={{ accentColor: COLORS.maroon }}
            />
            <label htmlFor="category-enabled" className="text-sm">
              <div className="font-semibold" style={{ color: COLORS.textPrimary }}>
                Enabled
              </div>
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                When off, this category is hidden everywhere — Configuration list, AI prompt, and the enrichment modal. Use the behavior flags below to control where an enabled category appears.
              </div>
            </label>
          </div>

          {/* Behavior Flags */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Category Behavior
            </label>
            <div className="space-y-2">
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ borderColor: COLORS.border }}>
                <input
                  type="checkbox"
                  checked={aiGenerated}
                  onChange={(e) => setAiGenerated(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>🤖 AI Generated</p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>Attributes here go to the AI prompt.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ borderColor: COLORS.border }}>
                <input
                  type="checkbox"
                  checked={editableInEnrichment}
                  onChange={(e) => setEditableInEnrichmentXor(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>✍️ Modal Tab</p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>Renders as a tab in the design-level enrichment modal.</p>
                </div>
              </label>

              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ borderColor: COLORS.border }}>
                <input
                  type="checkbox"
                  checked={perVariant}
                  onChange={(e) => setPerVariantXor(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>🧩 Per-Variant</p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>Renders as columns in the Combination table (per-variant editing).</p>
                </div>
              </label>
            </div>
            <p className="text-xs mt-2" style={{ color: COLORS.textSecondary }}>
              Modal Tab and Per-Variant are mutually exclusive.
            </p>
          </div>

          {/* Info Box */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.bgLight }}>
            <div className="flex gap-3">
              <Info size={20} style={{ color: COLORS.maroon, flexShrink: 0, marginTop: 2 }} />
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                <p className="font-semibold mb-1">Category Organization</p>
                <p>
                  Categories group related attributes together in the AI prompt. Attributes in this category
                  will appear under the "{name || 'New Category'}" section when generating design data.
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end pt-4 border-t" style={{ borderColor: COLORS.border }}>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border text-sm font-semibold rounded-lg transition-all hover:bg-gray-50"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {editMode ? 'Update Category' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// CONFIGURATION PAGE - SECTION 5A: ATTRIBUTE MODAL (Part 1/2)
// =============================================================================
// Add this component after the CategoryModal component

// Compact per-Product-Type chip row for the AttributeModal matrix editor.
const PtValueRow = ({ pt, values, onAdd, onRemove }) => {
  const [draft, setDraft] = React.useState('');
  return (
    <div className="border rounded-lg p-3" style={{ borderColor: COLORS.border }}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>{pt}</span>
        <span className="text-xs" style={{ color: COLORS.textSecondary }}>{values.length} value{values.length === 1 ? '' : 's'}</span>
      </div>
      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(draft);
              setDraft('');
            }
          }}
          placeholder={`Add value for ${pt}…`}
          className="flex-1 px-3 py-1.5 border rounded-lg text-sm focus:outline-none focus:border-2"
          style={{ borderColor: COLORS.border }}
        />
        <button
          type="button"
          onClick={() => { onAdd(draft); setDraft(''); }}
          className="px-3 py-1.5 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
          style={{ backgroundColor: COLORS.maroon }}
        >
          <Plus size={16} />
        </button>
      </div>
      {values.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {values.map((v, i) => (
            <div
              key={i}
              className="inline-flex items-center gap-1.5 px-2 py-0.5 border rounded text-xs"
              style={{ borderColor: COLORS.maroon, backgroundColor: COLORS.bgLight }}
            >
              <span style={{ color: COLORS.textPrimary }}>{v}</span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                className="text-red-600 hover:text-red-800"
              >
                <X size={12} />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-xs" style={{ color: COLORS.textSecondary }}>No values for {pt} yet.</p>
      )}
    </div>
  );
};

const AttributeModal = ({ show, attribute, attributes: allAttributes, categories, selectedCategory, editMode, itemNameTemplate, onSaveItemNameTemplate, onClose, onSave }) => {
  const confirm = useConfirm();
  const productTypeValues = React.useMemo(() => {
    const pt = (allAttributes || {})['Product Type'];
    return (pt && Array.isArray(pt.values)) ? pt.values : [];
  }, [allAttributes]);

  const confirmEmpty = () => confirm({
    title: 'No values added',
    message: "You haven't added any values for this attribute. Continue anyway?",
    confirmLabel: 'Continue',
    cancelLabel: 'Go back',
  });

  const [name, setName] = useState('');
  const [category, setCategory] = useState('primary');
  const [categoryOrder, setCategoryOrder] = useState(999);
  const [enabled, setEnabled] = useState(true);
  const [multiSelect, setMultiSelect] = useState(false);
  const [configurable, setConfigurable] = useState(true);
  const [behaviorType, setBehaviorType] = useState('provided-list');
  const [promptInstruction, setPromptInstruction] = useState('');
  const [values, setValues] = useState([]);
  const [newValue, setNewValue] = useState('');
  const [showCreateCategory, setShowCreateCategory] = useState(false);
  const [core, setCore] = useState(false);
  const [dataType, setDataType] = useState('string');
  const [fieldKey, setFieldKey] = useState('');
  const [dbColumn, setDbColumn] = useState('');
  const [required, setRequired] = useState(false);
  const [aiInput, setAiInput] = useState(false);
  // Partition-axis model: admin picks an attribute whose values become the axis.
  // Legacy valuesByProductType migrates into axis="productType" on load.
  const [axisFieldKey, setAxisFieldKey] = useState(''); // '' = flat values
  const [valuesByAxis, setValuesByAxis] = useState({}); // { axisValue: [values...] }
  // Item Name composition template — shown only for the Item Name attribute.
  // Backed by the global itemNameTemplate setting, not by this attribute's config.
  const [nameFormat, setNameFormat] = useState('');

  useEffect(() => {
    if (attribute) {
      // Edit mode
      setName(attribute.name || '');
      setCategory(attribute.category || 'primary');
      setCategoryOrder(attribute.categoryOrder || 999);
      setEnabled(attribute.enabled !== undefined ? attribute.enabled : true);
      setMultiSelect(attribute.multiSelect || false);
      setConfigurable(attribute.configurable !== undefined ? attribute.configurable : true);
      setBehaviorType(attribute.behaviorType || 'provided-list');
      setPromptInstruction(attribute.promptInstruction || '');
      setValues(attribute.values || []);
      setCore(attribute.core || false);
      setDataType(attribute.dataType || 'string');
      setFieldKey(attribute.fieldKey || '');
      setDbColumn(attribute.dbColumn || '');
      setRequired(!!attribute.required);
      setAiInput(!!attribute.ai_input);
      // Load axis config: new valuesByAxis takes precedence, legacy valuesByProductType migrates.
      if (attribute.valuesByAxis && attribute.valuesByAxis.axis) {
        setAxisFieldKey(attribute.valuesByAxis.axis);
        setValuesByAxis(attribute.valuesByAxis.values || {});
      } else if (attribute.valuesByProductType && Object.keys(attribute.valuesByProductType).length > 0) {
        setAxisFieldKey('productType');
        setValuesByAxis(attribute.valuesByProductType);
      } else {
        setAxisFieldKey('');
        setValuesByAxis({});
      }
    } else {
      // Create mode
      setName('');
      setCategory(selectedCategory?.id || 'primary');
      setCategoryOrder(999);
      setEnabled(true);
      setMultiSelect(false);
      setConfigurable(true);
      setBehaviorType('provided-list');
      setPromptInstruction('');
      setValues([]);
      setCore(false);
      setDataType('string');
      setFieldKey('');
      setDbColumn('');
      setRequired(false);
      setAiInput(false);
      setAxisFieldKey('');
      setValuesByAxis({});
    }
    setNameFormat(attribute ? (itemNameTemplate || '') : '');
    setNewValue('');
    setShowCreateCategory(false);
  }, [attribute, selectedCategory, show, itemNameTemplate]);

  const handleAddValue = () => {
    if (!newValue.trim()) return;

    if (values.includes(newValue.trim())) {
      notify.info('This value already exists');
      return;
    }

    setValues([...values, newValue.trim()]);
    setNewValue('');
  };

  const handleRemoveValue = (index) => {
    setValues(values.filter((_, i) => i !== index));
  };

  // Axis value editing helpers (works for any partition axis)
  const handleAddAxisValue = (axisValue, val) => {
    const trimmed = (val || '').trim();
    if (!trimmed) return;
    const existing = valuesByAxis[axisValue] || [];
    if (existing.includes(trimmed)) {
      notify.info('This value already exists for ' + axisValue);
      return;
    }
    setValuesByAxis({ ...valuesByAxis, [axisValue]: [...existing, trimmed] });
  };

  const handleRemoveAxisValue = (axisValue, index) => {
    const next = (valuesByAxis[axisValue] || []).filter((_, i) => i !== index);
    setValuesByAxis({ ...valuesByAxis, [axisValue]: next });
  };

  // Candidate axes: any attribute with a non-empty flat values list (except self).
  const axisCandidates = React.useMemo(() => {
    return Object.entries(allAttributes || {})
      .filter(([n, a]) => a && a.fieldKey && Array.isArray(a.values) && a.values.length > 0 && n !== (attribute?.name || name))
      .map(([n, a]) => ({ name: n, fieldKey: a.fieldKey, values: a.values }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allAttributes, attribute, name]);

  const selectedAxis = axisCandidates.find(c => c.fieldKey === axisFieldKey);
  const usingAxis = !!axisFieldKey && !!selectedAxis;

  // The Item Name attribute owns the global name-composition template.
  const isItemNameAttr = !!attribute && (attribute.fieldKey === 'itemName' || attribute.dbColumn === 'item_name');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim()) {
      notify.error('Attribute name is required');
      return;
    }

    if (behaviorType === 'provided-list' && !usingAxis && values.length === 0 && configurable) {
      const ok = await confirmEmpty();
      if (!ok) return;
    }

    const autoFieldKey = fieldKey || name.trim().replace(/\s+/g, '').replace(/^(.)/, c => c.toLowerCase());
    let autoDbColumn = dbColumn || autoFieldKey.replace(/([A-Z])/g, '_$1').toLowerCase();

    // Per-variant (combination) attributes must map to a real variant column or
    // the per-variant JSONB bucket — never an invented column (the backend guard
    // rejects those). For a new custom field that isn't one of the four ERP
    // columns, route it to the '_variant_meta' bucket so it stores per-variant.
    const RESERVED_VARIANT_COLUMNS = ['color', 'sub_color', 'polish', 'size'];
    const isPerVariantCategory = !!(categories[category] && categories[category].perVariant);
    if (isPerVariantCategory && !RESERVED_VARIANT_COLUMNS.includes(autoDbColumn)) {
      autoDbColumn = '_variant_meta';
    }

    const attributeData = {
      name: name.trim(),
      enabled,
      multiSelect,
      configurable,
      category,
      categoryOrder: parseInt(categoryOrder) || 999,
      promptInstruction: promptInstruction.trim(),
      behaviorType,
      values: (behaviorType === 'provided-list' && !usingAxis) ? values : [],
      valuesByAxis: (behaviorType === 'provided-list' && usingAxis)
        ? { axis: axisFieldKey, values: valuesByAxis }
        : {},
      // Keep legacy key in sync when axis is Product Type so older readers still work.
      valuesByProductType: (behaviorType === 'provided-list' && usingAxis && axisFieldKey === 'productType')
        ? valuesByAxis
        : {},
      // Per-variant attributes must be core: only core attributes are surfaced by
      // /api/attributes/core, which drives the Combination Details tab, the combo
      // write/read paths, and completeness. A non-core per-variant attr would save
      // but never render.
      core: isPerVariantCategory ? true : core,
      dataType,
      fieldKey: autoFieldKey,
      dbColumn: autoDbColumn,
      required,
      ai_input: aiInput,
    };

    if (isItemNameAttr && onSaveItemNameTemplate) {
      await onSaveItemNameTemplate(nameFormat);
    }

    onSave(attributeData);
  };

  if (!show) return null;

  const sortedCategories = Object.entries(categories).sort((a, b) => a[1].order - b[1].order);

  // Category-aware field visibility. These flags are set per-category in Configuration
  // (🤖 AI / ✍️ Modal Tab / 🧩 Per-Variant) and determine which attribute-modal fields
  // are meaningful. Required only gates user input → hidden on AI categories.
  // AI Behavior / Prompt Instruction only apply to AI categories.
  const selectedCat = categories[category] || {};
  const isAICategory   = !!selectedCat.aiGenerated;
  const isUserEditableCategory = !!(selectedCat.editableInEnrichment || selectedCat.perVariant);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b sticky top-0 bg-white z-10" style={{ borderColor: COLORS.border }}>
          <h3 className="text-2xl font-bold" style={{ color: COLORS.textPrimary }}>
            {editMode ? 'Edit Attribute' : 'Create New Attribute'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-all"
            style={{ color: COLORS.textSecondary }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Attribute Name */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Attribute Name *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Stone Weight"
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
              style={{ borderColor: COLORS.border }}
              required
            />
            {editMode && (
              <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                Renaming keeps all enrichment data (it's keyed by Field Key, not the name).
              </p>
            )}
          </div>

          {/* Name Format — composition template, only for the Item Name attribute.
              Saved to the global itemNameTemplate setting; auto-composes item_name on Save & Process. */}
          {isItemNameAttr && (
            <div>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Name Format
              </label>
              <input
                type="text"
                value={nameFormat}
                onChange={(e) => setNameFormat(e.target.value)}
                placeholder="{category} {productType}"
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
                style={{ borderColor: COLORS.border }}
              />
              <p className="text-xs mt-1 leading-relaxed" style={{ color: COLORS.textSecondary }}>
                Auto-composed on save from placeholders <code>{'{category}'}</code>, <code>{'{productType}'}</code>, <code>{'{brand}'}</code>.
                Leave blank to keep manually-entered names. Skipped when any referenced field is empty (the existing name is kept).
              </p>
            </div>
          )}

          {/* Category Selection */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Category *
            </label>
            <select
              value={category}
              onChange={(e) => {
                if (e.target.value === '__create_new__') {
                  setShowCreateCategory(true);
                } else {
                  setCategory(e.target.value);
                }
              }}
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
              style={{ borderColor: COLORS.border }}
            >
              {sortedCategories.map(([catId, catData]) => (
                <option key={catId} value={catId}>
                  {catData.emoji} {catData.name}
                </option>
              ))}
              <option value="__create_new__" style={{ borderTop: '1px solid #ddd' }}>
                + Create New Category
              </option>
            </select>
          </div>

          {/* Category Order */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Order in Category
            </label>
            <input
              type="number"
              value={categoryOrder}
              onChange={(e) => setCategoryOrder(e.target.value)}
              placeholder="999"
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
              style={{ borderColor: COLORS.border }}
              min="1"
            />
            <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
              Lower numbers appear first. Default is 999.
            </p>
          </div>

          {/* Required field — validation flag for the enrichment form, independent of
              Core/DB-backed status, so it lives OUTSIDE the {!core} block below.
              Only meaningful for user-editable categories (Modal Tab / Per-Variant). */}
          {isUserEditableCategory && (
            <div>
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ borderColor: COLORS.border }}>
                <input
                  type="checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                    ⭐ Required
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                    When on, the enrichment modal marks this field with * and blocks Save &amp; Process until it&apos;s filled.
                  </p>
                </div>
              </label>
            </div>
          )}

          {/* Selection Type — applies to ALL attributes (core or metadata, AI-generated
              or user-editable). Controls how many values the enrichment form accepts and,
              for AI-generated categories, how many values the model is asked to return. */}
          <div className="border-t pt-6" style={{ borderColor: COLORS.border }}>
            <h4 className="font-bold text-base mb-4" style={{ color: COLORS.textPrimary }}>
              Selection Type
            </h4>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
                <input
                  type="radio"
                  checked={!multiSelect}
                  onChange={() => setMultiSelect(false)}
                  className="w-4 h-4"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <div className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                    Single Select
                  </div>
                  <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                    One value only
                  </div>
                </div>
              </label>

              <label className="flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
                <input
                  type="radio"
                  checked={multiSelect}
                  onChange={() => setMultiSelect(true)}
                  className="w-4 h-4"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <div className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                    Multi-Select
                  </div>
                  <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                    Allow multiple values
                  </div>
                </div>
              </label>
            </div>
          </div>

          {/* AI Settings — hidden for core attributes (ERP/manual only) */}
          {!core && (<>
          {/* AI Context — value is fed into the AI prompt as reference. Only useful for
              non-AI-generated categories (AI-generated attributes are outputs, not inputs). */}
          {!isAICategory && (
            <div>
              <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-gray-50" style={{ borderColor: COLORS.border }}>
                <input
                  type="checkbox"
                  checked={aiInput}
                  onChange={(e) => setAiInput(e.target.checked)}
                  className="w-5 h-5 mt-0.5"
                  style={{ accentColor: COLORS.maroon }}
                />
                <div>
                  <p className="text-sm font-semibold" style={{ color: COLORS.textPrimary }}>
                    🧠 Use as AI context
                  </p>
                  <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                    When on, this field&apos;s value is injected into the AI prompt so generated attributes stay consistent with what the specialist entered.
                  </p>
                </div>
              </label>
            </div>
          )}
{/* // =============================================================================
// CONFIGURATION PAGE - SECTION 5B: ATTRIBUTE MODAL (Part 2/2)
// =============================================================================
// CONTINUES FROM PART 1 - This is the rest of the AttributeModal component */}

          {/* AI Behavior Type — only for AI-generated categories */}
          {isAICategory && (
            <div>
              <label className="block text-sm font-bold mb-3" style={{ color: COLORS.textPrimary }}>
                AI Behavior
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
                  <input
                    type="radio"
                    checked={behaviorType === 'provided-list'}
                    onChange={() => setBehaviorType('provided-list')}
                    className="w-4 h-4"
                    style={{ accentColor: COLORS.maroon }}
                  />
                  <div>
                    <div className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                      Provided List
                    </div>
                    <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                      AI chooses from your values
                    </div>
                  </div>
                </label>

                <label className="flex items-center gap-2 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 flex-1">
                  <input
                    type="radio"
                    checked={behaviorType === 'free-form'}
                    onChange={() => setBehaviorType('free-form')}
                    className="w-4 h-4"
                    style={{ accentColor: COLORS.maroon }}
                  />
                  <div>
                    <div className="font-semibold text-sm" style={{ color: COLORS.textPrimary }}>
                      Free-form
                    </div>
                    <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                      AI generates its own text
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Configurable Toggle — controls whether admin can edit this
              attribute's value list from the Configuration page. Off for
              AI-generated outputs where values are produced by the model. */}
          <div className="flex items-start gap-3 p-4 border rounded-lg" style={{ borderColor: COLORS.border, backgroundColor: COLORS.bgLight }}>
            <input
              type="checkbox"
              id="attr-configurable"
              checked={configurable}
              onChange={(e) => setConfigurable(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded"
              style={{ accentColor: COLORS.maroon }}
            />
            <label htmlFor="attr-configurable" className="text-sm">
              <div className="font-semibold" style={{ color: COLORS.textPrimary }}>
                Allow value-list editing from Configuration
              </div>
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                When on, admins can add/remove values on this attribute&apos;s card. Turn off for free-form AI outputs where the model generates its own values.
              </div>
            </label>
          </div>

          {/* Enabled Toggle — master on/off for the attribute. Disabled attributes
              are hidden everywhere: Configuration list, AI prompt, enrichment modal. */}
          <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: COLORS.border }}>
            <input
              type="checkbox"
              id="attr-enabled"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="w-5 h-5 mt-0.5 rounded"
              style={{ accentColor: COLORS.maroon }}
            />
            <label htmlFor="attr-enabled" className="text-sm">
              <div className="font-semibold" style={{ color: COLORS.textPrimary }}>
                Enabled
              </div>
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                When off, this attribute is hidden everywhere — Configuration list, AI prompt schema, enrichment modal, and combination columns.
              </div>
            </label>
          </div>
          </>)}
          {/* End of AI-only settings */}

          {/* Core Attribute Settings — only for user-editable categories (Modal Tab / Per-Variant).
              "Core" means this attribute is backed by a real DB column (not metadata JSON) —
              it needs a fieldKey + dbColumn + data type. Required for enrichment is a SEPARATE
              flag (the ⭐ Required checkbox above). */}
          {isUserEditableCategory && (
          <div className="pt-4 border-t space-y-4" style={{ borderColor: COLORS.border }}>
            <div className="flex items-start gap-3 p-3 rounded-lg border" style={{ borderColor: COLORS.border }}>
              <input
                type="checkbox"
                id="attr-core"
                checked={core}
                onChange={(e) => setCore(e.target.checked)}
                className="w-5 h-5 mt-0.5 rounded"
                style={{ accentColor: COLORS.maroon }}
              />
              <label htmlFor="attr-core" className="text-sm">
                <div className="font-semibold" style={{ color: COLORS.textPrimary }}>
                  Core (backed by a real DB column)
                </div>
                <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                  When on, this attribute maps to its own column in <code>app_external_designs</code>. Requires a field key + DB column + data type below. Leave off for metadata-only fields stored as JSON.
                </div>
              </label>
            </div>
            {core && (
              <div className="grid grid-cols-3 gap-4 ml-8">
                <div>
                  <label className="block text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Data Type</label>
                  <select
                    value={dataType}
                    onChange={(e) => setDataType(e.target.value)}
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                    style={{ borderColor: COLORS.border }}
                  >
                    <option value="string">Text</option>
                    <option value="numeric">Number</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>Field Key</label>
                  <input
                    value={fieldKey}
                    onChange={(e) => setFieldKey(e.target.value)}
                    placeholder="e.g. hsnCode"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                    style={{ borderColor: COLORS.border }}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1 uppercase tracking-wider" style={{ color: COLORS.textSecondary }}>DB Column</label>
                  <input
                    value={dbColumn}
                    onChange={(e) => setDbColumn(e.target.value)}
                    placeholder="e.g. hsn_code"
                    className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                    style={{ borderColor: COLORS.border }}
                  />
                </div>
              </div>
            )}
          </div>
          )}

          {/* Conditional Content Based on Behavior Type */}
          {behaviorType === 'free-form' ? (
            // Free-form: Show prompt instruction
            <div className="pt-6 border-t" style={{ borderColor: COLORS.border }}>
              <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                Prompt Instruction
              </label>
              <textarea
                value={promptInstruction}
                onChange={(e) => setPromptInstruction(e.target.value)}
                placeholder="e.g., Suggest what outfit best complements this piece"
                className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2 resize-none"
                style={{ borderColor: COLORS.border }}
                rows={3}
              />
              <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                This instruction will guide the AI on what to generate for this field
              </p>

              {/* Preview */}
              <div className="mt-4 p-4 rounded-lg" style={{ backgroundColor: COLORS.bgLight }}>
                <p className="text-xs font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                  PROMPT PREVIEW:
                </p>
                <p className="text-sm font-mono" style={{ color: COLORS.textPrimary }}>
                  - {name || 'Attribute Name'}: {promptInstruction || '(instruction will appear here)'}
                </p>
              </div>
            </div>
          ) : (
            // Provided List: Show values management
            <div className="pt-6 border-t space-y-4" style={{ borderColor: COLORS.border }}>
              {/* Partition-by-axis picker */}
              <div className="p-3 rounded-lg border" style={{ borderColor: COLORS.border }}>
                <label className="block text-sm font-semibold mb-1" style={{ color: COLORS.textPrimary }}>
                  🧬 Partition values by
                </label>
                <p className="text-xs mb-2" style={{ color: COLORS.textSecondary }}>
                  Keep a separate value list for each value of another attribute (e.g. different Style options per Product Type, different Sub Style per Category).
                </p>
                <select
                  value={axisFieldKey}
                  onChange={(e) => setAxisFieldKey(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none"
                  style={{ borderColor: COLORS.border }}
                >
                  <option value="">— None (flat list) —</option>
                  {axisCandidates.map(c => (
                    <option key={c.fieldKey} value={c.fieldKey}>{c.name}</option>
                  ))}
                </select>
                {axisFieldKey && !selectedAxis && (
                  <p className="text-xs mt-2" style={{ color: COLORS.maroon }}>
                    Axis attribute has no values yet. Add values to it first, or switch to another axis.
                  </p>
                )}
              </div>

              {!usingAxis && (
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                  Values ({values.length})
                </label>

                {/* Add Value Input */}
                <div className="flex gap-2 mb-4">
                  <input
                    type="text"
                    value={newValue}
                    onChange={(e) => setNewValue(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddValue();
                      }
                    }}
                    placeholder="Add a value..."
                    className="flex-1 px-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-2"
                    style={{ borderColor: COLORS.border }}
                  />
                  <button
                    type="button"
                    onClick={handleAddValue}
                    className="px-4 py-2 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    <Plus size={18} />
                  </button>
                </div>

                {/* Values List */}
                {values.length > 0 ? (
                  <div className="border rounded-lg p-3 max-h-[300px] overflow-y-auto" style={{ borderColor: COLORS.border }}>
                    <div className="flex flex-wrap gap-2">
                      {values.map((value, index) => (
                        <div
                          key={index}
                          className="inline-flex items-center gap-2 px-3 py-1 border rounded-lg text-sm"
                          style={{ borderColor: COLORS.maroon, backgroundColor: COLORS.bgLight }}
                        >
                          <span style={{ color: COLORS.textPrimary }}>{value}</span>
                          <button
                            type="button"
                            onClick={() => handleRemoveValue(index)}
                            className="text-red-600 hover:text-red-800 transition-colors"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8 border-2 border-dashed rounded-lg" style={{ borderColor: COLORS.border }}>
                    <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                      No values added yet
                    </p>
                  </div>
                )}
              </div>
              )}

              {usingAxis && (
                <div>
                  <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                    Values by {selectedAxis.name}
                  </label>
                  {selectedAxis.values.length === 0 ? (
                    <div className="text-center py-8 border-2 border-dashed rounded-lg" style={{ borderColor: COLORS.border }}>
                      <p className="text-sm" style={{ color: COLORS.textSecondary }}>
                        {selectedAxis.name} has no values yet. Edit that attribute and add options there first.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedAxis.values.map(axisVal => (
                        <PtValueRow
                          key={axisVal}
                          pt={axisVal}
                          values={valuesByAxis[axisVal] || []}
                          onAdd={(v) => handleAddAxisValue(axisVal, v)}
                          onRemove={(idx) => handleRemoveAxisValue(axisVal, idx)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Prompt Instruction — only relevant for AI-generated categories */}
              {isAICategory && (
              <div>
                <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
                  Additional Prompt Instruction (Optional)
                </label>
                <textarea
                  value={promptInstruction}
                  onChange={(e) => setPromptInstruction(e.target.value)}
                  placeholder="e.g., Focus on visible characteristics"
                  className="w-full px-4 py-2 border rounded-lg text-sm focus:outline-none focus:border-2 resize-none"
                  style={{ borderColor: COLORS.border }}
                  rows={2}
                />
                <p className="text-xs mt-1" style={{ color: COLORS.textSecondary }}>
                  Optional guidance for the AI when choosing from values
                </p>
              </div>
              )}

              {/* Preview — only relevant for AI */}
              {isAICategory && (
              <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.bgLight }}>
                <p className="text-xs font-semibold mb-2" style={{ color: COLORS.textSecondary }}>
                  PROMPT PREVIEW:
                </p>
                <p className="text-sm font-mono" style={{ color: COLORS.textPrimary }}>
                  - {name || 'Attribute Name'} ({multiSelect ? 'select one or more, comma-separated' : 'select ONE only'}: {values.slice(0, 3).join(', ')}{values.length > 3 ? `, +${values.length - 3} more` : ''})
                  {promptInstruction && (
                    <>
                      <br />
                      <span className="text-xs opacity-70">  {promptInstruction}</span>
                    </>
                  )}
                </p>
              </div>
              )}
            </div>
          )}

          {/* Info Box */}
          <div className="p-4 rounded-lg" style={{ backgroundColor: COLORS.bgLight }}>
            <div className="flex gap-3">
              <Info size={20} style={{ color: COLORS.maroon, flexShrink: 0, marginTop: 2 }} />
              <div className="text-xs" style={{ color: COLORS.textSecondary }}>
                <p className="font-semibold mb-1">How Attributes Work</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Single-Select:</strong> AI picks exactly one value (e.g., "Closure Type: Hook")</li>
                  <li><strong>Multi-Select:</strong> AI can pick multiple values (e.g., "Theme: Antique, Temple")</li>
                  <li><strong>Provided List:</strong> AI chooses from your predefined values</li>
                  <li><strong>Free-form:</strong> AI generates custom text based on your instruction</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex gap-3 justify-end pt-4 border-t" style={{ borderColor: COLORS.border }}>
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border text-sm font-semibold rounded-lg transition-all hover:bg-gray-50"
              style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg"
              style={{ backgroundColor: COLORS.maroon }}
            >
              {editMode ? 'Update Attribute' : 'Create Attribute'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// =============================================================================
// CONFIGURATION PAGE - SECTION 6: PROMPT PREVIEW TAB
// =============================================================================
// Add this component after the AttributeModal component

const PromptPreviewTab = ({
  promptPreview,
  promptLoading,
  estimatedTokens,
  estimatedCost,
  onGeneratePreview
}) => {
  const [productType, setProductType] = useState('Earring');

  const productTypes = [
    'Earring', 'Necklace', 'Bangles', 'Bracelet', 'Ring', 
    'Bajuband', 'Pendant Set', 'Maang Tikka', 'Kamarband',
    'Payal', 'Nath', 'Brooch', 'Matil', 'Sheeshphool'
  ];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="bg-white border rounded-xl p-6" style={{ borderColor: COLORS.border }}>
        <div className="flex items-end gap-4">
          {/* Product Type Selector */}
          <div className="flex-1">
            <label className="block text-sm font-bold mb-2" style={{ color: COLORS.textPrimary }}>
              Product Type (for preview)
            </label>
            <select
              value={productType}
              onChange={(e) => setProductType(e.target.value)}
              className="w-full px-4 py-3 border rounded-lg text-sm focus:outline-none focus:border-2"
              style={{ borderColor: COLORS.border }}
            >
              {productTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          {/* Generate Button */}
          <button
            onClick={onGeneratePreview}
            disabled={promptLoading}
            className="px-8 py-3 text-white text-sm font-semibold rounded-lg transition-all hover:opacity-90 shadow-lg flex items-center gap-2"
            style={{ backgroundColor: COLORS.maroon }}
          >
            {promptLoading ? (
              <>
                <RefreshCw size={18} className="animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Eye size={18} />
                Generate Preview
              </>
            )}
          </button>
        </div>

        {/* Info */}
        <div className="mt-4 p-3 rounded-lg flex gap-3" style={{ backgroundColor: COLORS.bgLight }}>
          <Info size={18} style={{ color: COLORS.maroon, flexShrink: 0, marginTop: 2 }} />
          <p className="text-xs" style={{ color: COLORS.textSecondary }}>
            This shows the exact prompt that will be sent to the AI when generating attributes for a design.
            Only enabled attributes are included.
          </p>
        </div>
      </div>

      {/* Preview Display */}
      {promptPreview && (
        <div className="bg-white border rounded-xl overflow-hidden" style={{ borderColor: COLORS.border }}>
          {/* Stats Header */}
          <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: COLORS.border, backgroundColor: COLORS.bgLight }}>
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs font-semibold" style={{ color: COLORS.textSecondary }}>
                  ESTIMATED TOKENS
                </p>
                <p className="text-2xl font-bold" style={{ color: COLORS.maroon }}>
                  {estimatedTokens.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold" style={{ color: COLORS.textSecondary }}>
                  ESTIMATED COST
                </p>
                <p className="text-2xl font-bold" style={{ color: COLORS.maroon }}>
                  ${estimatedCost.toFixed(6)}
                </p>
                <p className="text-xs" style={{ color: COLORS.textSecondary }}>
                  per design (input only)
                </p>
              </div>
              <div>
                <p className="text-xs font-semibold" style={{ color: COLORS.textSecondary }}>
                  CHARACTERS
                </p>
                <p className="text-2xl font-bold" style={{ color: COLORS.textSecondary }}>
                  {promptPreview.length.toLocaleString()}
                </p>
              </div>
            </div>

            {/* Copy Button */}
            <button
              onClick={() => {
                navigator.clipboard.writeText(promptPreview);
                alert('Prompt copied to clipboard!');
              }}
              className="flex items-center gap-2 px-4 py-2 border text-sm font-semibold rounded-lg transition-all hover:bg-white"
              style={{ borderColor: COLORS.maroon, color: COLORS.maroon }}
            >
              <Copy size={16} />
              Copy Prompt
            </button>
          </div>

          {/* Prompt Content */}
          <div className="p-6">
            <pre
              className="text-xs font-mono whitespace-pre-wrap p-4 rounded-lg overflow-x-auto"
              style={{
                backgroundColor: '#f8f9fa',
                color: COLORS.textPrimary,
                maxHeight: '600px',
                overflowY: 'auto'
              }}
            >
              {promptPreview}
            </pre>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!promptPreview && !promptLoading && (
        <div className="text-center py-20 border-2 border-dashed rounded-xl" style={{ borderColor: COLORS.border }}>
          <Eye size={64} className="mx-auto mb-4 opacity-20" />
          <h3 className="text-xl font-bold mb-2" style={{ color: COLORS.textPrimary }}>
            No Preview Generated
          </h3>
          <p className="text-sm mb-6" style={{ color: COLORS.textSecondary }}>
            Click "Generate Preview" to see how your prompt will look
          </p>
        </div>
      )}
    </div>
  );
};

// =============================================================================
// UPDATED DESIGN DETAIL MODAL - DYNAMIC TABS FROM CATEGORIES
// =============================================================================
// Replace your existing DesignDetailModal (lines 3164-3660) with this:

const DesignDetailModal = ({ design, attributes, attributesConfig, onUpdateAttribute, onVerify, onClose, onRerun, isRerunning, onEditDetails }) => {
  const [searchTerms, setSearchTerms] = React.useState({});
  const [expandedAttrs, setExpandedAttrs] = React.useState({});
  const [activeTab, setActiveTab] = React.useState('primary');
  const [originalAttributes] = React.useState({ ...attributes }); // Store AI original values
  
  // Extract categories and attributes from config
  const categories = attributesConfig.categories || {};
  const attributesData = attributesConfig.attributes || {};
  
  // Build dynamic attribute sections from categories
  const attributeSections = React.useMemo(() => {
    const sections = {};
    
    // Sort categories by order
    const sortedCategories = Object.entries(categories).sort((a, b) => 
      (a[1].order || 999) - (b[1].order || 999)
    );
    
    sortedCategories.forEach(([categoryId, categoryData]) => {
      // Skip manual categories — those are ERP/manual data, not AI-generated
      if (categories[categoryId]?.source === 'manual') return;
      // Get all attributes for this category (exclude core attributes)
      const categoryAttributes = Object.entries(attributesData)
        .filter(([attrName, attrConfig]) => attrConfig.category === categoryId && !attrConfig.core)
        .sort((a, b) => (a[1].categoryOrder || 999) - (b[1].categoryOrder || 999))
        .map(([attrName]) => attrName);
      
      if (categoryAttributes.length > 0) {
        sections[categoryId] = {
          title: categoryData.name || categoryId,
          emoji: categoryData.emoji || '📌',
          fields: categoryAttributes
        };
      }
    });
    
    return sections;
  }, [categories, attributesData]);
  
  // Set first category as default active tab
  React.useEffect(() => {
    const firstCategoryId = Object.keys(attributeSections)[0];
    if (firstCategoryId && !attributeSections[activeTab]) {
      setActiveTab(firstCategoryId);
    }
  }, [attributeSections]);

  const handleReset = () => {
    Object.keys(originalAttributes).forEach(field => {
      onUpdateAttribute(field, originalAttributes[field]);
    });
  };

  const handleRerun = async () => {
    await onRerun(design.designNumber);
  };

  const isValueUserAdded = (field, value) => {
    const originalValue = originalAttributes[field] || '';
    const originalValues = originalValue.split(',').map(v => v.trim()).filter(Boolean);
    return !originalValues.includes(value);
  };

  const renderAttributeField = (field) => {
    const config = attributesData[field];
    const searchTerm = searchTerms[field] || '';
    const isExpanded = expandedAttrs[field] || false;
    
    // Check if it's a free-form field (no values or behaviorType is free-form)
    const isFreeForm = config && (config.behaviorType === 'free-form' || config.values.length === 0);
    
    // Free-form fields - use textarea for long content, input for short
    if (isFreeForm) {
      // Long content fields get textarea
      if (field === 'Product_Description' || field === 'SEO_Keywords' || 
          field === 'Outfit Suggestion' || field === 'Accessories to Pair') {
        return (
          <textarea
            value={attributes[field] || ''}
            onChange={(e) => onUpdateAttribute(field, e.target.value)}
            rows={field === 'Product_Description' ? 3 : 2}
            className="w-full px-3 py-2 border border-gray-200 focus:border-[#8B1D3C] focus:outline-none text-xs resize-none rounded-sm"
            style={{ fontSize: '12px' }}
          />
        );
      }
      
      // Short content fields get input
      return (
        <input
          type="text"
          value={attributes[field] || ''}
          onChange={(e) => onUpdateAttribute(field, e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#8B1D3C] focus:outline-none text-xs rounded-sm"
          style={{ fontSize: '12px' }}
        />
      );
    }
    
    if (!config) {
      // If attribute not in config, use text input as fallback
      return (
        <input
          type="text"
          value={attributes[field] || ''}
          onChange={(e) => onUpdateAttribute(field, e.target.value)}
          className="w-full px-3 py-2 border border-gray-200 focus:border-[#8B1D3C] focus:outline-none text-xs rounded-sm"
          style={{ fontSize: '12px' }}
        />
      );
    }

    // Multi-select with searchable chip-based UI
    if (config.multiSelect) {
      const currentValues = attributes[field] ? attributes[field].split(',').map(v => v.trim()).filter(Boolean) : [];
      const allAvailableValues = config.values.filter(v => !currentValues.includes(v));
      
      // Get original AI-generated values for this field
      const originalValue = originalAttributes[field] || '';
      const originalAIValues = originalValue.split(',').map(v => v.trim()).filter(Boolean);
      
      // Filter available values based on search
      const filteredValues = searchTerm 
        ? allAvailableValues.filter(v => v.toLowerCase().includes(searchTerm.toLowerCase()))
        : allAvailableValues;
      
      // Sort filtered values to put AI original values first
      const sortedFilteredValues = [...filteredValues].sort((a, b) => {
        const aIsOriginal = originalAIValues.includes(a);
        const bIsOriginal = originalAIValues.includes(b);
        if (aIsOriginal && !bIsOriginal) return -1;
        if (!aIsOriginal && bIsOriginal) return 1;
        return 0;
      });
      
      // Show first 5, or all if expanded
      const displayValues = isExpanded ? sortedFilteredValues : sortedFilteredValues.slice(0, 5);
      const hasMore = sortedFilteredValues.length > 5 && !isExpanded;
      const remainingCount = sortedFilteredValues.length - 5;
      
      return (
        <div className="space-y-2">
          {/* Selected Values Box with Inline Search */}
          <div 
            className="min-h-[40px] w-full px-3 py-2 border border-gray-200 rounded-sm bg-white flex flex-wrap gap-2 items-center cursor-text"
            onClick={(e) => {
              if (e.target.tagName !== 'BUTTON') {
                document.getElementById(`search-${field}`)?.focus();
              }
            }}
          >
            {currentValues.map((val, idx) => {
              const isUserAdded = isValueUserAdded(field, val);
              return (
                <div
                  key={idx}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-sm text-xs"
                  style={{ 
                    fontSize: '12px',
                    backgroundColor: isUserAdded ? 'rgb(249, 115, 22)' : 'rgb(139, 29, 60)' // Orange for user-added, maroon for AI
                  }}
                >
                  <span>{val}</span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      const newValues = currentValues.filter((_, i) => i !== idx).join(', ');
                      onUpdateAttribute(field, newValues);
                    }}
                    className="hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
                  >
                    <X size={12} />
                  </button>
                </div>
              );
            })}
            <input
              id={`search-${field}`}
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerms(prev => ({ ...prev, [field]: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && searchTerm === '' && currentValues.length > 0) {
                  // Remove last chip on backspace when input is empty
                  const newValues = currentValues.slice(0, -1).join(', ');
                  onUpdateAttribute(field, newValues);
                } else if (e.key === 'Enter' && displayValues.length > 0) {
                  // Add first match on Enter
                  e.preventDefault();
                  const newValues = [...currentValues, displayValues[0]].join(', ');
                  onUpdateAttribute(field, newValues);
                  setSearchTerms(prev => ({ ...prev, [field]: '' }));
                }
              }}
              placeholder={currentValues.length === 0 ? "Type to search..." : ""}
              className="flex-1 min-w-[120px] outline-none text-xs"
              style={{ fontSize: '12px', border: 'none', background: 'transparent' }}
            />
          </div>
          
          {/* Filtered Available Chips */}
          {displayValues.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {displayValues.map((val, idx) => {
                // Check if this value is one of the AI original values
                const isAIOriginal = originalAIValues.includes(val);
                
                return (
                  <button
                    key={idx}
                    onClick={() => {
                      const newValues = [...currentValues, val].join(', ');
                      onUpdateAttribute(field, newValues);
                      setSearchTerms(prev => ({ ...prev, [field]: '' }));
                    }}
                    className={`flex items-center gap-1.5 px-3 py-1.5 ${isAIOriginal ? 'text-white' : 'text-gray-700'} rounded-sm text-xs transition-colors ${!isAIOriginal && 'bg-gray-100 hover:bg-gray-200 border border-gray-200'}`}
                    style={{ 
                      fontSize: '12px',
                      backgroundColor: isAIOriginal ? 'rgb(139, 29, 60)' : undefined
                    }}
                  >
                    <span>{val}</span>
                    <X size={12} className={isAIOriginal ? 'text-white' : 'text-gray-500'} />
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Show More/Less Button */}
          {!searchTerm && allAvailableValues.length > 5 && (
            <button
              onClick={() => setExpandedAttrs(prev => ({ ...prev, [field]: !isExpanded }))}
              className="text-xs font-semibold transition-colors hover:underline"
              style={{ color: '#6B7280', fontSize: '11px' }}
            >
              {isExpanded ? '− Show less' : `+ Show ${remainingCount} more`}
            </button>
          )}
          
          {/* No matches message */}
          {searchTerm && filteredValues.length === 0 && (
            <p className="text-xs text-gray-400" style={{ fontSize: '11px' }}>
              No matches found
            </p>
          )}
        </div>
      );
    }

    // Single-select with searchable chip-based UI
    const currentValue = attributes[field] || '';
    const allAvailableValues = config.values.filter(v => v !== currentValue);
    
    // Get original AI-generated value for this field
    const originalAIValue = originalAttributes[field] || '';
    
    // Filter available values based on search
    const filteredValues = searchTerm 
      ? allAvailableValues.filter(v => v.toLowerCase().includes(searchTerm.toLowerCase()))
      : allAvailableValues;
    
    // Sort filtered values to put AI original value first
    const sortedFilteredValues = [...filteredValues].sort((a, b) => {
      const aIsOriginal = a === originalAIValue;
      const bIsOriginal = b === originalAIValue;
      if (aIsOriginal && !bIsOriginal) return -1;
      if (!aIsOriginal && bIsOriginal) return 1;
      return 0;
    });
    
    // Show first 5, or all if expanded
    const displayValues = isExpanded ? sortedFilteredValues : sortedFilteredValues.slice(0, 5);
    const hasMore = sortedFilteredValues.length > 5 && !isExpanded;
    const remainingCount = sortedFilteredValues.length - 5;
    
    return (
      <div className="space-y-2">
        {/* Selected Value Box with Inline Search */}
        <div 
          className="min-h-[40px] w-full px-3 py-2 border border-gray-200 rounded-sm bg-white flex items-center gap-2 cursor-text"
          onClick={(e) => {
            if (e.target.tagName !== 'BUTTON') {
              document.getElementById(`search-${field}`)?.focus();
            }
          }}
        >
          {currentValue && (
            <div
              className="flex items-center gap-1.5 px-3 py-1.5 text-white rounded-sm text-xs"
              style={{ 
                fontSize: '12px',
                backgroundColor: currentValue === originalAIValue ? 'rgb(139, 29, 60)' : 'rgb(249, 115, 22)' // Maroon for AI, orange for user-modified
              }}
            >
              <span>{currentValue}</span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onUpdateAttribute(field, '');
                }}
                className="hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
              >
                <X size={12} />
              </button>
            </div>
          )}
          <input
            id={`search-${field}`}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerms(prev => ({ ...prev, [field]: e.target.value }))}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && searchTerm === '' && currentValue) {
                // Remove current value on backspace when input is empty
                onUpdateAttribute(field, '');
              } else if (e.key === 'Enter' && displayValues.length > 0) {
                // Select first match on Enter
                e.preventDefault();
                onUpdateAttribute(field, displayValues[0]);
                setSearchTerms(prev => ({ ...prev, [field]: '' }));
              }
            }}
            placeholder={!currentValue ? "Type to search..." : ""}
            className="flex-1 min-w-[120px] outline-none text-xs"
            style={{ fontSize: '12px', border: 'none', background: 'transparent' }}
          />
        </div>
        
        {/* Filtered Available Chips */}
        {displayValues.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {displayValues.map((val, idx) => {
              // Check if this value is the AI original value
              const isAIOriginal = val === originalAIValue;
              
              return (
                <button
                  key={idx}
                  onClick={() => {
                    onUpdateAttribute(field, val);
                    setSearchTerms(prev => ({ ...prev, [field]: '' }));
                  }}
                  className={`flex items-center gap-1.5 px-3 py-1.5 ${isAIOriginal ? 'text-white' : 'text-gray-700'} rounded-sm text-xs transition-colors ${!isAIOriginal && 'bg-gray-100 hover:bg-gray-200 border border-gray-200'}`}
                  style={{ 
                    fontSize: '12px',
                    backgroundColor: isAIOriginal ? 'rgb(139, 29, 60)' : undefined
                  }}
                >
                  <span>{val}</span>
                  <X size={12} className={isAIOriginal ? 'text-white' : 'text-gray-500'} />
                </button>
              );
            })}
          </div>
        )}
        
        {/* Show More/Less Button */}
        {!searchTerm && allAvailableValues.length > 5 && (
          <button
            onClick={() => setExpandedAttrs(prev => ({ ...prev, [field]: !isExpanded }))}
            className="text-xs font-semibold transition-colors hover:underline"
            style={{ color: '#6B7280', fontSize: '11px' }}
          >
            {isExpanded ? '− Show less' : `+ Show ${remainingCount} more`}
          </button>
        )}
        
        {/* No matches message */}
        {searchTerm && filteredValues.length === 0 && (
          <p className="text-xs text-gray-400" style={{ fontSize: '11px' }}>
            No matches found
          </p>
        )}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 overflow-y-auto">
      <div className="min-h-screen px-4 py-8">
        <div className="bg-white mx-auto shadow-2xl" style={{ width: '95%', maxWidth: '1400px' }}>
          {/* Header with Reset Button */}
          <div className="px-6 py-4 flex items-center justify-between border-b border-gray-100" style={{ backgroundColor: COLORS.maroon }}>
            <div>
              <h2 className="font-serif text-xl text-white">Design {design.designNumber}</h2>
              <p className="text-white opacity-75 text-xs mt-1">{design.productType} • {design.baseMaterial}</p>
              {design.rerunCount > 0 && (
                <div className="flex items-center gap-1 mt-1">
                  <RefreshCw size={10} className="text-white opacity-60" />
                  <span className="text-white opacity-60 text-[10px] font-medium">
                    Rerun {design.rerunCount}x
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-3">
              {onEditDetails && (
                <button
                  onClick={() => onEditDetails(design.designNumber)}
                  className="px-4 py-2 border border-white text-white text-xs font-semibold tracking-wider uppercase transition-colors hover:bg-white hover:bg-opacity-10 rounded-sm flex items-center gap-2"
                  title="Edit product details, core attributes and combinations"
                >
                  <Settings size={14} />
                  EDIT DETAILS
                </button>
              )}
              <button
                onClick={handleReset}
                className="px-4 py-2 bg-white text-xs font-semibold tracking-wider uppercase transition-colors hover:opacity-90 rounded-sm"
                style={{ color: COLORS.maroon }}
              >
                RESET
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors text-white"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="p-6">
            <div className="grid lg:grid-cols-[400px_1fr] gap-6">
              {/* Image Column */}
              <div className="lg:sticky lg:top-8 h-fit">
                <div className="bg-gray-50 border border-gray-100 rounded-sm overflow-hidden">
                  <img
                    src={design.imageUrl}
                    alt={design.designNumber}
                    className="w-full aspect-[4/5] object-cover"
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="600" height="750"%3E%3Crect fill="%23f5f5f5" width="600" height="750"/%3E%3Ctext fill="%23999" font-family="serif" font-size="20" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image Available%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
              </div>

              {/* Tabs and Content Column */}
              <div className="space-y-4">
                {/* Tab Navigation - DYNAMIC */}
                <div className="relative">
                  <div 
                    className="flex gap-2 border-b border-gray-200 overflow-x-auto pb-2"
                    style={{ 
                      scrollbarWidth: 'thin',
                      WebkitOverflowScrolling: 'touch'
                    }}
                  >
                    {Object.entries(attributeSections).map(([key, section]) => (
                      <button
                        key={key}
                        onClick={() => setActiveTab(key)}
                        className={`px-4 py-2 text-xs font-semibold tracking-wider uppercase transition-colors whitespace-nowrap flex items-center gap-2 flex-shrink-0 ${
                          activeTab === key
                            ? 'border-b-2 text-gray-900'
                            : 'text-gray-500 hover:text-gray-700'
                        }`}
                        style={{ borderColor: activeTab === key ? COLORS.maroon : 'transparent' }}
                      >
                        <span className="text-lg">{section.emoji}</span>
                        <span>{section.title}</span>
                      </button>
                    ))}
                  </div>
                  {/* Scroll hint */}
                  {Object.keys(attributeSections).length > 4 && (
                    <div className="absolute right-0 top-0 bottom-2 w-8 bg-gradient-to-l from-white pointer-events-none" />
                  )}
                </div>

                {/* Tab Content - DYNAMIC */}
                <div className="bg-white border border-gray-100 p-6 rounded-sm shadow-sm min-h-[500px]">
                  {(() => {
                    const hasAttrs = attributes && Object.keys(attributes).length > 0 && !attributes.Error;
                    if (!hasAttrs) {
                      return (
                        <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
                          <div className="w-14 h-14 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: '#FEF2F2', border: '1px solid #FCA5A5' }}>
                            <AlertTriangle size={26} style={{ color: COLORS.maroon }} />
                          </div>
                          <h3 className="font-serif text-xl text-gray-800 mb-2">AI processing failed</h3>
                          <p className="text-sm max-w-md mb-6" style={{ color: '#6B7280' }}>
                            {design.error || attributes?.Error || 'No attributes were generated for this design. This usually means the design was missing context (ERP master or Enrichment fields) at the time of processing.'}
                          </p>
                          <button
                            onClick={handleRerun}
                            disabled={isRerunning}
                            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white transition-opacity hover:opacity-90 disabled:opacity-50"
                            style={{ backgroundColor: COLORS.maroon }}
                          >
                            <RefreshCw size={16} className={isRerunning ? 'animate-spin' : ''} />
                            {isRerunning ? 'Rerunning…' : 'Rerun'}
                          </button>
                        </div>
                      );
                    }
                    return attributeSections[activeTab] && (
                      <>
                        <h3 className="font-serif text-lg text-gray-800 mb-4 pb-3 border-b-2 flex items-center gap-2" style={{ borderColor: COLORS.maroon }}>
                          <span className="text-2xl">{attributeSections[activeTab].emoji}</span>
                          <span>{attributeSections[activeTab].title}</span>
                        </h3>
                        <div className="space-y-4">
                          {attributeSections[activeTab].fields
                            .filter(field => {
                              // Show all attributes that exist in the design
                              return attributes[field] !== undefined;
                            })
                            .map(field => (
                              <div key={field}>
                                <label className="block text-[10px] font-bold tracking-wider uppercase mb-2" style={{ color: '#374151' }}>
                                  {field}
                                </label>
                                {renderAttributeField(field)}
                              </div>
                            ))}
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={handleRerun}
                    disabled={isRerunning}
                    className="flex-1 py-3 bg-gray-600 text-white text-xs font-semibold tracking-wider uppercase transition-all hover:opacity-90 flex items-center justify-center gap-2 shadow-lg rounded-sm disabled:opacity-50"
                  >
                    <RefreshCw size={16} className={isRerunning ? 'animate-spin' : ''} />
                    {isRerunning ? 'RERUNNING...' : 'RERUN'}
                  </button>
                  <button
                    onClick={onVerify}
                    className="flex-1 py-3 text-white text-xs font-semibold tracking-wider uppercase transition-all hover:opacity-90 flex items-center justify-center gap-2 shadow-lg rounded-sm"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    <Shield size={16} />
                    VERIFY & APPROVE
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// DesignEnrichmentModal
// Mirrors the visual language of DesignDetailModal (code is intentionally
// duplicated per Decision 10 — do NOT extract shared helpers).
// =============================================================================

const DesignEnrichmentModal = ({ designNumber, designs = [], coreAttributes, onClose, onSaved, onNavigate }) => {
  const confirm = useConfirm();
  const [design, setDesign] = React.useState(null);
  const [formData, setFormData] = React.useState({});
  const [originalFormData, setOriginalFormData] = React.useState({});
  const [activeTab, setActiveTab] = React.useState('product_details');
  const [saving, setSaving] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [fetchError, setFetchError] = React.useState(null);
  const [chipSearchTerms, setChipSearchTerms] = React.useState({});
  const [chipExpanded, setChipExpanded] = React.useState({});
  const [comboFormData, setComboFormData] = React.useState({});
  const [originalComboFormData, setOriginalComboFormData] = React.useState({});
  // Tracks which combination-tab multi-select cell currently has its picker open,
  // keyed as `${combinationNumber}::${fieldKey}`. Inline-flow picker (not a popover)
  // so the dropdown isn't clipped by the table's overflow container.
  const [openComboCell, setOpenComboCell] = React.useState(null);

  const formModel = React.useMemo(() => buildFormModel(coreAttributes), [coreAttributes]);
  const comboFieldKeys = React.useMemo(() => formModel.combination.map(f => f.fieldKey), [formModel]);

  // Reset activeTab if the current tab disappears (e.g., admin disables every
  // combination attribute mid-session) so the modal body never goes blank.
  React.useEffect(() => {
    const valid = new Set(['product_details', 'core_attributes']);
    if (formModel.combination.length > 0) valid.add('combination');
    if (!valid.has(activeTab)) setActiveTab('product_details');
  }, [activeTab, formModel.combination.length]);

  const buildFormData = React.useCallback((d) => initEnrichmentForm(d, coreAttributes), [coreAttributes]);
  const buildComboData = React.useCallback((d) => buildComboFormData(d, comboFieldKeys), [comboFieldKeys]);

  const fetchDesign = React.useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const url = `${config.apiBaseUrl}/api/enrichment/designs?design_number=${encodeURIComponent(designNumber)}&per_page=1`;
      const resp = await config.apiFetch(url);
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const items = data.designs || [];
      if (!items.length) { setFetchError('Design not found'); return; }
      const d = items[0];
      setDesign(d);
      const fd = buildFormData(d);
      setFormData(fd);
      setOriginalFormData(fd);
      const cfd = buildComboData(d);
      setComboFormData(cfd);
      setOriginalComboFormData(cfd);
      setChipSearchTerms({});
      setChipExpanded({});
    } catch (e) {
      setFetchError(e.message);
    } finally {
      setLoading(false);
    }
  }, [designNumber, buildFormData, buildComboData]);

  React.useEffect(() => { fetchDesign(); }, [fetchDesign]);

  const isDirty = JSON.stringify(formData) !== JSON.stringify(originalFormData)
    || JSON.stringify(comboFormData) !== JSON.stringify(originalComboFormData);

  const handleClose = async () => {
    if (isDirty) {
      const ok = await confirm({
        title: 'Discard unsaved changes?',
        message: "You have edits that haven't been saved. Close anyway?",
        confirmLabel: 'Discard',
        cancelLabel: 'Keep editing',
        tone: 'danger',
      });
      if (!ok) return;
    }
    onClose();
  };

  const setField = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  // Required-ness is driven by the attribute config's `required` flag (admin
  // controls it from the Configuration page). Falls back to the caller's
  // default only when the attribute isn't tracked in config at all (built-in
  // identity fields like itemName/imageUrl/baseMaterial).
  // Also demotes to optional when the attribute has a values-by-axis map and
  // the current axis value has no master values — star is misleading when
  // there's literally nothing to select.
  const isRequiredFor = (fieldKey, fallback = true) => {
    const cfg = coreAttributes?.[fieldKey];
    if (cfg && typeof cfg.required === 'boolean' && !cfg.required) return false;

    // New: valuesByAxis. axis is stored as a fieldKey (e.g., "productType").
    if (cfg && cfg.valuesByAxis && cfg.valuesByAxis.axis) {
      const axisVal = formData[cfg.valuesByAxis.axis];
      if (axisVal) {
        const list = cfg.valuesByAxis.values?.[axisVal] || [];
        if (list.length === 0) return false;
      }
    } else if (cfg && cfg.valuesByProductType) {
      // Legacy fallback
      const pt = formData.productType;
      if (pt) {
        const ptValues = cfg.valuesByProductType[pt] || [];
        if (ptValues.length === 0) return false;
      }
    }

    if (cfg && typeof cfg.required === 'boolean') return cfg.required;
    return fallback;
  };

  const REQUIRED = [
    ...requiredKeys(formModel, isRequiredFor, 'product_details'),
    ...requiredKeys(formModel, isRequiredFor, 'core_attributes'),
  ];
  const COMBO_REQUIRED = requiredKeys(formModel, isRequiredFor, 'combination');

  // Filter out fields the current product type has no master data for —
  // those stay visible but are not blocking.
  const activeRequired = REQUIRED.filter(f => isRequiredFor(f));
  const activeComboRequired = COMBO_REQUIRED.filter(f => isRequiredFor(f));

  const designComplete = activeRequired.every(f => {
    const v = formData[f];
    return v !== undefined && v !== null && String(v).trim() !== '';
  });
  const combosComplete = Object.keys(comboFormData).length > 0 &&
    Object.values(comboFormData).every(combo =>
      activeComboRequired.every(f => {
        const v = combo[f];
        return v !== undefined && v !== null && String(v).trim() !== '';
      })
    );
  const isComplete = designComplete && combosComplete;

  const handleSave = async (triggerAI) => {
    if (!design) return;
    const cn = design.combinations?.[0]?.combinationNumber;
    if (!cn) { notify.error('No combination found for this design'); return; }

    const endpoint = `${config.apiBaseUrl}/api/enrichment/combinations/${cn}`;

    const payload = buildEnrichmentPayload(formData, coreAttributes);

    setSaving(true);
    try {
      const doSave = async (force) => {
        const url = force ? `${endpoint}?force=true` : endpoint;
        const resp = await config.apiFetch(url, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
        return { status: resp.status, data: await resp.json() };
      };

      let { status, data } = await doSave(false);

      if (status === 409 && data.needs_confirmation) {
        const disagreeMsg = Object.entries(data.disagreements || {})
          .map(([f, vals]) => `${f}: ${vals.join(' / ')}`)
          .join('\n');
        const ok = await confirm({
          title: 'Variants disagree',
          message: 'Saving will overwrite different values on the other variants.',
          details: disagreeMsg,
          confirmLabel: 'Overwrite',
          cancelLabel: 'Cancel',
          tone: 'danger',
        });
        if (!ok) {
          setSaving(false);
          return;
        }
        ({ status, data } = await doSave(true));
      }

      if (status === 200 && data.success !== false) {
        // Save combination-level fields per combination
        const comboSavePromises = [];
        for (const [comboCn, comboFields] of Object.entries(comboFormData)) {
          const orig = originalComboFormData[comboCn] || {};
          const changed = Object.keys(comboFields).some(k => comboFields[k] !== (orig[k] || ''));
          if (changed) {
            comboSavePromises.push(
              config.apiFetch(`${config.apiBaseUrl}/api/enrichment/combinations/${comboCn}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(comboFields),
              })
            );
          }
        }
        if (comboSavePromises.length > 0) {
          const comboResults = await Promise.all(comboSavePromises);
          const comboFailures = comboResults.filter(r => !r.ok);
          if (comboFailures.length > 0) {
            notify.error(`Design saved, but ${comboFailures.length} combination(s) failed. Please retry.`);
            await fetchDesign();
            return;
          }
        }

        onSaved();
        const currentIdx = designs.findIndex(d => d.designNumber === designNumber);
        const nextDesign = designs.slice(currentIdx + 1).find(d => d.enrichmentStatus !== 'complete')
                        || designs.slice(0, currentIdx).find(d => d.enrichmentStatus !== 'complete');
        if (triggerAI) {
          if (data.complete && combosComplete) {
            // Fire AI generation. The strict-mode backend handles failures and writes
            // the reason to processingError, which surfaces on the enrichment card.
            try {
              const aiResp = await config.apiFetch(config.endpoints.designsGenerate, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ designNumbers: [designNumber] }),
              });
              const aiData = await aiResp.json();
              if (aiResp.ok && aiData.success) {
                notify.success('Saved. AI processing started — Verification queue will populate when it completes.');
              } else {
                notify.error(aiData.error || `AI trigger failed (HTTP ${aiResp.status})`);
              }
            } catch (aiErr) {
              notify.error('AI trigger failed: ' + aiErr.message);
            }

            if (nextDesign && onNavigate) {
              onNavigate(nextDesign.designNumber);
            } else {
              onClose();
            }
          } else {
            const missing = [...(data.emptyFields || []), ...(data.missingNumericFields || [])];
            notify.info(`Saved — still missing: ${missing.join(', ') || 'none'}. Fill required fields before processing.`);
            await fetchDesign();
          }
        } else {
          notify.success('Draft saved');
          if (nextDesign && onNavigate) {
            onNavigate(nextDesign.designNumber);
          } else {
            await fetchDesign();
          }
        }
      } else {
        notify.error('Save failed: ' + (data.error || data.message || 'Unknown error'));
      }
    } catch (e) {
      notify.error('Save failed: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const attrValues = (fieldKey) => {
    const cfg = coreAttributes[fieldKey];
    if (!cfg) return [];
    // New: valuesByAxis — axis is the fieldKey of the partitioning attribute.
    if (cfg.valuesByAxis && cfg.valuesByAxis.axis) {
      const axisVal = formData[cfg.valuesByAxis.axis];
      if (axisVal && cfg.valuesByAxis.values?.[axisVal]) {
        return cfg.valuesByAxis.values[axisVal];
      }
    }
    // Legacy fallback
    if (cfg.valuesByProductType && formData.productType && cfg.valuesByProductType[formData.productType]) {
      return cfg.valuesByProductType[formData.productType];
    }
    return cfg.values || [];
  };

  const renderChipField = (key, label, required, multiSelect) => {
    const opts = attrValues(key);
    const searchTerm = chipSearchTerms[key] || '';
    const isExpanded = chipExpanded[key] || false;
    const setSearch = (val) => setChipSearchTerms(prev => ({ ...prev, [key]: val }));
    const setExpanded = (val) => setChipExpanded(prev => ({ ...prev, [key]: val }));

    // Fallback: no options available → plain text input
    if (opts.length === 0) {
      return (
        <div key={key}>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
            {label}{required && <span style={{ color: COLORS.maroon }}> *</span>}
          </label>
          <input
            type="text"
            value={formData[key] || ''}
            onChange={e => setField(key, e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
            style={{ borderColor: COLORS.border, '--tw-ring-color': COLORS.maroon }}
          />
        </div>
      );
    }

    if (multiSelect) {
      // Multi-select chip UI (used for Occasions)
      const currentValues = formData[key] ? formData[key].split(',').map(v => v.trim()).filter(Boolean) : [];
      const available = opts.filter(v => !currentValues.includes(v));
      const filtered = searchTerm ? available.filter(v => v.toLowerCase().includes(searchTerm.toLowerCase())) : available;
      const displayed = isExpanded ? filtered : filtered.slice(0, 5);
      const hasMore = filtered.length > 5;
      return (
        <div key={key}>
          <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
            {label}{required && <span style={{ color: COLORS.maroon }}> *</span>}
          </label>
          <div className="space-y-2">
            <div
              className="min-h-[40px] w-full px-3 py-2 border rounded-lg bg-white flex flex-wrap gap-2 items-center cursor-text"
              style={{ borderColor: COLORS.border }}
              onClick={e => { if (e.target.tagName !== 'BUTTON') document.getElementById(`chip-${key}`)?.focus(); }}
            >
              {currentValues.map((val, idx) => (
                <div key={idx} className="flex items-center gap-1.5 px-3 py-1 text-white rounded text-xs" style={{ backgroundColor: COLORS.maroon }}>
                  <span>{val}</span>
                  <button onClick={e => { e.stopPropagation(); const next = currentValues.filter((_, i) => i !== idx).join(', '); setField(key, next); }} className="hover:bg-white/20 rounded-full transition-colors"><X size={11} /></button>
                </div>
              ))}
              <input
                id={`chip-${key}`}
                type="text"
                value={searchTerm}
                onChange={e => setSearch(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Backspace' && searchTerm === '' && currentValues.length > 0) { setField(key, currentValues.slice(0, -1).join(', ')); }
                  else if (e.key === 'Enter' && displayed.length > 0) { e.preventDefault(); setField(key, [...currentValues, displayed[0]].join(', ')); setSearch(''); }
                }}
                placeholder={currentValues.length === 0 ? 'Type to search…' : ''}
                className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
              />
            </div>
            {displayed.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {displayed.map((val, idx) => (
                  <button key={idx} onClick={() => { setField(key, [...currentValues, val].join(', ')); setSearch(''); }}
                    className="flex items-center gap-1 px-3 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 border text-gray-700 transition-colors"
                    style={{ borderColor: COLORS.border }}>
                    {val} <X size={10} className="text-gray-400" />
                  </button>
                ))}
              </div>
            )}
            {!searchTerm && hasMore && (
              <button onClick={() => setExpanded(!isExpanded)} className="text-xs hover:underline" style={{ color: COLORS.textSecondary }}>
                {isExpanded ? '− Show less' : `+ Show ${filtered.length - 5} more`}
              </button>
            )}
            {searchTerm && filtered.length === 0 && <p className="text-xs text-gray-400">No matches found</p>}
          </div>
        </div>
      );
    }

    // Single-select chip UI
    const currentValue = formData[key] || '';
    const available = opts.filter(v => v !== currentValue);
    const filtered = searchTerm ? available.filter(v => v.toLowerCase().includes(searchTerm.toLowerCase())) : available;
    const displayed = isExpanded ? filtered : filtered.slice(0, 5);
    const hasMore = filtered.length > 5;
    return (
      <div key={key}>
        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
          {label}{required && <span style={{ color: COLORS.maroon }}> *</span>}
        </label>
        <div className="space-y-2">
          <div
            className="min-h-[40px] w-full px-3 py-2 border rounded-lg bg-white flex flex-wrap gap-2 items-center cursor-text"
            style={{ borderColor: COLORS.border }}
            onClick={e => { if (e.target.tagName !== 'BUTTON') document.getElementById(`chip-${key}`)?.focus(); }}
          >
            {currentValue && (
              <div className="flex items-center gap-1.5 px-3 py-1 text-white rounded text-xs" style={{ backgroundColor: COLORS.maroon }}>
                <span>{currentValue}</span>
                <button onClick={e => { e.stopPropagation(); setField(key, ''); if (key === 'productType') { setField('style', ''); setField('subStyle', ''); } }} className="hover:bg-white/20 rounded-full transition-colors"><X size={11} /></button>
              </div>
            )}
            <input
              id={`chip-${key}`}
              type="text"
              value={searchTerm}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Backspace' && searchTerm === '' && currentValue) { setField(key, ''); if (key === 'productType') { setField('style', ''); setField('subStyle', ''); } }
                else if (e.key === 'Enter' && displayed.length > 0) { e.preventDefault(); setField(key, displayed[0]); if (key === 'productType') { setField('style', ''); setField('subStyle', ''); } setSearch(''); }
              }}
              placeholder={!currentValue ? 'Type to search…' : ''}
              className="flex-1 min-w-[100px] outline-none text-sm bg-transparent"
            />
          </div>
          {displayed.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {displayed.map((val, idx) => (
                <button key={idx} onClick={() => { setField(key, val); if (key === 'productType') { setField('style', ''); setField('subStyle', ''); } setSearch(''); }}
                  className="flex items-center gap-1 px-3 py-1 rounded text-xs bg-gray-100 hover:bg-gray-200 border text-gray-700 transition-colors"
                  style={{ borderColor: COLORS.border }}>
                  {val} <X size={10} className="text-gray-400" />
                </button>
              ))}
            </div>
          )}
          {!searchTerm && hasMore && (
            <button onClick={() => setExpanded(!isExpanded)} className="text-xs hover:underline" style={{ color: COLORS.textSecondary }}>
              {isExpanded ? '− Show less' : `+ Show ${filtered.length - 5} more`}
            </button>
          )}
          {searchTerm && filtered.length === 0 && <p className="text-xs text-gray-400">No matches found</p>}
        </div>
      </div>
    );
  };

  const renderInput = (key, label, type = 'text', required = false) => (
    <div key={key}>
      <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>
        {label}{required && <span style={{ color: COLORS.maroon }}> *</span>}
      </label>
      <input
        type={type}
        step={type === 'number' ? '0.01' : undefined}
        value={formData[key] ?? ''}
        onChange={e => setField(key, e.target.value)}
        className="w-full px-3 py-2 border rounded-lg text-sm focus:outline-none focus:ring-2"
        style={{ borderColor: COLORS.border, '--tw-ring-color': COLORS.maroon }}
      />
    </div>
  );

  const renderField = (f) => {
    if (f.widget === 'number') return renderInput(f.fieldKey, f.label, 'number', isRequiredFor(f.fieldKey));
    if (f.widget === 'text')   return renderInput(f.fieldKey, f.label, 'text', isRequiredFor(f.fieldKey));
    return renderChipField(f.fieldKey, f.label, isRequiredFor(f.fieldKey), f.widget === 'chips-multi');
  };


  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center overflow-hidden p-8">
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl flex flex-col"
        style={{ height: 'calc(100vh - 4rem)', maxHeight: 'calc(100vh - 4rem)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-5 border-b flex-shrink-0" style={{ borderColor: COLORS.border }}>
          <div>
            <p className="text-xs font-semibold tracking-wider uppercase mb-0.5" style={{ color: COLORS.textSecondary }}>
              Catalog Enrichment
            </p>
            <h2 className="text-xl font-bold" style={{ color: COLORS.textPrimary }}>
              {loading ? 'Loading…' : (design?.itemName || design?.designNumber || designNumber)}
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} style={{ color: COLORS.textSecondary }} />
          </button>
        </div>

        {/* Body */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-20">
            <RefreshCw size={32} className="animate-spin" style={{ color: COLORS.maroon }} />
          </div>
        )}
        {fetchError && (
          <div className="flex-1 flex items-center justify-center py-20 text-red-500">{fetchError}</div>
        )}
        {!loading && !fetchError && design && (
          <div className="flex flex-1 overflow-hidden">
            {/* Left: sticky image + identity */}
            <div className="w-64 flex-shrink-0 border-r p-6 flex flex-col gap-4" style={{ borderColor: COLORS.border }}>
              <div className="aspect-square rounded-xl overflow-hidden bg-gray-100">
                <img
                  src={design.imageUrl}
                  alt={design.itemName || designNumber}
                  className="w-full h-full object-cover"
                  onError={e => { e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect fill="%23f3f4f6" width="100" height="100"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="12">No Image</text></svg>'; }}
                />
              </div>
              <div className="space-y-2 text-xs" style={{ color: COLORS.textSecondary }}>
                <div className="flex justify-between">
                  <span className="font-bold uppercase tracking-wider">Design</span>
                  <span style={{ color: COLORS.textPrimary }}>{design.designNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold uppercase tracking-wider">Variants</span>
                  <span style={{ color: COLORS.textPrimary }}>{design.variantCount || 1}</span>
                </div>
                <div className="flex justify-between">
                  <span className="font-bold uppercase tracking-wider">Status</span>
                  <span
                    className="px-2 py-0.5 rounded-full text-[10px] font-semibold"
                    style={{
                      backgroundColor: design.enrichmentStatus === 'complete' ? '#D1FAE5' : '#FEE2E2',
                      color: design.enrichmentStatus === 'complete' ? '#065F46' : COLORS.maroon,
                    }}
                  >
                    {design.enrichmentStatus === 'complete' ? 'Complete' : 'Incomplete'}
                  </span>
                </div>
              </div>
            </div>

            {/* Right: tabs + content */}
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Tabs */}
              <div className="flex border-b px-6 pt-4 gap-1 flex-shrink-0" style={{ borderColor: COLORS.border }}>
                {[
                  { id: 'product_details', label: 'Product Details' },
                  { id: 'core_attributes', label: 'Core Attributes' },
                  // Combination Details only when the Configuration has combination-tab
                  // attributes; the tab content renders the existing per-variant table.
                  ...(formModel.combination.length > 0
                    ? [{ id: 'combination', label: 'Combination Details' }]
                    : []),
                ].map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className="px-5 py-2.5 text-sm font-semibold rounded-t-lg border-b-2 transition-colors"
                    style={{
                      borderColor: activeTab === tab.id ? COLORS.maroon : 'transparent',
                      color: activeTab === tab.id ? COLORS.maroon : COLORS.textSecondary,
                    }}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Tab content */}
              <div className="flex-1 overflow-y-auto p-6 space-y-5">
                {activeTab === 'product_details' && (
                  <>
                    {/* Read-only identity */}
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>Design Number</label>
                        <div className="px-3 py-2 rounded-lg text-sm bg-gray-50 border" style={{ borderColor: COLORS.border, color: COLORS.textPrimary }}>{design.designNumber}</div>
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold tracking-wider uppercase mb-1.5" style={{ color: COLORS.textSecondary }}>Combination Numbers</label>
                        <div className="flex flex-wrap gap-1">
                          {(design.combinations || []).map(c => (
                            <span key={c.combinationNumber} className="px-2 py-0.5 rounded text-xs border" style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}>
                              {c.combinationNumber}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-5">
                      {formModel.product_details.length === 0 && (
                        <p className="text-xs text-gray-400">No Product Details attributes are enabled in Configuration.</p>
                      )}
                      {formModel.product_details.map(renderField)}
                    </div>
                  </>
                )}

                {activeTab === 'core_attributes' && (
                  <div className="space-y-5">
                    {formModel.core_attributes.length === 0 && (
                      <p className="text-xs text-gray-400">No Core Attributes are enabled in Configuration.</p>
                    )}
                    {formModel.core_attributes.map(renderField)}
                  </div>
                )}

                {activeTab === 'combination' && (
                  <CombinationTab
                    design={design}
                    combinationFields={formModel.combination}
                    comboFormData={comboFormData}
                    setComboFormData={setComboFormData}
                    isRequiredFor={isRequiredFor}
                    attrValues={attrValues}
                    openComboCell={openComboCell}
                    setOpenComboCell={setOpenComboCell}
                  />
                )}
              </div>

              {/* Action bar */}
              <div className="flex items-center justify-between px-6 py-4 border-t flex-shrink-0 bg-white" style={{ borderColor: COLORS.border }}>
                <div className="flex items-center gap-2 text-xs" style={{ color: isComplete ? '#065F46' : '#92400E' }}>
                  {isComplete
                    ? <><CheckCircle size={14} /> All required fields complete</>
                    : <><AlertTriangle size={14} /> Fill required fields (*) to process</>}
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => { setFormData({ ...originalFormData }); setComboFormData({ ...originalComboFormData }); }}
                    disabled={saving || !isDirty}
                    className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border rounded-lg transition-all hover:bg-gray-50 disabled:opacity-40"
                    style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                  >
                    Reset
                  </button>
                  <button
                    onClick={() => handleSave(false)}
                    disabled={saving}
                    className="px-4 py-2 text-xs font-semibold tracking-wider uppercase border rounded-lg transition-all hover:bg-gray-50 disabled:opacity-50 flex items-center gap-2"
                    style={{ borderColor: COLORS.border, color: COLORS.textSecondary }}
                  >
                    <Save size={13} />
                    {saving ? 'Saving…' : 'Save Draft'}
                  </button>
                  <button
                    onClick={() => handleSave(true)}
                    disabled={saving || !isComplete}
                    className="px-5 py-2 text-xs font-semibold tracking-wider uppercase text-white rounded-lg shadow-md transition-all hover:opacity-90 disabled:opacity-50 flex items-center gap-2"
                    style={{ backgroundColor: COLORS.maroon }}
                  >
                    <Sparkles size={13} />
                    {saving ? 'Saving…' : 'Save & Process'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default App;